import { Component, Fragment, Suspense, createContext, forwardRef, lazy, useContext, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { APP_VERSION, CHANGELOG } from "./changelog";
import type {
  ActiveWorkSession,
  AddManualWorkSessionRequest,
  AddTicketMessageRequest,
  AdminDashboardData,
  ClientDashboardData,
  ClientOptionRecord,
  ClientRecord,
  ClientSourceOption,
  ClientsPageResponse,
  ClientStats,
  CreateClientRequest,
  CreateDepartmentRequest,
  CreateEmployeeRequest,
  CreateProjectRequest,
  CreateTicketRequest,
  CreateLeaveRequestRequest,
  CreateTaskCommentRequest,
  CreateTaskRequest,
  ChangePasswordRequest,
  DashboardResponse,
  DepartmentRecord,
  DocumentRecord,
  EmployeeDashboardData,
  EmployeeRecord,
  LeaveRequestRecord,
  LeaveType,
  ProjectBudgetResponse,
  ProjectRecord,
  RecurringSeriesRecord,
  ReorderTaskRequest,
  ReportGranularity,
  ResetPasswordRequest,
  IntegrationRecord,
  IntegrationType,
  CreateIntegrationRequest,
  UpdateIntegrationRequest,
  TestIntegrationResponse,
  TaskBillingResponse,
  SessionUser,
  TaskBudgetType,
  TaskCommentRecord,
  TaskDetailRecord,
  TaskPriority,
  TaskStatus,
  TaskSummary,
  ThemeKey,
  TicketAttachmentRecord,
  TicketDetailRecord,
  TicketRecord,
  TimeReportResponse,
  UpdateClientRequest,
  UpdateMyProfileRequest,
  UpdateProjectRequest,
  UpdateRecurringSeriesRequest,
  UpdateTaskRequest,
  UpdateClientContactsRequest,
  UpdateEmployeeRequest,
  UpdateWorkSessionRequest,
  UploadAvatarRequest,
  UpsertClientProfileRequest,
  UserRole,
  ViewColumnConfig,
  NoteDetailRecord,
  NoteSummary,
  NotificationListResponse,
  NotificationRecord,
  ReminderKind,
  ReminderRecord,
  WorkSessionRecord
} from "@platform/types";
import {
  addTaskChecklistItem,
  addTicketMessage,
  addManualWorkSession,
  changePassword,
  closeTicket,
  createClient,
  createDepartment,
  createEmployee,
  createLeaveRequest,
  createNote,
  createProject,
  createRecurringTaskSeries,
  createReminder,
  createTask,
  createTaskComment,
  createTicket,
  decideLeaveRequest,
  deleteAvatar,
  HttpError,
  deleteClient,
  deleteDocument,
  deleteEmployee,
  deleteNote,
  deleteProject,
  deleteRecurringSeries,
  deleteReminder,
  deleteTask,
  deleteTaskChecklistItem,
  deleteTaskComment,
  deleteWorkSession,
  downloadDocument,
  downloadTicketAttachment,
  ensureAppUpdateNotification,
  fetchDocuments,
  fetchNotifications,
  fetchTicketAttachmentBlob,
  fetchLeaveRequests,
  fetchNoteDetail,
  fetchNotes,
  fetchNotesForClient,
  fetchRemindersForClient,
  fetchTaskBilling,
  fetchTaskComments,
  fetchTaskDetail,
  fetchTaskReminders,
  fetchReminders,
  fetchCollapsedSubtaskIds,
  fetchDashboard,
  fetchProjectBudget,
  fetchTicketDetail,
  fetchTimeReport,
  fetchViewPreference,
  fetchClientSources,
  fetchClientsPage,
  fetchClientById,
  fetchTaskArchive,
  fetchClientStats,
  fetchClientOptions,
  fetchVisibleClientSourceKeys,
  saveVisibleClientSourceKeys,
  fetchIntegrations,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  testIntegrationConnection,
  syncIntegration,
  login,
  markAllNotificationsRead,
  markNotificationRead,
  reopenTicket,
  reorderTask,
  requestPasswordReset,
  resetPassword,
  resolveAssetUrl,
  saveCollapsedSubtaskIds,
  saveViewPreference,
  sendEmployeePasswordResetLink,
  setEmployeePassword,
  setTaskPaid,
  startTaskSession,
  stopTaskSession,
  updateClient,
  updateMyProfile,
  updateNote,
  updateNoteAccess,
  updateProject,
  updateRecurringSeries,
  updateReminder,
  updateTask,
  updateTaskChecklistItem,
  updateTaskComment,
  updateClientContacts,
  updateClientProfile,
  updateWorkSession,
  uploadAvatar,
  uploadDocument,
  updateEmployee
} from "../lib/api";
import {
  buildTaskNestOrReorderTargetProps,
  buildTaskReorderTargetProps,
  useTaskDragSource,
  useTaskDropTarget,
  useTaskReorderTarget
} from "./dragAndDrop";
import { getPolishHolidayName } from "../lib/polishHolidays";
import { readFileAsBase64 } from "../lib/files";

// Edytor notatek (BlockNote) to najciezsza czesc aplikacji - ladowany dopiero przy otwarciu notatki.
const NoteBodyEditor = lazy(() =>
  import("../features/notes/NoteBodyEditor")
    .then((module) => {
      window.sessionStorage.removeItem("note-editor-reload");
      return module;
    })
    .catch((error) => {
      // Po wdrozeniu nowej wersji stary plik edytora znika z serwera - otwarta karta przeladowuje
      // sie raz, zeby pobrac aktualna wersje aplikacji (flaga chroni przed petla przeladowan).
      if (!window.sessionStorage.getItem("note-editor-reload")) {
        window.sessionStorage.setItem("note-editor-reload", "1");
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw error;
    })
);

type RoleView = "CLIENT" | "REMOTE_EMPLOYEE" | "EMPLOYEE" | "ADMIN";
type MenuSection = {
  title: string;
  items: MenuItem[];
};
type MenuItem = {
  key: string;
  label: string;
};

const roleLabels: Record<RoleView, string> = {
  CLIENT: "Panel klienta",
  REMOTE_EMPLOYEE: "Pracownik zdalny",
  EMPLOYEE: "Panel pracownika",
  ADMIN: "Panel administratora"
};

const AUTH_TOKEN_KEY = "auth_token";
const AUTH_USER_KEY = "auth_user";
const ACTIVE_ROLE_KEY = "active_role";
const ACTIVE_MENU_KEY = "active_menu";
const PENDING_TICKET_KEY = "pending_ticket_id";
const PENDING_NOTE_KEY = "pending_note_id";
const PENDING_NEW_TASK_KEY = "pending_new_task";
const CLIENT_DETAIL_PREFIX = "client-detail:";
const PROJECT_DETAIL_PREFIX = "project-detail:";
const TASK_DETAIL_PREFIX = "task-detail:";
const EMPLOYEE_DETAIL_PREFIX = "employee-detail:";
const REMOTE_EMPLOYEE_PREFIX = "remote-employee:";

const TICKET_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: "bug", label: "Błąd" },
  { value: "question", label: "Pytanie" },
  { value: "change", label: "Zmiana" },
  { value: "other", label: "Inne" }
];

function ticketCategoryLabel(value?: string) {
  return TICKET_CATEGORIES.find((item) => item.value === value)?.label ?? value;
}

const roleMenus: Record<RoleView, MenuSection[]> = {
  CLIENT: [
    {
      title: "Glówne",
      items: [
        { key: "client-dashboard", label: "Pulpit" },
        { key: "client-tasks", label: "Moje zadania" },
        { key: "client-orders", label: "Nowe zlecenie" }
      ]
    },
    {
      title: "Obsługa",
      items: [
        { key: "client-tickets", label: "Tickety" },
        { key: "client-comments", label: "Komentarze" }
      ]
    },
    {
      title: "Konto",
      items: [
        { key: "client-finance", label: "Finanse" },
        { key: "client-reports", label: "Raporty" },
        { key: "client-documents", label: "Dokumenty" },
        { key: "client-company", label: "Dane firmy" },
        { key: "client-contacts", label: "Osoby kontaktowe" }
      ]
    }
  ],
  EMPLOYEE: [
    {
      title: "Praca",
      items: [
        { key: "employee-tasks", label: "Moje zadania" },
        { key: "employee-calendar", label: "Kalendarz" },
        { key: "employee-kanban", label: "Kanban" },
        { key: "employee-all-tasks", label: "Wszystkie zadania" },
        { key: "employee-unassigned", label: "Oczekujące zadania" },
        { key: "employee-archive", label: "Archiwum" },
        { key: "employee-notifications", label: "Powiadomienia" },
        { key: "notes", label: "Notatki" },
        { key: "reminders", label: "Przypomnienia" }
      ]
    },
    {
      title: "CRM",
      items: [
        { key: "employee-clients", label: "Klienci" },
        { key: "employee-projects", label: "Projekty" }
      ]
    },
    {
      title: "Operacje",
      items: [
        { key: "employee-tickets", label: "Tickety" }
      ]
    },
    {
      title: "Konto",
      items: [
        { key: "employee-summary", label: "Moje raporty" },
        { key: "employee-leave", label: "Urlopy" },
        { key: "employee-profile", label: "Profil" },
        { key: "employee-settings", label: "Ustawienia" },
        { key: "employee-changelog", label: "Changelog" }
      ]
    }
  ],
  REMOTE_EMPLOYEE: [
    {
      title: "Praca",
      items: [
        { key: "employee-tasks", label: "Moje zadania" },
        { key: "employee-calendar", label: "Kalendarz" },
        { key: "employee-kanban", label: "Kanban" },
        { key: "employee-notifications", label: "Powiadomienia" },
        { key: "notes", label: "Notatki" },
        { key: "reminders", label: "Przypomnienia" }
      ]
    },
    {
      title: "Konto",
      items: [
        { key: "employee-summary", label: "Moje raporty" },
        { key: "employee-profile", label: "Profil" },
        { key: "employee-settings", label: "Ustawienia" }
      ]
    }
  ],
  ADMIN: [
    {
      title: "Praca",
      items: [
        { key: "employee-tasks", label: "Moje zadania" },
        { key: "employee-calendar", label: "Kalendarz" },
        { key: "employee-kanban", label: "Kanban" },
        { key: "employee-all-tasks", label: "Wszystkie zadania" },
        { key: "employee-unassigned", label: "Oczekujące zadania" },
        { key: "employee-archive", label: "Archiwum" },
        { key: "employee-notifications", label: "Powiadomienia" },
        { key: "notes", label: "Notatki" },
        { key: "reminders", label: "Przypomnienia" }
      ]
    },
    {
      title: "CRM",
      items: [
        { key: "admin-clients", label: "Klienci" },
        { key: "admin-projects", label: "Projekty" }
      ]
    },
    {
      title: "Operacje",
      items: [
        { key: "employee-tickets", label: "Tickety" }
      ]
    },
    {
      title: "Konto",
      items: [
        { key: "employee-summary", label: "Moje raporty" },
        { key: "employee-leave", label: "Urlopy" },
        { key: "employee-profile", label: "Profil" },
        { key: "employee-settings", label: "Ustawienia" },
        { key: "employee-changelog", label: "Changelog" }
      ]
    },
    {
      title: "Administracja",
      items: [
        { key: "admin-dashboard", label: "Przegląd" },
        { key: "admin-departments", label: "Działy" },
        { key: "admin-employees", label: "Pracownicy" },
        { key: "admin-tasks", label: "Zarządzanie zadaniami" },
        { key: "admin-tickets", label: "Wszystkie tickety" },
        { key: "admin-reports", label: "Raporty" },
        { key: "admin-billing", label: "Rozliczenia zadań" },
        { key: "admin-settings", label: "Konfiguracja" },
        { key: "admin-integrations", label: "Integracje" }
      ]
    }
  ]
};

const defaultMenuItemByRole: Record<RoleView, string> = {
  CLIENT: roleMenus.CLIENT[0].items[0].key,
  REMOTE_EMPLOYEE: roleMenus.REMOTE_EMPLOYEE[0].items[0].key,
  EMPLOYEE: roleMenus.EMPLOYEE[0].items[0].key,
  ADMIN: "employee-tasks"
};

function secondsToClock(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatDurationLabel(minutes: number) {
  const total = Math.max(0, Math.round(minutes));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function formatDateInputValue(value?: string) {
  if (!value) return "";
  return value.slice(0, 10);
}

function hasExplicitTime(date: Date) {
  // Bare "YYYY-MM-DD" values (no time picked) always parse as UTC midnight,
  // regardless of the viewer's local timezone - checking local hours/minutes
  // here would misdetect them as an explicit local time (e.g. 02:00 in
  // Europe/Warsaw) on every date-only task.
  return date.getUTCHours() !== 0 || date.getUTCMinutes() !== 0;
}

function formatTimeInputValue(value?: string) {
  const date = parseIsoDate(value);
  if (!date || !hasExplicitTime(date)) return "";
  return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
}

function combineDueDateAndTime(date: string, time: string): string | undefined {
  if (!date) return undefined;
  // A naive "YYYY-MM-DDTHH:MM:00" string has no offset, so it would be parsed
  // as local time by whichever machine reads it - the browser here, but the
  // API server too if sent as-is, which desyncs the stored instant whenever
  // the server's OS timezone isn't Europe/Warsaw. Resolving it to a Date now
  // (in the user's own timezone) and serializing with toISOString() bakes in
  // the correct UTC offset before it ever leaves the browser.
  if (!time) return date;
  return new Date(`${date}T${time}:00`).toISOString();
}

function formatTimeSuffix(date: Date) {
  if (!hasExplicitTime(date)) return "";
  return ` ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
}

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function parseIsoDate(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDueDateLabel(value?: string) {
  const date = parseIsoDate(value);
  if (!date) return "Bez terminu";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);

  const timeSuffix = formatTimeSuffix(date);
  if (diffDays === 0) return `Dzisiaj${timeSuffix}`;
  if (diffDays === 1) return `Jutro${timeSuffix}`;
  if (diffDays === -1) return `Wczoraj${timeSuffix}`;

  return `${target.getDate().toString().padStart(2, "0")}.${(target.getMonth() + 1).toString().padStart(2, "0")}.${target.getFullYear()}${timeSuffix}`;
}

function formatDueDateAbsolute(value?: string) {
  const date = parseIsoDate(value);
  if (!date) return "Bez terminu";

  return `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1).toString().padStart(2, "0")}.${date.getFullYear()}${formatTimeSuffix(date)}`;
}

function formatDateTimeLabel(value?: string) {
  const date = parseIsoDate(value);
  if (!date) return "Brak";

  return `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1).toString().padStart(2, "0")}.${date.getFullYear()} ${date
    .getHours()
    .toString()
    .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
}

function isClosedTaskStatus(status: TaskSummary["status"]) {
  return status === "DONE" || status === "BILLED";
}

/**
 * Koszt "minutowy" (bez zaokraglania w gore i bufora 5 min) - wylicza efektywna
 * stawke z juz policzonego (zaokraglonego) kosztu i mnozy ja przez dokladny czas.
 * To tylko preferencja wyswietlania danego uzytkownika, nie zmienia oficjalnego
 * rozliczenia zadania.
 */
function computePerMinuteCost(task: TaskSummary) {
  if (task.budgetType !== "HOURLY" || task.roundedHoursForClient <= 0) return task.costAmount;
  const effectiveRate = task.costAmount / task.roundedHoursForClient;
  return Math.round(effectiveRate * (task.trackedMinutes / 60) * 100) / 100;
}

function getTaskBucket(task: TaskSummary) {
  if (isClosedTaskStatus(task.status)) return "done" as const;
  const date = parseIsoDate(task.dueDate);
  if (!date) return "no-date" as const;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);

  if (diffDays < 0) return "overdue" as const;
  if (diffDays === 0) return "today" as const;
  if (diffDays === 1) return "tomorrow" as const;
  return "future" as const;
}

function orderTasksWithSubtasks(
  items: TaskSummary[],
  collapsedTaskIds?: Set<string>
): Array<{ task: TaskSummary; depth: number; hasChildren: boolean }> {
  const idsInList = new Set(items.map((task) => task.id));
  const childrenByParent = new Map<string, TaskSummary[]>();
  const topLevel: TaskSummary[] = [];

  for (const task of items) {
    if (task.parentTaskId && idsInList.has(task.parentTaskId)) {
      const list = childrenByParent.get(task.parentTaskId) ?? [];
      list.push(task);
      childrenByParent.set(task.parentTaskId, list);
    } else {
      topLevel.push(task);
    }
  }

  const result: Array<{ task: TaskSummary; depth: number; hasChildren: boolean }> = [];
  function visit(task: TaskSummary, depth: number, ancestorIds: Set<string>) {
    const children = childrenByParent.get(task.id) ?? [];
    result.push({ task, depth, hasChildren: children.length > 0 });
    if (collapsedTaskIds?.has(task.id)) return;
    for (const child of children) {
      if (ancestorIds.has(child.id)) continue;
      visit(child, depth + 1, new Set(ancestorIds).add(child.id));
    }
  }
  for (const task of topLevel) {
    visit(task, 0, new Set([task.id]));
  }
  return result;
}

const TASK_PAGE_SIZE = 20;

function paginateOrderedTasks(
  ordered: Array<{ task: TaskSummary; depth: number; hasChildren: boolean }>,
  page: number,
  pageSize: number
) {
  const clusters: Array<Array<{ task: TaskSummary; depth: number; hasChildren: boolean }>> = [];
  for (const entry of ordered) {
    if (entry.depth === 0 || clusters.length === 0) {
      clusters.push([entry]);
    } else {
      clusters[clusters.length - 1].push(entry);
    }
  }
  const totalPages = Math.max(1, Math.ceil(clusters.length / pageSize));
  const clampedPage = Math.min(Math.max(1, page), totalPages);
  const rows = clusters.slice((clampedPage - 1) * pageSize, clampedPage * pageSize).flat();
  return { rows, totalPages, page: clampedPage };
}

function dedupeRecurringOccurrences(items: TaskSummary[]): TaskSummary[] {
  const nextBySeriesId = new Map<string, TaskSummary>();
  const result: TaskSummary[] = [];

  for (const task of items) {
    if (!task.recurringSeriesId || isClosedTaskStatus(task.status)) {
      result.push(task);
      continue;
    }

    const current = nextBySeriesId.get(task.recurringSeriesId);
    if (!current || new Date(task.dueDate ?? 0).getTime() < new Date(current.dueDate ?? 0).getTime()) {
      nextBySeriesId.set(task.recurringSeriesId, task);
    }
  }

  result.push(...nextBySeriesId.values());
  return result;
}

function formatRecurrenceSummary(series: RecurringSeriesRecord): string {
  if (series.frequency === "INTERVAL") {
    return `Co ${series.intervalDays ?? 1} ${series.intervalDays === 1 ? "dzień" : "dni"}`;
  }

  const labels: Record<string, string> = { MON: "Pon", TUE: "Wt", WED: "Sr", THU: "Czw", FRI: "Pt", SAT: "Sob", SUN: "Nie" };
  const order = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const days = (series.daysOfWeek ?? []).slice().sort((a, b) => order.indexOf(a) - order.indexOf(b));
  return days.length ? days.map((day) => labels[day] ?? day).join(", ") : "Brak wybranych dni";
}

function formatBadgeValue(value: string, tone: "priority" | "status") {
  const statusLabels: Record<string, string> = {
    TODO: "Do zrobienia",
    IN_PROGRESS: "W toku",
    BLOCKED: "Zablokowane",
    REVIEW: "Do akceptacji",
    DONE: "Zakończone",
    TO_BE_BILLED: "Do rozliczenia",
    BILLED: "Rozliczone",
    ACTIVE: "Aktywny",
    OPEN: "Otwarte",
    CLOSED: "Zamknięte",
    RESOLVED: "Rozwiązane",
    WAITING_FOR_CLIENT: "Czeka na klienta",
    INVITED: "Zaproszony",
    ON_HOLD: "Wstrzymany",
    COMPLETED: "Zakończony"
  };
  const priorityLabels: Record<string, string> = {
    LOW: "Niski",
    MEDIUM: "Średni",
    HIGH: "Wysoki",
    URGENT: "Pilny"
  };

  return tone === "priority" ? priorityLabels[value] ?? value : statusLabels[value] ?? value;
}

function roleTone(role: UserRole) {
  if (role === "ADMIN") return "tone-admin";
  if (role === "REMOTE_EMPLOYEE") return "tone-employee";
  if (role === "EMPLOYEE") return "tone-employee";
  return "tone-client";
}

function formatClientOptionLabel(client: { companyName: string; sourceKey: string; sourceLabel: string }) {
  return client.sourceKey === "internal" ? client.companyName : `${client.companyName} (${client.sourceLabel})`;
}

// null = brak filtra (pokaz wszystkie zrodla klientow) - tak wyglada domyslny
// stan, dopoki uzytkownik nie zawezi listy w Ustawieniach.
const VisibleClientSourcesContext = createContext<Set<string> | null>(null);

// Standard "an action is in flight" tracker: wrap any async click/submit handler in run(...) to
// get a pending flag for free, so every button (create/delete/save/toggle) can show a spinner and
// disable itself while its request is running instead of looking frozen or accepting double-clicks.
function useAsyncAction() {
  const [pending, setPending] = useState(false);
  async function run(action: () => Promise<unknown> | unknown) {
    setPending(true);
    try {
      await action();
    } finally {
      setPending(false);
    }
  }
  return { pending, run };
}

function useVisibleClientOptions(clientOptions: ClientRecord[]): ClientRecord[] {
  const visibleSources = useContext(VisibleClientSourcesContext);
  return useMemo(() => {
    if (!visibleSources) return clientOptions;
    return clientOptions.filter((client) => visibleSources.has(client.sourceKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientOptions, visibleSources]);
}

function useVisibleClientSourcesProviderValue() {
  const [visibleSources, setVisibleSources] = useState<Set<string> | null>(null);

  useEffect(() => {
    void fetchVisibleClientSourceKeys().then((keys) => {
      if (keys) setVisibleSources(new Set(keys));
    });
  }, []);

  return visibleSources;
}

function roleBadge(role: UserRole) {
  if (role === "ADMIN") return "Administrator";
  if (role === "REMOTE_EMPLOYEE") return "Pracownik zdalny";
  if (role === "EMPLOYEE") return "Pracownik";
  return "Klient";
}

function employeeRoleLabel(role: EmployeeRecord["role"]) {
  if (role === "ADMIN") return "Administrator";
  if (role === "REMOTE_EMPLOYEE") return "Pracownik zdalny";
  return "Pracownik";
}

function employeeToUpdatePayload(employee: EmployeeRecord): UpdateEmployeeRequest {
  const [firstName = "", ...rest] = employee.fullName.split(" ");
  const lastName = rest.join(" ");
  return {
    firstName,
    lastName,
    email: employee.email,
    phone: employee.phone ?? "",
    role: employee.role,
    title: employee.title,
    hourlyRate: Number(employee.hourlyRate.replace(" zl", "")),
    status: employee.status,
    departmentIds: employee.departmentIds,
    canDeleteTasks: employee.canDeleteTasks,
    assignedProjectIds: employee.assignedProjectIds
  };
}

function clientToUpdatePayload(client: ClientRecord): UpdateClientRequest {
  return {
    companyName: client.companyName,
    billingEmail: client.billingEmail,
    phone: client.phone,
    vatNumber: client.vatNumber,
    accountManagerId: client.accountManagerId,
    status: client.status,
    emailsEnabled: client.emailsEnabled
  };
}

function projectToUpdatePayload(project: ProjectRecord): UpdateProjectRequest {
  return {
    clientId: project.clientId,
    name: project.name,
    description: project.description ?? "",
    status: project.status
  };
}

function toBadgeClass(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function StatusBadge({
  tone = "status",
  value
}: {
  tone?: "priority" | "status" | "ticket";
  value: string;
}) {
  const labelTone = tone === "priority" ? "priority" : "status";
  return <span className={`badge badge-${tone} badge-${toBadgeClass(value)}`}>{formatBadgeValue(value, labelTone)}</span>;
}

function getMenuLabel(role: RoleView, activeItem: string) {
  if (activeItem.startsWith(CLIENT_DETAIL_PREFIX)) {
    return "Karta firmy";
  }

  if (activeItem.startsWith(PROJECT_DETAIL_PREFIX)) {
    return "Karta projektu";
  }

  if (activeItem.startsWith(TASK_DETAIL_PREFIX)) {
    return "Karta zadania";
  }

  if (activeItem.startsWith(EMPLOYEE_DETAIL_PREFIX)) {
    return "Karta pracownika";
  }

  for (const section of roleMenus[role]) {
    const match = section.items.find((item) => item.key === activeItem);
    if (match) return match.label;
  }

  return roleLabels[role];
}

function buildMenuStateSnapshot(role: RoleView, activeItem: string) {
  const existing = window.localStorage.getItem(ACTIVE_MENU_KEY);
  const parsed = existing ? (JSON.parse(existing) as Partial<Record<RoleView, string>>) : {};

  return {
    ...parsed,
    [role]: activeItem
  };
}

function buildClientDetailKey(clientId: string) {
  return `${CLIENT_DETAIL_PREFIX}${clientId}`;
}

function buildProjectDetailKey(projectId: string) {
  return `${PROJECT_DETAIL_PREFIX}${projectId}`;
}

function parseClientDetailId(activeItem: string) {
  return activeItem.startsWith(CLIENT_DETAIL_PREFIX) ? activeItem.slice(CLIENT_DETAIL_PREFIX.length) : null;
}

function buildTaskDetailKey(taskId: string) {
  return `${TASK_DETAIL_PREFIX}${taskId}`;
}

function parseTaskDetailId(activeItem: string) {
  return activeItem.startsWith(TASK_DETAIL_PREFIX) ? activeItem.slice(TASK_DETAIL_PREFIX.length) : null;
}

function parseProjectDetailId(activeItem: string) {
  return activeItem.startsWith(PROJECT_DETAIL_PREFIX) ? activeItem.slice(PROJECT_DETAIL_PREFIX.length) : null;
}

function buildEmployeeDetailKey(employeeId: string) {
  return `${EMPLOYEE_DETAIL_PREFIX}${employeeId}`;
}

function parseEmployeeDetailId(activeItem: string) {
  return activeItem.startsWith(EMPLOYEE_DETAIL_PREFIX) ? activeItem.slice(EMPLOYEE_DETAIL_PREFIX.length) : null;
}

type RemoteEmployeeTab = "tasks" | "billing" | "billed";

function buildRemoteEmployeeKey(employeeId: string, tab: RemoteEmployeeTab) {
  return `${REMOTE_EMPLOYEE_PREFIX}${tab}:${employeeId}`;
}

function parseRemoteEmployeeKey(activeItem: string): { employeeId: string; tab: RemoteEmployeeTab } | null {
  if (!activeItem.startsWith(REMOTE_EMPLOYEE_PREFIX)) return null;
  const rest = activeItem.slice(REMOTE_EMPLOYEE_PREFIX.length);
  const separatorIndex = rest.indexOf(":");
  if (separatorIndex === -1) return null;
  const tab = rest.slice(0, separatorIndex);
  const employeeId = rest.slice(separatorIndex + 1);
  if (!employeeId || (tab !== "tasks" && tab !== "billing" && tab !== "billed")) return null;
  return { employeeId, tab };
}

function getInitials(fullName: string) {
  return fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function Avatar({
  avatarUrl,
  className,
  fullName,
  title
}: {
  avatarUrl?: string | null;
  className: string;
  fullName: string;
  title?: string;
}) {
  if (avatarUrl) {
    return <img alt={fullName} className={`${className} avatar-img`} src={resolveAssetUrl(avatarUrl)} title={title ?? fullName} />;
  }

  return (
    <span className={className} title={title ?? fullName}>
      {getInitials(fullName)}
    </span>
  );
}

function EntityLink({
  label,
  muted = false,
  onClick
}: {
  label: string;
  muted?: boolean;
  onClick?: () => void;
}) {
  if (!onClick) {
    return <span>{label}</span>;
  }

  return (
    <button
      className={muted ? "entity-link is-muted" : "entity-link"}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      type="button"
    >
      {label}
    </button>
  );
}

function AssigneeCluster({
  assignees,
  employees,
  editable = false,
  onChange
}: {
  assignees: TaskSummary["assignees"];
  employees: EmployeeRecord[];
  editable?: boolean;
  onChange?: (assigneeIds: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [openLeft, setOpenLeft] = useState(false);
  const selectedIds = assignees.map((assignee) => assignee.id);
  const clusterRef = useRef<HTMLDivElement | null>(null);

  function toggle(employeeId: string) {
    if (!onChange) return;
    const next = selectedIds.includes(employeeId) ? selectedIds.filter((id) => id !== employeeId) : [...selectedIds, employeeId];
    onChange(next);
  }

  function handleToggleOpen() {
    if (!editable) return;
    setOpen((current) => {
      const next = !current;
      if (next && clusterRef.current) {
        const rect = clusterRef.current.getBoundingClientRect();
        const menuWidth = 260;
        setOpenLeft(rect.left + menuWidth > window.innerWidth);
      }
      return next;
    });
  }

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (!clusterRef.current) return;
      if (!clusterRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div className="assignee-cluster" ref={clusterRef}>
      <button className={editable ? "assignee-stack is-editable" : "assignee-stack"} onClick={handleToggleOpen} type="button">
        {assignees.slice(0, 3).map((assignee) => (
          <Avatar avatarUrl={assignee.avatarUrl} className="assignee-avatar" fullName={assignee.fullName} key={assignee.id} />
        ))}
        {assignees.length === 0 ? <span className="assignee-add">+</span> : null}
        {assignees.length > 3 ? <span className="assignee-more">+{assignees.length - 3}</span> : null}
      </button>
      {editable && open ? (
        <div className={openLeft ? "assignee-menu is-flipped" : "assignee-menu"}>
          {employees.map((employee) => {
            const checked = selectedIds.includes(employee.id);
            return (
              <label className={checked ? "assignee-menu-item is-active" : "assignee-menu-item"} key={employee.id}>
                <input checked={checked} onChange={() => toggle(employee.id)} type="checkbox" />
                <Avatar avatarUrl={employee.avatarUrl} className="assignee-avatar small" fullName={employee.fullName} />
                <strong>{employee.fullName}</strong>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <circle cx="11" cy="11" fill="none" r="6.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="m20 20-4.3-4.3" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M12 3a4 4 0 0 0-4 4v1.2c0 1.3-.4 2.6-1.1 3.7L5.8 13.6A1 1 0 0 0 6.6 15h10.8a1 1 0 0 0 .8-1.4l-1.1-1.7A6.8 6.8 0 0 1 16 8.2V7a4 4 0 0 0-4-4Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path d="M10 18a2 2 0 0 0 4 0" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M5 7.5A2.5 2.5 0 0 1 7.5 5h9A2.5 2.5 0 0 1 19 7.5v6A2.5 2.5 0 0 1 16.5 16H10l-3.8 3v-3H7.5A2.5 2.5 0 0 1 5 13.5Z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M4 6h16M4 12h16M4 18h16" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <circle cx="12" cy="12" fill="none" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 7.8v4.6l3 1.8" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function Spinner({ label }: { label?: string } = {}) {
  return (
    <span className="spinner-inline" role="status">
      <span aria-hidden="true" className="spinner" />
      {label ? <span>{label}</span> : <span className="sr-only">Ładowanie...</span>}
    </span>
  );
}

function ChevronIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="m7 10 5 5 5-5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <rect fill="currentColor" height="10" rx="1.5" width="10" x="7" y="7" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="m4 16.8 9.9-9.9 3.2 3.2-9.9 9.9-4 0.8z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path d="m12.9 7.9 3.2 3.2" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function CalendarTodayIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <rect x="4" y="5.5" width="16" height="15" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M4 9.5h16M8 3.5v3M16 3.5v3" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
      <circle cx="12" cy="15" r="2.2" fill="currentColor" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M5 7h14M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-8 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function DragHandleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <circle cx="9" cy="6" fill="currentColor" r="1.6" />
      <circle cx="15" cy="6" fill="currentColor" r="1.6" />
      <circle cx="9" cy="12" fill="currentColor" r="1.6" />
      <circle cx="15" cy="12" fill="currentColor" r="1.6" />
      <circle cx="9" cy="18" fill="currentColor" r="1.6" />
      <circle cx="15" cy="18" fill="currentColor" r="1.6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <rect
        fill="none"
        height="10"
        rx="2"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.6"
        width="14"
        x="5"
        y="11"
      />
      <path
        d="M8 11V8a4 4 0 0 1 8 0v3"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <rect fill="none" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" width="18" x="3" y="5" />
      <path d="m3 6 9 7 9-7" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <circle cx="8" cy="15" fill="none" r="4" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M11 12 19 4m0 0v4m0-4h-4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function ColumnsIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <rect fill="none" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" width="18" x="3" y="4" />
      <path d="M9 4v16M15 4v16" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
    </svg>
  );
}

function EmptyBoxIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M3 8.5 12 4l9 4.5-9 4.5-9-4.5Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.6" />
      <path d="M3 8.5V16l9 4.5 9-4.5V8.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" />
      <path d="M12 13v7.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
    </svg>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="empty-state">
      <span className="empty-state-icon" aria-hidden="true">
        <EmptyBoxIcon />
      </span>
      <p>{message}</p>
    </div>
  );
}

type TopbarNotificationItem = {
  id: string;
  title: string;
  description: string;
  isUpdate?: boolean;
  notificationId?: string;
  linkType?: NotificationRecord["linkType"];
  linkId?: string;
};

function buildTopNotifications(activeRole: RoleView, dashboard: DashboardResponse): TopbarNotificationItem[] {
  const tickets =
    activeRole === "CLIENT" ? dashboard.client.tickets : activeRole === "ADMIN" ? dashboard.admin.tickets : dashboard.employee.tickets;

  return tickets
    .filter((ticket) => ticket.requiresAttention || ticket.hasUnreadMessage)
    .slice(0, 5)
    .map((ticket) => ({
      id: ticket.id,
      title: ticket.subject,
      description:
        activeRole === "CLIENT"
          ? ticket.hasUnreadMessage
            ? `Odpowiedź firmy • ${ticket.lastMessageAt}`
            : `${formatBadgeValue(ticket.status, "status")} • ${ticket.lastMessageAt}`
          : ticket.hasUnreadMessage
            ? `${ticket.clientName} • nowa wiadomość`
            : `${ticket.clientName} • ${formatBadgeValue(ticket.priority, "priority")}`
    }));
}

function TopBar({
  activeItem,
  activeRole,
  dashboard,
  onLogout,
  onNavigate,
  onOpenTask,
  onStopSession,
  onToggleMenu,
  onUpdateSessionUser,
  sessionUser
}: {
  activeItem: string;
  activeRole: RoleView;
  dashboard: DashboardResponse;
  onLogout: () => void;
  onNavigate: (itemKey: string) => void;
  onOpenTask: (taskId: string) => void;
  onStopSession: (sessionId: string) => void;
  onToggleMenu: () => void;
  onUpdateSessionUser: (user: SessionUser) => void;
  sessionUser: SessionUser;
}) {
  const [openPanel, setOpenPanel] = useState<null | "notifications" | "account">(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [stoppingSession, setStoppingSession] = useState(false);
  async function handleStopClick(sessionId: string) {
    setStoppingSession(true);
    try {
      await onStopSession(sessionId);
    } finally {
      setStoppingSession(false);
    }
  }
  const avatarInputRef = useRef<HTMLInputElement | null>(null);

  async function handleAvatarSelected(file: File | undefined) {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      window.alert("Wybierz plik obrazu.");
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      window.alert("Obraz jest za duży (limit 3 MB).");
      return;
    }

    setAvatarSaving(true);
    try {
      const base64Data = await readFileAsBase64(file);
      const { user } = await uploadAvatar({ fileName: file.name, mimeType: file.type, base64Data });
      onUpdateSessionUser(user);
    } finally {
      setAvatarSaving(false);
    }
  }

  async function handleRemoveAvatar() {
    setAvatarSaving(true);
    try {
      const { user } = await deleteAvatar();
      onUpdateSessionUser(user);
    } finally {
      setAvatarSaving(false);
    }
  }
  const notifications = buildTopNotifications(activeRole, dashboard);
  const activeSession = activeRole === "CLIENT" ? null : dashboard.employee.activeSession;
  const [now, setNow] = useState(() => Date.now());
  const topbarRef = useRef<HTMLElement | null>(null);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [unreadPersistentNotifications, setUnreadPersistentNotifications] = useState<NotificationRecord[]>([]);

  function reloadPersistentNotifications() {
    void fetchNotifications(1, 10)
      .then(({ items }) => setUnreadPersistentNotifications(items.filter((item) => !item.isRead)))
      .catch(() => setUnreadPersistentNotifications([]));
  }

  useEffect(() => {
    if (activeRole === "CLIENT") return;

    reloadPersistentNotifications();
    const timer = window.setInterval(reloadPersistentNotifications, 60000);
    return () => window.clearInterval(timer);
  }, [activeRole]);

  const myEmployeeRecord =
    activeRole === "CLIENT" ? undefined : dashboard.employee.teamMembers.find((member) => member.id === sessionUser.id);
  const hasUnseenUpdate =
    activeRole !== "CLIENT" && myEmployeeRecord != null && myEmployeeRecord.lastSeenChangelogVersion !== APP_VERSION && !updateDismissed;
  const [appUpdateNotificationId, setAppUpdateNotificationId] = useState<string | null>(null);

  useEffect(() => {
    if (!hasUnseenUpdate || appUpdateNotificationId) return;
    void ensureAppUpdateNotification({
      version: APP_VERSION,
      title: `Nowa wersja: ${APP_VERSION}`,
      body: CHANGELOG[0]?.items[0] ?? "Zobacz listę zmian w changelogu."
    })
      .then(({ id }) => setAppUpdateNotificationId(id))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnseenUpdate]);

  function handleDismissUpdateNotification() {
    setUpdateDismissed(true);
    onNavigate("employee-changelog");
    void updateMyProfile({ lastSeenChangelogVersion: APP_VERSION });
    if (appUpdateNotificationId) void markNotificationRead(appUpdateNotificationId);
  }

  const updateNotification: TopbarNotificationItem[] = hasUnseenUpdate
    ? [
        {
          id: "app-update",
          title: `Nowa wersja: ${APP_VERSION}`,
          description: CHANGELOG[0]?.items[0] ?? "Zobacz listę zmian w changelogu.",
          isUpdate: true
        }
      ]
    : [];

  const persistentNotificationItems: TopbarNotificationItem[] = unreadPersistentNotifications
    .filter((item) => !(hasUnseenUpdate && item.type === "app_update"))
    .map((item) => ({
      id: `notif:${item.id}`,
      title: item.title,
      description: item.body,
      notificationId: item.id,
      linkType: item.linkType,
      linkId: item.linkId
    }));

  const allNotifications: TopbarNotificationItem[] = [...updateNotification, ...persistentNotificationItems, ...notifications];

  function handleDismissPersistentNotification(event: React.MouseEvent, notificationId: string) {
    event.stopPropagation();
    setUnreadPersistentNotifications((current) => current.filter((item) => item.id !== notificationId));
    void markNotificationRead(notificationId);
  }

  function handleOpenNotificationItem(item: TopbarNotificationItem) {
    if (item.isUpdate) {
      handleDismissUpdateNotification();
    } else if (item.notificationId) {
      setUnreadPersistentNotifications((current) => current.filter((entry) => entry.id !== item.notificationId));
      void markNotificationRead(item.notificationId);
      if (item.linkType === "task" && item.linkId) {
        onOpenTask(item.linkId);
      } else if (item.linkType === "note" && item.linkId) {
        window.localStorage.setItem(PENDING_NOTE_KEY, item.linkId);
        onNavigate("notes");
      } else if (item.linkType === "ticket" && item.linkId) {
        window.localStorage.setItem(PENDING_TICKET_KEY, item.linkId);
        onNavigate(ticketsTarget);
      } else if (item.linkType === "employee") {
        onNavigate("employee-profile");
      } else if (item.linkType === "changelog") {
        onNavigate("employee-changelog");
      }
    } else {
      window.localStorage.setItem(PENDING_TICKET_KEY, item.id);
      onNavigate(ticketsTarget);
    }
    setOpenPanel(null);
  }

  useEffect(() => {
    if (!activeSession) return;

    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [activeSession]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!topbarRef.current) return;
      if (!topbarRef.current.contains(event.target as Node)) {
        setOpenPanel(null);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const tasksTarget = activeRole === "CLIENT" ? "client-tasks" : "employee-tasks";
  const pendingGoRef = useRef(false);

  useEffect(() => {
    function isTypingTarget(target: EventTarget | null) {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setShowSearch(true);
        return;
      }

      if (isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        setShowSearch(true);
        return;
      }

      if (event.key === "?") {
        event.preventDefault();
        setShowShortcuts(true);
        return;
      }

      if (event.key.toLowerCase() === "g") {
        pendingGoRef.current = true;
        window.setTimeout(() => {
          pendingGoRef.current = false;
        }, 900);
        return;
      }

      if (event.key.toLowerCase() === "t" && pendingGoRef.current) {
        pendingGoRef.current = false;
        onNavigate(tasksTarget);
        return;
      }

      if (event.key.toLowerCase() === "n" && activeRole !== "CLIENT") {
        event.preventDefault();
        window.localStorage.setItem(PENDING_NEW_TASK_KEY, "1");
        onNavigate(tasksTarget);
        window.dispatchEvent(new Event("shortcut:new-task"));
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeRole, onNavigate, tasksTarget]);

  const [searchNotes, setSearchNotes] = useState<NoteSummary[]>([]);

  useEffect(() => {
    if (!showSearch || activeRole === "CLIENT") return;
    void fetchNotes()
      .then(({ items }) => setSearchNotes(items))
      .catch(() => setSearchNotes([]));
  }, [showSearch, activeRole]);

  const elapsed = activeSession ? Math.max(0, Math.floor((now - new Date(activeSession.startedAt).getTime()) / 1000)) : 0;
  const settingsTarget = activeRole === "CLIENT" ? "client-company" : "employee-settings";
  const profileTarget = activeRole === "CLIENT" ? "client-contacts" : "employee-profile";
  const ticketsTarget = activeRole === "CLIENT" ? "client-tickets" : activeRole === "ADMIN" ? "admin-tickets" : "employee-tickets";
  const searchTasks = activeRole === "CLIENT" ? dashboard.client.tasks : dashboard.employee.tasks;
  const searchProjects = activeRole === "CLIENT" ? dashboard.client.projects : activeRole === "ADMIN" ? dashboard.admin.projects : dashboard.employee.projects;
  const searchTickets = activeRole === "CLIENT" ? dashboard.client.tickets : activeRole === "ADMIN" ? dashboard.admin.tickets : dashboard.employee.tickets;

  return (
    <header className="topbar" ref={topbarRef}>
      <button aria-label="Otwórz menu" className="topbar-menu-toggle" onClick={onToggleMenu} type="button">
        <MenuIcon />
      </button>
      <div className="topbar-title">
        <p className="eyebrow">Workspace</p>
        <strong>{getMenuLabel(activeRole, activeItem)}</strong>
        <span className="topbar-subtitle">{roleLabels[activeRole]}</span>
      </div>
      <div className="topbar-actions">
        <button
          aria-label="Szukaj (Ctrl+K)"
          className="topbar-button topbar-icon-button"
          onClick={() => setShowSearch(true)}
          title="Szukaj (Ctrl+K)"
          type="button"
        >
          <span className="topbar-icon">
            <SearchIcon />
          </span>
        </button>
        {activeRole !== "CLIENT" ? (
          <div className={activeSession ? "topbar-chip is-live" : "topbar-chip"}>
            {activeSession ? <span className="topbar-live-dot" aria-hidden="true" /> : null}
            <span className="topbar-icon timer-icon">
              <ClockIcon />
            </span>
            <div>
              <strong>{activeSession ? secondsToClock(elapsed) : "Brak sesji"}</strong>
              <span>{activeSession ? activeSession.taskTitle : "Licznik czasu"}</span>
            </div>
            {activeSession ? (
              <button
                className="topbar-timer-stop"
                disabled={stoppingSession}
                onClick={() => void handleStopClick(activeSession.id)}
                title="Zatrzymaj licznik"
                type="button"
              >
                {stoppingSession ? <Spinner /> : <StopIcon />}
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="topbar-dropdown">
          <button
            aria-label="Powiadomienia"
            className="topbar-button topbar-icon-button"
            onClick={() => setOpenPanel(openPanel === "notifications" ? null : "notifications")}
            type="button"
          >
            <span className="topbar-icon">
              <BellIcon />
            </span>
            {allNotifications.length > 0 ? <span className="topbar-badge">{allNotifications.length}</span> : null}
          </button>
          {openPanel === "notifications" ? (
            <div className="topbar-menu">
              <div className="topbar-menu-head">
                <strong>Powiadomienia</strong>
              </div>
              {allNotifications.length > 0 ? (
                allNotifications.map((item) => (
                  <div className="topbar-menu-item-wrap" key={item.id}>
                    <button className="topbar-menu-item" onClick={() => handleOpenNotificationItem(item)} type="button">
                      <strong>{item.title}</strong>
                      <span>{item.description}</span>
                    </button>
                    {item.notificationId ? (
                      <button
                        aria-label="Oznacz jako przeczytane"
                        className="topbar-menu-item-dismiss"
                        onClick={(event) => handleDismissPersistentNotification(event, item.notificationId!)}
                        title="Oznacz jako przeczytane"
                        type="button"
                      >
                        <CloseIcon />
                      </button>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="topbar-menu-empty">Brak nowych powiadomień.</div>
              )}
            </div>
          ) : null}
        </div>

        <div className="topbar-dropdown">
          <button className="topbar-button topbar-account-button" onClick={() => setOpenPanel(openPanel === "account" ? null : "account")} type="button">
            <Avatar avatarUrl={sessionUser.avatarUrl} className="topbar-avatar" fullName={sessionUser.fullName} />
            <span className="topbar-account-copy">
              <strong>{sessionUser.fullName}</strong>
              <em>{sessionUser.email}</em>
            </span>
            <span className="topbar-chevron">
              <ChevronIcon />
            </span>
          </button>
          {openPanel === "account" ? (
            <div className="topbar-menu account-menu">
              <div className="topbar-avatar-editor">
                <Avatar avatarUrl={sessionUser.avatarUrl} className="topbar-avatar large" fullName={sessionUser.fullName} />
                <div className="topbar-avatar-actions">
                  <input
                    accept="image/*"
                    hidden
                    onChange={(event) => void handleAvatarSelected(event.target.files?.[0])}
                    ref={avatarInputRef}
                    type="file"
                  />
                  <button
                    className="secondary compact-action"
                    disabled={avatarSaving}
                    onClick={() => avatarInputRef.current?.click()}
                    type="button"
                  >
                    {avatarSaving ? "Zapisywanie..." : "Zmień zdjęcie"}
                  </button>
                  {sessionUser.avatarUrl ? (
                    <button className="secondary compact-action" disabled={avatarSaving} onClick={() => void handleRemoveAvatar()} type="button">
                      Usuń zdjecie
                    </button>
                  ) : null}
                </div>
              </div>
              <button
                className="topbar-menu-item"
                onClick={() => {
                  onNavigate(profileTarget);
                  setOpenPanel(null);
                }}
                type="button"
              >
                <strong>Moje konto</strong>
                <span>Profil i dane użytkownika</span>
              </button>
              <button
                className="topbar-menu-item"
                onClick={() => {
                  onNavigate(settingsTarget);
                  setOpenPanel(null);
                }}
                type="button"
              >
                <strong>Ustawienia</strong>
                <span>Dostęp, konfiguracja i opcje</span>
              </button>
              <button
                className="topbar-menu-item"
                onClick={() => {
                  setShowShortcuts(true);
                  setOpenPanel(null);
                }}
                type="button"
              >
                <strong>Skróty klawiszowe</strong>
                <span>Lista dostępnych skrótów</span>
              </button>
              <button className="topbar-menu-item danger" onClick={onLogout} type="button">
                <strong>Wyloguj</strong>
                <span>Zakończ aktywna sesje</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
      {showShortcuts ? <KeyboardShortcutsModal onClose={() => setShowShortcuts(false)} showTaskShortcuts={activeRole !== "CLIENT"} /> : null}
      {showSearch ? (
        <GlobalSearch
          notes={searchNotes}
          onClose={() => setShowSearch(false)}
          onNavigate={onNavigate}
          onOpenTask={onOpenTask}
          projects={searchProjects}
          tasks={searchTasks}
          tickets={searchTickets}
          ticketsTarget={ticketsTarget}
        />
      ) : null}
    </header>
  );
}

function GlobalSearch({
  notes,
  onClose,
  onNavigate,
  onOpenTask,
  projects,
  tasks,
  tickets,
  ticketsTarget
}: {
  notes: NoteSummary[];
  onClose: () => void;
  onNavigate: (itemKey: string) => void;
  onOpenTask: (taskId: string) => void;
  projects: ProjectRecord[];
  tasks: TaskSummary[];
  tickets: TicketRecord[];
  ticketsTarget: string;
}) {
  const [query, setQuery] = useState("");
  const [matchedClients, setMatchedClients] = useState<ClientOptionRecord[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const needle = query.trim().toLowerCase();
  const maxResults = 6;

  // The full client directory is no longer preloaded into the app, so client matches are
  // searched on the server (same lean endpoint the client pickers already use) instead of
  // filtering an in-memory array.
  useEffect(() => {
    if (!needle) {
      setMatchedClients([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      fetchClientOptions({ search: needle })
        .then((items) => {
          if (!cancelled) setMatchedClients(items.slice(0, maxResults));
        })
        .catch(() => {});
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [needle]);

  const matchedTasks = needle
    ? tasks.filter((task) => `${task.title} ${task.clientName} ${task.projectName ?? ""}`.toLowerCase().includes(needle)).slice(0, maxResults)
    : [];
  const matchedProjects = needle
    ? projects.filter((project) => `${project.name} ${project.clientName}`.toLowerCase().includes(needle)).slice(0, maxResults)
    : [];
  const matchedTickets = needle
    ? tickets.filter((ticket) => `${ticket.subject} ${ticket.clientName}`.toLowerCase().includes(needle)).slice(0, maxResults)
    : [];
  const matchedNotes = needle ? notes.filter((note) => note.title.toLowerCase().includes(needle)).slice(0, maxResults) : [];

  const hasResults =
    matchedTasks.length + matchedClients.length + matchedProjects.length + matchedTickets.length + matchedNotes.length > 0;

  function goTo(itemKey: string) {
    onNavigate(itemKey);
    onClose();
  }

  function goToTicket(ticketId: string) {
    window.localStorage.setItem(PENDING_TICKET_KEY, ticketId);
    goTo(ticketsTarget);
  }

  function goToNote(noteId: string) {
    window.localStorage.setItem(PENDING_NOTE_KEY, noteId);
    goTo("notes");
  }

  function goToTask(taskId: string) {
    onOpenTask(taskId);
    onClose();
  }

  return (
    <Modal onClose={onClose} title="Szukaj" wide>
      <input
        className="global-search-input"
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Szukaj po zadaniu, kliencie, projekcie lub tickecie..."
        ref={inputRef}
        value={query}
      />
      {!needle ? <p className="placeholder-copy">Zacznij pisać, aby wyszukać.</p> : null}
      {needle && !hasResults ? <p className="placeholder-copy">Brak wynikow dla "{query}".</p> : null}

      {matchedTasks.length > 0 ? (
        <div className="global-search-group">
          <p className="eyebrow">Zadania</p>
          {matchedTasks.map((task) => (
            <button className="global-search-item" key={task.id} onClick={() => goToTask(task.id)} type="button">
              <strong>{task.title}</strong>
              <span>
                {task.clientName}
                {task.projectName ? ` • ${task.projectName}` : ""}
              </span>
              <span className="global-search-assignees">
                {task.assignees.length > 0 ? (
                  task.assignees.map((assignee) => (
                    <Avatar avatarUrl={assignee.avatarUrl} className="assignee-avatar small" fullName={assignee.fullName} key={assignee.id} />
                  ))
                ) : (
                  <span className="global-search-assignees-empty">Nieprzypisane</span>
                )}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {matchedClients.length > 0 ? (
        <div className="global-search-group">
          <p className="eyebrow">Klienci</p>
          {matchedClients.map((client) => (
            <button className="global-search-item" key={client.id} onClick={() => goTo(buildClientDetailKey(client.id))} type="button">
              <strong>{client.companyName}</strong>
              <span>{client.sourceLabel}</span>
            </button>
          ))}
        </div>
      ) : null}

      {matchedProjects.length > 0 ? (
        <div className="global-search-group">
          <p className="eyebrow">Projekty</p>
          {matchedProjects.map((project) => (
            <button className="global-search-item" key={project.id} onClick={() => goTo(buildProjectDetailKey(project.id))} type="button">
              <strong>{project.name}</strong>
              <span>{project.clientName}</span>
            </button>
          ))}
        </div>
      ) : null}

      {matchedTickets.length > 0 ? (
        <div className="global-search-group">
          <p className="eyebrow">Tickety</p>
          {matchedTickets.map((ticket) => (
            <button className="global-search-item" key={ticket.id} onClick={() => goToTicket(ticket.id)} type="button">
              <strong>{ticket.subject}</strong>
              <span>{ticket.clientName}</span>
            </button>
          ))}
        </div>
      ) : null}

      {matchedNotes.length > 0 ? (
        <div className="global-search-group">
          <p className="eyebrow">Notatki</p>
          {matchedNotes.map((note) => (
            <button className="global-search-item" key={note.id} onClick={() => goToNote(note.id)} type="button">
              <strong>{note.title}</strong>
              <span>{note.createdByName}</span>
            </button>
          ))}
        </div>
      ) : null}
    </Modal>
  );
}

function KeyboardShortcutsModal({ onClose, showTaskShortcuts }: { onClose: () => void; showTaskShortcuts: boolean }) {
  const shortcuts = [
    { keys: "Ctrl/Cmd + K", description: "Otwórz wyszukiwarkę" },
    { keys: "/", description: "Otwórz wyszukiwarkę" },
    ...(showTaskShortcuts
      ? [
          { keys: "N", description: "Nowe zadanie" },
          { keys: "G, T", description: "Przejdź do \"Moje zadania\"" }
        ]
      : []),
    { keys: "?", description: "Pokaz ten popup" }
  ];

  return (
    <Modal onClose={onClose} title="Skróty klawiszowe">
      <div className="shortcuts-list">
        {shortcuts.map((shortcut) => (
          <div className="shortcuts-item" key={shortcut.keys}>
            <kbd>{shortcut.keys}</kbd>
            <span>{shortcut.description}</span>
          </div>
        ))}
      </div>
      <p className="placeholder-copy">Skróty nie działają podczas pisania w polach tekstowych.</p>
    </Modal>
  );
}

function ToastStack({
  items,
  onDismiss
}: {
  items: Array<{ id: string; title: string; description: string }>;
  onDismiss: (id: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="floating-toasts" aria-live="polite">
      {items.map((item) => (
        <article className="floating-toast" key={item.id}>
          <div className="floating-toast-icon">
            <BellIcon />
          </div>
          <div className="floating-toast-copy">
            <strong>{item.title}</strong>
            <span>{item.description}</span>
          </div>
          <button className="floating-toast-close" onClick={() => onDismiss(item.id)} type="button">
            Zamknij
          </button>
        </article>
      ))}
    </div>
  );
}

function SidebarMenu({
  activeItem,
  onLogout,
  onSelect,
  role,
  remoteEmployees
}: {
  activeItem: string;
  onLogout: () => void;
  onSelect: (itemKey: string) => void;
  role: RoleView;
  remoteEmployees?: EmployeeRecord[];
}) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  function toggleExpanded(employeeId: string) {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  }

  return (
    <nav className="sidebar-nav" aria-label={`Menu ${roleLabels[role]}`}>
      {roleMenus[role].map((section) => (
        <Fragment key={section.title}>
          <div className="menu-section">
            <p className="menu-title">{section.title}</p>
            <div className="menu-items">
              {section.items.map((item) => (
                <button
                  className={activeItem === item.key ? "menu-item is-active" : "menu-item"}
                  key={item.key}
                  onClick={() => onSelect(item.key)}
                  type="button"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          {role === "ADMIN" && section.title === "Praca" && remoteEmployees ? (
            <div className="menu-section">
              <p className="menu-title">Zdalni</p>
              <div className="menu-items">
                {remoteEmployees.map((employee) => {
                  const isExpanded = expandedIds.has(employee.id);
                  return (
                    <div className="menu-tree-item" key={employee.id}>
                      <button className="menu-item menu-tree-toggle" onClick={() => toggleExpanded(employee.id)} type="button">
                        <span className="menu-tree-caret">{isExpanded ? "▾" : "▸"}</span>
                        {employee.fullName}
                      </button>
                      {isExpanded ? (
                        <div className="menu-items menu-tree-children">
                          <button
                            className={activeItem === buildRemoteEmployeeKey(employee.id, "tasks") ? "menu-item is-active" : "menu-item"}
                            onClick={() => onSelect(buildRemoteEmployeeKey(employee.id, "tasks"))}
                            type="button"
                          >
                            Zadania
                          </button>
                          <button
                            className={activeItem === buildRemoteEmployeeKey(employee.id, "billing") ? "menu-item is-active" : "menu-item"}
                            onClick={() => onSelect(buildRemoteEmployeeKey(employee.id, "billing"))}
                            type="button"
                          >
                            Do rozliczenia
                          </button>
                          <button
                            className={activeItem === buildRemoteEmployeeKey(employee.id, "billed") ? "menu-item is-active" : "menu-item"}
                            onClick={() => onSelect(buildRemoteEmployeeKey(employee.id, "billed"))}
                            type="button"
                          >
                            Rozliczone
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {remoteEmployees.length === 0 ? <span className="ghost-tag">Brak pracowników zdalnych</span> : null}
              </div>
            </div>
          ) : null}
        </Fragment>
      ))}
      <div className="menu-section">
        <p className="menu-title">Sesja</p>
        <div className="menu-items">
          <button className="menu-item" onClick={onLogout} type="button">
            Wyloguj
          </button>
        </div>
      </div>
      <p className="sidebar-version">wersja {APP_VERSION}</p>
    </nav>
  );
}

function StatsGrid({ items }: { items: ClientDashboardData["stats"] }) {
  return (
    <section className="stats-grid">
      {items.map((item) => (
        <article className="card stat-card" key={item.label}>
          <span className="card-label">{item.label}</span>
          <strong>{item.value}</strong>
          <span className="trend">{item.change}</span>
        </article>
      ))}
    </section>
  );
}

function TasksTable({
  tasks = [],
  employeeView = false,
  activeSession,
  currentUserId,
  isAdmin = false,
  canDeleteAnyTask = false,
  onDelete,
  onStart,
  onStop,
  onOpenClient,
  onOpenProject
}: {
  tasks: TaskSummary[];
  employeeView?: boolean;
  activeSession?: ActiveWorkSession | null;
  currentUserId?: string;
  isAdmin?: boolean;
  canDeleteAnyTask?: boolean;
  onDelete?: (taskId: string) => Promise<void>;
  onStart?: (taskId: string) => void;
  onStop?: (sessionId: string) => void;
  onOpenClient?: (clientId: string) => void;
  onOpenProject?: (projectId: string) => void;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!activeSession) return;

    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [activeSession]);

  return (
    <div className="table">
      <div className="table-head row row-task row-head">
        <span>Zadanie</span>
        <span>Priorytet</span>
        <span>Status</span>
        <span>Czas</span>
        <span>Zespół</span>
        <span>Akcja</span>
      </div>
      {orderTasksWithSubtasks(tasks).map(({ task, depth }) => {
        const canDelete =
          Boolean(onDelete) && (isAdmin || canDeleteAnyTask || (currentUserId != null && task.createdByUserId === currentUserId));

        return (
        <div
          className={depth > 0 ? "row row-task is-subtask" : "row row-task"}
          key={task.id}
          style={depth > 1 ? { marginLeft: `${(depth - 1) * 1.4}rem` } : undefined}
        >
          <div>
            {depth === 0 ? (
              <p>
                {task.clientId && task.clientName ? (
                  <EntityLink label={task.clientName} muted onClick={() => onOpenClient?.(task.clientId!)} />
                ) : (
                  <span className="entity-link is-muted">Bez klienta</span>
                )}
                {task.projectName && task.projectId ? (
                  <>
                    {" • "}
                    <EntityLink label={task.projectName} muted onClick={() => onOpenProject?.(task.projectId!)} />
                  </>
                ) : null}
              </p>
            ) : null}
            <strong>{task.title}</strong>
            {task.budgetType === "FREE" ? <span className="badge badge-free">Bezpłatne</span> : null}
          </div>
          <StatusBadge tone="priority" value={task.priority} />
          <StatusBadge value={task.status} />
          <span className={task.activeSessionId && activeSession?.id === task.activeSessionId ? "timer" : undefined}>
            {formatDurationLabel(
              task.trackedMinutes +
                (task.activeSessionId && activeSession?.id === task.activeSessionId
                  ? Math.max(0, Math.floor((now - new Date(activeSession.startedAt).getTime()) / 60000))
                  : 0)
            )}
          </span>
          <span>{task.assignees.map((assignee) => assignee.fullName).join(", ") || "Brak"}</span>
          <div className="task-row-actions">
            {employeeView ? (
              task.activeSessionId && activeSession?.id === task.activeSessionId ? (
                <button
                  aria-label="Zatrzymaj tracker"
                  className="secondary tracker-action stop"
                  onClick={() => onStop?.(task.activeSessionId!)}
                  title="Zatrzymaj tracker"
                  type="button"
                >
                  <span className="tracker-icon stop" aria-hidden="true" />
                </button>
              ) : (
                <button
                  aria-label="Uruchom tracker"
                  className="secondary tracker-action play"
                  onClick={() => onStart?.(task.id)}
                  title="Uruchom tracker"
                  type="button"
                >
                  <span className="tracker-icon play" aria-hidden="true" />
                </button>
              )
            ) : null}
            {canDelete ? (
              <button
                aria-label="Usuń zadanie"
                className="secondary compact-action icon-action danger"
                onClick={() => {
                  if (!window.confirm(`Usunąć zadanie "${task.title}"? Ta operacja usunie też powiązane komentarze i sesje czasu.`)) return;
                  void onDelete!(task.id);
                }}
                title="Usuń zadanie"
                type="button"
              >
                <TrashIcon />
              </button>
            ) : null}
          </div>
        </div>
        );
      })}
    </div>
  );
}

interface SearchableSelectHandle {
  focus: () => void;
  open: () => void;
}

const SearchableSelect = forwardRef<
  SearchableSelectHandle,
  {
    value: string;
    onChange: (value: string) => void;
    options: Array<{ value: string; label: string }>;
    emptyOptionLabel: string;
    onQueryChange?: (query: string) => void;
    disabled?: boolean;
  }
>(function SearchableSelect({ value, onChange, options, emptyOptionLabel, onQueryChange, disabled = false }, forwardedRef) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useImperativeHandle(forwardedRef, () => ({
    focus: () => triggerRef.current?.focus(),
    open: () => setOpen(true)
  }));

  useEffect(() => {
    if (!open) return;
    searchInputRef.current?.focus();

    function handlePointerDown(event: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const allOptions = [{ value: "", label: emptyOptionLabel }, ...options];
  const needle = query.trim().toLowerCase();
  const filteredOptions = needle ? allOptions.filter((option) => option.label.toLowerCase().includes(needle)) : allOptions;
  const selectedLabel = allOptions.find((option) => option.value === value)?.label ?? emptyOptionLabel;

  function selectOption(optionValue: string) {
    setOpen(false);
    setQuery("");
    onChange(optionValue);
  }

  return (
    <div className="searchable-select" ref={ref}>
      <button
        aria-expanded={open}
        className="searchable-select-trigger"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        ref={triggerRef}
        type="button"
      >
        <span>{selectedLabel}</span>
        <ChevronIcon />
      </button>
      {open ? (
        <div className="searchable-select-menu">
          <input
            className="searchable-select-search"
            onChange={(event) => {
              setQuery(event.target.value);
              onQueryChange?.(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setOpen(false);
                setQuery("");
              } else if (event.key === "Enter") {
                event.preventDefault();
                if (filteredOptions.length === 1) selectOption(filteredOptions[0].value);
              }
            }}
            placeholder="Szukaj..."
            ref={searchInputRef}
            value={query}
          />
          <div className="searchable-select-options">
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-empty">Brak wyników</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  className={option.value === value ? "searchable-select-option is-selected" : "searchable-select-option"}
                  key={option.value || "__empty__"}
                  onClick={() => selectOption(option.value)}
                  type="button"
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
});

// Loads only the 50 most-used clients (plus live search results) instead of rendering every
// client as an <option> - with tens of thousands of clients that used to mean a heavy DB fetch
// and a heavy DOM render on every picker in the app, even though almost every pick is one of a
// handful of clients someone actually works with.
function ClientPickerField({
  value,
  onChange,
  currentLabel,
  emptyOptionLabel = "Bez klienta",
  disabled = false
}: {
  value: string;
  onChange: (clientId: string) => void;
  currentLabel?: string;
  emptyOptionLabel?: string;
  disabled?: boolean;
}) {
  const visibleSources = useContext(VisibleClientSourcesContext);
  const [options, setOptions] = useState<ClientOptionRecord[]>([]);
  const searchTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    fetchClientOptions()
      .then((items) => {
        if (!cancelled) setOptions(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!value || options.some((option) => option.id === value)) return;
    let cancelled = false;
    fetchClientOptions({ id: value })
      .then((items) => {
        if (!cancelled && items.length > 0) {
          setOptions((current) => (current.some((option) => option.id === value) ? current : [...items, ...current]));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function handleQueryChange(query: string) {
    window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => {
      fetchClientOptions(query.trim() ? { search: query.trim() } : undefined)
        .then(setOptions)
        .catch(() => {});
    }, 250);
  }

  const visibleOptions = visibleSources ? options.filter((option) => visibleSources.has(option.sourceKey)) : options;
  const withCurrent =
    value && !visibleOptions.some((option) => option.id === value)
      ? [{ id: value, companyName: currentLabel ?? "Wybrany klient", sourceKey: "internal", sourceLabel: "Wewnętrzny" }, ...visibleOptions]
      : visibleOptions;

  return (
    <SearchableSelect
      disabled={disabled}
      emptyOptionLabel={emptyOptionLabel}
      onChange={onChange}
      onQueryChange={handleQueryChange}
      options={withCurrent.map((option) => ({ value: option.id, label: formatClientOptionLabel(option) }))}
      value={value}
    />
  );
}

function TaskQuickAddModal({
  projectOptions,
  teamMembers,
  currentUserId,
  parentTask = null,
  initialDueDate,
  initialAssigneeIds,
  lockedProjectId,
  startInRecurringMode = false,
  onCreateTask,
  onCreateClient,
  onCreateProject,
  onClose
}: {
  projectOptions: ProjectRecord[];
  teamMembers: EmployeeRecord[];
  currentUserId?: string;
  parentTask?: TaskSummary | null;
  initialDueDate?: string;
  initialAssigneeIds?: string[];
  lockedProjectId?: string;
  startInRecurringMode?: boolean;
  onCreateTask: (payload: CreateTaskRequest) => Promise<void>;
  onCreateClient?: (payload: CreateClientRequest) => Promise<ClientRecord>;
  onCreateProject?: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onClose: () => void;
}) {
  const { pending: creatingTask, run: runCreateTask } = useAsyncAction();
  const { pending: creatingQuickClient, run: runCreateQuickClient } = useAsyncAction();
  const { pending: creatingQuickProject, run: runCreateQuickProject } = useAsyncAction();
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [quickClientForm, setQuickClientForm] = useState<CreateClientRequest>({ companyName: "", emailsEnabled: false });
  const [quickProjectName, setQuickProjectName] = useState("");
  const [quickProjectDescription, setQuickProjectDescription] = useState("");
  const lockedProject = lockedProjectId ? projectOptions.find((project) => project.id === lockedProjectId) : undefined;
  const [clientId, setClientId] = useState(lockedProject?.clientId ?? "");
  const [projectId, setProjectId] = useState(lockedProject?.id ?? "");
  const projectSelectRef = useRef<SearchableSelectHandle | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(initialDueDate ?? "");
  const [dueTime, setDueTime] = useState("");
  const currentEmployeeRecord = teamMembers.find((member) => member.id === currentUserId);
  const [priority, setPriority] = useState<TaskPriority>(currentEmployeeRecord?.defaultTaskPriority ?? "MEDIUM");
  const [budgetType, setBudgetType] = useState<TaskBudgetType>(currentEmployeeRecord?.defaultTaskBudgetType ?? "HOURLY");
  const [budgetAmount, setBudgetAmount] = useState(
    currentEmployeeRecord?.defaultTaskBudgetAmount != null ? String(currentEmployeeRecord.defaultTaskBudgetAmount) : ""
  );
  const [newTaskAssigneeIds, setNewTaskAssigneeIds] = useState<string[]>(
    initialAssigneeIds ?? (currentUserId ? [currentUserId] : [])
  );
  const [recurringEnabled, setRecurringEnabled] = useState(startInRecurringMode);
  const [recurringMode, setRecurringMode] = useState<"INTERVAL" | "WEEKLY">("INTERVAL");
  const [recurringIntervalDays, setRecurringIntervalDays] = useState("7");
  const [recurringDaysOfWeek, setRecurringDaysOfWeek] = useState<string[]>([]);
  const [recurringEndDate, setRecurringEndDate] = useState("");

  useEffect(() => {
    if (lockedProjectId) return;
    const nextProjectId = clientId ? projectOptions.find((project) => project.clientId === clientId)?.id ?? "" : "";
    setProjectId(nextProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return (
    <>
      <Modal closeOnBackdropClick={false} onClose={onClose} title={parentTask ? `Dodaj podzadanie - ${parentTask.title}` : "Dodaj zadanie"}>
        <form
          className="task-form stacked-form"
          onKeyDown={(event) => {
            if (event.altKey && event.key === "Enter") {
              event.preventDefault();
              event.currentTarget.requestSubmit();
            }
          }}
          onSubmit={(event) => {
            event.preventDefault();
            if (!title.trim()) return;
            if (recurringEnabled && !parentTask && (!clientId || !projectId)) return;
            const combinedDueDate = combineDueDateAndTime(dueDate, dueTime);
            void runCreateTask(async () => {
              await onCreateTask({
                clientId: parentTask ? parentTask.clientId : clientId || undefined,
                projectId: parentTask ? parentTask.projectId : projectId || undefined,
                parentTaskId: parentTask?.id,
                title,
                description,
                dueDate: combinedDueDate,
                priority,
                budgetType,
                budgetAmount: budgetType === "FREE" ? undefined : budgetAmount ? Number(budgetAmount) : undefined,
                assigneeIds: newTaskAssigneeIds,
                recurrence:
                  recurringEnabled && !parentTask
                    ? {
                        frequency: recurringMode,
                        intervalDays: recurringMode === "INTERVAL" ? Number(recurringIntervalDays) || 1 : undefined,
                        daysOfWeek: recurringMode === "WEEKLY" ? recurringDaysOfWeek : undefined,
                        endDate: recurringEndDate || undefined
                      }
                    : undefined
              });
              onClose();
            });
          }}
        >
          {parentTask ? (
            <p className="placeholder-copy">
              Klient: {parentTask.clientName ?? "Bez klienta"}
              {parentTask.projectName ? ` • Projekt: ${parentTask.projectName}` : ""} (dziedziczone z zadania głównego)
            </p>
          ) : lockedProject ? (
            <p className="placeholder-copy">
              Projekt: {lockedProject.name} • Klient: {lockedProject.clientName} (ten widok)
            </p>
          ) : (
            <>
              <div className="field">
                <span>Klient (opcjonalnie)</span>
                <div className="field-inline-group">
                  <ClientPickerField
                    emptyOptionLabel="Bez klienta"
                    onChange={(nextClientId) => {
                      setClientId(nextClientId);
                      if (nextClientId) projectSelectRef.current?.open();
                    }}
                    value={clientId}
                  />
                  {onCreateClient ? (
                    <button
                      className="secondary compact-action"
                      onClick={() => setShowAddClient(true)}
                      title="Dodaj nowego klienta"
                      type="button"
                    >
                      + Nowy klient
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="field">
                <span>Projekt (opcjonalnie)</span>
                <div className="field-inline-group">
                  <SearchableSelect
                    emptyOptionLabel="Bez projektu"
                    onChange={(nextProjectId) => {
                      setProjectId(nextProjectId);
                      const nextProject = projectOptions.find((project) => project.id === nextProjectId);
                      if (nextProject) {
                        setClientId(nextProject.clientId);
                      }
                      if (nextProjectId) titleInputRef.current?.focus();
                    }}
                    options={(clientId ? projectOptions.filter((project) => project.clientId === clientId) : projectOptions).map(
                      (project) => ({
                        value: project.id,
                        label: clientId ? project.name : `${project.name} (${project.clientName})`
                      })
                    )}
                    ref={projectSelectRef}
                    value={projectId}
                  />
                  {onCreateProject && clientId ? (
                    <button
                      className="secondary compact-action"
                      onClick={() => setShowAddProject(true)}
                      title="Dodaj nowy projekt"
                      type="button"
                    >
                      + Nowy projekt
                    </button>
                  ) : null}
                </div>
              </div>
            </>
          )}
          <label className="field">
            <span>Nazwa zadania</span>
            <input
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Np. Przygotuj wycenę"
              ref={titleInputRef}
              value={title}
            />
          </label>
          <label className="field">
            <span>Opis</span>
            <input onChange={(event) => setDescription(event.target.value)} placeholder="Opcjonalny opis" value={description} />
          </label>
          <label className="field">
            <span>Termin (opcjonalnie)</span>
            <div className="field-inline-group">
              <input onChange={(event) => setDueDate(event.target.value)} type="date" value={dueDate} />
              <input
                aria-label="Godzina terminu (opcjonalnie)"
                onChange={(event) => setDueTime(event.target.value)}
                type="time"
                value={dueTime}
              />
              <button
                aria-label="Ustaw dzisiejsza date"
                className="secondary compact-action icon-action"
                onClick={() => setDueDate(todayInputValue())}
                title="Ustaw dzisiejsza date"
                type="button"
              >
                <CalendarTodayIcon />
              </button>
            </div>
          </label>
          <label className="field">
            <span>Priorytet</span>
            <select onChange={(event) => setPriority(event.target.value as TaskPriority)} value={priority}>
              <option value="LOW">Niski</option>
              <option value="MEDIUM">Średni</option>
              <option value="HIGH">Wysoki</option>
              <option value="URGENT">Pilny</option>
            </select>
          </label>
          <label className="field">
            <span>Zespół</span>
            <AssigneeCluster
              assignees={teamMembers.filter((member) => newTaskAssigneeIds.includes(member.id))}
              editable
              employees={teamMembers}
              onChange={setNewTaskAssigneeIds}
            />
          </label>
          <label className="field">
            <span>Rozliczenie</span>
            <select onChange={(event) => setBudgetType(event.target.value as TaskBudgetType)} value={budgetType}>
              <option value="HOURLY">Stawka godzinowa</option>
              <option value="FIXED">Cena stała</option>
              <option value="FREE">Bezpłatne</option>
            </select>
          </label>
          {budgetType !== "FREE" ? (
            <label className="field">
              <span>{budgetType === "FIXED" ? "Cena stała (zl)" : "Stawka godzinowa (zl/h, opcjonalnie)"}</span>
              <input
                min="0"
                onChange={(event) => setBudgetAmount(event.target.value)}
                placeholder={budgetType === "FIXED" ? "Np. 1500" : "Domyślnie stawka pracownika"}
                type="number"
                value={budgetAmount}
              />
            </label>
          ) : null}
          {!parentTask ? (
            <>
              <label className="field-inline-group">
                <input checked={recurringEnabled} onChange={(event) => setRecurringEnabled(event.target.checked)} type="checkbox" />
                <span>Zadanie cykliczne</span>
              </label>
              {recurringEnabled ? (
                <>
                  <label className="field">
                    <span>Tryb powtarzania</span>
                    <select onChange={(event) => setRecurringMode(event.target.value as "INTERVAL" | "WEEKLY")} value={recurringMode}>
                      <option value="INTERVAL">Co N dni</option>
                      <option value="WEEKLY">Wybrane dni tygodnia</option>
                    </select>
                  </label>
                  {recurringMode === "INTERVAL" ? (
                    <label className="field">
                      <span>Co ile dni</span>
                      <input
                        min="1"
                        onChange={(event) => setRecurringIntervalDays(event.target.value)}
                        type="number"
                        value={recurringIntervalDays}
                      />
                    </label>
                  ) : (
                    <label className="field">
                      <span>Dni tygodnia</span>
                      <WeekdayPicker onChange={setRecurringDaysOfWeek} selected={recurringDaysOfWeek} />
                    </label>
                  )}
                  <label className="field">
                    <span>Data zakonczenia (opcjonalnie)</span>
                    <input onChange={(event) => setRecurringEndDate(event.target.value)} type="date" value={recurringEndDate} />
                  </label>
                </>
              ) : null}
            </>
          ) : null}
          <button disabled={creatingTask} type="submit">
            {creatingTask ? <Spinner label="Dodawanie..." /> : parentTask ? "Dodaj podzadanie" : "Dodaj zadanie"}
          </button>
        </form>
      </Modal>

      {showAddClient && onCreateClient ? (
        <Modal onClose={() => setShowAddClient(false)} title="Nowy klient">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!quickClientForm.companyName.trim()) return;
              void runCreateQuickClient(async () => {
                const client = await onCreateClient(quickClientForm);
                setClientId(client.id);
                setQuickClientForm({ companyName: "", emailsEnabled: false });
                setShowAddClient(false);
              });
            }}
          >
            <label className="field">
              <span>Nazwa firmy</span>
              <input
                onChange={(event) => setQuickClientForm((current) => ({ ...current, companyName: event.target.value }))}
                placeholder="Nazwa firmy"
                value={quickClientForm.companyName}
              />
            </label>
            <label className="field">
              <span>Email logowania (opcjonalnie)</span>
              <input
                onChange={(event) => setQuickClientForm((current) => ({ ...current, email: event.target.value }))}
                placeholder="Zostanie wygenerowany, jeśli puste"
                value={quickClientForm.email ?? ""}
              />
            </label>
            <label className="field-inline-group">
              <input
                checked={quickClientForm.emailsEnabled ?? false}
                onChange={(event) => setQuickClientForm((current) => ({ ...current, emailsEnabled: event.target.checked }))}
                type="checkbox"
              />
              <span>Wyślij mail o koncie (dane logowania) na podany adres</span>
            </label>
            <label className="field">
              <span>Telefon (opcjonalnie)</span>
              <input
                onChange={(event) => setQuickClientForm((current) => ({ ...current, phone: event.target.value }))}
                placeholder="Telefon"
                value={quickClientForm.phone ?? ""}
              />
            </label>
            <button disabled={creatingQuickClient} type="submit">
              {creatingQuickClient ? <Spinner label="Dodawanie..." /> : "Dodaj klienta"}
            </button>
          </form>
        </Modal>
      ) : null}

      {showAddProject && onCreateProject && clientId ? (
        <Modal onClose={() => setShowAddProject(false)} title="Nowy projekt">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!quickProjectName.trim()) return;
              void runCreateQuickProject(async () => {
                const project = await onCreateProject({
                  clientId,
                  name: quickProjectName,
                  description: quickProjectDescription
                });
                setProjectId(project.id);
                setQuickProjectName("");
                setQuickProjectDescription("");
                setShowAddProject(false);
              });
            }}
          >
            <label className="field">
              <span>Nazwa projektu</span>
              <input onChange={(event) => setQuickProjectName(event.target.value)} placeholder="Nazwa projektu" value={quickProjectName} />
            </label>
            <label className="field">
              <span>Opis (opcjonalnie)</span>
              <input
                onChange={(event) => setQuickProjectDescription(event.target.value)}
                placeholder="Opis projektu"
                value={quickProjectDescription}
              />
            </label>
            <button disabled={creatingQuickProject} type="submit">
              {creatingQuickProject ? <Spinner label="Dodawanie..." /> : "Dodaj projekt"}
            </button>
          </form>
        </Modal>
      ) : null}
    </>
  );
}

const WEEKDAY_OPTIONS = [
  { key: "MON", label: "Pon" },
  { key: "TUE", label: "Wt" },
  { key: "WED", label: "Sr" },
  { key: "THU", label: "Czw" },
  { key: "FRI", label: "Pt" },
  { key: "SAT", label: "Sob" },
  { key: "SUN", label: "Nie" }
];

function WeekdayPicker({ selected, onChange }: { selected: string[]; onChange: (days: string[]) => void }) {
  return (
    <div className="field-inline-group">
      {WEEKDAY_OPTIONS.map((day) => {
        const checked = selected.includes(day.key);
        return (
          <label className={checked ? "day-toggle is-active" : "day-toggle"} key={day.key}>
            <input
              checked={checked}
              onChange={() => onChange(checked ? selected.filter((d) => d !== day.key) : [...selected, day.key])}
              type="checkbox"
            />
            <span>{day.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function RecurringSeriesEditModal({
  series,
  clientOptions,
  projectOptions,
  teamMembers,
  onUpdate,
  onClose
}: {
  series: RecurringSeriesRecord;
  clientOptions: ClientRecord[];
  projectOptions: ProjectRecord[];
  teamMembers: EmployeeRecord[];
  onUpdate: (seriesId: string, payload: UpdateRecurringSeriesRequest) => Promise<void>;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(series.title);
  const [description, setDescription] = useState(series.description ?? "");
  const [priority, setPriority] = useState<TaskPriority>(series.priority);
  const [budgetType, setBudgetType] = useState<TaskBudgetType>(series.budgetType);
  const [budgetAmount, setBudgetAmount] = useState(series.budgetAmount != null ? String(series.budgetAmount) : "");
  const [assigneeIds, setAssigneeIds] = useState<string[]>(series.assigneeIds);
  const [recurringMode, setRecurringMode] = useState<"INTERVAL" | "WEEKLY">(series.frequency);
  const [recurringIntervalDays, setRecurringIntervalDays] = useState(String(series.intervalDays ?? 7));
  const [recurringDaysOfWeek, setRecurringDaysOfWeek] = useState<string[]>(series.daysOfWeek ?? []);
  const [recurringEndDate, setRecurringEndDate] = useState(series.endDate ? series.endDate.slice(0, 10) : "");

  const clientName = clientOptions.find((client) => client.id === series.clientId)?.companyName ?? series.clientName;
  const projectName = projectOptions.find((project) => project.id === series.projectId)?.name ?? series.projectName;

  return (
    <Modal onClose={onClose} title={`Edytuj serie - ${series.title}`}>
      <form
        className="task-form stacked-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!title.trim()) return;
          void onUpdate(series.id, {
            title,
            description,
            priority,
            budgetType,
            budgetAmount: budgetType === "FREE" ? null : budgetAmount ? Number(budgetAmount) : null,
            assigneeIds,
            frequency: recurringMode,
            intervalDays: recurringMode === "INTERVAL" ? Number(recurringIntervalDays) || 1 : undefined,
            daysOfWeek: recurringMode === "WEEKLY" ? recurringDaysOfWeek : undefined,
            endDate: recurringEndDate || null
          }).then(onClose);
        }}
      >
        <p className="placeholder-copy">
          Klient: {clientName}
          {projectName ? ` • Projekt: ${projectName}` : ""}
        </p>
        <label className="field">
          <span>Nazwa serii</span>
          <input onChange={(event) => setTitle(event.target.value)} value={title} />
        </label>
        <label className="field">
          <span>Opis</span>
          <input onChange={(event) => setDescription(event.target.value)} placeholder="Opcjonalny opis" value={description} />
        </label>
        <label className="field">
          <span>Priorytet</span>
          <select onChange={(event) => setPriority(event.target.value as TaskPriority)} value={priority}>
            <option value="LOW">Niski</option>
            <option value="MEDIUM">Średni</option>
            <option value="HIGH">Wysoki</option>
            <option value="URGENT">Pilny</option>
          </select>
        </label>
        <label className="field">
          <span>Zespół</span>
          <AssigneeCluster
            assignees={teamMembers.filter((member) => assigneeIds.includes(member.id))}
            editable
            employees={teamMembers}
            onChange={setAssigneeIds}
          />
        </label>
        <label className="field">
          <span>Rozliczenie</span>
          <select onChange={(event) => setBudgetType(event.target.value as TaskBudgetType)} value={budgetType}>
            <option value="HOURLY">Stawka godzinowa</option>
            <option value="FIXED">Cena stała</option>
            <option value="FREE">Bezpłatne</option>
          </select>
        </label>
        {budgetType !== "FREE" ? (
          <label className="field">
            <span>{budgetType === "FIXED" ? "Cena stała (zl)" : "Stawka godzinowa (zl/h, opcjonalnie)"}</span>
            <input
              min="0"
              onChange={(event) => setBudgetAmount(event.target.value)}
              placeholder={budgetType === "FIXED" ? "Np. 1500" : "Domyślnie stawka pracownika"}
              type="number"
              value={budgetAmount}
            />
          </label>
        ) : null}
        <label className="field">
          <span>Tryb powtarzania</span>
          <select onChange={(event) => setRecurringMode(event.target.value as "INTERVAL" | "WEEKLY")} value={recurringMode}>
            <option value="INTERVAL">Co N dni</option>
            <option value="WEEKLY">Wybrane dni tygodnia</option>
          </select>
        </label>
        {recurringMode === "INTERVAL" ? (
          <label className="field">
            <span>Co ile dni</span>
            <input
              min="1"
              onChange={(event) => setRecurringIntervalDays(event.target.value)}
              type="number"
              value={recurringIntervalDays}
            />
          </label>
        ) : (
          <label className="field">
            <span>Dni tygodnia</span>
            <WeekdayPicker onChange={setRecurringDaysOfWeek} selected={recurringDaysOfWeek} />
          </label>
        )}
        <label className="field">
          <span>Data zakonczenia (opcjonalnie)</span>
          <input onChange={(event) => setRecurringEndDate(event.target.value)} type="date" value={recurringEndDate} />
        </label>
        <button type="submit">Zapisz zmiany</button>
      </form>
    </Modal>
  );
}

const DEFAULT_STATUS_OPTIONS: Array<{ value: TaskSummary["status"]; label: string }> = [
  { value: "TODO", label: "Do zrobienia" },
  { value: "IN_PROGRESS", label: "W toku" },
  { value: "BLOCKED", label: "Zablokowane" },
  { value: "REVIEW", label: "Do akceptacji" },
  { value: "DONE", label: "Zamknięte" },
  { value: "TO_BE_BILLED", label: "Do rozliczenia" },
  { value: "BILLED", label: "Rozliczone" }
];

const REMOTE_EMPLOYEE_STATUS_OPTIONS: Array<{ value: TaskSummary["status"]; label: string }> = [
  { value: "TODO", label: "Do zrobienia" },
  { value: "IN_PROGRESS", label: "W toku" },
  { value: "REVIEW", label: "Do akceptacji" }
];

const TASK_COLUMN_LABELS: Record<string, string> = {
  priority: "Priorytet",
  time: "Czas",
  cost: "Koszt",
  assignees: "Zespół",
  dueDate: "Termin",
  status: "Status"
};

const DEFAULT_TASK_COLUMNS: ViewColumnConfig[] = [
  { key: "priority", visible: true, width: 92 },
  { key: "time", visible: true, width: 96 },
  { key: "cost", visible: true, width: 96 },
  { key: "assignees", visible: true, width: 132 },
  { key: "dueDate", visible: true, width: 208 },
  { key: "status", visible: true, width: 132 }
];

const TASK_COLUMN_MIN_WIDTH = 64;

function normalizeTaskColumns(columns: ViewColumnConfig[]): ViewColumnConfig[] {
  const byKey = new Map(columns.map((column) => [column.key, column]));
  return DEFAULT_TASK_COLUMNS.map((defaultColumn) => {
    const stored = byKey.get(defaultColumn.key);
    return stored ? { ...defaultColumn, ...stored } : defaultColumn;
  });
}

function useViewPreference(viewKey: string | undefined, defaultColumns: ViewColumnConfig[]) {
  const [columns, setColumns] = useState<ViewColumnConfig[]>(defaultColumns);
  const saveTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!viewKey) {
      setColumns(defaultColumns);
      return;
    }

    let cancelled = false;
    fetchViewPreference(viewKey)
      .then(({ preference }) => {
        if (cancelled) return;
        setColumns(preference ? normalizeTaskColumns(preference.columns) : defaultColumns);
      })
      .catch(() => {
        if (!cancelled) setColumns(defaultColumns);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey]);

  function persist(next: ViewColumnConfig[]) {
    setColumns(next);
    if (viewKey) void saveViewPreference(viewKey, { columns: next });
  }

  function toggleVisible(key: string) {
    persist(columns.map((column) => (column.key === key ? { ...column, visible: !column.visible } : column)));
  }

  function setWidth(key: string, width: number) {
    const next = columns.map((column) => (column.key === key ? { ...column, width } : column));
    setColumns(next);

    if (!viewKey) return;
    window.clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = window.setTimeout(() => {
      void saveViewPreference(viewKey, { columns: next });
    }, 400);
  }

  function reset() {
    window.clearTimeout(saveTimeoutRef.current);
    persist(defaultColumns);
  }

  return { columns, toggleVisible, setWidth, reset };
}

function useCollapsedSubtasks() {
  const [collapsedTaskIds, setCollapsedTaskIds] = useState<Set<string>>(() => new Set());
  const saveTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    fetchCollapsedSubtaskIds()
      .then((ids) => {
        if (!cancelled) setCollapsedTaskIds(new Set(ids));
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  function toggleTaskCollapse(taskId: string) {
    setCollapsedTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }

      window.clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = window.setTimeout(() => {
        void saveCollapsedSubtaskIds(Array.from(next));
      }, 300);

      return next;
    });
  }

  return { collapsedTaskIds, toggleTaskCollapse };
}

function ColumnSettingsPopover({
  columns,
  onReset,
  onToggleVisible
}: {
  columns: ViewColumnConfig[];
  onReset: () => void;
  onToggleVisible: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div className="column-settings" ref={ref}>
      <button
        aria-label="Konfiguracja kolumn"
        className="secondary compact-action icon-action"
        onClick={() => setOpen((current) => !current)}
        title="Konfiguracja kolumn"
        type="button"
      >
        <ColumnsIcon />
      </button>
      {open ? (
        <div className="column-settings-menu">
          <p className="eyebrow">Widoczne kolumny</p>
          {columns.map((column) => (
            <label className="column-settings-item" key={column.key}>
              <input checked={column.visible} onChange={() => onToggleVisible(column.key)} type="checkbox" />
              <span>{TASK_COLUMN_LABELS[column.key] ?? column.key}</span>
            </label>
          ))}
          <button className="secondary compact-action column-settings-reset" onClick={onReset} type="button">
            Przywroc domyslne
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ColumnResizeHandle({ width, onWidthChange }: { width: number; onWidthChange: (nextWidth: number) => void }) {
  function handlePointerDown(event: React.PointerEvent<HTMLSpanElement>) {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = width;

    function handleMove(moveEvent: PointerEvent) {
      const totalDeltaX = moveEvent.clientX - startX;
      onWidthChange(Math.max(TASK_COLUMN_MIN_WIDTH, startWidth + totalDeltaX));
    }

    function handleUp() {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    }

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  }

  return <span className="column-resize-handle" onPointerDown={handlePointerDown} />;
}

const UNASSIGNED_FILTER_VALUE = "__unassigned__";

function EmployeeTaskBoard({
  activeSession,
  allowManage = true,
  clientOptions = [],
  currentUserId,
  hideExactTime = false,
  isAdmin = false,
  onChangeTask,
  onCreateTask,
  onCreateComment,
  onCreateClient,
  onCreateProject,
  onDeleteTask,
  onLoadDetail,
  onOpenTask,
  onReorderTask,
  projectOptions = [],
  readOnly = false,
  readOnlyDateMode = "relative",
  searchPlaceholder,
  showCreate = true,
  showDoneTasks = false,
  showSummary = true,
  boardTitle,
  onOpenClient,
  onOpenProject,
  onStart,
  onStop,
  teamMembers = [],
  tasks: tasksProp = [],
  allTasks: allTasksProp = [],
  summaryTasks,
  recurringSeries,
  onUpdateRecurringSeries,
  onDeleteRecurringSeries,
  canChangeStatus,
  statusOptions,
  quickAddAssigneeIds,
  quickAddLockedProjectId,
  canTrackTime,
  columnsViewKey,
  paginated,
  showAssigneeFilter = false
}: {
  activeSession?: ActiveWorkSession | null;
  allowManage?: boolean;
  allTasks: TaskSummary[];
  clientOptions: ClientRecord[];
  currentUserId?: string;
  hideExactTime?: boolean;
  isAdmin?: boolean;
  onChangeTask: (taskId: string, payload: UpdateTaskRequest) => Promise<void>;
  onCreateTask: (payload: CreateTaskRequest) => Promise<void>;
  onCreateComment: (taskId: string, payload: CreateTaskCommentRequest) => Promise<TaskCommentRecord[]>;
  onCreateClient?: (payload: CreateClientRequest) => Promise<ClientRecord>;
  onCreateProject?: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onDeleteTask?: (taskId: string) => Promise<void>;
  onLoadDetail: (taskId: string) => Promise<TaskDetailRecord>;
  onOpenTask: (taskId: string) => void;
  onReorderTask?: (taskId: string, payload: ReorderTaskRequest) => Promise<void>;
  projectOptions: ProjectRecord[];
  readOnly?: boolean;
  readOnlyDateMode?: "relative" | "absolute";
  searchPlaceholder?: string;
  showCreate?: boolean;
  showDoneTasks?: boolean;
  showSummary?: boolean;
  boardTitle?: string;
  onOpenClient?: (clientId: string) => void;
  onOpenProject?: (projectId: string) => void;
  onStart: (taskId: string) => void;
  onStop: (sessionId: string) => void;
  teamMembers: EmployeeRecord[];
  tasks: TaskSummary[];
  summaryTasks?: TaskSummary[];
  recurringSeries?: RecurringSeriesRecord[];
  onUpdateRecurringSeries?: (seriesId: string, payload: UpdateRecurringSeriesRequest) => Promise<void>;
  onDeleteRecurringSeries?: (seriesId: string) => Promise<void>;
  canChangeStatus?: boolean;
  statusOptions?: Array<{ value: TaskSummary["status"]; label: string }>;
  quickAddAssigneeIds?: string[];
  quickAddLockedProjectId?: string;
  canTrackTime?: boolean;
  columnsViewKey?: string;
  paginated?: boolean;
  showAssigneeFilter?: boolean;
}) {
  const { columns: taskColumns, toggleVisible: toggleTaskColumnVisible, setWidth: setTaskColumnWidth, reset: resetTaskColumns } =
    useViewPreference(columnsViewKey, DEFAULT_TASK_COLUMNS);
  const [busyTrackerTaskId, setBusyTrackerTaskId] = useState<string | null>(null);
  async function handleTrackerClick(action: () => void, taskId: string) {
    setBusyTrackerTaskId(taskId);
    try {
      await action();
    } finally {
      setBusyTrackerTaskId(null);
    }
  }
  const isColVisible = (key: string) => taskColumns.find((column) => column.key === key)?.visible ?? true;
  const colWidth = (key: string) => taskColumns.find((column) => column.key === key)?.width ?? 100;
  const taskGridTemplate =
    "20px minmax(220px, 1.7fr) " +
    taskColumns
      .filter((column) => column.visible)
      .map((column) => `${Math.max(TASK_COLUMN_MIN_WIDTH, column.width ?? 100)}px`)
      .join(" ") +
    " 110px";
  const tasks = dedupeRecurringOccurrences(tasksProp);
  const allTasks = dedupeRecurringOccurrences(allTasksProp);
  const [now, setNow] = useState(() => Date.now());
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(tasks[0]?.id ?? null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddStartRecurring, setQuickAddStartRecurring] = useState(false);
  const [editingSeries, setEditingSeries] = useState<RecurringSeriesRecord | null>(null);
  const [quickAddParentTask, setQuickAddParentTask] = useState<TaskSummary | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set(showDoneTasks ? [] : ["done"]));
  const { collapsedTaskIds, toggleTaskCollapse } = useCollapsedSubtasks();
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [dragOverTarget, setDragOverTarget] = useState<{ taskId: string; edge: "before" | "middle" | "after" } | null>(null);

  useEffect(() => {
    setPage(1);
  }, [search, assigneeFilter]);
  const [editDraft, setEditDraft] = useState<{
    clientId: string;
    projectId: string;
    title: string;
    description: string;
    assigneeIds: string[];
    status: TaskSummary["status"];
    dueDate: string;
    dueTime: string;
    priority: TaskPriority;
  } | null>(null);

  useEffect(() => {
    if (!activeSession) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [activeSession]);

  useEffect(() => {
    if (!(allowManage && !readOnly && showCreate)) return;

    function consumePendingNewTask() {
      if (!window.localStorage.getItem(PENDING_NEW_TASK_KEY)) return;
      window.localStorage.removeItem(PENDING_NEW_TASK_KEY);
      setQuickAddParentTask(null);
      setShowQuickAdd(true);
    }

    consumePendingNewTask();
    window.addEventListener("shortcut:new-task", consumePendingNewTask);
    return () => window.removeEventListener("shortcut:new-task", consumePendingNewTask);
  }, [allowManage, readOnly, showCreate]);

  useEffect(() => {
    if (!tasks.length) {
      setSelectedTaskId(null);
      return;
    }

    if (!selectedTaskId || !tasks.some((task) => task.id === selectedTaskId)) {
      setSelectedTaskId(tasks[0].id);
    }
  }, [selectedTaskId, tasks]);

  const buckets: Array<{ key: "overdue" | "today" | "tomorrow" | "future" | "no-date" | "done"; label: string }> = [
    { key: "overdue", label: "Po terminie" },
    { key: "today", label: "Dzisiaj" },
    { key: "tomorrow", label: "Jutro" },
    { key: "future", label: "W przyszłości" },
    { key: "no-date", label: "Bez terminu" },
    { key: "done", label: "Zrealizowane" }
  ];

  const myTrackedTodayLabel = teamMembers.find((member) => member.id === currentUserId)?.trackedToday ?? "00:00";
  const [trackedTodayHours, trackedTodayMinutesPart] = myTrackedTodayLabel.split(":").map(Number);
  const liveSessionMinutes =
    activeSession && tasks.some((task) => task.activeSessionId === activeSession.id)
      ? Math.max(0, Math.floor((now - new Date(activeSession.startedAt).getTime()) / 60000))
      : 0;
  const summaryTaskList = summaryTasks ? dedupeRecurringOccurrences(summaryTasks) : tasks;

  const summary = {
    todayMinutes: trackedTodayHours * 60 + trackedTodayMinutesPart + liveSessionMinutes,
    inProgress: tasks.filter((task) => task.status === "IN_PROGRESS").length,
    waiting: tasks.filter((task) => task.status === "TODO" || task.status === "REVIEW").length,
    done: summaryTaskList.filter(
      (task) => isClosedTaskStatus(task.status) && task.completedAt && sameDay(new Date(task.completedAt), new Date())
    ).length
  };

  async function handleInlineUpdate(taskId: string, payload: UpdateTaskRequest) {
    await onChangeTask(taskId, payload);
  }

  async function handleReorder(targetTaskId: string, draggedTaskId: string, edge: "before" | "middle" | "after") {
    if (!onReorderTask || draggedTaskId === targetTaskId) return;
    if (edge === "middle") {
      await onReorderTask(draggedTaskId, { nestUnderTaskId: targetTaskId });
      return;
    }
    await onReorderTask(draggedTaskId, edge === "before" ? { beforeTaskId: targetTaskId } : { afterTaskId: targetTaskId });
  }

  function toggleGroup(key: string) {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function beginEdit(task: TaskSummary) {
    setEditingTaskId(task.id);
    setEditDraft({
      clientId: task.clientId ?? "",
      projectId: task.projectId ?? "",
      title: task.title,
      description: task.description ?? "",
      assigneeIds: task.assignees.map((assignee) => assignee.id),
      status: task.status,
      dueDate: formatDateInputValue(task.dueDate),
      dueTime: formatTimeInputValue(task.dueDate),
      priority: task.priority
    });
  }

  async function handleDelete(task: TaskSummary) {
    if (!onDeleteTask) return;
    if (!window.confirm(`Usunąć zadanie "${task.title}"? Ta operacja usunie też powiązane komentarze i sesje czasu.`)) return;
    await onDeleteTask(task.id);
  }

  const canDeleteAnyTask = teamMembers.find((member) => member.id === currentUserId)?.canDeleteTasks ?? false;
  const viewerCostDisplayMode = teamMembers.find((member) => member.id === currentUserId)?.costDisplayMode ?? "HOURLY";

  const boardTasks = readOnly ? allTasks : tasks;
  const boardTasksById = new Map(boardTasks.map((task) => [task.id, task]));

  function getGroupBucket(task: TaskSummary) {
    let current = task;
    const seen = new Set([task.id]);
    while (current.parentTaskId && boardTasksById.has(current.parentTaskId) && !seen.has(current.parentTaskId)) {
      current = boardTasksById.get(current.parentTaskId)!;
      seen.add(current.id);
    }
    return getTaskBucket(current);
  }

  const visibleTasks = boardTasks.filter((task) => {
    if (assigneeFilter === UNASSIGNED_FILTER_VALUE && task.assignees.length > 0) return false;
    if (assigneeFilter && assigneeFilter !== UNASSIGNED_FILTER_VALUE && !task.assignees.some((assignee) => assignee.id === assigneeFilter)) {
      return false;
    }
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return `${task.title} ${task.clientName} ${task.projectName ?? ""}`.toLowerCase().includes(query);
  });

  return (
    <section className="section-stack">
      {showSummary ? (
        <article className="card compact-card task-summary-strip">
          <div className="metric-stack compact-stack task-summary-metrics">
            <div>
              <span>Dzisiaj</span>
              <strong>{formatDurationLabel(summary.todayMinutes)}</strong>
            </div>
            <div>
              <span>W toku</span>
              <strong>{summary.inProgress}</strong>
            </div>
            <div>
              <span>Oczekujące</span>
              <strong>{summary.waiting}</strong>
            </div>
            <div>
              <span>Zamknięte</span>
              <strong>{summary.done}</strong>
            </div>
          </div>
        </article>
      ) : null}

      <section className="panel-grid task-board-layout">
        <article className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">{readOnly ? "Zadania" : "Moje zadania"}</p>
              <h3>{boardTitle ?? (readOnly ? "Podgląd pracy całego zespołu" : "Lista pracy")}</h3>
            </div>
            {allowManage && !readOnly && showCreate ? (
              <button
                onClick={() => {
                  setQuickAddParentTask(null);
                  setShowQuickAdd(true);
                }}
                type="button"
              >
                + Dodaj zadanie
              </button>
            ) : null}
          </div>
          <div className="task-list-toolbar">
            <input
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder ?? "Szukaj po zadaniu, kliencie lub projekcie"}
              value={search}
            />
            {showAssigneeFilter ? (
              <select
                aria-label="Filtruj po pracowniku"
                className={assigneeFilter ? "task-list-filter is-active" : "task-list-filter"}
                onChange={(event) => setAssigneeFilter(event.target.value)}
                value={assigneeFilter}
              >
                <option value="">Wszyscy pracownicy</option>
                <option value={UNASSIGNED_FILTER_VALUE}>Bez przypisania</option>
                {[...teamMembers]
                  .sort((a, b) => a.fullName.localeCompare(b.fullName, "pl"))
                  .map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.fullName}
                    </option>
                  ))}
              </select>
            ) : null}
            {columnsViewKey ? (
              <ColumnSettingsPopover columns={taskColumns} onReset={resetTaskColumns} onToggleVisible={toggleTaskColumnVisible} />
            ) : null}
          </div>
          <div className="task-list-scroll">
          <div
            className="task-list-columns"
            aria-hidden="true"
            style={{ "--task-grid-template": taskGridTemplate } as React.CSSProperties}
          >
            <span />
            <span>Zadanie</span>
            {isColVisible("priority") ? (
              <span className="task-list-col-header">
                Priorytet
                {columnsViewKey ? (
                  <ColumnResizeHandle onWidthChange={(w) => setTaskColumnWidth("priority", w)} width={colWidth("priority")} />
                ) : null}
              </span>
            ) : null}
            {isColVisible("time") ? (
              <span className="cell-numeric task-list-col-header">
                Czas
                {columnsViewKey ? (
                  <ColumnResizeHandle onWidthChange={(w) => setTaskColumnWidth("time", w)} width={colWidth("time")} />
                ) : null}
              </span>
            ) : null}
            {isColVisible("cost") ? (
              <span className="cell-numeric task-list-col-header">
                Koszt
                {columnsViewKey ? (
                  <ColumnResizeHandle onWidthChange={(w) => setTaskColumnWidth("cost", w)} width={colWidth("cost")} />
                ) : null}
              </span>
            ) : null}
            {isColVisible("assignees") ? (
              <span className="task-list-col-header">
                Zespół
                {columnsViewKey ? (
                  <ColumnResizeHandle onWidthChange={(w) => setTaskColumnWidth("assignees", w)} width={colWidth("assignees")} />
                ) : null}
              </span>
            ) : null}
            {isColVisible("dueDate") ? (
              <span className="task-list-col-header">
                Termin
                {columnsViewKey ? (
                  <ColumnResizeHandle onWidthChange={(w) => setTaskColumnWidth("dueDate", w)} width={colWidth("dueDate")} />
                ) : null}
              </span>
            ) : null}
            {isColVisible("status") ? (
              <span className="task-list-col-header">
                Status
                {columnsViewKey ? (
                  <ColumnResizeHandle onWidthChange={(w) => setTaskColumnWidth("status", w)} width={colWidth("status")} />
                ) : null}
              </span>
            ) : null}
            <span>Akcje</span>
          </div>
          <div className="task-groups">
            {buckets
              .filter((bucket) => (showDoneTasks ? bucket.key === "done" : bucket.key !== "done"))
              .map((bucket) => {
              const items = boardTasks.filter((task) => getGroupBucket(task) === bucket.key);
              if (items.length === 0) return null;
              const isCollapsed = collapsedGroups.has(bucket.key);
              const orderedItems = orderTasksWithSubtasks(
                items.filter((task) => visibleTasks.some((visibleTask) => visibleTask.id === task.id)),
                collapsedTaskIds
              );
              const paginatedView = paginated ? paginateOrderedTasks(orderedItems, page, TASK_PAGE_SIZE) : null;
              const rowsToRender = paginatedView ? paginatedView.rows : orderedItems;
              const rows = rowsToRender.map(({ task, depth, hasChildren }) => {
                      const liveMinutes =
                        task.activeSessionId && activeSession?.id === task.activeSessionId
                          ? Math.max(0, Math.floor((now - new Date(activeSession.startedAt).getTime()) / 60000))
                          : 0;
                      const canDelete =
                        Boolean(onDeleteTask) &&
                        (isAdmin || canDeleteAnyTask || (currentUserId != null && task.createdByUserId === currentUserId));
                      const canReorder = allowManage && !readOnly && Boolean(onReorderTask);
                      const dragSourceProps = useTaskDragSource(task.id, canReorder);
                      const dragOverEdge = dragOverTarget?.taskId === task.id ? dragOverTarget.edge : null;
                      const reorderTargetProps = buildTaskNestOrReorderTargetProps(
                        (edge) => setDragOverTarget(edge ? { taskId: task.id, edge } : null),
                        (draggedTaskId, edge) => void handleReorder(task.id, draggedTaskId, edge),
                        canReorder
                      );

                      return (
                        <div
                          className={[
                            "task-list-row",
                            selectedTaskId === task.id ? "is-active" : "",
                            depth > 0 ? "is-subtask" : "",
                            depth > 0 && isClosedTaskStatus(task.status) ? "is-closed-subtask" : "",
                            dragOverEdge ? `is-drag-over-${dragOverEdge}` : ""
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          key={task.id}
                          style={
                            {
                              "--task-grid-template": taskGridTemplate,
                              ...(depth > 1 ? { marginLeft: `${(depth - 1) * 1.4}rem` } : {})
                            } as React.CSSProperties
                          }
                          {...(canReorder ? reorderTargetProps : {})}
                        >
                          {canReorder ? (
                            <span className="task-drag-handle" title="Przeciągnij, aby zmienić kolejność" {...dragSourceProps}>
                              <DragHandleIcon />
                            </span>
                          ) : (
                            <span className="task-drag-handle-spacer" />
                          )}
                          {editingTaskId === task.id && editDraft ? (
                            <>
                              <div className="task-list-title">
                                <ClientPickerField
                                  currentLabel={task.clientName}
                                  emptyOptionLabel="Klient"
                                  onChange={(nextClientId) => setEditDraft({ ...editDraft, clientId: nextClientId, projectId: "" })}
                                  value={editDraft.clientId}
                                />
                                <select
                                  className="task-status-select"
                                  onChange={(event) => setEditDraft({ ...editDraft, projectId: event.target.value })}
                                  value={editDraft.projectId}
                                >
                                  <option value="">Projekt</option>
                                  {projectOptions
                                    .filter((project) => project.clientId === editDraft.clientId)
                                    .map((project) => (
                                      <option key={project.id} value={project.id}>
                                        {project.name}
                                      </option>
                                    ))}
                                </select>
                                <input
                                  className="task-inline-input"
                                  onChange={(event) => setEditDraft({ ...editDraft, title: event.target.value })}
                                  value={editDraft.title}
                                />
                              </div>
                              {isColVisible("priority") ? (
                                <select
                                  className="task-status-select"
                                  onChange={(event) => setEditDraft({ ...editDraft, priority: event.target.value as TaskPriority })}
                                  value={editDraft.priority}
                                >
                                  <option value="LOW">Niski</option>
                                  <option value="MEDIUM">Średni</option>
                                  <option value="HIGH">Wysoki</option>
                                  <option value="URGENT">Pilny</option>
                                </select>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <div className="task-list-title">
                                {depth === 0 ? (
                                  <p>
                                    {task.clientId && task.clientName ? (
                                      <EntityLink label={task.clientName} muted onClick={() => onOpenClient?.(task.clientId!)} />
                                    ) : (
                                      <span className="entity-link is-muted">Bez klienta</span>
                                    )}
                                    {task.projectName && task.projectId ? (
                                      <>
                                        {" • "}
                                        <EntityLink label={task.projectName} muted onClick={() => onOpenProject?.(task.projectId!)} />
                                      </>
                                    ) : null}
                                  </p>
                                ) : null}
                                <div className="task-list-title-row">
                                  {hasChildren ? (
                                    <button
                                      aria-expanded={!collapsedTaskIds.has(task.id)}
                                      aria-label={collapsedTaskIds.has(task.id) ? "Rozwiń podzadania" : "Zwiń podzadania"}
                                      className="task-subtask-toggle"
                                      onClick={() => toggleTaskCollapse(task.id)}
                                      title={collapsedTaskIds.has(task.id) ? "Rozwiń podzadania" : "Zwiń podzadania"}
                                      type="button"
                                    >
                                      <span aria-hidden="true">{collapsedTaskIds.has(task.id) ? "▸" : "▾"}</span>
                                    </button>
                                  ) : null}
                                  <a
                                    className="task-link-button"
                                    href={`#${encodeURIComponent(buildTaskDetailKey(task.id))}`}
                                    onClick={(event) => {
                                      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                                      event.preventDefault();
                                      setSelectedTaskId(task.id);
                                      onOpenTask(task.id);
                                    }}
                                  >
                                    <strong>{task.title}</strong>
                                  </a>
                                  {task.budgetType === "FREE" ? <span className="badge badge-free">Bezpłatne</span> : null}
                                  {allowManage && !readOnly ? (
                                    <button
                                      aria-label="Dodaj podzadanie"
                                      className="secondary compact-action icon-action task-add-subtask-action"
                                      onClick={() => {
                                        setQuickAddParentTask(task);
                                        setShowQuickAdd(true);
                                      }}
                                      title="Dodaj podzadanie"
                                      type="button"
                                    >
                                      <PlusIcon />
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                              {isColVisible("priority") ? <StatusBadge tone="priority" value={task.priority} /> : null}
                            </>
                          )}
                          {isColVisible("time") ? (
                            <span className={task.activeSessionId && activeSession?.id === task.activeSessionId ? "timer" : "task-list-time"}>
                              {hideExactTime ? `${task.roundedHoursForClient} h` : formatDurationLabel(task.trackedMinutes + liveMinutes)}
                            </span>
                          ) : null}
                          {isColVisible("cost") ? (
                            <span className="task-list-cost">
                              {formatMoneyLabel(viewerCostDisplayMode === "PER_MINUTE" ? computePerMinuteCost(task) : task.costAmount)}
                            </span>
                          ) : null}
                          {isColVisible("assignees") ? (
                            <AssigneeCluster
                              assignees={task.assignees}
                              editable={allowManage && !readOnly}
                              employees={teamMembers}
                              onChange={(assigneeIds) =>
                                editingTaskId === task.id && editDraft
                                  ? setEditDraft({ ...editDraft, assigneeIds })
                                  : void handleInlineUpdate(task.id, {
                                      assigneeIds
                                    })
                              }
                            />
                          ) : null}
                          {isColVisible("dueDate") ? (
                            readOnly ? (
                              <span className="task-date-label">
                                {readOnlyDateMode === "absolute" ? formatDueDateAbsolute(task.dueDate) : formatDueDateLabel(task.dueDate)}
                              </span>
                            ) : (
                              <div className="field-inline-group task-date-group">
                                <input
                                  className="task-date-input"
                                  disabled={!allowManage}
                                  onChange={(event) => {
                                    const nextDate = event.target.value;
                                    if (editingTaskId === task.id && editDraft) {
                                      setEditDraft({ ...editDraft, dueDate: nextDate });
                                    } else {
                                      const currentTime = formatTimeInputValue(task.dueDate);
                                      void handleInlineUpdate(task.id, {
                                        dueDate: nextDate ? (combineDueDateAndTime(nextDate, currentTime) ?? null) : null
                                      });
                                    }
                                  }}
                                  type="date"
                                  value={editingTaskId === task.id && editDraft ? editDraft.dueDate : formatDateInputValue(task.dueDate)}
                                />
                                <input
                                  aria-label="Godzina terminu (opcjonalnie)"
                                  className="task-date-input"
                                  disabled={!allowManage || (!(editingTaskId === task.id && editDraft) && !task.dueDate)}
                                  onChange={(event) => {
                                    const nextTime = event.target.value;
                                    if (editingTaskId === task.id && editDraft) {
                                      setEditDraft({ ...editDraft, dueTime: nextTime });
                                    } else {
                                      const currentDate = formatDateInputValue(task.dueDate);
                                      if (!currentDate) return;
                                      void handleInlineUpdate(task.id, { dueDate: combineDueDateAndTime(currentDate, nextTime) ?? null });
                                    }
                                  }}
                                  type="time"
                                  value={editingTaskId === task.id && editDraft ? editDraft.dueTime : formatTimeInputValue(task.dueDate)}
                                />
                              </div>
                            )
                          ) : null}
                          {isColVisible("status") ? (
                            readOnly ? (
                              <StatusBadge value={task.status} />
                            ) : (
                              <select
                                className={`task-status-select status-select status-${toBadgeClass(
                                  editingTaskId === task.id && editDraft ? editDraft.status : task.status
                                )}`}
                                disabled={!(canChangeStatus ?? allowManage)}
                                onChange={(event) =>
                                  editingTaskId === task.id && editDraft
                                    ? setEditDraft({ ...editDraft, status: event.target.value as TaskSummary["status"] })
                                    : void handleInlineUpdate(task.id, {
                                        status: event.target.value as UpdateTaskRequest["status"]
                                      })
                                }
                                value={editingTaskId === task.id && editDraft ? editDraft.status : task.status}
                              >
                                {(statusOptions ?? DEFAULT_STATUS_OPTIONS).map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            )
                          ) : null}
                          {allowManage && !readOnly ? (
                            editingTaskId === task.id && editDraft ? (
                              <div className="task-row-actions">
                                <button
                                  className="secondary compact-action"
                                  onClick={() => {
                                    setEditingTaskId(null);
                                    setEditDraft(null);
                                  }}
                                  type="button"
                                >
                                  Anuluj
                                </button>
                                <button
                                  className="compact-action"
                                  onClick={() =>
                                    void handleInlineUpdate(task.id, {
                                      clientId: editDraft.clientId,
                                      projectId: editDraft.projectId,
                                      title: editDraft.title,
                                      description: editDraft.description,
                                      assigneeIds: editDraft.assigneeIds,
                                      dueDate: editDraft.dueDate ? (combineDueDateAndTime(editDraft.dueDate, editDraft.dueTime) ?? null) : null,
                                      status: editDraft.status,
                                      priority: editDraft.priority
                                    }).then(() => {
                                      setEditingTaskId(null);
                                      setEditDraft(null);
                                    })
                                  }
                                  type="button"
                                >
                                  Zapisz
                                </button>
                              </div>
                            ) : task.activeSessionId && activeSession?.id === task.activeSessionId ? (
                              <div className="task-row-actions">
                                <button
                                  aria-label="Zatrzymaj tracker"
                                  className={`secondary tracker-action stop${busyTrackerTaskId === task.id ? " is-loading" : ""}`}
                                  disabled={busyTrackerTaskId === task.id}
                                  onClick={() => void handleTrackerClick(() => onStop(task.activeSessionId!), task.id)}
                                  type="button"
                                >
                                  {busyTrackerTaskId === task.id ? <Spinner /> : <span className="tracker-icon stop" aria-hidden="true" />}
                                </button>
                                <button
                                  aria-label="Edytuj zadanie"
                                  className="secondary compact-action icon-action"
                                  onClick={() => beginEdit(task)}
                                  title="Edytuj zadanie"
                                  type="button"
                                >
                                  <PencilIcon />
                                </button>
                                {canDelete ? (
                                  <button
                                    aria-label="Usuń zadanie"
                                    className="secondary compact-action icon-action danger"
                                    onClick={() => void handleDelete(task)}
                                    title="Usuń zadanie"
                                    type="button"
                                  >
                                    <TrashIcon />
                                  </button>
                                ) : null}
                              </div>
                            ) : (
                              <div className="task-row-actions">
                                <button
                                  aria-label="Uruchom tracker"
                                  className={`secondary tracker-action play${busyTrackerTaskId === task.id ? " is-loading" : ""}`}
                                  disabled={busyTrackerTaskId === task.id}
                                  onClick={() => void handleTrackerClick(() => onStart(task.id), task.id)}
                                  type="button"
                                >
                                  {busyTrackerTaskId === task.id ? <Spinner /> : <span className="tracker-icon play" aria-hidden="true" />}
                                </button>
                                <button
                                  aria-label="Edytuj zadanie"
                                  className="secondary compact-action icon-action"
                                  onClick={() => beginEdit(task)}
                                  title="Edytuj zadanie"
                                  type="button"
                                >
                                  <PencilIcon />
                                </button>
                                {canDelete ? (
                                  <button
                                    aria-label="Usuń zadanie"
                                    className="secondary compact-action icon-action danger"
                                    onClick={() => void handleDelete(task)}
                                    title="Usuń zadanie"
                                    type="button"
                                  >
                                    <TrashIcon />
                                  </button>
                                ) : null}
                              </div>
                            )
                          ) : (canTrackTime ?? allowManage) && !readOnly ? (
                            task.activeSessionId && activeSession?.id === task.activeSessionId ? (
                              <div className="task-row-actions">
                                <button
                                  aria-label="Zatrzymaj tracker"
                                  className={`secondary tracker-action stop${busyTrackerTaskId === task.id ? " is-loading" : ""}`}
                                  disabled={busyTrackerTaskId === task.id}
                                  onClick={() => void handleTrackerClick(() => onStop(task.activeSessionId!), task.id)}
                                  type="button"
                                >
                                  {busyTrackerTaskId === task.id ? <Spinner /> : <span className="tracker-icon stop" aria-hidden="true" />}
                                </button>
                                <button
                                  className="secondary compact-action"
                                  onClick={() => {
                                    setSelectedTaskId(task.id);
                                    onOpenTask(task.id);
                                  }}
                                  type="button"
                                >
                                  Podgląd
                                </button>
                                {canDelete ? (
                                  <button
                                    aria-label="Usuń zadanie"
                                    className="secondary compact-action icon-action danger"
                                    onClick={() => void handleDelete(task)}
                                    title="Usuń zadanie"
                                    type="button"
                                  >
                                    <TrashIcon />
                                  </button>
                                ) : null}
                              </div>
                            ) : (
                              <div className="task-row-actions">
                                <button
                                  aria-label="Uruchom tracker"
                                  className={`secondary tracker-action play${busyTrackerTaskId === task.id ? " is-loading" : ""}`}
                                  disabled={busyTrackerTaskId === task.id}
                                  onClick={() => void handleTrackerClick(() => onStart(task.id), task.id)}
                                  type="button"
                                >
                                  {busyTrackerTaskId === task.id ? <Spinner /> : <span className="tracker-icon play" aria-hidden="true" />}
                                </button>
                                <button
                                  className="secondary compact-action"
                                  onClick={() => {
                                    setSelectedTaskId(task.id);
                                    onOpenTask(task.id);
                                  }}
                                  type="button"
                                >
                                  Podgląd
                                </button>
                                {canDelete ? (
                                  <button
                                    aria-label="Usuń zadanie"
                                    className="secondary compact-action icon-action danger"
                                    onClick={() => void handleDelete(task)}
                                    title="Usuń zadanie"
                                    type="button"
                                  >
                                    <TrashIcon />
                                  </button>
                                ) : null}
                              </div>
                            )
                          ) : (
                            <div className="task-row-actions">
                              <button
                                className="secondary compact-action"
                                onClick={() => {
                                  setSelectedTaskId(task.id);
                                  onOpenTask(task.id);
                                }}
                                type="button"
                              >
                                Podgląd
                              </button>
                              {canDelete ? (
                                <button
                                  aria-label="Usuń zadanie"
                                  className="secondary compact-action icon-action danger"
                                  onClick={() => void handleDelete(task)}
                                  title="Usuń zadanie"
                                  type="button"
                                >
                                  <TrashIcon />
                                </button>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    });

              const listBody = (
                <>
                  <div className="task-list">{rows}</div>
                  {paginatedView && paginatedView.totalPages > 1 ? (
                    <div className="task-list-pagination">
                      <button
                        className="secondary compact-action"
                        disabled={paginatedView.page <= 1}
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                        type="button"
                      >
                        ‹ Poprzednia
                      </button>
                      <span>
                        Strona {paginatedView.page} z {paginatedView.totalPages}
                      </span>
                      <button
                        className="secondary compact-action"
                        disabled={paginatedView.page >= paginatedView.totalPages}
                        onClick={() => setPage((current) => Math.min(paginatedView.totalPages, current + 1))}
                        type="button"
                      >
                        Następna ›
                      </button>
                    </div>
                  ) : null}
                </>
              );

              if (showDoneTasks) {
                return <Fragment key={bucket.key}>{listBody}</Fragment>;
              }

              return (
                <section className={isCollapsed ? "task-group is-collapsed" : "task-group"} key={bucket.key}>
                  <button className="task-group-head" onClick={() => toggleGroup(bucket.key)} type="button">
                    <span className="task-group-toggle" aria-hidden="true">
                      {isCollapsed ? "▸" : "▾"}
                    </span>
                    <strong>{bucket.label}</strong>
                    <span className="task-group-count">{items.length}</span>
                  </button>
                  {isCollapsed ? null : listBody}
                </section>
              );
            })}
            {visibleTasks.length === 0 ? <EmptyState message="Brak zadań spełniających kryteria wyszukiwania." /> : null}
          </div>
          </div>
        </article>
      </section>

      {recurringSeries ? (
        <article className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Zadania cykliczne</p>
              <h3>Zadania cykliczne</h3>
            </div>
            <button
              className="compact-action"
              onClick={() => {
                setQuickAddStartRecurring(true);
                setShowQuickAdd(true);
              }}
              type="button"
            >
              + Dodaj serie
            </button>
          </div>
          {recurringSeries.length === 0 ? (
            <EmptyState message="Brak zadań cyklicznych." />
          ) : (
            <div className="recurring-series-list">
              {recurringSeries.map((series) => (
                <div className="recurring-series-row" key={series.id}>
                  <div className="recurring-series-info">
                    <strong>{series.title}</strong>
                    <span>
                      {series.clientName}
                      {series.projectName ? ` • ${series.projectName}` : ""}
                    </span>
                    <span>{formatRecurrenceSummary(series)}</span>
                    <span>
                      {series.nextOccurrenceDate
                        ? `Najbliższy termin: ${formatDueDateAbsolute(series.nextOccurrenceDate)}`
                        : "Brak nadchodzących terminów"}
                    </span>
                  </div>
                  <span className={series.active ? "badge badge-status badge-active" : "badge badge-status badge-paused"}>
                    {series.active ? "Aktywna" : "Wstrzymana"}
                  </span>
                  <div className="task-row-actions">
                    <button className="secondary compact-action" onClick={() => setEditingSeries(series)} type="button">
                      Edytuj
                    </button>
                    <button
                      className="secondary compact-action"
                      onClick={() => void onUpdateRecurringSeries?.(series.id, { active: !series.active })}
                      type="button"
                    >
                      {series.active ? "Wstrzymaj" : "Wznów"}
                    </button>
                    <button
                      className="secondary compact-action icon-action danger"
                      aria-label="Usuń serie"
                      onClick={() => {
                        if (window.confirm(`Usunąć serię "${series.title}"? Już wygenerowane zadania zostaną jako zwykłe zadania.`)) {
                          void onDeleteRecurringSeries?.(series.id);
                        }
                      }}
                      title="Usuń serie"
                      type="button"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>
      ) : null}

      {showQuickAdd ? (
        <TaskQuickAddModal
          currentUserId={currentUserId}
          initialAssigneeIds={quickAddParentTask ? undefined : quickAddAssigneeIds}
          lockedProjectId={quickAddParentTask ? undefined : quickAddLockedProjectId}
          onClose={() => {
            setShowQuickAdd(false);
            setQuickAddParentTask(null);
            setQuickAddStartRecurring(false);
          }}
          onCreateClient={onCreateClient}
          onCreateProject={onCreateProject}
          onCreateTask={onCreateTask}
          parentTask={quickAddParentTask}
          projectOptions={projectOptions}
          startInRecurringMode={quickAddStartRecurring}
          teamMembers={teamMembers}
        />
      ) : null}

      {editingSeries && onUpdateRecurringSeries ? (
        <RecurringSeriesEditModal
          clientOptions={clientOptions}
          onClose={() => setEditingSeries(null)}
          onUpdate={onUpdateRecurringSeries}
          projectOptions={projectOptions}
          series={editingSeries}
          teamMembers={teamMembers}
        />
      ) : null}

    </section>
  );
}

const WEEKDAY_LABELS: Record<string, string> = {
  MON: "Pn",
  TUE: "Wt",
  WED: "Sr",
  THU: "Cz",
  FRI: "Pt",
  SAT: "So",
  SUN: "Nd"
};

function describeReminder(reminder: ReminderRecord) {
  if (reminder.kind === "ONE_TIME") return `Jednorazowo: ${formatDateTimeLabel(reminder.fireAt)}`;
  if (reminder.kind === "RELATIVE_TO_DUE") {
    const minutes = reminder.offsetMinutesBeforeDue ?? 0;
    if (minutes % 1440 === 0) return `${minutes / 1440} dni przed terminem`;
    if (minutes % 60 === 0) return `${minutes / 60} godz. przed terminem`;
    return `${minutes} min przed terminem`;
  }
  if (reminder.frequency === "WEEKLY") {
    const days = (reminder.daysOfWeek ?? "")
      .split(",")
      .filter(Boolean)
      .map((day) => WEEKDAY_LABELS[day] ?? day)
      .join(", ");
    return `Cyklicznie: ${days || "brak dni"}`;
  }
  return `Cyklicznie co ${reminder.intervalDays ?? 1} dni`;
}

function TaskReminders({ task, readOnly }: { task: TaskSummary; readOnly: boolean }) {
  const [reminders, setReminders] = useState<ReminderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<ReminderKind>("ONE_TIME");
  const [fireAt, setFireAt] = useState("");
  const [offsetValue, setOffsetValue] = useState("60");
  const [offsetUnit, setOffsetUnit] = useState<"minutes" | "hours" | "days">("minutes");
  const [frequency, setFrequency] = useState<"INTERVAL" | "WEEKLY">("INTERVAL");
  const [intervalDays, setIntervalDays] = useState("1");
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>([]);
  const [notifyBell, setNotifyBell] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void fetchTaskReminders(task.id)
      .then(({ items }) => setReminders(items))
      .finally(() => setLoading(false));
  }, [task.id]);

  async function handleCreate() {
    setCreating(true);
    try {
      const offsetMultiplier = offsetUnit === "minutes" ? 1 : offsetUnit === "hours" ? 60 : 1440;
      const { reminder } = await createReminder({
        taskId: task.id,
        kind,
        fireAt: kind === "ONE_TIME" && fireAt ? new Date(fireAt).toISOString() : undefined,
        offsetMinutesBeforeDue: kind === "RELATIVE_TO_DUE" ? Number(offsetValue) * offsetMultiplier : undefined,
        frequency: kind === "RECURRING" ? frequency : undefined,
        intervalDays: kind === "RECURRING" && frequency === "INTERVAL" ? Number(intervalDays) : undefined,
        daysOfWeek: kind === "RECURRING" && frequency === "WEEKLY" ? daysOfWeek.join(",") : undefined,
        notifyBell,
        notifyEmail
      });
      setReminders((current) => [reminder, ...current]);
      setFireAt("");
    } finally {
      setCreating(false);
    }
  }

  async function handleToggle(reminder: ReminderRecord) {
    const { reminder: updated } = await updateReminder(reminder.id, { enabled: !reminder.enabled });
    setReminders((current) => current.map((item) => (item.id === updated.id ? updated : item)));
  }

  async function handleDelete(reminderId: string) {
    await deleteReminder(reminderId);
    setReminders((current) => current.filter((item) => item.id !== reminderId));
  }

  const canCreate =
    kind === "ONE_TIME" ? Boolean(fireAt) : kind === "RELATIVE_TO_DUE" ? Boolean(task.dueDate) && Number(offsetValue) > 0 : true;

  return (
    <article className="card detail-block">
      <div className="card-header compact-head">
        <div>
          <p className="eyebrow">Przypomnienia</p>
        </div>
      </div>
      <div className="reminder-list">
        {loading ? <p className="placeholder-copy">Wczytywanie...</p> : null}
        {!loading && reminders.length === 0 ? <p className="placeholder-copy">Brak przypomnień dla tego zadania.</p> : null}
        {reminders.map((reminder) => (
          <div className="reminder-item" key={reminder.id}>
            <span className={reminder.enabled ? "" : "is-muted"}>{describeReminder(reminder)}</span>
            {!readOnly ? (
              <div className="reminder-item-actions">
                <label className="field-inline-group compact">
                  <input checked={reminder.enabled} onChange={() => void handleToggle(reminder)} type="checkbox" />
                  <span>Aktywne</span>
                </label>
                <button
                  aria-label="Usuń przypomnienie"
                  className="secondary compact-action icon-action danger"
                  onClick={() => void handleDelete(reminder.id)}
                  type="button"
                >
                  <TrashIcon />
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {!readOnly ? (
        <div className="reminder-form">
          <select onChange={(event) => setKind(event.target.value as ReminderKind)} value={kind}>
            <option value="ONE_TIME">Jednorazowo</option>
            <option disabled={!task.dueDate} value="RELATIVE_TO_DUE">
              Wzgledem terminu
            </option>
            <option value="RECURRING">Cyklicznie</option>
          </select>
          {kind === "ONE_TIME" ? (
            <input onChange={(event) => setFireAt(event.target.value)} type="datetime-local" value={fireAt} />
          ) : null}
          {kind === "RELATIVE_TO_DUE" ? (
            <div className="field-inline-group">
              <input min="1" onChange={(event) => setOffsetValue(event.target.value)} type="number" value={offsetValue} />
              <select onChange={(event) => setOffsetUnit(event.target.value as "minutes" | "hours" | "days")} value={offsetUnit}>
                <option value="minutes">minut przed</option>
                <option value="hours">godzin przed</option>
                <option value="days">dni przed</option>
              </select>
            </div>
          ) : null}
          {kind === "RECURRING" ? (
            <>
              <select onChange={(event) => setFrequency(event.target.value as "INTERVAL" | "WEEKLY")} value={frequency}>
                <option value="INTERVAL">Co X dni</option>
                <option value="WEEKLY">W wybrane dni tygodnia</option>
              </select>
              {frequency === "INTERVAL" ? (
                <input min="1" onChange={(event) => setIntervalDays(event.target.value)} type="number" value={intervalDays} />
              ) : (
                <div className="weekday-picker">
                  {Object.keys(WEEKDAY_LABELS).map((day) => (
                    <label key={day}>
                      <input
                        checked={daysOfWeek.includes(day)}
                        onChange={() =>
                          setDaysOfWeek((current) => (current.includes(day) ? current.filter((item) => item !== day) : [...current, day]))
                        }
                        type="checkbox"
                      />
                      {WEEKDAY_LABELS[day]}
                    </label>
                  ))}
                </div>
              )}
            </>
          ) : null}
          <label className="field-inline-group compact">
            <input checked={notifyBell} onChange={(event) => setNotifyBell(event.target.checked)} type="checkbox" />
            <span>Powiadomienie w dzwonku</span>
          </label>
          <label className="field-inline-group compact">
            <input checked={notifyEmail} onChange={(event) => setNotifyEmail(event.target.checked)} type="checkbox" />
            <span>Wyślij e-mail</span>
          </label>
          <button disabled={creating || !canCreate} onClick={() => void handleCreate()} type="button">
            {creating ? <Spinner label="Dodawanie..." /> : "+ Dodaj przypomnienie"}
          </button>
        </div>
      ) : null}
    </article>
  );
}

function ReminderFormModal({
  reminder,
  sessionUser,
  teamMembers,
  canTargetOthers,
  clientOptions,
  projectOptions,
  fixedClientId,
  onClose,
  onSaved
}: {
  reminder: ReminderRecord | null;
  sessionUser: SessionUser;
  teamMembers: EmployeeRecord[];
  canTargetOthers: boolean;
  clientOptions?: ClientRecord[];
  projectOptions?: ProjectRecord[];
  fixedClientId?: string;
  onClose: () => void;
  onSaved: (reminder: ReminderRecord) => void;
}) {
  const [title, setTitle] = useState(reminder?.title ?? "");
  const [targetUserId, setTargetUserId] = useState(reminder?.targetUserId ?? sessionUser.id);
  const [kind, setKind] = useState<"ONE_TIME" | "RECURRING">(reminder?.kind === "RECURRING" ? "RECURRING" : "ONE_TIME");
  const [fireAt, setFireAt] = useState(reminder?.fireAt ? reminder.fireAt.slice(0, 16) : "");
  const [frequency, setFrequency] = useState<"INTERVAL" | "WEEKLY">(reminder?.frequency === "WEEKLY" ? "WEEKLY" : "INTERVAL");
  const [intervalDays, setIntervalDays] = useState(String(reminder?.intervalDays ?? 1));
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>(reminder?.daysOfWeek ? reminder.daysOfWeek.split(",").filter(Boolean) : []);
  const [notifyBell, setNotifyBell] = useState(reminder?.notifyBell ?? true);
  const [notifyEmail, setNotifyEmail] = useState(reminder?.notifyEmail ?? false);
  const [clientId, setClientId] = useState(fixedClientId ?? reminder?.clientId ?? "");
  const [projectId, setProjectId] = useState(reminder?.projectId ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = title.trim().length > 0 && (kind !== "ONE_TIME" || Boolean(fireAt));
  const projectChoices = clientId ? (projectOptions ?? []).filter((project) => project.clientId === clientId) : projectOptions ?? [];

  async function handleSubmit() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const basePayload = {
        title: title.trim(),
        targetUserId: canTargetOthers ? targetUserId : sessionUser.id,
        kind,
        fireAt: kind === "ONE_TIME" && fireAt ? new Date(fireAt).toISOString() : undefined,
        frequency: kind === "RECURRING" ? frequency : undefined,
        intervalDays: kind === "RECURRING" && frequency === "INTERVAL" ? Number(intervalDays) || 1 : undefined,
        daysOfWeek: kind === "RECURRING" && frequency === "WEEKLY" ? daysOfWeek.join(",") : undefined,
        notifyBell,
        notifyEmail
      };
      const { reminder: saved } = reminder
        ? await updateReminder(reminder.id, { ...basePayload, clientId: clientId || null, projectId: projectId || null })
        : await createReminder({ ...basePayload, clientId: clientId || undefined, projectId: projectId || undefined });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nie udało się zapisać przypomnienia.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title={reminder ? "Edytuj przypomnienie" : "Nowe przypomnienie"}>
      <div className="task-form stacked-form">
        <label className="field">
          <span>Tytuł</span>
          <input onChange={(event) => setTitle(event.target.value)} placeholder="Np. Zadzwonić do klienta" value={title} />
        </label>
        {canTargetOthers ? (
          <label className="field">
            <span>Dla</span>
            <select onChange={(event) => setTargetUserId(event.target.value)} value={targetUserId}>
              <option value={sessionUser.id}>Dla mnie</option>
              {teamMembers
                .filter((member) => member.id !== sessionUser.id)
                .map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.fullName}
                  </option>
                ))}
            </select>
          </label>
        ) : null}
        <label className="field">
          <span>Rodzaj</span>
          <select onChange={(event) => setKind(event.target.value as "ONE_TIME" | "RECURRING")} value={kind}>
            <option value="ONE_TIME">Jednorazowo</option>
            <option value="RECURRING">Cyklicznie</option>
          </select>
        </label>
        {kind === "ONE_TIME" ? (
          <label className="field">
            <span>Data i godzina</span>
            <input onChange={(event) => setFireAt(event.target.value)} type="datetime-local" value={fireAt} />
          </label>
        ) : (
          <>
            <select onChange={(event) => setFrequency(event.target.value as "INTERVAL" | "WEEKLY")} value={frequency}>
              <option value="INTERVAL">Co X dni</option>
              <option value="WEEKLY">W wybrane dni tygodnia</option>
            </select>
            {frequency === "INTERVAL" ? (
              <input min="1" onChange={(event) => setIntervalDays(event.target.value)} type="number" value={intervalDays} />
            ) : (
              <div className="weekday-picker">
                {Object.keys(WEEKDAY_LABELS).map((day) => (
                  <label key={day}>
                    <input
                      checked={daysOfWeek.includes(day)}
                      onChange={() =>
                        setDaysOfWeek((current) => (current.includes(day) ? current.filter((item) => item !== day) : [...current, day]))
                      }
                      type="checkbox"
                    />
                    {WEEKDAY_LABELS[day]}
                  </label>
                ))}
              </div>
            )}
          </>
        )}
        {!fixedClientId && clientOptions ? (
          <label className="field">
            <span>Klient (opcjonalnie)</span>
            <ClientPickerField
              currentLabel={reminder?.clientName}
              emptyOptionLabel="Brak"
              onChange={(nextClientId) => {
                setClientId(nextClientId);
                setProjectId("");
              }}
              value={clientId}
            />
          </label>
        ) : null}
        {projectOptions ? (
          <label className="field">
            <span>Projekt (opcjonalnie)</span>
            <select onChange={(event) => setProjectId(event.target.value)} value={projectId}>
              <option value="">Brak</option>
              {projectChoices.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="field-inline-group compact">
          <input checked={notifyBell} onChange={(event) => setNotifyBell(event.target.checked)} type="checkbox" />
          <span>Powiadomienie w dzwonku</span>
        </label>
        <label className="field-inline-group compact">
          <input checked={notifyEmail} onChange={(event) => setNotifyEmail(event.target.checked)} type="checkbox" />
          <span>Wyślij e-mail</span>
        </label>
        {error ? <p className="error-banner">{error}</p> : null}
        <div className="task-detail-save-row">
          <button disabled={!canSave || saving} onClick={() => void handleSubmit()} type="button">
            {saving ? "Zapisywanie..." : "Zapisz"}
          </button>
          <button className="secondary" onClick={onClose} type="button">
            Anuluj
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RemindersPage({
  sessionUser,
  teamMembers,
  clientOptions,
  projectOptions
}: {
  sessionUser: SessionUser;
  teamMembers: EmployeeRecord[];
  clientOptions?: ClientRecord[];
  projectOptions?: ProjectRecord[];
}) {
  const [reminders, setReminders] = useState<ReminderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<ReminderRecord | null>(null);

  const canTargetOthers = sessionUser.role !== "REMOTE_EMPLOYEE";

  useEffect(() => {
    void fetchReminders()
      .then(({ items }) => setReminders(items))
      .finally(() => setLoading(false));
  }, []);

  async function handleToggleEnabled(reminder: ReminderRecord) {
    const { reminder: updated } = await updateReminder(reminder.id, { enabled: !reminder.enabled });
    setReminders((current) => current.map((item) => (item.id === updated.id ? updated : item)));
  }

  async function handleToggleEmail(reminder: ReminderRecord) {
    const { reminder: updated } = await updateReminder(reminder.id, { notifyEmail: !reminder.notifyEmail });
    setReminders((current) => current.map((item) => (item.id === updated.id ? updated : item)));
  }

  async function handleDelete(reminderId: string) {
    if (!window.confirm("Czy na pewno usunąć to przypomnienie?")) return;
    await deleteReminder(reminderId);
    setReminders((current) => current.filter((item) => item.id !== reminderId));
  }

  function handleSaved(reminder: ReminderRecord) {
    setReminders((current) => {
      const exists = current.some((item) => item.id === reminder.id);
      return exists ? current.map((item) => (item.id === reminder.id ? reminder : item)) : [reminder, ...current];
    });
    setFormOpen(false);
    setEditingReminder(null);
  }

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Przypomnienia</p>
          <h3>Moje przypomnienia</h3>
        </div>
        <button
          onClick={() => {
            setEditingReminder(null);
            setFormOpen(true);
          }}
          type="button"
        >
          + Nowe przypomnienie
        </button>
      </div>
      {loading ? <p className="placeholder-copy">Wczytywanie...</p> : null}
      {!loading && reminders.length === 0 ? <EmptyState message="Brak przypomnień." /> : null}
      <div className="reminder-list">
        {reminders.map((reminder) => {
          const isStandalone = !reminder.taskId;
          const metaParts = [
            isStandalone ? "Osobiste" : "Zadanie",
            reminder.clientName ? `Klient: ${reminder.clientName}` : "",
            reminder.projectName ? `Projekt: ${reminder.projectName}` : "",
            reminder.targetUserId !== sessionUser.id ? `Dla: ${reminder.targetUserName}` : "",
            reminder.createdByUserId !== sessionUser.id ? `Od: ${reminder.createdByUserName}` : ""
          ].filter(Boolean);

          return (
            <div className="reminder-item" key={reminder.id}>
              <div>
                <span className={reminder.enabled ? "" : "is-muted"}>
                  <strong>{reminder.title}</strong> — {describeReminder(reminder)}
                </span>
                <p className="placeholder-copy">{metaParts.join(" • ")}</p>
              </div>
              <div className="reminder-item-actions">
                <label className="field-inline-group compact">
                  <input checked={reminder.enabled} onChange={() => void handleToggleEnabled(reminder)} type="checkbox" />
                  <span>Aktywne</span>
                </label>
                <label className="field-inline-group compact">
                  <input checked={reminder.notifyEmail} onChange={() => void handleToggleEmail(reminder)} type="checkbox" />
                  <span>E-mail</span>
                </label>
                {isStandalone ? (
                  <button
                    className="secondary compact-action"
                    onClick={() => {
                      setEditingReminder(reminder);
                      setFormOpen(true);
                    }}
                    type="button"
                  >
                    Edytuj
                  </button>
                ) : null}
                <button
                  aria-label="Usuń przypomnienie"
                  className="secondary compact-action icon-action danger"
                  onClick={() => void handleDelete(reminder.id)}
                  type="button"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {formOpen ? (
        <ReminderFormModal
          canTargetOthers={canTargetOthers}
          clientOptions={clientOptions}
          onClose={() => {
            setFormOpen(false);
            setEditingReminder(null);
          }}
          onSaved={handleSaved}
          projectOptions={projectOptions}
          reminder={editingReminder}
          sessionUser={sessionUser}
          teamMembers={teamMembers}
        />
      ) : null}
    </article>
  );
}

function QuickAddNoteModal({ client, onClose }: { client: ClientRecord; onClose: () => void }) {
  const [title, setTitle] = useState(`Notatka: ${client.companyName}`);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await createNote({ title: title.trim(), body: body || undefined, clientId: client.id });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title={`Nowa notatka - ${client.companyName}`}>
      <div className="task-form stacked-form">
        <label className="field">
          <span>Tytuł</span>
          <input onChange={(event) => setTitle(event.target.value)} value={title} />
        </label>
        <label className="field">
          <span>Treść (opcjonalnie)</span>
          <textarea onChange={(event) => setBody(event.target.value)} rows={4} value={body} />
        </label>
        <div className="task-detail-save-row">
          <button disabled={!title.trim() || saving} onClick={() => void handleSubmit()} type="button">
            {saving ? "Zapisywanie..." : "Zapisz"}
          </button>
          <button className="secondary" onClick={onClose} type="button">
            Anuluj
          </button>
        </div>
      </div>
    </Modal>
  );
}

function TaskDetailPage({
  activeSession,
  allowManage,
  clientOptions = [],
  currentUserId,
  hideExactTime = false,
  isAdmin = false,
  isModal = false,
  onAddWorkSession,
  onDeleteWorkSession,
  onUpdateWorkSession,
  onBack,
  onChangeTask,
  onCreateComment,
  onDeleteTask,
  onLoadDetail,
  onOpenClient,
  onOpenProject,
  onOpenTask,
  onStart,
  onStop,
  projectOptions = [],
  readOnly = false,
  task,
  teamMembers = [],
  canTrackTime,
  canComment
}: {
  activeSession?: ActiveWorkSession | null;
  allowManage: boolean;
  clientOptions?: ClientRecord[];
  currentUserId?: string;
  hideExactTime?: boolean;
  isAdmin?: boolean;
  isModal?: boolean;
  onAddWorkSession?: (taskId: string, payload: AddManualWorkSessionRequest) => Promise<TaskDetailRecord>;
  onDeleteWorkSession?: (taskId: string, sessionId: string) => Promise<TaskDetailRecord>;
  onUpdateWorkSession?: (taskId: string, sessionId: string, payload: UpdateWorkSessionRequest) => Promise<TaskDetailRecord>;
  onBack: () => void;
  onChangeTask: (taskId: string, payload: UpdateTaskRequest) => Promise<void>;
  onCreateComment: (taskId: string, payload: CreateTaskCommentRequest) => Promise<TaskCommentRecord[]>;
  onDeleteTask?: (taskId: string) => Promise<void>;
  onLoadDetail: (taskId: string) => Promise<TaskDetailRecord>;
  onOpenClient?: (clientId: string) => void;
  onOpenProject?: (projectId: string) => void;
  onOpenTask?: (taskId: string) => void;
  onStart: (taskId: string) => void;
  onStop: (sessionId: string) => void;
  projectOptions?: ProjectRecord[];
  readOnly?: boolean;
  task: TaskSummary;
  teamMembers?: EmployeeRecord[];
  canTrackTime?: boolean;
  canComment?: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  const [taskDetail, setTaskDetail] = useState<TaskDetailRecord | null>(null);
  const [trackerActionPending, setTrackerActionPending] = useState(false);
  async function handleTrackerClick(action: () => void) {
    setTrackerActionPending(true);
    try {
      await action();
    } finally {
      setTrackerActionPending(false);
    }
  }
  const [commentBody, setCommentBody] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentBody, setEditingCommentBody] = useState("");
  const [checklistTitle, setChecklistTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAddTime, setShowAddTime] = useState(false);
  const [manualDate, setManualDate] = useState(todayInputValue());
  const [manualHours, setManualHours] = useState("");
  const [manualMinutes, setManualMinutes] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  const [savingTime, setSavingTime] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editSessionDate, setEditSessionDate] = useState("");
  const [editSessionHours, setEditSessionHours] = useState("");
  const [editSessionMinutes, setEditSessionMinutes] = useState("");
  const [editSessionNotes, setEditSessionNotes] = useState("");
  const [savingSessionEdit, setSavingSessionEdit] = useState(false);
  const [editDraft, setEditDraft] = useState(() => ({
    clientId: task.clientId ?? "",
    projectId: task.projectId ?? "",
    description: task.description ?? "",
    assigneeIds: task.assignees.map((assignee) => assignee.id),
    status: task.status,
    dueDate: formatDateInputValue(task.dueDate),
    priority: task.priority,
    budgetType: task.budgetType,
    budgetAmount: task.budgetAmount != null ? String(task.budgetAmount) : ""
  }));
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [savingTitle, setSavingTitle] = useState(false);
  const cancelNextTitleSaveRef = useRef(false);
  const editDraftInitializedRef = useRef(false);

  useEffect(() => {
    editDraftInitializedRef.current = false;
  }, [task.id]);

  useEffect(() => {
    setTitleDraft(task.title);
  }, [task.id, task.title]);

  async function handleTitleSave() {
    if (cancelNextTitleSaveRef.current) {
      cancelNextTitleSaveRef.current = false;
      setTitleDraft(task.title);
      return;
    }
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === task.title) {
      setTitleDraft(task.title);
      return;
    }
    setSavingTitle(true);
    try {
      await onChangeTask(task.id, { title: trimmed });
    } finally {
      setSavingTitle(false);
    }
  }

  const canEdit = allowManage && !readOnly;
  const canDeleteAnyTask = teamMembers.find((member) => member.id === currentUserId)?.canDeleteTasks ?? false;
  const canDelete =
    Boolean(onDeleteTask) && (isAdmin || canDeleteAnyTask || (currentUserId != null && task.createdByUserId === currentUserId));

  useEffect(() => {
    if (!activeSession) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [activeSession]);

  useEffect(() => {
    let cancelled = false;
    void onLoadDetail(task.id)
      .then((detail) => {
        if (!cancelled) setTaskDetail(detail);
      })
      .catch(() => {
        if (!cancelled) setTaskDetail(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  useEffect(() => {
    setEditDraft({
      clientId: task.clientId ?? "",
      projectId: task.projectId ?? "",
      description: task.description ?? "",
      assigneeIds: task.assignees.map((assignee) => assignee.id),
      status: task.status,
      dueDate: formatDateInputValue(task.dueDate),
      priority: task.priority,
      budgetType: task.budgetType,
      budgetAmount: task.budgetAmount != null ? String(task.budgetAmount) : ""
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  useEffect(() => {
    if (!canEdit) return;
    if (!editDraftInitializedRef.current) {
      // Skip the run caused by loading a task / switching between tasks -
      // only field edits made by the user should trigger an autosave.
      editDraftInitializedRef.current = true;
      return;
    }
    const timeout = window.setTimeout(() => {
      void handleSave();
    }, 800);
    return () => window.clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDraft]);

  async function handleSave() {
    setSaving(true);
    try {
      await onChangeTask(task.id, {
        clientId: editDraft.clientId,
        projectId: editDraft.projectId,
        description: editDraft.description,
        assigneeIds: editDraft.assigneeIds,
        dueDate: editDraft.dueDate || null,
        status: editDraft.status,
        priority: editDraft.priority,
        budgetType: editDraft.budgetType,
        budgetAmount: editDraft.budgetType === "FREE" ? null : editDraft.budgetAmount ? Number(editDraft.budgetAmount) : null
      });
      const refreshed = await onLoadDetail(task.id);
      setTaskDetail(refreshed);
    } finally {
      setSaving(false);
    }
  }

  async function handleAddManualTime() {
    if (!onAddWorkSession) return;
    const durationMinutes = (Number(manualHours) || 0) * 60 + (Number(manualMinutes) || 0);
    if (!manualDate || durationMinutes <= 0) return;
    setSavingTime(true);
    try {
      const updated = await onAddWorkSession(task.id, {
        date: manualDate,
        durationMinutes,
        notes: manualNotes.trim() || undefined
      });
      setTaskDetail(updated);
      setManualHours("");
      setManualMinutes("");
      setManualNotes("");
      setManualDate(todayInputValue());
      setShowAddTime(false);
    } finally {
      setSavingTime(false);
    }
  }

  function startEditingSession(session: WorkSessionRecord) {
    setEditingSessionId(session.id);
    setEditSessionDate(formatDateInputValue(session.startedAt));
    setEditSessionHours(String(Math.floor(session.durationMinutes / 60)));
    setEditSessionMinutes(String(session.durationMinutes % 60));
    setEditSessionNotes(session.notes ?? "");
  }

  async function handleUpdateManualTime() {
    if (!onUpdateWorkSession || !editingSessionId) return;
    const durationMinutes = (Number(editSessionHours) || 0) * 60 + (Number(editSessionMinutes) || 0);
    if (!editSessionDate || durationMinutes <= 0) return;
    setSavingSessionEdit(true);
    try {
      const updated = await onUpdateWorkSession(task.id, editingSessionId, {
        date: editSessionDate,
        durationMinutes,
        notes: editSessionNotes.trim() || undefined
      });
      setTaskDetail(updated);
      setEditingSessionId(null);
    } finally {
      setSavingSessionEdit(false);
    }
  }

  async function handleDelete() {
    if (!onDeleteTask) return;
    if (!window.confirm(`Usunąć zadanie "${task.title}"? Ta operacja usunie też powiązane komentarze i sesje czasu.`)) return;
    await onDeleteTask(task.id);
    onBack();
  }

  async function handleAddChecklistItem() {
    if (!taskDetail || !checklistTitle.trim()) return;
    const { items } = await addTaskChecklistItem(taskDetail.id, { title: checklistTitle.trim() });
    setTaskDetail({ ...taskDetail, checklistItems: items });
    setChecklistTitle("");
  }

  async function handleToggleChecklistItem(itemId: string, isDone: boolean) {
    if (!taskDetail) return;
    const { items } = await updateTaskChecklistItem(taskDetail.id, itemId, { isDone });
    setTaskDetail({ ...taskDetail, checklistItems: items });
  }

  async function handleDeleteChecklistItem(itemId: string) {
    if (!taskDetail) return;
    const { items } = await deleteTaskChecklistItem(taskDetail.id, itemId);
    setTaskDetail({ ...taskDetail, checklistItems: items });
  }

  const liveMinutes =
    task.activeSessionId && activeSession?.id === task.activeSessionId
      ? Math.max(0, Math.floor((now - new Date(activeSession.startedAt).getTime()) / 60000))
      : 0;

  return (
    <section className="section-stack task-detail-redesign">
      {!isModal ? (
        <button className="secondary compact-action task-detail-back" onClick={onBack} type="button">
          ← Wróć do listy
        </button>
      ) : null}

      <article className="card task-summary-card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Zadanie</p>
            {canEdit ? (
              <input
                aria-busy={savingTitle}
                className="task-detail-title-input"
                onBlur={() => void handleTitleSave()}
                onChange={(event) => setTitleDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.currentTarget.blur();
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    cancelNextTitleSaveRef.current = true;
                    event.currentTarget.blur();
                  }
                }}
                value={titleDraft}
              />
            ) : (
              <h3>{task.title}</h3>
            )}
          </div>
          <div className="card-header-actions">
            <StatusBadge tone="priority" value={task.priority} />
            {taskDetail ? <StatusBadge value={taskDetail.status} /> : null}
            {(canTrackTime ?? canEdit) && task.activeSessionId && activeSession?.id === task.activeSessionId ? (
              <button
                aria-label="Zatrzymaj tracker"
                className={`secondary tracker-action stop${trackerActionPending ? " is-loading" : ""}`}
                disabled={trackerActionPending}
                onClick={() => void handleTrackerClick(() => onStop(task.activeSessionId!))}
                type="button"
              >
                {trackerActionPending ? <Spinner /> : <span className="tracker-icon stop" aria-hidden="true" />}
              </button>
            ) : (canTrackTime ?? canEdit) ? (
              <button
                aria-label="Uruchom tracker"
                className={`secondary tracker-action play${trackerActionPending ? " is-loading" : ""}`}
                disabled={trackerActionPending}
                onClick={() => void handleTrackerClick(() => onStart(task.id))}
                type="button"
              >
                {trackerActionPending ? <Spinner /> : <span className="tracker-icon play" aria-hidden="true" />}
              </button>
            ) : null}
            {canDelete ? (
              <button
                aria-label="Usuń zadanie"
                className="secondary compact-action icon-action danger"
                onClick={() => void handleDelete()}
                title="Usuń zadanie"
                type="button"
              >
                <TrashIcon />
              </button>
            ) : null}
          </div>
        </div>

        {canEdit ? (
          <p className="task-detail-meta">
            Utworzono {formatDateTimeLabel(taskDetail?.createdAt)}
            {taskDetail?.createdByName ? ` przez ${taskDetail.createdByName}` : ""}
            {" • "}Łączny czas: {formatDurationLabel(task.trackedMinutes)}
          </p>
        ) : null}
      </article>

      {canEdit ? (
        <article className="card detail-block task-detail-edit-card">
          <div className="card-header compact-head">
            <p className="eyebrow">Edycja zadania</p>
          </div>
          <div className="task-detail-edit-grid">
            <label className="field">
              <span>Status</span>
              <select
                className={`status-select status-${toBadgeClass(editDraft.status)}`}
                onChange={(event) => setEditDraft({ ...editDraft, status: event.target.value as TaskSummary["status"] })}
                value={editDraft.status}
              >
                <option value="TODO">Do zrobienia</option>
                <option value="IN_PROGRESS">W toku</option>
                <option value="BLOCKED">Zablokowane</option>
                <option value="REVIEW">Do akceptacji</option>
                <option value="DONE">Zamknięte</option>
                <option value="TO_BE_BILLED">Do rozliczenia</option>
                <option value="BILLED">Rozliczone</option>
              </select>
            </label>
            <label className="field">
              <span>Zespół</span>
              <AssigneeCluster
                assignees={teamMembers.filter((member) => editDraft.assigneeIds.includes(member.id))}
                editable
                employees={teamMembers}
                onChange={(assigneeIds) => setEditDraft({ ...editDraft, assigneeIds })}
              />
            </label>
            <label className="field">
              <span>Termin</span>
              <input
                onChange={(event) => setEditDraft({ ...editDraft, dueDate: event.target.value })}
                type="date"
                value={editDraft.dueDate}
              />
            </label>
            <label className="field">
              <span>Priorytet</span>
              <select
                onChange={(event) => setEditDraft({ ...editDraft, priority: event.target.value as TaskPriority })}
                value={editDraft.priority}
              >
                <option value="LOW">Niski</option>
                <option value="MEDIUM">Średni</option>
                <option value="HIGH">Wysoki</option>
                <option value="URGENT">Pilny</option>
              </select>
            </label>
            <label className="field">
              <span>Klient</span>
              <ClientPickerField
                currentLabel={task.clientName}
                emptyOptionLabel="Klient"
                onChange={(nextClientId) => setEditDraft({ ...editDraft, clientId: nextClientId, projectId: "" })}
                value={editDraft.clientId}
              />
            </label>
            <label className="field">
              <span>Projekt</span>
              <select onChange={(event) => setEditDraft({ ...editDraft, projectId: event.target.value })} value={editDraft.projectId}>
                <option value="">Projekt</option>
                {projectOptions
                  .filter((project) => project.clientId === editDraft.clientId)
                  .map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="field">
              <span>Budżet</span>
              <select
                onChange={(event) => setEditDraft({ ...editDraft, budgetType: event.target.value as TaskBudgetType })}
                value={editDraft.budgetType}
              >
                <option value="FREE">Bezpłatne</option>
                <option value="FIXED">Cena stała</option>
                <option value="HOURLY">Godzinowo</option>
              </select>
            </label>
            {editDraft.budgetType !== "FREE" ? (
              <label className="field">
                <span>{editDraft.budgetType === "FIXED" ? "Cena stała (zl)" : "Stawka godzinowa (zl/h, opcjonalnie)"}</span>
                <input
                  onChange={(event) => setEditDraft({ ...editDraft, budgetAmount: event.target.value })}
                  placeholder={editDraft.budgetType === "FIXED" ? "Np. 1500" : "Domyślnie stawka pracownika"}
                  type="number"
                  value={editDraft.budgetAmount}
                />
              </label>
            ) : null}
          </div>
        </article>
      ) : null}

      <div className="task-detail-columns">
        <div className="task-detail-col-left">
          <article className="card detail-block task-detail-info-card">
            <div className="task-detail-info-grid">
              {task.parentTaskId ? (
                <div>
                  <span>Zadanie główne</span>
                  <strong>
                    <EntityLink
                      label={taskDetail?.parentTaskTitle ?? task.parentTaskTitle ?? "Otwórz zadanie główne"}
                      onClick={onOpenTask ? () => onOpenTask(task.parentTaskId!) : undefined}
                    />
                  </strong>
                </div>
              ) : null}
              <div>
                <span>Klient</span>
                <strong>
                  {task.clientId && task.clientName ? (
                    <EntityLink label={task.clientName} onClick={() => onOpenClient?.(task.clientId!)} />
                  ) : (
                    "Bez klienta"
                  )}
                </strong>
              </div>
              <div>
                <span>Projekt</span>
                <strong>
                  {task.projectName && task.projectId ? (
                    <EntityLink label={task.projectName} onClick={() => onOpenProject?.(task.projectId!)} />
                  ) : (
                    "Brak"
                  )}
                </strong>
              </div>
              <div>
                <span>Termin</span>
                <strong>{formatDueDateLabel(task.dueDate)}</strong>
              </div>
              <div>
                <span>Czas</span>
                <strong>{hideExactTime ? `${task.roundedHoursForClient} h` : formatDurationLabel(task.trackedMinutes + liveMinutes)}</strong>
              </div>
              <div>
                <span>Budżet</span>
                <strong>
                  {task.budgetType === "FREE"
                    ? "Bezpłatne"
                    : task.budgetType === "FIXED"
                      ? "Cena stała"
                      : "Godzinowo"}
                </strong>
              </div>
              <div>
                <span>Koszt</span>
                <strong>{formatMoneyLabel(task.costAmount)}</strong>
              </div>
            </div>
          </article>

          <TaskReminders readOnly={readOnly} task={task} />
        </div>

        <div className="task-detail-col-right">
          <article className="card detail-block">
            <div className="card-header compact-head">
              <p className="eyebrow">Opis</p>
            </div>
            {canEdit ? (
              <>
                <label className="field">
                  <textarea
                    onChange={(event) => setEditDraft({ ...editDraft, description: event.target.value })}
                    value={editDraft.description}
                  />
                </label>
                <div className="task-detail-save-row">
                  <button disabled={saving} onClick={() => void handleSave()} type="button">
                    {saving ? "Zapisywanie..." : "Zapisz zmiany"}
                  </button>
                </div>
              </>
            ) : task.description ? (
              <p className="placeholder-copy">{task.description}</p>
            ) : null}
          </article>

          {!taskDetail ? (
            <p className="placeholder-copy">Wczytywanie szczegółów zadania...</p>
          ) : (
            <>
              <article className="card detail-block">
                <div className="card-header compact-head">
                  <div>
                    <p className="eyebrow">Checklista</p>
                  </div>
                  {taskDetail.checklistItems.length > 0 ? (
                    <span className="ghost-tag">
                      {taskDetail.checklistItems.filter((item) => item.isDone).length}/{taskDetail.checklistItems.length}
                    </span>
                  ) : null}
                </div>
                <div className="checklist">
                  {taskDetail.checklistItems.map((item) => (
                    <label className={item.isDone ? "checklist-item is-done" : "checklist-item"} key={item.id}>
                      <input
                        checked={item.isDone}
                        disabled={readOnly}
                        onChange={(event) => void handleToggleChecklistItem(item.id, event.target.checked)}
                        type="checkbox"
                      />
                      <span>{item.title}</span>
                      {!readOnly ? (
                        <button
                          aria-label="Usuń pozycję checklisty"
                          className="secondary compact-action icon-action danger"
                          onClick={() => void handleDeleteChecklistItem(item.id)}
                          title="Usuń pozycję checklisty"
                          type="button"
                        >
                          <TrashIcon />
                        </button>
                      ) : null}
                    </label>
                  ))}
                  {taskDetail.checklistItems.length === 0 ? <p className="placeholder-copy">Brak pozycji checklisty.</p> : null}
                </div>
                {!readOnly ? (
                  <form
                    className="checklist-add-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleAddChecklistItem();
                    }}
                  >
                    <input
                      onChange={(event) => setChecklistTitle(event.target.value)}
                      placeholder="Dodaj pozycję checklisty"
                      value={checklistTitle}
                    />
                    <button type="submit">Dodaj</button>
                  </form>
                ) : null}
              </article>

              <article className="card detail-block">
                <div className="card-header compact-head">
                  <div>
                    <p className="eyebrow">Komentarze</p>
                  </div>
                </div>
                <div className="message-thread">
                  {taskDetail.comments.map((comment) => {
                    const canEditComment = comment.authorId === currentUserId;
                    const canDeleteComment = canEditComment || isAdmin;
                    return (
                      <div className="message-card" key={comment.id}>
                        <div className="message-card-head">
                          <strong>
                            {comment.authorName} • {roleBadge(comment.authorRole)}
                          </strong>
                          {canEditComment || canDeleteComment ? (
                            <div className="message-card-actions">
                              {canEditComment ? (
                                <button
                                  className="secondary compact-action icon-action"
                                  onClick={() => {
                                    setEditingCommentId(comment.id);
                                    setEditingCommentBody(comment.body);
                                  }}
                                  title="Edytuj komentarz"
                                  type="button"
                                >
                                  <PencilIcon />
                                </button>
                              ) : null}
                              {canDeleteComment ? (
                                <button
                                  className="secondary compact-action icon-action danger"
                                  onClick={() => {
                                    if (!window.confirm("Usunąć ten komentarz?")) return;
                                    void deleteTaskComment(taskDetail.id, comment.id).then((response) => {
                                      setTaskDetail({ ...taskDetail, comments: response.items });
                                    });
                                  }}
                                  title="Usuń komentarz"
                                  type="button"
                                >
                                  <TrashIcon />
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        {editingCommentId === comment.id ? (
                          <form
                            className="task-form stacked-form"
                            onSubmit={(event) => {
                              event.preventDefault();
                              if (!editingCommentBody.trim()) return;
                              void updateTaskComment(taskDetail.id, comment.id, { body: editingCommentBody }).then((response) => {
                                setTaskDetail({ ...taskDetail, comments: response.items });
                                setEditingCommentId(null);
                              });
                            }}
                          >
                            <textarea
                              autoFocus
                              onChange={(event) => setEditingCommentBody(event.target.value)}
                              value={editingCommentBody}
                            />
                            <div className="field-inline-group">
                              <button type="submit">Zapisz</button>
                              <button className="secondary" onClick={() => setEditingCommentId(null)} type="button">
                                Anuluj
                              </button>
                            </div>
                          </form>
                        ) : (
                          <p>{comment.body}</p>
                        )}
                        <span>
                          {formatDateTimeLabel(comment.createdAt)}
                          {comment.editedAt ? " • edytowano" : ""}
                        </span>
                      </div>
                    );
                  })}
                  {taskDetail.comments.length === 0 ? <p className="placeholder-copy">Brak komentarzy.</p> : null}
                </div>
                {(canComment ?? !readOnly) ? (
                  <form
                    className="task-form stacked-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!commentBody.trim()) return;
                      void onCreateComment(taskDetail.id, { body: commentBody }).then((items) => {
                        setTaskDetail({ ...taskDetail, comments: items });
                        setCommentBody("");
                      });
                    }}
                  >
                    <textarea onChange={(event) => setCommentBody(event.target.value)} placeholder="Dodaj komentarz do zadania" value={commentBody} />
                    <button type="submit">Dodaj komentarz</button>
                  </form>
                ) : null}
              </article>

              <article className="card detail-block">
                <div className="card-header compact-head">
                  <div>
                    <p className="eyebrow">Sesje czasu</p>
                  </div>
                  {canEdit && onAddWorkSession ? (
                    <button className="secondary compact-action" onClick={() => setShowAddTime(true)} type="button">
                      + Dodaj czas
                    </button>
                  ) : null}
                </div>
                {hideExactTime ? (
                  <p className="placeholder-copy">Łączny czas pracy: {task.roundedHoursForClient} h.</p>
                ) : (
                  <div className="detail-session-list">
                    {taskDetail.workSessions.map((session) => (
                      <div className="detail-session-row" key={session.id}>
                        <strong>{session.employeeName}</strong>
                        <span>
                          {formatDurationLabel(session.durationMinutes)}
                          {session.isManual ? <span className="ghost-tag">Ręczny wpis</span> : null}
                        </span>
                        <em>
                          {formatDateTimeLabel(session.startedAt)} • {session.endedAt ? "zakończona" : "aktywna"}
                        </em>
                        {canEdit && session.endedAt ? (
                          <div className="task-row-actions">
                            {onUpdateWorkSession ? (
                              <button
                                aria-label="Edytuj wpis czasu"
                                className="secondary compact-action icon-action"
                                onClick={() => startEditingSession(session)}
                                title="Edytuj wpis czasu"
                                type="button"
                              >
                                <PencilIcon />
                              </button>
                            ) : null}
                            {onDeleteWorkSession ? (
                              <button
                                aria-label="Usuń wpis czasu"
                                className="secondary compact-action icon-action danger"
                                onClick={() => {
                                  if (!window.confirm("Usunąć ten wpis czasu?")) return;
                                  void onDeleteWorkSession(task.id, session.id).then(setTaskDetail);
                                }}
                                title="Usuń wpis czasu"
                                type="button"
                              >
                                <TrashIcon />
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ))}
                    {taskDetail.workSessions.length === 0 ? <p className="placeholder-copy">Brak zarejestrowanych sesji czasu.</p> : null}
                  </div>
                )}
              </article>

              {allowManage ? (
                <article className="card detail-block">
                  <div className="card-header compact-head">
                    <div>
                      <p className="eyebrow">Historia zmian</p>
                    </div>
                  </div>
                  <div className="detail-session-list">
                    {taskDetail.activityLog.map((entry) => (
                      <div className="detail-session-row" key={entry.id}>
                        <strong>{entry.actorName}</strong>
                        <span>{entry.message}</span>
                        <em>{formatDateTimeLabel(entry.createdAt)}</em>
                      </div>
                    ))}
                    {taskDetail.activityLog.length === 0 ? <p className="placeholder-copy">Brak zarejestrowanych zmian.</p> : null}
                  </div>
                </article>
              ) : null}
            </>
          )}
        </div>
      </div>

      {editingSessionId ? (
        <Modal onClose={() => setEditingSessionId(null)} title="Edytuj wpis czasu">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleUpdateManualTime();
            }}
          >
            <label className="field">
              <span>Data</span>
              <input max={todayInputValue()} onChange={(event) => setEditSessionDate(event.target.value)} type="date" value={editSessionDate} />
            </label>
            <label className="field">
              <span>Godziny</span>
              <input min="0" onChange={(event) => setEditSessionHours(event.target.value)} placeholder="0" type="number" value={editSessionHours} />
            </label>
            <label className="field">
              <span>Minuty</span>
              <input
                max="59"
                min="0"
                onChange={(event) => setEditSessionMinutes(event.target.value)}
                placeholder="0"
                type="number"
                value={editSessionMinutes}
              />
            </label>
            <label className="field">
              <span>Notatka (opcjonalnie)</span>
              <input onChange={(event) => setEditSessionNotes(event.target.value)} placeholder="Np. powód ręcznego wpisu" value={editSessionNotes} />
            </label>
            <button disabled={savingSessionEdit} type="submit">
              {savingSessionEdit ? "Zapisywanie..." : "Zapisz zmiany"}
            </button>
          </form>
        </Modal>
      ) : null}

      {showAddTime && onAddWorkSession ? (
        <Modal onClose={() => setShowAddTime(false)} title="Dodaj czas ręcznie">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleAddManualTime();
            }}
          >
            <label className="field">
              <span>Data</span>
              <input max={todayInputValue()} onChange={(event) => setManualDate(event.target.value)} type="date" value={manualDate} />
            </label>
            <label className="field">
              <span>Godziny</span>
              <input min="0" onChange={(event) => setManualHours(event.target.value)} placeholder="0" type="number" value={manualHours} />
            </label>
            <label className="field">
              <span>Minuty</span>
              <input max="59" min="0" onChange={(event) => setManualMinutes(event.target.value)} placeholder="0" type="number" value={manualMinutes} />
            </label>
            <label className="field">
              <span>Notatka (opcjonalnie)</span>
              <input onChange={(event) => setManualNotes(event.target.value)} placeholder="Np. powód ręcznego wpisu" value={manualNotes} />
            </label>
            <button disabled={savingTime} type="submit">
              {savingTime ? "Zapisywanie..." : "Dodaj czas"}
            </button>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}

function TicketsTable({
  tickets = [],
  onOpenClient
}: {
  tickets: TicketRecord[];
  onOpenClient?: (clientId: string) => void;
}) {
  return (
    <div className="table">
      <div className="table-head row row-ticket row-head">
        <span>Zgłoszenie</span>
        <span>Status</span>
        <span>Priorytet</span>
        <span>Aktualizacja</span>
      </div>
      {tickets.map((ticket) => (
        <div className="row row-ticket" key={ticket.id}>
          <div>
            <div className="ticket-list-item-title">
              {ticket.hasUnreadMessage ? (
                <span className="ticket-unread-dot" title="Nowa wiadomość" aria-label="Nowa wiadomość" />
              ) : null}
              <strong>{ticket.subject}</strong>
            </div>
            <p>
              <EntityLink label={ticket.clientName} muted onClick={() => onOpenClient?.(ticket.clientId)} /> • #{ticket.ticketNumber}
            </p>
          </div>
          <StatusBadge tone="ticket" value={ticket.status} />
          <StatusBadge tone="priority" value={ticket.priority} />
          <span>{formatDateTimeLabel(ticket.lastMessageAt)}</span>
        </div>
      ))}
    </div>
  );
}

// Fetches only the first handful of clients (for the admin home page's small preview card)
// instead of depending on the full roster that used to be preloaded into every dashboard load.
function RecentClientsCard({ onOpenClient }: { onOpenClient?: (clientId: string) => void }) {
  const [clients, setClients] = useState<ClientRecord[]>([]);

  useEffect(() => {
    fetchClientsPage({ pageSize: 6 })
      .then((response) => setClients(response.items))
      .catch(() => {});
  }, []);

  return <ClientsTable clients={clients} onOpenClient={onOpenClient} />;
}

function ClientsTable({
  clients = [],
  onOpenClient
}: {
  clients: ClientRecord[];
  onOpenClient?: (clientId: string) => void;
}) {
  return (
    <div className="table">
      <div className="table-head row row-client row-head">
        <span>Firma</span>
        <span className="cell-numeric">Projekty</span>
        <span className="cell-numeric">Zadania</span>
        <span className="cell-numeric">Tickety</span>
        <span className="cell-numeric">Saldo</span>
      </div>
      {clients.map((client) => (
        <div className="row row-client" key={client.id}>
          <div>
            <strong>
              <EntityLink label={client.companyName} onClick={() => onOpenClient?.(client.id)} />
            </strong>
            <p>{client.billingEmail}</p>
          </div>
          <span className="cell-numeric">{client.activeProjects}</span>
          <span className="cell-numeric">{client.taskCount}</span>
          <span className="cell-numeric">{client.openTickets} tickety</span>
          <span className="cell-numeric">{client.balance}</span>
        </div>
      ))}
    </div>
  );
}

function ProjectProgressBar({
  done,
  total,
  tone = "default"
}: {
  done: number;
  total: number;
  tone?: "default" | "danger";
}) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className={tone === "danger" ? "progress-bar tone-danger" : "progress-bar"} title={`${done}/${total} zadań ukończonych (${pct}%)`}>
      <div className="progress-bar-track">
        <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="progress-bar-label">{pct}%</span>
    </div>
  );
}

const PROJECTS_PAGE_SIZE = 20;

function ProjectsTable({
  projects = [],
  onOpenClient,
  onOpenProject
}: {
  projects: ProjectRecord[];
  onOpenClient?: (clientId: string) => void;
  onOpenProject?: (projectId: string) => void;
}) {
  return (
    <div className="table">
      <div className="table-head row row-project row-head">
        <span>Projekt</span>
        <span>Klient</span>
        <span>Status</span>
        <span>Zadania</span>
      </div>
      {projects.map((project) => (
        <div className="row row-project" key={project.id}>
          <div>
            <strong>
              <EntityLink label={project.name} onClick={() => onOpenProject?.(project.id)} />
            </strong>
            <p>{project.description || "Brak opisu"}</p>
          </div>
          <span>
            <EntityLink label={project.clientName} muted onClick={() => onOpenClient?.(project.clientId)} />
          </span>
          <StatusBadge value={project.status} />
          <div className="project-progress-cell">
            <span>{project.doneTaskCount}/{project.taskCount}</span>
            <ProjectProgressBar done={project.doneTaskCount} total={project.taskCount} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ProjectAdminTable({
  onDelete,
  onOpenClient,
  onOpenProject,
  onUpdate,
  projects
}: {
  onDelete: (projectId: string) => Promise<void>;
  onOpenClient?: (clientId: string) => void;
  onOpenProject?: (projectId: string) => void;
  onUpdate: (projectId: string, payload: UpdateProjectRequest) => Promise<void>;
  projects: ProjectRecord[];
}) {
  return (
    <div className="table">
      <div className="table-head row row-project-admin row-head">
        <span>Projekt</span>
        <span>Klient</span>
        <span>Status</span>
        <span className="cell-numeric">Zadania</span>
        <span>Akcje</span>
      </div>
      {projects.map((project) => (
        <div className="row row-project-admin" key={project.id}>
          <div>
            <strong>
              <EntityLink label={project.name} onClick={() => onOpenProject?.(project.id)} />
            </strong>
            <p>{project.description || "Brak opisu"}</p>
          </div>
          <span>
            <EntityLink label={project.clientName} muted onClick={() => onOpenClient?.(project.clientId)} />
          </span>
          <StatusBadge value={project.status} />
          <span className="cell-numeric">{project.doneTaskCount}/{project.taskCount}</span>
          <div className="task-row-actions">
            <button
              aria-label={project.status === "ON_HOLD" ? "Wznów projekt" : "Wstrzymaj projekt"}
              className="secondary compact-action icon-action"
              onClick={() => {
                void onUpdate(project.id, {
                  ...projectToUpdatePayload(project),
                  status: project.status === "ON_HOLD" ? "ACTIVE" : "ON_HOLD"
                });
              }}
              title={project.status === "ON_HOLD" ? "Wznów projekt" : "Wstrzymaj projekt"}
              type="button"
            >
              <LockIcon />
            </button>
            <button
              aria-label="Usuń projekt"
              className="secondary compact-action icon-action danger"
              onClick={() => {
                if (
                  !window.confirm(`Usunąć projekt "${project.name}"? Projekt zostanie zdezaktywowany - zadania pozostaną zachowane jako "bez projektu".`)
                )
                  return;
                void onDelete(project.id);
              }}
              title="Usuń projekt"
              type="button"
            >
              <TrashIcon />
            </button>
          </div>
        </div>
      ))}
      {projects.length === 0 ? <EmptyState message="Brak projektów pasujących do wybranych filtrów." /> : null}
    </div>
  );
}

function ProjectManager({
  onCreateProject,
  onDeleteProject,
  onOpenClient,
  onOpenProject,
  onUpdateProject,
  projects
}: {
  onCreateProject: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onDeleteProject: (projectId: string) => Promise<void>;
  onOpenClient?: (clientId: string) => void;
  onOpenProject?: (projectId: string) => void;
  onUpdateProject: (projectId: string, payload: UpdateProjectRequest) => Promise<void>;
  projects: ProjectRecord[];
}) {
  const { pending: creatingProject, run: runCreateProject } = useAsyncAction();
  const [draft, setDraft] = useState<CreateProjectRequest>({
    clientId: "",
    name: "",
    description: ""
  });
  const [showAddProject, setShowAddProject] = useState(false);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(projects.length / PROJECTS_PAGE_SIZE));
  const pagedProjects = projects.slice((page - 1) * PROJECTS_PAGE_SIZE, page * PROJECTS_PAGE_SIZE);

  return (
    <section className="section-stack">
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Projekty</p>
            <h3>Wszystkie projekty w systemie</h3>
          </div>
          <button onClick={() => setShowAddProject(true)} type="button">
            + Dodaj projekt
          </button>
        </div>
        <ProjectAdminTable onDelete={onDeleteProject} onOpenClient={onOpenClient} onOpenProject={onOpenProject} onUpdate={onUpdateProject} projects={pagedProjects} />
        {totalPages > 1 ? (
          <div className="task-list-pagination">
            <button className="secondary compact-action" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">
              ‹ Poprzednia
            </button>
            <span>
              Strona {page} z {totalPages}
            </span>
            <button
              className="secondary compact-action"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              type="button"
            >
              Następna ›
            </button>
          </div>
        ) : null}
      </article>

      {showAddProject ? (
        <Modal onClose={() => setShowAddProject(false)} title="Nowy projekt">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!draft.name.trim() || !draft.clientId) return;
              void runCreateProject(async () => {
                await onCreateProject(draft);
                setDraft({ clientId: "", name: "", description: "" });
                setShowAddProject(false);
              });
            }}
          >
            <label className="field">
              <span>Klient</span>
              <ClientPickerField
                emptyOptionLabel="Wybierz klienta"
                onChange={(nextClientId) => setDraft({ ...draft, clientId: nextClientId })}
                value={draft.clientId}
              />
            </label>
            <label className="field">
              <span>Nazwa projektu</span>
              <input onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="Nazwa projektu" value={draft.name} />
            </label>
            <label className="field">
              <span>Opis</span>
              <input onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder="Opis projektu" value={draft.description ?? ""} />
            </label>
            <button disabled={creatingProject} type="submit">
              {creatingProject ? <Spinner label="Dodawanie..." /> : "Dodaj projekt"}
            </button>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}

function EmployeesTable({ employees = [] }: { employees: EmployeeRecord[] }) {
  return (
    <div className="table">
      <div className="table-head row row-client row-head">
        <span>Pracownik</span>
        <span>Stawka</span>
        <span>Dzisiaj</span>
        <span>Status</span>
      </div>
      {employees.map((employee) => (
        <div className="row row-client" key={employee.id}>
          <div>
            <strong>{employee.fullName}</strong>
            <p>
              {employee.title} • {employeeRoleLabel(employee.role)}
              {employee.departments.length > 0 ? ` • ${employee.departments.join(", ")}` : ""}
            </p>
          </div>
          <span>{employee.hourlyRate}</span>
          <span>{employee.trackedToday}</span>
          <StatusBadge value={employee.status} />
        </div>
      ))}
    </div>
  );
}

function DepartmentsTable({ departments = [] }: { departments: DepartmentRecord[] }) {
  return (
    <div className="table">
      <div className="table-head row row-department row-head">
        <span>Dział</span>
        <span>Opis</span>
        <span>Członkowie</span>
      </div>
      {departments.map((department) => (
        <div className="row row-department" key={department.id}>
          <div>
            <strong>{department.name}</strong>
          </div>
          <span>{department.description || "Brak opisu"}</span>
          <span>{department.memberCount}</span>
        </div>
      ))}
    </div>
  );
}

function AssigneePicker({
  employees,
  selectedIds,
  onToggle
}: {
  employees: EmployeeRecord[];
  selectedIds: string[];
  onToggle: (employeeId: string) => void;
}) {
  return (
    <div className="assignee-picker">
      {employees.map((employee) => {
        const checked = selectedIds.includes(employee.id);

        return (
          <label className={checked ? "assignee-chip is-active" : "assignee-chip"} key={employee.id}>
            <input checked={checked} onChange={() => onToggle(employee.id)} type="checkbox" />
            <span>{employee.fullName}</span>
            <em>{employee.departments.join(", ") || employee.title}</em>
          </label>
        );
      })}
    </div>
  );
}

function DepartmentPicker({
  departments,
  selectedIds,
  onToggle
}: {
  departments: DepartmentRecord[];
  selectedIds: string[];
  onToggle: (departmentId: string) => void;
}) {
  return (
    <div className="assignee-picker">
      {departments.map((department) => {
        const checked = selectedIds.includes(department.id);

        return (
          <label className={checked ? "assignee-chip is-active" : "assignee-chip"} key={department.id}>
            <input checked={checked} onChange={() => onToggle(department.id)} type="checkbox" />
            <span>{department.name}</span>
            <em>{department.description || `${department.memberCount} osób`}</em>
          </label>
        );
      })}
    </div>
  );
}

function ClientAdminTable({
  allowManage = false,
  clients,
  onDelete,
  onOpenClient,
  onUpdate,
  onQuickAddNote,
  onQuickAddReminder
}: {
  allowManage?: boolean;
  clients: ClientRecord[];
  onDelete?: (clientId: string) => Promise<void>;
  onOpenClient?: (clientId: string) => void;
  onUpdate?: (clientId: string, payload: UpdateClientRequest) => Promise<void>;
  onQuickAddNote?: (client: ClientRecord) => void;
  onQuickAddReminder?: (client: ClientRecord) => void;
}) {
  return (
    <div className="table">
      <div className="table-head row row-client-admin row-head">
        <span>Firma</span>
        <span className="cell-numeric">Projekty</span>
        <span className="cell-numeric">Zadania</span>
        <span className="cell-numeric">Tickety</span>
        <span className="cell-numeric">Saldo</span>
        <span>Źródło</span>
        <span>Status</span>
        <span>Akcje</span>
      </div>
      {clients.map((client) => (
        <div className="row row-client-admin" key={client.id}>
          <div>
            <strong>
              <EntityLink label={client.companyName} onClick={() => onOpenClient?.(client.id)} />
            </strong>
            <p>{client.billingEmail}</p>
          </div>
          <span className="cell-numeric">{client.activeProjects}</span>
          <span className="cell-numeric">{client.taskCount}</span>
          <span className="cell-numeric">{client.openTickets} tickety</span>
          <span className="cell-numeric">{client.balance}</span>
          <span className="badge">{client.sourceLabel}</span>
          <StatusBadge value={client.status} />
          <div className="task-row-actions">
            {onQuickAddNote ? (
              <button
                aria-label="Dodaj notatkę"
                className="secondary compact-action icon-action"
                onClick={() => onQuickAddNote(client)}
                title="Dodaj notatkę dla tego klienta"
                type="button"
              >
                <MessageIcon />
              </button>
            ) : null}
            {onQuickAddReminder ? (
              <button
                aria-label="Dodaj przypomnienie"
                className="secondary compact-action icon-action"
                onClick={() => onQuickAddReminder(client)}
                title="Dodaj przypomnienie dla tego klienta"
                type="button"
              >
                <BellIcon />
              </button>
            ) : null}
            {allowManage && onUpdate && onDelete ? (
              <>
                <button
                  className="secondary compact-action"
                  onClick={() => onOpenClient?.(client.id)}
                  title="Raport czasu i kosztów pracy dla tego klienta"
                  type="button"
                >
                  Raport
                </button>
                <button
                  aria-label={client.status === "BLOCKED" ? "Odblokuj klienta" : "Zablokuj klienta"}
                  className="secondary compact-action icon-action"
                  onClick={() => {
                    void onUpdate(client.id, {
                      ...clientToUpdatePayload(client),
                      status: client.status === "BLOCKED" ? "ACTIVE" : "BLOCKED"
                    });
                  }}
                  title={client.status === "BLOCKED" ? "Odblokuj klienta" : "Zablokuj klienta"}
                  type="button"
                >
                  <LockIcon />
                </button>
                <button
                  aria-label="Usuń klienta"
                  className="secondary compact-action icon-action danger"
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Usunąć klienta "${client.companyName}"? Konto zostanie zdezaktywowane - historia projektów, zadań i ticketów zostanie zachowana.`
                      )
                    )
                      return;
                    void onDelete(client.id);
                  }}
                  title="Usuń klienta"
                  type="button"
                >
                  <TrashIcon />
                </button>
              </>
            ) : null}
          </div>
        </div>
      ))}
      {clients.length === 0 ? <EmptyState message="Brak klientów pasujących do wyszukiwania." /> : null}
    </div>
  );
}

const CLIENTS_PAGE_SIZE = 20;

// Loads the client roster only when this page is actually opened, and paginates/searches at the
// database level - previously the entire (now 24,000+ row) client list was embedded in every
// dashboard load and 30s poll just so this page could slice it client-side.
function ClientDirectory({
  employees = [],
  projects = [],
  sessionUser,
  allowCreate = false,
  allowManage = false,
  onCreateClient,
  onDeleteClient,
  onOpenClient,
  onUpdateClient
}: {
  employees?: EmployeeRecord[];
  projects?: ProjectRecord[];
  sessionUser?: SessionUser;
  allowCreate?: boolean;
  allowManage?: boolean;
  onCreateClient?: (payload: CreateClientRequest) => Promise<ClientRecord>;
  onCreateProject?: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onDeleteClient?: (clientId: string) => Promise<void>;
  onOpenClient?: (clientId: string) => void;
  onOpenProject?: (projectId: string) => void;
  onUpdateClient?: (clientId: string, payload: UpdateClientRequest) => Promise<void>;
}) {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [page, setPage] = useState(1);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sourceOptions, setSourceOptions] = useState<ClientSourceOption[]>([]);
  const [clientForm, setClientForm] = useState<CreateClientRequest>({
    companyName: "",
    billingEmail: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    vatNumber: "",
    accountManagerId: "",
    emailsEnabled: false
  });
  const [showAddClient, setShowAddClient] = useState(false);
  const { pending: creatingClient, run: runCreateClient } = useAsyncAction();
  const [quickNoteClient, setQuickNoteClient] = useState<ClientRecord | null>(null);
  const [quickReminderClient, setQuickReminderClient] = useState<ClientRecord | null>(null);

  useEffect(() => {
    fetchClientSources()
      .then((response) => setSourceOptions(response.items))
      .catch(() => {});
  }, []);

  function loadClients() {
    setLoading(true);
    return fetchClientsPage({ page, pageSize: CLIENTS_PAGE_SIZE, search: search.trim() || undefined, sourceKey: sourceFilter || undefined })
      .then((response) => {
        setClients(response.items);
        setTotal(response.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sourceFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadClients();
    }, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, sourceFilter]);

  const handleUpdate = onUpdateClient
    ? async (clientId: string, payload: UpdateClientRequest) => {
        await onUpdateClient(clientId, payload);
        void loadClients();
      }
    : undefined;

  const handleDelete = onDeleteClient
    ? async (clientId: string) => {
        await onDeleteClient(clientId);
        void loadClients();
      }
    : undefined;

  const totalPages = Math.max(1, Math.ceil(total / CLIENTS_PAGE_SIZE));

  return (
    <section className="section-stack">
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Klienci</p>
            <h3>Baza firm</h3>
          </div>
          {allowCreate && onCreateClient ? (
            <button onClick={() => setShowAddClient(true)} type="button">
              + Dodaj klienta
            </button>
          ) : null}
        </div>
        <div className="employee-toolbar">
          <input onChange={(event) => setSearch(event.target.value)} placeholder="Szukaj firmy, emaila lub telefonu" value={search} />
          <select onChange={(event) => setSourceFilter(event.target.value)} value={sourceFilter}>
            <option value="">Wszystkie źródła</option>
            {sourceOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {loading ? <p className="placeholder-copy">Wczytywanie...</p> : null}
        <ClientAdminTable
          allowManage={allowManage}
          clients={clients}
          onDelete={handleDelete}
          onOpenClient={onOpenClient}
          onQuickAddNote={(client) => setQuickNoteClient(client)}
          onQuickAddReminder={(client) => setQuickReminderClient(client)}
          onUpdate={handleUpdate}
        />
        {totalPages > 1 ? (
          <div className="task-list-pagination">
            <button className="secondary compact-action" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">
              ‹ Poprzednia
            </button>
            <span>
              Strona {page} z {totalPages}
            </span>
            <button
              className="secondary compact-action"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              type="button"
            >
              Następna ›
            </button>
          </div>
        ) : null}
      </article>

      {quickNoteClient ? (
        <QuickAddNoteModal client={quickNoteClient} onClose={() => setQuickNoteClient(null)} />
      ) : null}

      {quickReminderClient && sessionUser ? (
        <ReminderFormModal
          canTargetOthers={sessionUser.role !== "REMOTE_EMPLOYEE"}
          fixedClientId={quickReminderClient.id}
          onClose={() => setQuickReminderClient(null)}
          onSaved={() => setQuickReminderClient(null)}
          projectOptions={projects}
          reminder={null}
          sessionUser={sessionUser}
          teamMembers={employees}
        />
      ) : null}

      {showAddClient && onCreateClient ? (
        <Modal onClose={() => setShowAddClient(false)} title="Dodaj firmę i konto klienta">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!clientForm.companyName.trim()) return;
              void runCreateClient(async () => {
                await onCreateClient(clientForm);
                setClientForm({
                  companyName: "",
                  billingEmail: "",
                  firstName: "",
                  lastName: "",
                  email: "",
                  phone: "",
                  vatNumber: "",
                  accountManagerId: "",
                  emailsEnabled: false
                });
                setShowAddClient(false);
                void loadClients();
              });
            }}
          >
            <label className="field">
              <span>Nazwa firmy</span>
              <input
                onChange={(event) => setClientForm((current) => ({ ...current, companyName: event.target.value }))}
                placeholder="Nazwa firmy"
                value={clientForm.companyName}
              />
            </label>
            <label className="field">
              <span>NIP</span>
              <input
                onChange={(event) => setClientForm((current) => ({ ...current, vatNumber: event.target.value }))}
                placeholder="Np. 1234567890"
                value={clientForm.vatNumber ?? ""}
              />
            </label>
            <label className="field">
              <span>Email rozliczeniowy (opcjonalnie)</span>
              <input
                onChange={(event) => setClientForm((current) => ({ ...current, billingEmail: event.target.value }))}
                placeholder="Email rozliczeniowy"
                value={clientForm.billingEmail}
              />
            </label>
            {employees.length > 0 ? (
              <label className="field">
                <span>Opiekun (pracownik)</span>
                <select
                  onChange={(event) => setClientForm((current) => ({ ...current, accountManagerId: event.target.value }))}
                  value={clientForm.accountManagerId ?? ""}
                >
                  <option value="">Brak przypisania</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="field">
              <span>Imię osoby kontaktowej (opcjonalnie)</span>
              <input
                onChange={(event) => setClientForm((current) => ({ ...current, firstName: event.target.value }))}
                placeholder="Imię osoby kontaktowej po stronie klienta"
                value={clientForm.firstName}
              />
            </label>
            <label className="field">
              <span>Nazwisko osoby kontaktowej (opcjonalnie)</span>
              <input
                onChange={(event) => setClientForm((current) => ({ ...current, lastName: event.target.value }))}
                placeholder="Nazwisko osoby kontaktowej po stronie klienta"
                value={clientForm.lastName}
              />
            </label>
            <label className="field">
              <span>Email logowania (opcjonalnie)</span>
              <input
                onChange={(event) => setClientForm((current) => ({ ...current, email: event.target.value }))}
                placeholder="Zostanie wygenerowany, jeśli puste"
                value={clientForm.email}
              />
            </label>
            <label className="field-inline-group">
              <input
                checked={clientForm.emailsEnabled ?? false}
                onChange={(event) => setClientForm((current) => ({ ...current, emailsEnabled: event.target.checked }))}
                type="checkbox"
              />
              <span>Wyślij mail o koncie (dane logowania) na podany adres</span>
            </label>
            <label className="field">
              <span>Telefon (opcjonalnie)</span>
              <input
                onChange={(event) => setClientForm((current) => ({ ...current, phone: event.target.value }))}
                placeholder="Telefon"
                value={clientForm.phone}
              />
            </label>
            <button disabled={creatingClient} type="submit">
              {creatingClient ? <Spinner label="Dodawanie..." /> : "Dodaj klienta"}
            </button>
          </form>
          <p className="placeholder-copy">Nowe konto klienta otrzyma domyślne hasło startowe `Start123!`.</p>
        </Modal>
      ) : null}
    </section>
  );
}

function TicketAttachmentItem({ attachment }: { attachment: TicketAttachmentRecord }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const isImage = attachment.mimeType.startsWith("image/");

  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    void fetchTicketAttachmentBlob(attachment.id).then((blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setImageUrl(objectUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, isImage]);

  if (isImage) {
    return (
      <button
        className="ticket-attachment-thumb"
        onClick={() => void downloadTicketAttachment(attachment.id, attachment.fileName)}
        title={attachment.fileName}
        type="button"
      >
        {imageUrl ? <img alt={attachment.fileName} src={imageUrl} /> : <span className="ticket-attachment-loading">...</span>}
      </button>
    );
  }

  return (
    <button
      className="ticket-attachment-file"
      onClick={() => void downloadTicketAttachment(attachment.id, attachment.fileName)}
      type="button"
    >
      {attachment.fileName} ({formatFileSize(attachment.sizeBytes)})
    </button>
  );
}

function TicketAttachmentList({ attachments }: { attachments: TicketAttachmentRecord[] }) {
  if (attachments.length === 0) return null;
  return (
    <div className="ticket-attachment-list">
      {attachments.map((attachment) => (
        <TicketAttachmentItem attachment={attachment} key={attachment.id} />
      ))}
    </div>
  );
}

function TicketCenter({
  allowCreate = false,
  onCloseTicket,
  onCreateTicket,
  onReopenTicket,
  onReplyToTicket,
  onSelectTicket,
  tickets = [],
  viewerRole = "STAFF"
}: {
  allowCreate?: boolean;
  onCloseTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onCreateTicket: (payload: CreateTicketRequest) => Promise<TicketDetailRecord>;
  onReopenTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onReplyToTicket: (ticketId: string, payload: AddTicketMessageRequest) => Promise<TicketDetailRecord>;
  onSelectTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  tickets: TicketRecord[];
  viewerRole?: "CLIENT" | "STAFF";
}) {
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetailRecord | null>(null);
  const [loadingTicketId, setLoadingTicketId] = useState<string | null>(null);
  const [showThread, setShowThread] = useState(false);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [closingTicket, setClosingTicket] = useState(false);
  const [reopeningTicket, setReopeningTicket] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [clientId, setClientId] = useState("");
  const [priority, setPriority] = useState<CreateTicketRequest["priority"]>("MEDIUM");
  const [category, setCategory] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [newTicketFiles, setNewTicketFiles] = useState<File[]>([]);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const chatThreadRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = chatThreadRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [selectedTicketId, selectedTicket?.messages.length]);

  async function openTicket(ticketId: string) {
    setSelectedTicketId(ticketId);
    setShowThread(true);
    setLoadingTicketId(ticketId);
    try {
      const ticket = await onSelectTicket(ticketId);
      setSelectedTicket(ticket);
    } finally {
      setLoadingTicketId(null);
    }
  }

  useEffect(() => {
    const pendingTicketId = window.localStorage.getItem(PENDING_TICKET_KEY);
    if (pendingTicketId && tickets.some((ticket) => ticket.id === pendingTicketId)) {
      window.localStorage.removeItem(PENDING_TICKET_KEY);
      void openTicket(pendingTicketId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  async function submitReply() {
    if (!selectedTicketId || !replyBody.trim()) return;
    setSendingReply(true);
    try {
      const attachments = await Promise.all(
        replyFiles.map(async (file) => ({
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          base64Data: await readFileAsBase64(file)
        }))
      );
      const ticket = await onReplyToTicket(selectedTicketId, { body: replyBody, attachments: attachments.length > 0 ? attachments : undefined });
      setSelectedTicket(ticket);
      setReplyBody("");
      setReplyFiles([]);
    } finally {
      setSendingReply(false);
    }
  }

  async function submitClose() {
    if (!selectedTicketId) return;
    setClosingTicket(true);
    try {
      const ticket = await onCloseTicket(selectedTicketId);
      setSelectedTicket(ticket);
    } finally {
      setClosingTicket(false);
    }
  }

  async function submitReopen() {
    if (!selectedTicketId) return;
    setReopeningTicket(true);
    try {
      const ticket = await onReopenTicket(selectedTicketId);
      setSelectedTicket(ticket);
    } finally {
      setReopeningTicket(false);
    }
  }

  async function submitTicket() {
    if (!subject.trim() || !message.trim()) return;
    setCreatingTicket(true);
    try {
      const attachments = await Promise.all(
        newTicketFiles.map(async (file) => ({
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          base64Data: await readFileAsBase64(file)
        }))
      );
      const ticket = await onCreateTicket({
        clientId: clientId || undefined,
        subject,
        message,
        priority,
        category: category || undefined,
        attachments: attachments.length > 0 ? attachments : undefined
      });
      setSubject("");
      setMessage("");
      setReplyBody("");
      setCategory("");
      setNewTicketFiles([]);
      setClientId("");
      setShowCreateTicket(false);
      setSelectedTicketId(ticket.id);
      setSelectedTicket(ticket);
      setShowThread(true);
    } finally {
      setCreatingTicket(false);
    }
  }

  const filteredTickets = categoryFilter ? tickets.filter((ticket) => ticket.category === categoryFilter) : tickets;

  return (
    <section className="section-stack">
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Tickety</p>
            <h3>Zgłoszenia</h3>
          </div>
          <div className="card-header-actions">
            <select
              className="compact-action"
              onChange={(event) => setCategoryFilter(event.target.value)}
              value={categoryFilter}
            >
              <option value="">Wszystkie kategorie</option>
              {TICKET_CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <span className="ghost-tag">{filteredTickets.length} pozycji</span>
            {allowCreate ? (
              <button onClick={() => setShowCreateTicket(true)} type="button">
                + Nowy ticket
              </button>
            ) : null}
          </div>
        </div>
        <div className="table">
          <div className={viewerRole === "CLIENT" ? "table-head row row-ticket-wide-client row-head" : "table-head row row-ticket-wide row-head"}>
            <span>Zgłoszenie</span>
            {viewerRole === "CLIENT" ? null : <span>Klient</span>}
            <span>Status</span>
            <span>Priorytet</span>
            <span>Kategoria</span>
            <span>Aktualizacja</span>
          </div>
          {filteredTickets.map((ticket) => (
            <div
              className={viewerRole === "CLIENT" ? "row row-ticket-wide-client" : "row row-ticket-wide"}
              key={ticket.id}
              onClick={() => void openTicket(ticket.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void openTicket(ticket.id);
              }}
              role="button"
              tabIndex={0}
            >
              <div className="ticket-list-item-title">
                {ticket.hasUnreadMessage ? (
                  <span className="ticket-unread-dot" title="Nowa wiadomość" aria-label="Nowa wiadomość" />
                ) : null}
                <strong>#{ticket.ticketNumber} {ticket.subject}</strong>
              </div>
              {viewerRole === "CLIENT" ? null : <span>{ticket.clientName}</span>}
              <StatusBadge tone="ticket" value={ticket.status} />
              <StatusBadge tone="priority" value={ticket.priority} />
              <span>{ticket.category ? ticketCategoryLabel(ticket.category) : "-"}</span>
              <span>{ticket.lastMessageAt}</span>
            </div>
          ))}
          {filteredTickets.length === 0 ? <p className="admin-empty">Brak zgłoszeń.</p> : null}
        </div>
      </article>

      {showCreateTicket ? (
        <Modal onClose={() => setShowCreateTicket(false)} title="Nowy ticket">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitTicket();
            }}
          >
            {viewerRole !== "CLIENT" ? (
              <label className="field">
                <span>Klient</span>
                <ClientPickerField emptyOptionLabel="Wybierz klienta" onChange={setClientId} value={clientId} />
              </label>
            ) : null}
            <label className="field">
              <span>Temat</span>
              <input onChange={(event) => setSubject(event.target.value)} placeholder="Temat zgłoszenia" value={subject} />
            </label>
            <label className="field">
              <span>Priorytet</span>
              <select onChange={(event) => setPriority(event.target.value as CreateTicketRequest["priority"])} value={priority}>
                <option value="LOW">Niski</option>
                <option value="MEDIUM">Średni</option>
                <option value="HIGH">Wysoki</option>
                <option value="URGENT">Pilny</option>
              </select>
            </label>
            <label className="field">
              <span>Kategoria</span>
              <select onChange={(event) => setCategory(event.target.value)} value={category}>
                <option value="">Brak kategorii</option>
                {TICKET_CATEGORIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Opis</span>
              <textarea onChange={(event) => setMessage(event.target.value)} placeholder="Opisz problem lub pytanie" value={message} />
            </label>
            <label className="field">
              <span>Załączniki</span>
              <input
                multiple
                onChange={(event) => setNewTicketFiles(Array.from(event.target.files ?? []))}
                type="file"
              />
              {newTicketFiles.length > 0 ? (
                <span className="ghost-tag">{newTicketFiles.map((file) => file.name).join(", ")}</span>
              ) : null}
            </label>
            <button disabled={creatingTicket} type="submit">
              {creatingTicket ? <Spinner label="Dodawanie..." /> : "Dodaj ticket"}
            </button>
          </form>
        </Modal>
      ) : null}

      {showThread ? (
        <Modal
          className="modal-card-chat"
          onClose={() => setShowThread(false)}
          title={selectedTicket ? `#${selectedTicket.ticketNumber} ${selectedTicket.subject}` : "Watek"}
          wide
        >
          {loadingTicketId ? <p className="placeholder-copy">Ładowanie rozmowy...</p> : null}
          {!loadingTicketId && selectedTicket ? (
            <div className="chat-shell">
              <div className="chat-meta">
                <div className="inline-badges">
                  <span className="chat-meta-client">{selectedTicket.clientName}</span>
                  <StatusBadge tone="priority" value={selectedTicket.priority} />
                  <StatusBadge value={selectedTicket.status} />
                  {selectedTicket.category ? <span className="ghost-tag">{ticketCategoryLabel(selectedTicket.category)}</span> : null}
                </div>
                {selectedTicket.status !== "CLOSED" ? (
                  <button
                    className="secondary compact-action"
                    disabled={closingTicket}
                    onClick={() => void submitClose()}
                    type="button"
                  >
                    {closingTicket ? "Zamykanie..." : "Zamknij ticket"}
                  </button>
                ) : (
                  <button
                    className="secondary compact-action"
                    disabled={reopeningTicket}
                    onClick={() => void submitReopen()}
                    type="button"
                  >
                    {reopeningTicket ? "Otwieranie..." : "Otwórz ponownie"}
                  </button>
                )}
              </div>
              <div className="chat-thread" ref={chatThreadRef}>
                {selectedTicket.messages.map((messageItem) => {
                  const isOwn = viewerRole === "CLIENT" ? messageItem.authorRole === "CLIENT" : messageItem.authorRole !== "CLIENT";

                  return (
                    <div className={isOwn ? "chat-row is-own" : "chat-row is-other"} key={messageItem.id}>
                      <Avatar avatarUrl={messageItem.authorAvatarUrl} className="chat-avatar" fullName={messageItem.authorName} />
                      <div className="chat-bubble">
                        <div className="chat-bubble-head">
                          <strong>{messageItem.authorName}</strong>
                          <span>{roleBadge(messageItem.authorRole)}</span>
                        </div>
                        <p>{messageItem.body}</p>
                        <TicketAttachmentList attachments={messageItem.attachments} />
                        <span className="chat-bubble-time">{formatDateTimeLabel(messageItem.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
                {selectedTicket.messages.length === 0 ? <p className="placeholder-copy">Brak wiadomości w tym wątku.</p> : null}
              </div>
              {selectedTicket.status === "CLOSED" ? (
                <p className="placeholder-copy">Ticket jest zamknięty. Kliknij "Otwórz ponownie", aby kontynuować watek.</p>
              ) : (
                <form
                  className="chat-composer"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void submitReply();
                  }}
                >
                  <textarea
                    onChange={(event) => setReplyBody(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        void submitReply();
                      }
                    }}
                    placeholder="Napisz wiadomość..."
                    value={replyBody}
                  />
                  <div className="chat-composer-attachments">
                    <input multiple onChange={(event) => setReplyFiles(Array.from(event.target.files ?? []))} type="file" />
                    {replyFiles.length > 0 ? (
                      <span className="ghost-tag">{replyFiles.map((file) => file.name).join(", ")}</span>
                    ) : null}
                  </div>
                  <button className="chat-send" disabled={!replyBody.trim() || sendingReply} type="submit">
                    {sendingReply ? <Spinner label="Wysyłanie..." /> : "Wyślij"}
                  </button>
                </form>
              )}
            </div>
          ) : null}
        </Modal>
      ) : null}
    </section>
  );
}

function ClientFinanceCard({ data }: { data: ClientDashboardData }) {
  return (
    <article className="card accent-card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Finanse</p>
          <h3>Saldo i rozliczenia</h3>
        </div>
      </div>
      <div className="metric-stack">
        <div>
          <span>Aktualne saldo</span>
          <strong>{data.finance.currentBalance}</strong>
        </div>
        <div>
          <span>Rozliczono dotychczas</span>
          <strong>{data.finance.paidTotal}</strong>
        </div>
        <div>
          <span>Niezaplacone zadania</span>
          <strong>{data.finance.unpaidTaskCount}</strong>
        </div>
        <div>
          <span>Średnia stawka</span>
          <strong>{data.finance.averageHourlyRate}</strong>
        </div>
      </div>
    </article>
  );
}

function ClientProfileCard({
  data,
  onEdit
}: {
  data: ClientDashboardData;
  onEdit?: () => void;
}) {
  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Dane firmy</p>
          <h3>Profil klienta</h3>
        </div>
        <button className="secondary" onClick={onEdit} type="button">
          Edytuj dane
        </button>
      </div>
      <div className="profile-block">
        <div>
          <span>Firma</span>
          <strong>{data.profile.companyName}</strong>
        </div>
        <div>
          <span>Email rozliczeń</span>
          <strong>{data.profile.billingEmail}</strong>
        </div>
        <div>
          <span>Telefon</span>
          <strong>{data.profile.phone}</strong>
        </div>
      </div>
    </article>
  );
}

function ClientContactsCard({ data }: { data: ClientDashboardData }) {
  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Kontakty</p>
          <h3>Osoby kontaktowe</h3>
        </div>
      </div>
      <div className="contact-list">
        {data.profile.contacts.map((contact) => (
          <div className="contact-card" key={contact.id}>
            <strong>{contact.fullName}</strong>
            <p>{contact.position}</p>
            <span>{contact.email}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

function ClientProfileEditor({
  profile,
  onSave
}: {
  profile: ClientDashboardData["profile"];
  onSave: (payload: UpsertClientProfileRequest) => Promise<void>;
}) {
  const [companyName, setCompanyName] = useState(profile.companyName);
  const [billingEmail, setBillingEmail] = useState(profile.billingEmail);
  const [phone, setPhone] = useState(profile.phone);
  const [vatNumber, setVatNumber] = useState(profile.vatNumber ?? "");

  useEffect(() => {
    setCompanyName(profile.companyName);
    setBillingEmail(profile.billingEmail);
    setPhone(profile.phone);
    setVatNumber(profile.vatNumber ?? "");
  }, [profile]);

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Dane firmy</p>
          <h3>Edytuj profil klienta</h3>
        </div>
      </div>
      <form
        className="task-form stacked-form"
        onSubmit={(event) => {
          event.preventDefault();
          void onSave({ companyName, billingEmail, phone, vatNumber });
        }}
      >
        <label className="field">
          <span>Nazwa firmy</span>
          <input onChange={(event) => setCompanyName(event.target.value)} placeholder="Nazwa firmy" value={companyName} />
        </label>
        <label className="field">
          <span>Email rozliczeń</span>
          <input onChange={(event) => setBillingEmail(event.target.value)} placeholder="Email rozliczeń" value={billingEmail} />
        </label>
        <label className="field">
          <span>Telefon</span>
          <input onChange={(event) => setPhone(event.target.value)} placeholder="Telefon" value={phone} />
        </label>
        <label className="field">
          <span>NIP</span>
          <input onChange={(event) => setVatNumber(event.target.value)} placeholder="Np. 1234567890" value={vatNumber} />
        </label>
        <button type="submit">Zapisz dane</button>
      </form>
    </article>
  );
}

function ClientContactsEditor({
  contacts,
  onSave
}: {
  contacts: ClientDashboardData["profile"]["contacts"];
  onSave: (payload: UpdateClientContactsRequest) => Promise<void>;
}) {
  const [items, setItems] = useState(contacts);

  useEffect(() => {
    setItems(contacts);
  }, [contacts]);

  function updateField(index: number, field: keyof ClientDashboardData["profile"]["contacts"][number], value: string | boolean) {
    setItems((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item))
    );
  }

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Kontakty</p>
          <h3>Osoby kontaktowe</h3>
        </div>
        <button
          className="secondary"
          onClick={() =>
            setItems((current) => [
              ...current,
              { id: `new-${Date.now()}`, fullName: "", email: "", phone: "", position: "", isPrimary: false }
            ])
          }
          type="button"
        >
          Dodaj kontakt
        </button>
      </div>
      <div className="editor-stack">
        {items.map((contact, index) => (
          <div className="card nested-card" key={contact.id}>
            <div className="task-form stacked-form">
              <input
                onChange={(event) => updateField(index, "fullName", event.target.value)}
                placeholder="Imię i nazwisko"
                value={contact.fullName}
              />
              <input
                onChange={(event) => updateField(index, "email", event.target.value)}
                placeholder="Email"
                value={contact.email}
              />
              <input
                onChange={(event) => updateField(index, "phone", event.target.value)}
                placeholder="Telefon"
                value={contact.phone ?? ""}
              />
              <input
                onChange={(event) => updateField(index, "position", event.target.value)}
                placeholder="Stanowisko"
                value={contact.position ?? ""}
              />
              <label className="checkbox-row">
                <input
                  checked={contact.isPrimary}
                  onChange={(event) => updateField(index, "isPrimary", event.target.checked)}
                  type="checkbox"
                />
                <span>Kontakt główny</span>
              </label>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={() =>
          void onSave({
            contacts: items.map((contact) => ({
              id: contact.id.startsWith("new-") ? undefined : contact.id,
              fullName: contact.fullName,
              email: contact.email,
              phone: contact.phone,
              position: contact.position,
              isPrimary: contact.isPrimary
            }))
          })
        }
        type="button"
      >
        Zapisz kontakty
      </button>
    </article>
  );
}

function NoteEditor({
  note,
  teamMembers,
  currentUserId,
  isAdmin,
  clientOptions,
  projectOptions,
  onBack,
  onChanged,
  onDeleted
}: {
  note: NoteDetailRecord;
  teamMembers: EmployeeRecord[];
  currentUserId?: string;
  isAdmin: boolean;
  clientOptions?: ClientRecord[];
  projectOptions?: ProjectRecord[];
  onBack: () => void;
  onChanged: (note: NoteDetailRecord) => void;
  onDeleted: () => void;
}) {
  const [titleDraft, setTitleDraft] = useState(note.title);
  const [savingLabel, setSavingLabel] = useState<string | null>(null);
  const [addUserId, setAddUserId] = useState("");
  const canManage = note.isOwner || isAdmin;

  useEffect(() => {
    setTitleDraft(note.title);
  }, [note.id, note.title]);

  async function handleTitleSave() {
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === note.title) {
      setTitleDraft(note.title);
      return;
    }

    const response = await updateNote(note.id, { title: trimmed });
    onChanged(response.note);
  }

  async function handleDelete() {
    if (!window.confirm(`Usunąć notatkę "${note.title}"? Tej operacji nie można cofnąć.`)) return;
    await deleteNote(note.id);
    onDeleted();
  }

  async function handleAddAccess() {
    if (!addUserId) return;
    const nextUserIds = [...note.access.map((entry) => entry.userId), addUserId];
    const response = await updateNoteAccess(note.id, { userIds: nextUserIds });
    onChanged(response.note);
    setAddUserId("");
  }

  async function handleRemoveAccess(userId: string) {
    const nextUserIds = note.access.map((entry) => entry.userId).filter((id) => id !== userId);
    const response = await updateNoteAccess(note.id, { userIds: nextUserIds });
    onChanged(response.note);
  }

  async function handleClientChange(nextClientId: string) {
    const response = await updateNote(note.id, { clientId: nextClientId || null, projectId: null });
    onChanged(response.note);
  }

  async function handleProjectChange(nextProjectId: string) {
    const response = await updateNote(note.id, { projectId: nextProjectId || null });
    onChanged(response.note);
  }

  const noteProjectChoices = note.clientId
    ? (projectOptions ?? []).filter((project) => project.clientId === note.clientId)
    : projectOptions ?? [];

  const ownerMember = teamMembers.find((member) => member.id === note.createdByUserId);
  const availableMembers = teamMembers.filter(
    (member) => member.id !== note.createdByUserId && !note.access.some((entry) => entry.userId === member.id)
  );

  return (
    <section className="panel-grid two-columns note-editor">
      <article className="card">
        <div className="card-header">
          <button className="secondary compact-action" onClick={onBack} type="button">
            ← Wszystkie notatki
          </button>
          <div className="note-save-indicator">{savingLabel}</div>
        </div>
        <input
          className="note-title-input"
          disabled={!canManage}
          onBlur={() => void handleTitleSave()}
          onChange={(event) => setTitleDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
          value={titleDraft}
        />
        <Suspense fallback={<div className="note-body-blocknote note-body-loading">Ładowanie edytora...</div>}>
          <NoteBodyEditor canManage={canManage} key={note.id} note={note} onChanged={onChanged} onSavingChange={setSavingLabel} />
        </Suspense>
      </article>
      <article className="card compact-card note-details-card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Informacje</p>
            <h3>Szczegóły notatki</h3>
          </div>
          {canManage ? (
            <button className="secondary compact-action icon-action danger" onClick={() => void handleDelete()} title="Usuń notatkę" type="button">
              <TrashIcon />
            </button>
          ) : null}
        </div>

        <div className="note-details-meta">
          <div>
            <span>Autor</span>
            <strong>{note.createdByName}</strong>
          </div>
          <div>
            <span>Utworzono</span>
            <strong>{formatDateTimeLabel(note.createdAt)}</strong>
          </div>
          <div>
            <span>Ostatnia zmiana</span>
            <strong>{formatDateTimeLabel(note.updatedAt)}</strong>
          </div>
        </div>

        {clientOptions ? (
          <section className="note-details-section">
            <h4>Powiązania</h4>
            <div className="note-details-fields">
              <label className="field">
                <span>Klient (opcjonalnie)</span>
                <ClientPickerField
                  currentLabel={note.clientName}
                  disabled={!canManage}
                  emptyOptionLabel="Brak"
                  onChange={(nextClientId) => void handleClientChange(nextClientId)}
                  value={note.clientId ?? ""}
                />
              </label>
              {projectOptions ? (
                <label className="field">
                  <span>Projekt (opcjonalnie)</span>
                  <select disabled={!canManage} onChange={(event) => void handleProjectChange(event.target.value)} value={note.projectId ?? ""}>
                    <option value="">Brak</option>
                    {noteProjectChoices.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </section>
        ) : null}

        <section className="note-details-section">
          <h4>
            Dostęp <span className="note-details-count">{note.access.length + 1}</span>
          </h4>
          <div className="note-access-list">
            <div className="note-access-item">
              <Avatar avatarUrl={ownerMember?.avatarUrl} className="assignee-avatar small" fullName={note.createdByName} />
              <span className="note-access-name">{note.createdByName}</span>
              <span className="note-access-role">Właściciel</span>
            </div>
            {note.access.map((entry) => (
              <div className="note-access-item" key={entry.userId}>
                <Avatar avatarUrl={entry.avatarUrl} className="assignee-avatar small" fullName={entry.fullName} />
                <span className="note-access-name">{entry.fullName}</span>
                {canManage ? (
                  <button
                    aria-label={`Usuń dostęp dla ${entry.fullName}`}
                    className="secondary compact-action icon-action"
                    onClick={() => void handleRemoveAccess(entry.userId)}
                    title="Usuń dostęp"
                    type="button"
                  >
                    <TrashIcon />
                  </button>
                ) : (
                  <span className="note-access-role">Odczyt i edycja</span>
                )}
              </div>
            ))}
          </div>
          {canManage && availableMembers.length > 0 ? (
            <div className="note-access-add">
              <select aria-label="Dodaj dostęp" onChange={(event) => setAddUserId(event.target.value)} value={addUserId}>
                <option value="">Udostępnij osobie...</option>
                {availableMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.fullName}
                  </option>
                ))}
              </select>
              <button className="secondary compact-action" disabled={!addUserId} onClick={() => void handleAddAccess()} type="button">
                Dodaj
              </button>
            </div>
          ) : null}
        </section>

        <section className="note-details-section">
          <h4>
            Historia zmian <span className="note-details-count">{note.revisions.length}</span>
          </h4>
          {note.revisions.length === 0 ? (
            <p className="placeholder-copy">Brak historii zmian.</p>
          ) : (
            <ol className="note-revision-list">
              {note.revisions.map((revision) => (
                <li className="note-revision-item" key={revision.id}>
                  <strong>{revision.summary}</strong>
                  <span>
                    {revision.changedByName} • {formatDateTimeLabel(revision.createdAt)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </article>
    </section>
  );
}

function NotesPanel({
  teamMembers,
  currentUserId,
  isAdmin,
  clientOptions,
  projectOptions
}: {
  teamMembers: EmployeeRecord[];
  currentUserId?: string;
  isAdmin: boolean;
  clientOptions?: ClientRecord[];
  projectOptions?: ProjectRecord[];
}) {
  const [notes, setNotes] = useState<NoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [detail, setDetail] = useState<NoteDetailRecord | null>(null);

  useEffect(() => {
    void fetchNotes()
      .then(({ items }) => {
        setNotes(items);
        const pendingNoteId = window.localStorage.getItem(PENDING_NOTE_KEY);
        if (pendingNoteId) {
          window.localStorage.removeItem(PENDING_NOTE_KEY);
          if (items.some((item) => item.id === pendingNoteId)) {
            setSelectedNoteId(pendingNoteId);
          }
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedNoteId) {
      setDetail(null);
      return;
    }

    void fetchNoteDetail(selectedNoteId)
      .then(({ note }) => setDetail(note))
      .catch(() => setSelectedNoteId(null));
  }, [selectedNoteId]);

  const { pending: creatingNote, run: runCreateNote } = useAsyncAction();

  function handleCreate() {
    void runCreateNote(async () => {
      const { note } = await createNote({ title: "Nowa notatka" });
      setNotes((current) => [
        { id: note.id, title: note.title, updatedAt: note.updatedAt, createdByUserId: note.createdByUserId, createdByName: note.createdByName, isOwner: true },
        ...current
      ]);
      setSelectedNoteId(note.id);
    });
  }

  function handleChanged(next: NoteDetailRecord) {
    setDetail(next);
    setNotes((current) =>
      current.map((item) =>
        item.id === next.id ? { id: next.id, title: next.title, updatedAt: next.updatedAt, createdByUserId: next.createdByUserId, createdByName: next.createdByName, isOwner: next.isOwner } : item
      )
    );
  }

  function handleDeleted() {
    setNotes((current) => current.filter((item) => item.id !== selectedNoteId));
    setSelectedNoteId(null);
  }

  if (selectedNoteId && detail) {
    return (
      <NoteEditor
        clientOptions={clientOptions}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        note={detail}
        onBack={() => setSelectedNoteId(null)}
        onChanged={handleChanged}
        onDeleted={handleDeleted}
        projectOptions={projectOptions}
        teamMembers={teamMembers}
      />
    );
  }

  const myNotes = notes.filter((note) => note.isOwner);
  const sharedNotes = notes.filter((note) => !note.isOwner);

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Notatki</p>
          <h3>Dokumenty zespołu</h3>
        </div>
        <button disabled={creatingNote} onClick={() => handleCreate()} type="button">
          {creatingNote ? <Spinner label="Dodawanie..." /> : "+ Nowa notatka"}
        </button>
      </div>
      {loading ? (
        <p className="placeholder-copy">Wczytywanie...</p>
      ) : notes.length === 0 ? (
        <EmptyState message="Brak notatek. Utwórz pierwsza notatkę." />
      ) : (
        <>
          <p className="eyebrow">Moje notatki</p>
          <div className="note-list">
            {myNotes.length === 0 ? <p className="placeholder-copy">Brak własnych notatek.</p> : null}
            {myNotes.map((note) => (
              <button className="note-list-item" key={note.id} onClick={() => setSelectedNoteId(note.id)} type="button">
                <strong>{note.title}</strong>
                <span>Zaktualizowano {formatDateTimeLabel(note.updatedAt)}</span>
              </button>
            ))}
          </div>
          <p className="eyebrow">Udostępnione dla mnie</p>
          <div className="note-list">
            {sharedNotes.length === 0 ? <p className="placeholder-copy">Brak udostępnionych notatek.</p> : null}
            {sharedNotes.map((note) => (
              <button className="note-list-item" key={note.id} onClick={() => setSelectedNoteId(note.id)} type="button">
                <strong>{note.title}</strong>
                <span>
                  {note.createdByName} • {formatDateTimeLabel(note.updatedAt)}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </article>
  );
}

function TaskCommentsCenter({
  tasks,
  onCreateComment,
  onLoadComments
}: {
  tasks: TaskSummary[];
  onCreateComment: (taskId: string, payload: CreateTaskCommentRequest) => Promise<TaskCommentRecord[]>;
  onLoadComments: (taskId: string) => Promise<TaskCommentRecord[]>;
}) {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(tasks[0]?.id ?? null);
  const [comments, setComments] = useState<TaskCommentRecord[]>([]);
  const [body, setBody] = useState("");

  useEffect(() => {
    const nextTaskId = tasks[0]?.id ?? null;
    if (!selectedTaskId && nextTaskId) {
      setSelectedTaskId(nextTaskId);
    }
  }, [selectedTaskId, tasks]);

  useEffect(() => {
    if (!selectedTaskId) return;
    void onLoadComments(selectedTaskId).then(setComments);
  }, [onLoadComments, selectedTaskId]);

  return (
    <section className="panel-grid ticket-workspace">
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Zadania</p>
            <h3>Wybierz zlecenie</h3>
          </div>
        </div>
        <div className="ticket-list">
          {tasks.map((task) => (
            <button
              className={selectedTaskId === task.id ? "ticket-list-item is-active" : "ticket-list-item"}
              key={task.id}
              onClick={() => setSelectedTaskId(task.id)}
              type="button"
            >
              <strong>{task.title}</strong>
              <span>{task.clientName}</span>
              <span>{formatBadgeValue(task.status, "status")}</span>
            </button>
          ))}
        </div>
      </article>
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Komentarze</p>
            <h3>Historia rozmowy</h3>
          </div>
        </div>
        <div className="message-thread">
          {comments.map((comment) => (
            <div className="message-card" key={comment.id}>
              <strong>
                {comment.authorName} • {roleBadge(comment.authorRole)}
              </strong>
              <p>{comment.body}</p>
              <span>{formatDateTimeLabel(comment.createdAt)}</span>
            </div>
          ))}
          {comments.length === 0 ? <p className="placeholder-copy">Brak komentarzy dla wybranego zadania.</p> : null}
        </div>
        {selectedTaskId ? (
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!body.trim()) return;
              void onCreateComment(selectedTaskId, { body }).then((items) => {
                setComments(items);
                setBody("");
              });
            }}
          >
            <textarea onChange={(event) => setBody(event.target.value)} placeholder="Dodaj komentarz do zadania" value={body} />
            <button type="submit">Dodaj komentarz</button>
          </form>
        ) : null}
      </article>
    </section>
  );
}

function formatEmployeePasswordActionLabel(employee: EmployeeRecord) {
  if (!employee.lastPasswordActionAt) return "Nigdy";
  const actionLabel =
    employee.lastPasswordAction === "MANUAL_SET"
      ? "hasło ustawione ręcznie"
      : employee.lastPasswordAction === "LINK_RESENT"
        ? "link wysłany ponownie"
        : "link wysłany";
  return `${formatDateTimeLabel(employee.lastPasswordActionAt)} (${actionLabel}, ${employee.lastPasswordActionByName ?? "?"})`;
}

function EmployeeAdminTable({
  employees,
  onDelete,
  onOpenEmployee,
  onSendPasswordResetLink,
  onSetPassword,
  onUpdate
}: {
  employees: EmployeeRecord[];
  onDelete: (employeeId: string) => Promise<void>;
  onOpenEmployee: (employeeId: string) => void;
  onSendPasswordResetLink: (employeeId: string) => Promise<void>;
  onSetPassword: (employeeId: string, newPassword: string) => Promise<void>;
  onUpdate: (employeeId: string, payload: UpdateEmployeeRequest) => Promise<void>;
}) {
  const [passwordModalEmployee, setPasswordModalEmployee] = useState<EmployeeRecord | null>(null);
  const [newPasswordDraft, setNewPasswordDraft] = useState("");

  return (
    <div className="table">
      <div className="table-head row row-employee row-head">
        <span>Pracownik</span>
        <span>Stawka</span>
        <span>Dzisiaj</span>
        <span>Status</span>
        <span>Akcje</span>
      </div>
      {employees.map((employee) => (
        <div className="row row-employee" key={employee.id}>
          <div>
            <strong>
              <EntityLink label={employee.fullName} onClick={() => onOpenEmployee(employee.id)} />
            </strong>
            <p>
              {employee.title} • {employeeRoleLabel(employee.role)}
              {employee.departments.length > 0 ? ` • ${employee.departments.join(", ")}` : ""}
            </p>
            <p className="muted-note">
              Konto utworzone: {formatDateTimeLabel(employee.createdAt)} • Hasło: {formatEmployeePasswordActionLabel(employee)}
            </p>
          </div>
          <span>{employee.hourlyRate}</span>
          <span>{employee.trackedToday}</span>
          <StatusBadge value={employee.status} />
          <div className="task-row-actions">
            <button
              aria-label="Wyślij link do ustawienia hasła"
              className="secondary compact-action icon-action"
              onClick={() => void onSendPasswordResetLink(employee.id)}
              title={employee.lastPasswordActionAt ? "Wyślij link ponownie" : "Wyślij link do ustawienia hasła"}
              type="button"
            >
              <MailIcon />
            </button>
            <button
              aria-label="Ustaw hasło ręcznie"
              className="secondary compact-action icon-action"
              onClick={() => {
                setNewPasswordDraft("");
                setPasswordModalEmployee(employee);
              }}
              title="Ustaw hasło ręcznie"
              type="button"
            >
              <KeyIcon />
            </button>
            <button
              aria-label={employee.status === "BLOCKED" ? "Odblokuj pracownika" : "Zablokuj pracownika"}
              className="secondary compact-action icon-action"
              onClick={() => {
                void onUpdate(employee.id, {
                  ...employeeToUpdatePayload(employee),
                  status: employee.status === "BLOCKED" ? "ACTIVE" : "BLOCKED"
                });
              }}
              title={employee.status === "BLOCKED" ? "Odblokuj pracownika" : "Zablokuj pracownika"}
              type="button"
            >
              <LockIcon />
            </button>
            <button
              aria-label="Usuń pracownika"
              className="secondary compact-action icon-action danger"
              onClick={() => {
                if (
                  !window.confirm(
                    `Usunąć pracownika "${employee.fullName}"? Konto zostanie zdezaktywowane - historia czasu pracy i komentarzy zostanie zachowana.`
                  )
                )
                  return;
                void onDelete(employee.id);
              }}
              title="Usuń pracownika"
              type="button"
            >
              <TrashIcon />
            </button>
          </div>
        </div>
      ))}
      {employees.length === 0 ? <EmptyState message="Brak pracowników pasujących do wybranych filtrów." /> : null}
      {passwordModalEmployee ? (
        <Modal onClose={() => setPasswordModalEmployee(null)} title={`Ustaw hasło - ${passwordModalEmployee.fullName}`}>
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (newPasswordDraft.length < 8) return;
              void onSetPassword(passwordModalEmployee.id, newPasswordDraft).then(() => {
                setPasswordModalEmployee(null);
                setNewPasswordDraft("");
              });
            }}
          >
            <label className="field">
              <span>Nowe hasło (min. 8 znaków)</span>
              <input
                autoFocus
                minLength={8}
                onChange={(event) => setNewPasswordDraft(event.target.value)}
                type="text"
                value={newPasswordDraft}
              />
            </label>
            <button type="submit">Ustaw hasło</button>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}

function EmployeeManager({
  employees,
  departments,
  onCreate,
  onDelete,
  onOpenEmployee,
  onSendPasswordResetLink,
  onSetPassword,
  onUpdate
}: {
  employees: EmployeeRecord[];
  departments: DepartmentRecord[];
  onCreate: (payload: CreateEmployeeRequest) => Promise<void>;
  onDelete: (employeeId: string) => Promise<void>;
  onOpenEmployee: (employeeId: string) => void;
  onSendPasswordResetLink: (employeeId: string) => Promise<void>;
  onSetPassword: (employeeId: string, newPassword: string) => Promise<void>;
  onUpdate: (employeeId: string, payload: UpdateEmployeeRequest) => Promise<void>;
}) {
  const [draft, setDraft] = useState<CreateEmployeeRequest>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "EMPLOYEE",
    title: "",
    hourlyRate: 100,
    departmentIds: [],
    canDeleteTasks: false
  });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | UpdateEmployeeRequest["status"]>("ALL");
  const [departmentFilter, setDepartmentFilter] = useState<string>("ALL");
  const filteredEmployees = employees.filter((employee) => {
    const query = search.trim().toLowerCase();
    const matchesSearch =
      query.length === 0 ||
      employee.fullName.toLowerCase().includes(query) ||
      employee.email.toLowerCase().includes(query) ||
      employee.title.toLowerCase().includes(query);
    const matchesStatus = statusFilter === "ALL" || employee.status === statusFilter;
    const matchesDepartment = departmentFilter === "ALL" || employee.departmentIds.includes(departmentFilter);

    return matchesSearch && matchesStatus && matchesDepartment;
  });
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const { pending: creatingEmployee, run: runCreateEmployee } = useAsyncAction();

  function toggleDepartment(ids: string[], departmentId: string) {
    return ids.includes(departmentId) ? ids.filter((id) => id !== departmentId) : [...ids, departmentId];
  }

  return (
    <section className="section-stack">
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Edycja</p>
            <h3>Zarządzanie zespołem</h3>
          </div>
          <div className="card-header-actions">
            <span className="ghost-tag">{filteredEmployees.length} widoczne</span>
            <button onClick={() => setShowAddEmployee(true)} type="button">
              + Dodaj pracownika
            </button>
          </div>
        </div>
        <div className="employee-toolbar">
          <input onChange={(event) => setSearch(event.target.value)} placeholder="Szukaj po nazwie, mailu lub stanowisku" value={search} />
          <select onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)} value={statusFilter}>
            <option value="ALL">Wszystkie statusy</option>
            <option value="ACTIVE">Aktywni</option>
            <option value="BLOCKED">Zablokowani</option>
            <option value="INVITED">Zaproszeni</option>
          </select>
          <select onChange={(event) => setDepartmentFilter(event.target.value)} value={departmentFilter}>
            <option value="ALL">Wszystkie zespoły</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </div>
        <EmployeeAdminTable
          employees={filteredEmployees}
          onDelete={onDelete}
          onOpenEmployee={onOpenEmployee}
          onSendPasswordResetLink={onSendPasswordResetLink}
          onSetPassword={onSetPassword}
          onUpdate={onUpdate}
        />
      </article>

      {showAddEmployee ? (
        <Modal onClose={() => setShowAddEmployee(false)} title="Dodaj konto pracownika">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              void runCreateEmployee(async () => {
                await onCreate(draft);
                setDraft({
                  firstName: "",
                  lastName: "",
                  email: "",
                  phone: "",
                  role: "EMPLOYEE",
                  title: "",
                  hourlyRate: 100,
                  departmentIds: [],
                  canDeleteTasks: false
                });
                setShowAddEmployee(false);
              });
            }}
          >
            <label className="field">
              <span>Imię</span>
              <input onChange={(event) => setDraft({ ...draft, firstName: event.target.value })} placeholder="Imię" value={draft.firstName} />
            </label>
            <label className="field">
              <span>Nazwisko</span>
              <input onChange={(event) => setDraft({ ...draft, lastName: event.target.value })} placeholder="Nazwisko" value={draft.lastName} />
            </label>
            <label className="field">
              <span>Email</span>
              <input onChange={(event) => setDraft({ ...draft, email: event.target.value })} placeholder="Email" value={draft.email} />
            </label>
            <label className="field">
              <span>Telefon</span>
              <input onChange={(event) => setDraft({ ...draft, phone: event.target.value })} placeholder="Telefon" value={draft.phone ?? ""} />
            </label>
            <label className="field">
              <span>Rola</span>
              <select onChange={(event) => setDraft({ ...draft, role: event.target.value as CreateEmployeeRequest["role"] })} value={draft.role}>
                <option value="REMOTE_EMPLOYEE">Pracownik zdalny</option>
                <option value="EMPLOYEE">Pracownik</option>
                <option value="ADMIN">Administrator</option>
              </select>
            </label>
            <label className="field">
              <span>Stanowisko</span>
              <input onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder="Stanowisko" value={draft.title} />
            </label>
            <label className="field">
              <span>Stawka godzinowa</span>
              <input
                min="0"
                onChange={(event) => setDraft({ ...draft, hourlyRate: Number(event.target.value) })}
                placeholder="Stawka godzinowa"
                type="number"
                value={draft.hourlyRate}
              />
            </label>
            <div className="selected-departments">
              <span>Przypisane zespoły</span>
              <div className="inline-badges">
                {(draft.departmentIds ?? []).length > 0 ? (
                  (draft.departmentIds ?? []).map((departmentId) => {
                    const department = departments.find((item) => item.id === departmentId);
                    return department ? <span className="department-badge" key={department.id}>{department.name}</span> : null;
                  })
                ) : (
                  <span className="ghost-tag">Brak przypisania</span>
                )}
              </div>
            </div>
            <div className="editor-stack">
              <p className="eyebrow">Zespoły</p>
              <DepartmentPicker
                departments={departments}
                onToggle={(departmentId) =>
                  setDraft({
                    ...draft,
                    departmentIds: toggleDepartment(draft.departmentIds ?? [], departmentId)
                  })
                }
                selectedIds={draft.departmentIds ?? []}
              />
            </div>
            <label className="field-inline-group">
              <input
                checked={draft.canDeleteTasks ?? false}
                onChange={(event) => setDraft({ ...draft, canDeleteTasks: event.target.checked })}
                type="checkbox"
              />
              <span>Może usuwać zadania (bez uprawnień administratora)</span>
            </label>
            <button disabled={creatingEmployee} type="submit">
              {creatingEmployee ? <Spinner label="Dodawanie..." /> : "Dodaj pracownika"}
            </button>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}

function EmployeeDetailPage({
  departments,
  employee,
  projects,
  onBack,
  onDelete,
  onUpdate
}: {
  departments: DepartmentRecord[];
  employee: EmployeeRecord;
  projects: ProjectRecord[];
  onBack: () => void;
  onDelete: (employeeId: string) => Promise<void>;
  onUpdate: (employeeId: string, payload: UpdateEmployeeRequest) => Promise<void>;
}) {
  const [editDraft, setEditDraft] = useState<UpdateEmployeeRequest>(employeeToUpdatePayload(employee));
  const employeeIdRef = useRef(employee.id);

  useEffect(() => {
    if (employeeIdRef.current !== employee.id) {
      employeeIdRef.current = employee.id;
      setEditDraft(employeeToUpdatePayload(employee));
    }
  }, [employee]);

  function toggleDepartment(ids: string[], departmentId: string) {
    return ids.includes(departmentId) ? ids.filter((id) => id !== departmentId) : [...ids, departmentId];
  }

  return (
    <section className="section-stack">
      <div className="detail-toolbar">
        <button className="secondary" onClick={onBack} type="button">
          Wróć
        </button>
        <div className="inline-badges">
          <button
            className="secondary"
            onClick={() => {
              void onUpdate(employee.id, {
                ...employeeToUpdatePayload(employee),
                status: employee.status === "BLOCKED" ? "ACTIVE" : "BLOCKED"
              });
            }}
            type="button"
          >
            {employee.status === "BLOCKED" ? "Odblokuj konto" : "Zablokuj konto"}
          </button>
          <button
            className="secondary danger"
            onClick={() => {
              if (
                !window.confirm(
                  `Usunąć pracownika "${employee.fullName}"? Konto zostanie zdezaktywowane - historia czasu pracy i komentarzy zostanie zachowana.`
                )
              )
                return;
              void onDelete(employee.id).then(() => onBack());
            }}
            type="button"
          >
            Usuń pracownika
          </button>
        </div>
      </div>

      <article className="card compact-card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Szczegóły pracownika</p>
            <h3>{employee.fullName}</h3>
          </div>
          <div className="inline-badges">
            <StatusBadge value={employee.status} />
            <span className="department-badge">{employeeRoleLabel(employee.role)}</span>
          </div>
        </div>
        <div className="profile-block employee-summary-grid">
          <div>
            <span>Email</span>
            <strong>{employee.email}</strong>
          </div>
          <div>
            <span>Stanowisko</span>
            <strong>{employee.title}</strong>
          </div>
          <div>
            <span>Dzisiaj</span>
            <strong>{employee.trackedToday}</strong>
          </div>
          <div>
            <span>Stawka</span>
            <strong>{employee.hourlyRate}</strong>
          </div>
        </div>
        <form
          className="task-form stacked-form"
          onSubmit={(event) => {
            event.preventDefault();
            void onUpdate(employee.id, editDraft);
          }}
        >
          <label className="field">
            <span>Imię</span>
            <input onChange={(event) => setEditDraft({ ...editDraft, firstName: event.target.value })} placeholder="Imię" value={editDraft.firstName} />
          </label>
          <label className="field">
            <span>Nazwisko</span>
            <input onChange={(event) => setEditDraft({ ...editDraft, lastName: event.target.value })} placeholder="Nazwisko" value={editDraft.lastName} />
          </label>
          <label className="field">
            <span>Email</span>
            <input onChange={(event) => setEditDraft({ ...editDraft, email: event.target.value })} placeholder="Email" value={editDraft.email} />
          </label>
          <label className="field">
            <span>Telefon</span>
            <input onChange={(event) => setEditDraft({ ...editDraft, phone: event.target.value })} placeholder="Telefon" value={editDraft.phone ?? ""} />
          </label>
          <label className="field">
            <span>Rola</span>
            <select onChange={(event) => setEditDraft({ ...editDraft, role: event.target.value as UpdateEmployeeRequest["role"] })} value={editDraft.role}>
              <option value="REMOTE_EMPLOYEE">Pracownik zdalny</option>
              <option value="EMPLOYEE">Pracownik</option>
              <option value="ADMIN">Administrator</option>
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select onChange={(event) => setEditDraft({ ...editDraft, status: event.target.value as UpdateEmployeeRequest["status"] })} value={editDraft.status}>
              <option value="ACTIVE">Aktywny</option>
              <option value="BLOCKED">Zablokowany</option>
              <option value="INVITED">Zaproszony</option>
            </select>
          </label>
          <label className="field">
            <span>Stanowisko</span>
            <input onChange={(event) => setEditDraft({ ...editDraft, title: event.target.value })} placeholder="Stanowisko" value={editDraft.title} />
          </label>
          <label className="field">
            <span>Stawka godzinowa</span>
            <input
              min="0"
              onChange={(event) => setEditDraft({ ...editDraft, hourlyRate: Number(event.target.value) })}
              placeholder="Stawka godzinowa"
              type="number"
              value={editDraft.hourlyRate}
            />
          </label>
          <div className="selected-departments">
            <span>Przypisane zespoły</span>
            <div className="inline-badges">
              {(editDraft.departmentIds ?? []).length > 0 ? (
                (editDraft.departmentIds ?? []).map((departmentId) => {
                  const department = departments.find((item) => item.id === departmentId);
                  return department ? <span className="department-badge" key={department.id}>{department.name}</span> : null;
                })
              ) : (
                <span className="ghost-tag">Brak przypisania</span>
              )}
            </div>
          </div>
          <div className="editor-stack">
            <p className="eyebrow">Zespoły</p>
            <DepartmentPicker
              departments={departments}
              onToggle={(departmentId) =>
                setEditDraft({
                  ...editDraft,
                  departmentIds: toggleDepartment(editDraft.departmentIds ?? [], departmentId)
                })
              }
              selectedIds={editDraft.departmentIds ?? []}
            />
          </div>
          <label className="field-inline-group">
            <input
              checked={editDraft.canDeleteTasks ?? false}
              onChange={(event) => setEditDraft({ ...editDraft, canDeleteTasks: event.target.checked })}
              type="checkbox"
            />
            <span>Może usuwać zadania (bez uprawnień administratora)</span>
          </label>
          {editDraft.role === "REMOTE_EMPLOYEE" ? (
            <>
              <div className="selected-departments">
                <span>Przypisane projekty</span>
                <div className="inline-badges">
                  {(editDraft.assignedProjectIds ?? []).length > 0 ? (
                    (editDraft.assignedProjectIds ?? []).map((projectId) => {
                      const project = projects.find((item) => item.id === projectId);
                      return project ? <span className="department-badge" key={project.id}>{project.name}</span> : null;
                    })
                  ) : (
                    <span className="ghost-tag">Brak przypisania - nie zobaczy żadnych zadań</span>
                  )}
                </div>
              </div>
              <div className="editor-stack">
                <p className="eyebrow">Projekty</p>
                <ProjectPicker
                  projects={projects}
                  onToggle={(projectId) =>
                    setEditDraft({
                      ...editDraft,
                      assignedProjectIds: toggleDepartment(editDraft.assignedProjectIds ?? [], projectId)
                    })
                  }
                  selectedIds={editDraft.assignedProjectIds ?? []}
                />
              </div>
            </>
          ) : null}
          <button type="submit">Zapisz pracownika</button>
        </form>
      </article>
    </section>
  );
}

function ProjectPicker({
  projects,
  selectedIds,
  onToggle
}: {
  projects: ProjectRecord[];
  selectedIds: string[];
  onToggle: (projectId: string) => void;
}) {
  return (
    <div className="assignee-picker">
      {projects.map((project) => {
        const checked = selectedIds.includes(project.id);

        return (
          <label className={checked ? "assignee-chip is-active" : "assignee-chip"} key={project.id}>
            <input checked={checked} onChange={() => onToggle(project.id)} type="checkbox" />
            <span>{project.name}</span>
            <em>{project.clientName}</em>
          </label>
        );
      })}
    </div>
  );
}

function DepartmentManager({
  departments,
  onCreate
}: {
  departments: DepartmentRecord[];
  onCreate: (payload: CreateDepartmentRequest) => Promise<void>;
}) {
  const [draft, setDraft] = useState<CreateDepartmentRequest>({
    name: "",
    description: ""
  });
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const { pending: creatingDepartment, run: runCreateDepartment } = useAsyncAction();

  return (
    <section className="section-stack">
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Struktura</p>
            <h3>Aktualne działy</h3>
          </div>
          <button onClick={() => setShowAddDepartment(true)} type="button">
            + Dodaj dział
          </button>
        </div>
        <DepartmentsTable departments={departments} />
      </article>

      {showAddDepartment ? (
        <Modal onClose={() => setShowAddDepartment(false)} title="Nowy dział">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!draft.name.trim()) return;
              void runCreateDepartment(async () => {
                await onCreate(draft);
                setDraft({ name: "", description: "" });
                setShowAddDepartment(false);
              });
            }}
          >
            <label className="field">
              <span>Nazwa działu</span>
              <input onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="Nazwa działu" value={draft.name} />
            </label>
            <label className="field">
              <span>Opis</span>
              <textarea
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                placeholder="Opis działu i zakres odpowiedzialnosci"
                value={draft.description ?? ""}
              />
            </label>
            <button disabled={creatingDepartment} type="submit">
              {creatingDepartment ? <Spinner label="Dodawanie..." /> : "Dodaj dział"}
            </button>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}

function MyProfileCard({
  sessionUser,
  onUpdateProfile
}: {
  sessionUser: SessionUser;
  onUpdateProfile: (payload: UpdateMyProfileRequest) => Promise<void>;
}) {
  const [phone, setPhone] = useState(sessionUser.phone ?? "");
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  return (
    <article className="card">
      <p className="eyebrow">Profil</p>
      <h3>Dane pracownika</h3>
      <div className="profile-block">
        <div>
          <span>Imię i nazwisko</span>
          <strong>{sessionUser.fullName}</strong>
        </div>
        <div>
          <span>Email</span>
          <strong>{sessionUser.email}</strong>
        </div>
        <div>
          <span>Rola</span>
          <strong>{roleBadge(sessionUser.role)}</strong>
        </div>
      </div>
      <form
        className="task-form stacked-form"
        onSubmit={(event) => {
          event.preventDefault();
          setSaving(true);
          setSuccessMessage(null);
          void onUpdateProfile({ phone }).then(() => {
            setSaving(false);
            setSuccessMessage("Dane zostały zapisane.");
          });
        }}
      >
        <label className="field">
          <span>Telefon</span>
          <input onChange={(event) => setPhone(event.target.value)} placeholder="Numer telefonu" value={phone} />
        </label>
        <button disabled={saving} type="submit">
          {saving ? "Zapisywanie..." : "Zapisz dane"}
        </button>
      </form>
      {successMessage ? <p className="success-banner">{successMessage}</p> : null}
    </article>
  );
}

const TASK_PRIORITY_OPTIONS: Array<{ value: TaskPriority; label: string }> = [
  { value: "LOW", label: "Niski" },
  { value: "MEDIUM", label: "Sredni" },
  { value: "HIGH", label: "Wysoki" },
  { value: "URGENT", label: "Pilny" }
];

const TASK_BUDGET_TYPE_OPTIONS: Array<{ value: TaskBudgetType; label: string }> = [
  { value: "HOURLY", label: "Godzinowy" },
  { value: "FIXED", label: "Stały" },
  { value: "FREE", label: "Bezpłatny" }
];

const THEME_OPTIONS: Array<{
  key: ThemeKey;
  label: string;
  description: string;
  swatch: { bg: string; chromeBg: string; chromeText: string; accent: string };
}> = [
  {
    key: "gold-lion",
    label: "Gold Lion",
    description: "Domyślny wygląd - ciemne panele boczne, złoto-żółty akcent.",
    swatch: { bg: "#f5f3ec", chromeBg: "#15151a", chromeText: "#ffffff", accent: "#f0b400" }
  },
  {
    key: "white-lion",
    label: "White Lion",
    description: "Jasne panele boczne i górne, stonowany błękitny akcent.",
    swatch: { bg: "#f5f3ec", chromeBg: "#ffffff", chromeText: "#17171c", accent: "#3d7ca6" }
  },
  {
    key: "modern-minimal",
    label: "Modern minimal",
    description: "Czysty, kompaktowy układ w szarościach z pastelowym akcentem.",
    swatch: { bg: "#f6f6f8", chromeBg: "#ffffff", chromeText: "#1c1c22", accent: "#6366f1" }
  }
];

function ThemeSwitcher({
  currentThemeKey,
  onSelect,
  saving
}: {
  currentThemeKey: ThemeKey;
  onSelect: (themeKey: ThemeKey) => void;
  saving: boolean;
}) {
  return (
    <div className="theme-option-grid">
      {THEME_OPTIONS.map((option) => (
        <button
          className={`theme-option${option.key === currentThemeKey ? " is-selected" : ""}`}
          disabled={saving}
          key={option.key}
          onClick={() => onSelect(option.key)}
          type="button"
        >
          <span className="theme-swatch" style={{ background: option.swatch.bg }}>
            <span className="theme-swatch-sidebar" style={{ background: option.swatch.chromeBg }}>
              <span className="theme-swatch-dot" style={{ background: option.swatch.accent }} />
              <span className="theme-swatch-line" style={{ background: option.swatch.chromeText, opacity: 0.7 }} />
              <span className="theme-swatch-line short" style={{ background: option.swatch.chromeText, opacity: 0.4 }} />
            </span>
            <span className="theme-swatch-content">
              <span className="theme-swatch-card" />
              <span className="theme-swatch-card" />
            </span>
          </span>
          <strong>{option.label}</strong>
          <span>{option.description}</span>
        </button>
      ))}
    </div>
  );
}

function MySettingsCard({
  employeeRecord,
  onUpdateProfile,
  sessionUser
}: {
  employeeRecord: EmployeeRecord;
  onUpdateProfile: (payload: UpdateMyProfileRequest) => Promise<void>;
  sessionUser: SessionUser;
}) {
  const [themeSaving, setThemeSaving] = useState(false);
  const [autoCompleteEnabled, setAutoCompleteEnabled] = useState(employeeRecord.autoCompleteEnabled);
  const [autoCompleteTime, setAutoCompleteTime] = useState(employeeRecord.autoCompleteTime ?? "18:00");
  const [emailRemindersEnabled, setEmailRemindersEnabled] = useState(employeeRecord.emailRemindersEnabled);
  const [costDisplayMode, setCostDisplayMode] = useState(employeeRecord.costDisplayMode);
  const [defaultTaskPriority, setDefaultTaskPriority] = useState(employeeRecord.defaultTaskPriority ?? "");
  const [defaultTaskBudgetType, setDefaultTaskBudgetType] = useState(employeeRecord.defaultTaskBudgetType ?? "");
  const [defaultTaskBudgetAmount, setDefaultTaskBudgetAmount] = useState(
    employeeRecord.defaultTaskBudgetAmount != null ? String(employeeRecord.defaultTaskBudgetAmount) : ""
  );
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  async function handleSelectTheme(themeKey: ThemeKey) {
    setThemeSaving(true);
    try {
      await onUpdateProfile({ themeKey });
    } finally {
      setThemeSaving(false);
    }
  }

  return (
    <>
      <article className="card">
        <p className="eyebrow">Konto</p>
        <h3>Wygląd</h3>
        <p className="placeholder-copy">Wybierz szablon wyglądu aplikacji. Zmiana obowiązuje tylko dla Twojego konta.</p>
        <ThemeSwitcher currentThemeKey={sessionUser.themeKey} onSelect={(key) => void handleSelectTheme(key)} saving={themeSaving} />
      </article>
      <article className="card">
      <p className="eyebrow">Konto</p>
      <h3>Ustawienia</h3>
      <form
        className="task-form stacked-form"
        onSubmit={(event) => {
          event.preventDefault();
          setSaving(true);
          setSuccessMessage(null);
          void onUpdateProfile({
            autoCompleteEnabled,
            autoCompleteTime: autoCompleteEnabled ? autoCompleteTime : undefined,
            emailRemindersEnabled,
            costDisplayMode,
            defaultTaskPriority: defaultTaskPriority === "" ? null : (defaultTaskPriority as TaskPriority),
            defaultTaskBudgetType: defaultTaskBudgetType === "" ? null : (defaultTaskBudgetType as TaskBudgetType),
            defaultTaskBudgetAmount: defaultTaskBudgetAmount.trim() === "" ? null : Number(defaultTaskBudgetAmount)
          }).then(() => {
            setSaving(false);
            setSuccessMessage("Ustawienia zostały zapisane.");
          });
        }}
      >
        <label className="field-inline-group">
          <input checked={autoCompleteEnabled} onChange={(event) => setAutoCompleteEnabled(event.target.checked)} type="checkbox" />
          <span>Automatycznie kończ zadania (zatrzymaj licznik czasu o wybranej godzinie)</span>
        </label>
        {autoCompleteEnabled ? (
          <label className="field">
            <span>Godzina automatycznego zakonczenia</span>
            <input onChange={(event) => setAutoCompleteTime(event.target.value)} type="time" value={autoCompleteTime} />
          </label>
        ) : null}
        <label className="field-inline-group">
          <input checked={emailRemindersEnabled} onChange={(event) => setEmailRemindersEnabled(event.target.checked)} type="checkbox" />
          <span>Wysyłaj przypomnienia o zadaniach na e-mail</span>
        </label>

        <p className="eyebrow">Sposób obliczania kosztu zadania (tylko w Twoim widoku)</p>
        <label className="field">
          <span>Rozliczanie czasu</span>
          <select onChange={(event) => setCostDisplayMode(event.target.value as "HOURLY" | "PER_MINUTE")} value={costDisplayMode}>
            <option value="HOURLY">Godzinowo (zaokrąglanie do pełnej godziny, bufor 5 min)</option>
            <option value="PER_MINUTE">Minutowo (bez zaokrąglania i bufora)</option>
          </select>
        </label>

        <p className="eyebrow">Domyślne ustawienia nowego zadania</p>
        <label className="field">
          <span>Priorytet</span>
          <select onChange={(event) => setDefaultTaskPriority(event.target.value)} value={defaultTaskPriority}>
            <option value="">Domyślny aplikacji (Sredni)</option>
            {TASK_PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Typ budżetu</span>
          <select onChange={(event) => setDefaultTaskBudgetType(event.target.value)} value={defaultTaskBudgetType}>
            <option value="">Domyślny aplikacji (Godzinowy)</option>
            {TASK_BUDGET_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {defaultTaskBudgetType === "FIXED" ? (
          <label className="field">
            <span>Domyślna kwota budżetu (zł)</span>
            <input
              onChange={(event) => setDefaultTaskBudgetAmount(event.target.value)}
              type="number"
              value={defaultTaskBudgetAmount}
            />
          </label>
        ) : null}

        <button disabled={saving} type="submit">
          {saving ? "Zapisywanie..." : "Zapisz ustawienia"}
        </button>
      </form>
      {successMessage ? <p className="success-banner">{successMessage}</p> : null}
      </article>
      <ClientSourceVisibilitySettings />
    </>
  );
}

function ClientSourceVisibilitySettings() {
  const [sources, setSources] = useState<ClientSourceOption[]>([]);
  const [visibleKeys, setVisibleKeys] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchClientSources(), fetchVisibleClientSourceKeys()]).then(([sourcesResponse, storedKeys]) => {
      setSources(sourcesResponse.items);
      setVisibleKeys(storedKeys ? new Set(storedKeys) : new Set(sourcesResponse.items.map((item) => item.key)));
      setLoading(false);
    });
  }, []);

  function isChecked(key: string) {
    return visibleKeys?.has(key) ?? true;
  }

  function toggle(key: string) {
    setVisibleKeys((current) => {
      const base = current ?? new Set(sources.map((item) => item.key));
      const next = new Set(base);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      void saveVisibleClientSourceKeys(Array.from(next));
      return next;
    });
  }

  if (loading || sources.length <= 1) return null;

  return (
    <article className="card">
      <p className="eyebrow">Konto</p>
      <h3>Widoczność klientów</h3>
      <p className="placeholder-copy">
        Wybierz, których klientów chcesz widzieć w listach wyboru klienta (zadania, projekty, tickety, notatki, przypomnienia).
      </p>
      <div className="task-form stacked-form">
        {sources.map((source) => (
          <label className="field-inline-group" key={source.key}>
            <input checked={isChecked(source.key)} onChange={() => toggle(source.key)} type="checkbox" />
            <span>{source.label}</span>
          </label>
        ))}
      </div>
    </article>
  );
}

function IntegrationTypeBadge({ type }: { type: IntegrationType }) {
  return <span className="badge">{type === "SHOPER" ? "Shoper" : "SMTP"}</span>;
}

function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<IntegrationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<IntegrationRecord | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  function reload() {
    setLoading(true);
    void fetchIntegrations()
      .then(({ items }) => setIntegrations(items))
      .finally(() => setLoading(false));
  }

  useEffect(reload, []);

  async function handleDelete(integration: IntegrationRecord) {
    if (!window.confirm(`Usunąć integrację "${integration.name}"? Tej operacji nie można cofnąć.`)) return;
    await deleteIntegration(integration.id);
    reload();
  }

  async function handleToggleEnabled(integration: IntegrationRecord) {
    await updateIntegration(integration.id, { enabled: !integration.enabled });
    reload();
  }

  async function handleSync(integration: IntegrationRecord) {
    setSyncingId(integration.id);
    try {
      await syncIntegration(integration.id);
      reload();
    } finally {
      setSyncingId(null);
    }
  }

  function handleSaved() {
    setFormOpen(false);
    setEditingIntegration(null);
    reload();
  }

  return (
    <section className="section-stack">
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Integracje</p>
            <h3>Połączenia systemowe</h3>
          </div>
          <button
            onClick={() => {
              setEditingIntegration(null);
              setFormOpen(true);
            }}
            type="button"
          >
            + Nowa integracja
          </button>
        </div>
        {loading ? <p className="placeholder-copy">Wczytywanie...</p> : null}
        {!loading && integrations.length === 0 ? (
          <EmptyState message="Brak skonfigurowanych integracji. Dodaj Shoper, aby importować klientów ze sklepu, albo SMTP, aby zmienić serwer poczty." />
        ) : null}
        <div className="reminder-list">
          {integrations.map((integration) => (
            <div className="reminder-item" key={integration.id}>
              <div>
                <span className={integration.enabled ? "" : "is-muted"}>
                  <strong>{integration.name}</strong> <IntegrationTypeBadge type={integration.type} />
                </span>
                <p className="placeholder-copy">
                  {integration.type === "SHOPER" ? (
                    <>
                      {integration.clientCount} klientów • co {integration.config.syncFrequencyHours}h •{" "}
                      {integration.lastSyncAt
                        ? `Ostatnia synchronizacja: ${formatDateTimeLabel(integration.lastSyncAt)} (${integration.lastSyncStatus ?? "?"})`
                        : "Jeszcze nie zsynchronizowano"}
                      {integration.lastSyncMessage ? ` — ${integration.lastSyncMessage}` : ""}
                    </>
                  ) : (
                    "Serwer poczty wychodzącej"
                  )}
                </p>
              </div>
              <div className="reminder-item-actions">
                <label className="field-inline-group compact">
                  <input checked={integration.enabled} onChange={() => void handleToggleEnabled(integration)} type="checkbox" />
                  <span>Włączona</span>
                </label>
                {integration.type === "SHOPER" && integration.enabled ? (
                  <button
                    className="secondary compact-action"
                    disabled={syncingId === integration.id}
                    onClick={() => void handleSync(integration)}
                    type="button"
                  >
                    {syncingId === integration.id ? "Synchronizuję..." : "Synchronizuj teraz"}
                  </button>
                ) : null}
                <button
                  className="secondary compact-action"
                  onClick={() => {
                    setEditingIntegration(integration);
                    setFormOpen(true);
                  }}
                  type="button"
                >
                  Edytuj
                </button>
                <button
                  aria-label="Usuń integrację"
                  className="secondary compact-action icon-action danger"
                  onClick={() => void handleDelete(integration)}
                  type="button"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))}
        </div>
      </article>
      {formOpen ? (
        <IntegrationFormModal
          integration={editingIntegration}
          onClose={() => {
            setFormOpen(false);
            setEditingIntegration(null);
          }}
          onSaved={handleSaved}
        />
      ) : null}
    </section>
  );
}

function IntegrationFormModal({
  integration,
  onClose,
  onSaved
}: {
  integration: IntegrationRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [type, setType] = useState<IntegrationType>(integration?.type ?? "SHOPER");
  const [name, setName] = useState(integration?.name ?? (type === "SHOPER" ? "Sklep Shoper" : "Serwer SMTP"));
  const [enabled, setEnabled] = useState(integration?.enabled ?? true);
  const [storeUrl, setStoreUrl] = useState(integration?.type === "SHOPER" ? integration.config.storeUrl : "");
  const [apiLogin, setApiLogin] = useState(integration?.type === "SHOPER" ? integration.config.apiLogin : "");
  const [apiPassword, setApiPassword] = useState("");
  const [syncFrequencyHours, setSyncFrequencyHours] = useState(
    String(integration?.type === "SHOPER" ? integration.config.syncFrequencyHours : 12)
  );
  const [host, setHost] = useState(integration?.type === "SMTP" ? integration.config.host : "");
  const [port, setPort] = useState(String(integration?.type === "SMTP" ? integration.config.port : 587));
  const [secure, setSecure] = useState(integration?.type === "SMTP" ? integration.config.secure : false);
  const [user, setUser] = useState(integration?.type === "SMTP" ? integration.config.user : "");
  const [from, setFrom] = useState(integration?.type === "SMTP" ? integration.config.from : "");
  const [pass, setPass] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestIntegrationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hasExistingSecret =
    integration?.type === "SHOPER" ? integration.config.hasApiPassword : integration?.type === "SMTP" ? integration.config.hasPassword : false;

  function buildConfig(): CreateIntegrationRequest["config"] {
    return type === "SHOPER"
      ? {
          storeUrl: storeUrl.trim(),
          apiLogin: apiLogin.trim(),
          apiPassword: apiPassword || undefined,
          syncFrequencyHours: Number(syncFrequencyHours) || 12
        }
      : { host: host.trim(), port: Number(port) || 587, secure, user: user.trim(), from: from.trim(), pass: pass || undefined };
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testIntegrationConnection({ type, integrationId: integration?.id, config: buildConfig() });
      setTestResult(result);
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : "Nie udało się przetestować połączenia." });
    } finally {
      setTesting(false);
    }
  }

  async function handleSubmit() {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (integration) {
        await updateIntegration(integration.id, { name: name.trim(), enabled, config: buildConfig() });
      } else {
        await createIntegration({ type, name: name.trim(), enabled, config: buildConfig() });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nie udało się zapisać integracji.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} title={integration ? "Edytuj integrację" : "Nowa integracja"}>
      <div className="task-form stacked-form">
        {!integration ? (
          <label className="field">
            <span>Typ integracji</span>
            <select onChange={(event) => setType(event.target.value as IntegrationType)} value={type}>
              <option value="SHOPER">Sklep Shoper (import klientów)</option>
              <option value="SMTP">Serwer SMTP (poczta wychodząca)</option>
            </select>
          </label>
        ) : null}
        <label className="field">
          <span>Nazwa</span>
          <input onChange={(event) => setName(event.target.value)} value={name} />
        </label>
        <label className="field-inline-group">
          <input checked={enabled} onChange={(event) => setEnabled(event.target.checked)} type="checkbox" />
          <span>Włączona</span>
        </label>
        {type === "SHOPER" ? (
          <>
            <label className="field">
              <span>Adres sklepu</span>
              <input onChange={(event) => setStoreUrl(event.target.value)} placeholder="https://twojsklep.pl" value={storeUrl} />
            </label>
            <label className="field">
              <span>Login API</span>
              <input onChange={(event) => setApiLogin(event.target.value)} value={apiLogin} />
            </label>
            <label className="field">
              <span>Hasło API</span>
              <input
                onChange={(event) => setApiPassword(event.target.value)}
                placeholder={hasExistingSecret ? "•••••• (pozostaw puste, aby nie zmieniać)" : ""}
                type="password"
                value={apiPassword}
              />
            </label>
            <label className="field">
              <span>Częstotliwość aktualizacji (godziny)</span>
              <input
                min={1}
                onChange={(event) => setSyncFrequencyHours(event.target.value)}
                type="number"
                value={syncFrequencyHours}
              />
            </label>
          </>
        ) : (
          <>
            <label className="field">
              <span>Host</span>
              <input onChange={(event) => setHost(event.target.value)} placeholder="smtp.example.com" value={host} />
            </label>
            <label className="field">
              <span>Port</span>
              <input onChange={(event) => setPort(event.target.value)} type="number" value={port} />
            </label>
            <label className="field-inline-group">
              <input checked={secure} onChange={(event) => setSecure(event.target.checked)} type="checkbox" />
              <span>Połączenie SSL/TLS (zwykle port 465)</span>
            </label>
            <label className="field">
              <span>Użytkownik</span>
              <input onChange={(event) => setUser(event.target.value)} value={user} />
            </label>
            <label className="field">
              <span>Adres nadawcy (From)</span>
              <input onChange={(event) => setFrom(event.target.value)} placeholder="powiadomienia@twojafirma.pl" value={from} />
            </label>
            <label className="field">
              <span>Hasło</span>
              <input
                onChange={(event) => setPass(event.target.value)}
                placeholder={hasExistingSecret ? "•••••• (pozostaw puste, aby nie zmieniać)" : ""}
                type="password"
                value={pass}
              />
            </label>
          </>
        )}
        <div className="task-detail-save-row">
          <button className="secondary" disabled={testing} onClick={() => void handleTest()} type="button">
            {testing ? "Testowanie..." : "Testuj połączenie"}
          </button>
        </div>
        {testResult ? <p className={testResult.ok ? "success-banner" : "error-banner"}>{testResult.message}</p> : null}
        {testResult?.details ? (
          <label className="field">
            <span>Przykładowa odpowiedź API (do diagnostyki)</span>
            <textarea readOnly rows={6} value={testResult.details} />
          </label>
        ) : null}
        {error ? <p className="error-banner">{error}</p> : null}
        <div className="task-detail-save-row">
          <button disabled={saving || !name.trim()} onClick={() => void handleSubmit()} type="button">
            {saving ? "Zapisywanie..." : "Zapisz"}
          </button>
          <button className="secondary" onClick={onClose} type="button">
            Anuluj
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ChangelogView() {
  return (
    <section className="section-stack">
      <article className="card">
        <p className="eyebrow">Panel</p>
        <h3>Changelog</h3>
        <p className="placeholder-copy">Aktualna wersja: {APP_VERSION}</p>
      </article>
      {CHANGELOG.map((entry) => (
        <article className="card changelog-entry" key={entry.version}>
          <div className="card-header compact-head">
            <div>
              <p className="eyebrow">Wersja {entry.version}</p>
              <h3>{formatDueDateAbsolute(entry.date)}</h3>
            </div>
          </div>
          <ul className="changelog-items">
            {entry.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </article>
      ))}
    </section>
  );
}

function PasswordChangeCard({
  onChangePassword
}: {
  onChangePassword: (payload: ChangePasswordRequest) => Promise<void>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { pending: changingPassword, run: runChangePassword } = useAsyncAction();

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Bezpieczenstwo</p>
          <h3>Zmiana hasła</h3>
        </div>
      </div>
      <form
        className="task-form stacked-form"
        onSubmit={(event) => {
          event.preventDefault();
          setLocalError(null);
          setSuccessMessage(null);

          if (newPassword !== confirmPassword) {
            setLocalError("Nowe hasła nie sa identyczne.");
            return;
          }

          void runChangePassword(async () => {
            try {
              await onChangePassword({ currentPassword, newPassword });
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
              setSuccessMessage("Hasło zostało zmienione.");
            } catch (changeError) {
              setLocalError(changeError instanceof Error ? changeError.message : "Nie udało się zmienić hasła.");
            }
          });
        }}
      >
        <input
          onChange={(event) => setCurrentPassword(event.target.value)}
          placeholder="Obecne hasło"
          type="password"
          value={currentPassword}
        />
        <input
          onChange={(event) => setNewPassword(event.target.value)}
          placeholder="Nowe hasło"
          type="password"
          value={newPassword}
        />
        <input
          onChange={(event) => setConfirmPassword(event.target.value)}
          placeholder="Powtórz nowe hasło"
          type="password"
          value={confirmPassword}
        />
        <button disabled={changingPassword} type="submit">
          {changingPassword ? <Spinner label="Zapisywanie..." /> : "Zmień hasło"}
        </button>
      </form>
      {localError ? <p className="error-banner">{localError}</p> : null}
      {successMessage ? <p className="success-banner">{successMessage}</p> : null}
    </article>
  );
}

function formatMoneyLabel(value: number) {
  return `${Math.round(value).toLocaleString("pl-PL")} zl`;
}

function downloadCsv(filename: string, headers: string[], rows: Array<Array<string | number>>) {
  const escapeCell = (cell: string | number) => {
    const text = String(cell);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = [headers, ...rows].map((row) => row.map(escapeCell).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function printHtmlReport(title: string, subtitle: string, headers: string[], rows: Array<Array<string | number>>) {
  const win = window.open("", "_blank");
  if (!win) return;

  const escapeHtml = (value: string | number) =>
    String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const tableRows = rows
    .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
    .join("");

  win.document.write(`<!doctype html>
<html lang="pl">
<head>
<meta charset="UTF-8" />
<title>${escapeHtml(title)}</title>
<style>
  body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color: #14140f; margin: 2.5rem; }
  h1 { font-size: 1.3rem; margin: 0 0 0.2rem; }
  p.subtitle { color: #606060; margin: 0 0 1.5rem; font-size: 0.9rem; }
  table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
  th, td { text-align: left; padding: 0.5rem 0.7rem; border-bottom: 1px solid #ddd; }
  th { text-transform: uppercase; font-size: 0.72rem; letter-spacing: 0.04em; color: #606060; }
  tbody tr:nth-child(even) { background: #f7f7f4; }
</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="subtitle">${escapeHtml(subtitle)}</p>
  <table>
    <thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("")}</tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
</body>
</html>`);
  win.document.close();
  win.focus();
  win.print();
}

function roundedTopBarPath(x: number, y: number, width: number, height: number, radius: number) {
  if (height <= 0) return "";
  const r = Math.min(radius, width / 2, height);
  return `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} Z`;
}

function TrendBarChart({
  data,
  valueFormatter,
  ariaLabel
}: {
  data: Array<{ label: string; value: number }>;
  valueFormatter: (value: number) => string;
  ariaLabel: string;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const gradientId = `trend-bar-gradient-${useId()}`;
  const width = 720;
  const height = 200;
  const paddingLeft = 6;
  const paddingRight = 6;
  const paddingTop = 26;
  const paddingBottom = 24;
  const plotWidth = width - paddingLeft - paddingRight;
  const plotHeight = height - paddingTop - paddingBottom;
  const maxValue = Math.max(1, ...data.map((item) => item.value));
  const barSlot = data.length > 0 ? plotWidth / data.length : plotWidth;
  const barWidth = Math.max(4, Math.min(24, barSlot - 6));
  const maxIndex = data.reduce((best, item, index) => (item.value > data[best]?.value ? index : best), 0);

  if (data.length === 0 || maxValue === 0) {
    return <p className="placeholder-copy">Brak danych dla wybranego okresu.</p>;
  }

  return (
    <div className="trend-chart">
      <svg aria-label={ariaLabel} role="img" viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" />
            <stop offset="100%" stopColor="var(--accent-strong)" />
          </linearGradient>
        </defs>
        <line
          stroke="var(--line)"
          strokeDasharray="3 3"
          strokeWidth={1}
          x1={paddingLeft}
          x2={width - paddingRight}
          y1={paddingTop + plotHeight * 0.33}
          y2={paddingTop + plotHeight * 0.33}
        />
        <line
          stroke="var(--line)"
          strokeDasharray="3 3"
          strokeWidth={1}
          x1={paddingLeft}
          x2={width - paddingRight}
          y1={paddingTop + plotHeight * 0.66}
          y2={paddingTop + plotHeight * 0.66}
        />
        <line
          stroke="var(--line-strong)"
          strokeWidth={1}
          x1={paddingLeft}
          x2={width - paddingRight}
          y1={height - paddingBottom}
          y2={height - paddingBottom}
        />
        {data.map((item, index) => {
          const barHeight = (item.value / maxValue) * plotHeight;
          const x = paddingLeft + index * barSlot + (barSlot - barWidth) / 2;
          const y = height - paddingBottom - barHeight;
          const isHovered = hoverIndex === index;
          const showDirectLabel = item.value > 0 && (index === maxIndex || index === data.length - 1);

          return (
            <g key={`${item.label}-${index}`}>
              <rect
                fill="transparent"
                height={plotHeight}
                onMouseEnter={() => setHoverIndex(index)}
                onMouseLeave={() => setHoverIndex((current) => (current === index ? null : current))}
                width={barSlot}
                x={paddingLeft + index * barSlot}
                y={paddingTop}
              />
              <path
                d={roundedTopBarPath(x, y, barWidth, Math.max(barHeight, item.value > 0 ? 2 : 0), 4)}
                fill={isHovered ? "var(--accent-strong)" : `url(#${gradientId})`}
              />
              {showDirectLabel ? (
                <text fill="var(--muted)" fontSize="11" textAnchor="middle" x={x + barWidth / 2} y={Math.max(y - 8, 12)}>
                  {valueFormatter(item.value)}
                </text>
              ) : null}
              <text fill="var(--muted)" fontSize="10" textAnchor="middle" x={x + barWidth / 2} y={height - 8}>
                {item.label}
              </text>
            </g>
          );
        })}
      </svg>
      {hoverIndex !== null ? (
        <div className="trend-chart-tooltip">
          <strong>{data[hoverIndex].label}</strong>
          <span>{valueFormatter(data[hoverIndex].value)}</span>
        </div>
      ) : null}
    </div>
  );
}

function RankingBarList({
  items,
  valueFormatter
}: {
  items: Array<{ id: string; name: string; value: number }>;
  valueFormatter: (value: number) => string;
}) {
  const maxValue = Math.max(1, ...items.map((item) => item.value));

  if (items.length === 0) {
    return <p className="placeholder-copy">Brak danych.</p>;
  }

  return (
    <div className="ranking-bars">
      {items.slice(0, 8).map((item) => (
        <div className="ranking-bar-row" key={item.id}>
          <span className="ranking-bar-label">{item.name}</span>
          <div className="ranking-bar-track">
            <div className="ranking-bar-fill" style={{ width: `${Math.max(4, (item.value / maxValue) * 100)}%` }} />
          </div>
          <span className="ranking-bar-value">{valueFormatter(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

function GranularityTabs({
  value,
  onChange
}: {
  value: ReportGranularity;
  onChange: (value: ReportGranularity) => void;
}) {
  const options: Array<{ key: ReportGranularity; label: string }> = [
    { key: "day", label: "Dzienny" },
    { key: "week", label: "Tygodniowy" },
    { key: "month", label: "Miesięczny" }
  ];

  return (
    <div className="granularity-tabs" role="tablist">
      {options.map((option) => (
        <button
          className={value === option.key ? "granularity-tab is-active" : "granularity-tab"}
          key={option.key}
          onClick={() => onChange(option.key)}
          role="tab"
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function useTimeReport(scope: "client" | "employee" | "admin", clientId?: string) {
  const [granularity, setGranularity] = useState<ReportGranularity>("day");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [mode, setMode] = useState<"period" | "taskTotal">("period");
  const [report, setReport] = useState<TimeReportResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const hasCustomRange = Boolean(rangeFrom && rangeTo);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchTimeReport(
      granularity,
      hasCustomRange ? { from: rangeFrom, to: rangeTo } : undefined,
      mode,
      scope === "employee",
      clientId
    ).then((response) => {
      if (!cancelled) {
        setReport(response);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [granularity, hasCustomRange, rangeFrom, rangeTo, mode, scope, clientId]);

  function exportCsv() {
    if (!report) return;
    const headers = report.amountLabel
      ? ["Okres", "Godziny", "Godziny rozliczalne", report.amountLabel]
      : ["Okres", "Godziny", "Godziny rozliczalne"];
    const rows = report.buckets.map((bucket) =>
      report.amountLabel
        ? [bucket.label, formatDurationLabel(bucket.minutes), formatDurationLabel(bucket.billableMinutes), bucket.amount.toFixed(2)]
        : [bucket.label, formatDurationLabel(bucket.minutes), formatDurationLabel(bucket.billableMinutes)]
    );
    downloadCsv(`czas-pracy-${hasCustomRange ? `${rangeFrom}_${rangeTo}` : granularity}.csv`, headers, rows);
  }

  function exportPdf() {
    if (!report) return;
    const headers = report.amountLabel
      ? ["Okres", "Godziny", "Godziny rozliczalne", report.amountLabel]
      : ["Okres", "Godziny", "Godziny rozliczalne"];
    const rows = report.buckets.map((bucket) =>
      report.amountLabel
        ? [bucket.label, formatDurationLabel(bucket.minutes), formatDurationLabel(bucket.billableMinutes), bucket.amount.toFixed(2)]
        : [bucket.label, formatDurationLabel(bucket.minutes), formatDurationLabel(bucket.billableMinutes)]
    );
    printHtmlReport(
      "Czas pracy w okresach",
      hasCustomRange ? `Zakres: ${rangeFrom} - ${rangeTo}` : `Zestawienie: ${granularity}`,
      headers,
      rows
    );
  }

  return {
    scope,
    granularity,
    setGranularity,
    rangeFrom,
    setRangeFrom,
    rangeTo,
    setRangeTo,
    mode,
    setMode,
    report,
    loading,
    hasCustomRange,
    exportCsv,
    exportPdf
  };
}

type TimeReportState = ReturnType<typeof useTimeReport>;

function ProjectTimeBreakdownTable({ report, onOpenTask }: { report: TimeReportResponse; onOpenTask?: (taskId: string) => void }) {
  if (!report.projectBreakdown || report.projectBreakdown.length === 0) return null;
  return (
    <div className={report.amountLabel ? "project-time-table has-amount" : "project-time-table"}>
      <div className="project-time-row project-time-head">
        <span>Projekt / zadanie</span>
        <span>Czas</span>
        {report.amountLabel ? <span>Zarobek</span> : null}
      </div>
      {report.projectBreakdown.map((project) => (
        <div key={project.id}>
          <div className="project-time-row is-project">
            <span>
              {project.clientName} • {project.name}
            </span>
            <span>{formatDurationLabel(project.minutes)}</span>
            {report.amountLabel ? <span>{formatMoneyLabel(project.amount)}</span> : null}
          </div>
          {project.tasks.map((task) => (
            <div className="project-time-row is-task" key={task.id}>
              {onOpenTask ? (
                <button className="task-link-button" onClick={() => onOpenTask(task.id)} type="button">
                  {task.name}
                </button>
              ) : (
                <span>{task.name}</span>
              )}
              <span>{formatDurationLabel(task.minutes)}</span>
              {report.amountLabel ? <span>{formatMoneyLabel(task.amount)}</span> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function TimeReportBreakdownCard({
  report,
  onOpenTask
}: {
  report: TimeReportResponse;
  onOpenTask?: (taskId: string) => void;
}) {
  if (!report.projectBreakdown || report.projectBreakdown.length === 0) return null;
  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Raport</p>
          <h3>Czas i zarobek wg projektów i zadań</h3>
        </div>
      </div>
      <ProjectTimeBreakdownTable onOpenTask={onOpenTask} report={report} />
    </article>
  );
}

function TimeReportChartCard({
  state,
  showBreakdown = true,
  onOpenTask
}: {
  state: TimeReportState;
  showBreakdown?: boolean;
  onOpenTask?: (taskId: string) => void;
}) {
  const {
    scope,
    granularity,
    setGranularity,
    rangeFrom,
    setRangeFrom,
    rangeTo,
    setRangeTo,
    mode,
    setMode,
    report,
    loading,
    hasCustomRange,
    exportCsv,
    exportPdf
  } = state;

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Raport</p>
          <h3>Czas pracy w okresach</h3>
        </div>
        <div className="card-header-actions">
          <button className="secondary" disabled={!report} onClick={exportPdf} type="button">
            Eksport PDF
          </button>
          <button className="secondary" disabled={!report} onClick={exportCsv} type="button">
            Eksport CSV
          </button>
        </div>
      </div>
      <div className="report-range-toolbar">
        {hasCustomRange ? (
          <span className="ghost-tag">Zakres własny (dzienny)</span>
        ) : (
          <GranularityTabs onChange={setGranularity} value={granularity} />
        )}
        <div className="granularity-tabs" role="tablist">
          <button
            className={mode === "period" ? "granularity-tab is-active" : "granularity-tab"}
            onClick={() => setMode("period")}
            role="tab"
            type="button"
          >
            Czas w okresie
          </button>
          <button
            className={mode === "taskTotal" ? "granularity-tab is-active" : "granularity-tab"}
            onClick={() => setMode("taskTotal")}
            role="tab"
            type="button"
          >
            Czas całkowity zadań
          </button>
        </div>
        <div className="report-date-range">
          <label className="field">
            <span>Od</span>
            <input onChange={(event) => setRangeFrom(event.target.value)} type="date" value={rangeFrom} />
          </label>
          <label className="field">
            <span>Do</span>
            <input onChange={(event) => setRangeTo(event.target.value)} type="date" value={rangeTo} />
          </label>
          {hasCustomRange ? (
            <button
              className="secondary compact-action"
              onClick={() => {
                setRangeFrom("");
                setRangeTo("");
              }}
              type="button"
            >
              Wyczyść zakres
            </button>
          ) : null}
        </div>
      </div>
      {loading || !report ? (
        <p className="placeholder-copy">Ładowanie danych...</p>
      ) : (
        <>
          <div className="metric-stack compact-stack report-totals">
            <div>
              <span>Łączny czas</span>
              <strong>{formatDurationLabel(report.totals.minutes)}</strong>
            </div>
            <div>
              <span>Godziny rozliczalne</span>
              <strong>{formatDurationLabel(report.totals.billableMinutes)}</strong>
            </div>
            {report.amountLabel ? (
              <div>
                <span>{report.amountLabel}</span>
                <strong>{formatMoneyLabel(report.totals.amount)}</strong>
              </div>
            ) : null}
          </div>
          {mode === "taskTotal" ? (
            <p className="placeholder-copy">Uwzględnia pełną historię wybranych zadań (nie tylko czas z tego okresu).</p>
          ) : (
            <TrendBarChart
              ariaLabel="Godziny pracy w wybranym okresie"
              data={report.buckets.map((bucket) => ({ label: bucket.label, value: bucket.billableMinutes / 60 }))}
              valueFormatter={(value) => formatDurationLabel(value * 60)}
            />
          )}
          {showBreakdown && report.projectBreakdown && report.projectBreakdown.length > 0 ? (
            <div className="report-breakdown">
              <h4>Czas i zarobek wg projektów i zadań</h4>
              <ProjectTimeBreakdownTable onOpenTask={onOpenTask} report={report} />
            </div>
          ) : null}
          {scope === "admin" && report.byEmployee && report.byEmployee.length > 0 ? (
            <div className="report-breakdown">
              <h4>Godziny wg pracownika</h4>
              <RankingBarList
                items={report.byEmployee.map((item) => ({ id: item.id, name: item.name, value: item.minutes / 60 }))}
                valueFormatter={(value) => formatDurationLabel(value * 60)}
              />
            </div>
          ) : null}
          {scope === "admin" && report.byClient && report.byClient.length > 0 ? (
            <div className="report-breakdown">
              <h4>Godziny wg klienta</h4>
              <RankingBarList
                items={report.byClient.map((item) => ({ id: item.id, name: item.name, value: item.minutes / 60 }))}
                valueFormatter={(value) => formatDurationLabel(value * 60)}
              />
            </div>
          ) : null}
        </>
      )}
    </article>
  );
}

function TimeReportPanel({
  scope,
  onOpenTask,
  clientId
}: {
  scope: "client" | "employee" | "admin";
  onOpenTask?: (taskId: string) => void;
  clientId?: string;
}) {
  const state = useTimeReport(scope, clientId);
  return <TimeReportChartCard onOpenTask={onOpenTask} state={state} />;
}

function TaskBillingPanel({
  isAdmin = false,
  onOpenTask
}: {
  isAdmin?: boolean;
  onOpenTask?: (taskId: string) => void;
}) {
  const [billing, setBilling] = useState<TaskBillingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  function reload() {
    void fetchTaskBilling().then((response) => {
      setBilling(response);
      setLoading(false);
    });
  }

  useEffect(() => {
    let cancelled = false;
    void fetchTaskBilling().then((response) => {
      if (!cancelled) {
        setBilling(response);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function togglePaid(taskId: string, paid: boolean) {
    setUpdatingId(taskId);
    try {
      await setTaskPaid(taskId, paid);
      reload();
    } finally {
      setUpdatingId(null);
    }
  }

  function exportCsv() {
    if (!billing) return;
    downloadCsv(
      "rozliczenia-zadań.csv",
      ["Zadanie", "Klient", "Projekt", "Kwota", "Status"],
      billing.items.map((item) => [
        item.taskTitle,
        item.clientName ?? "Bez klienta",
        item.projectName ?? "",
        item.amount,
        item.paidAt ? "Zapłacone" : "Do zapłaty"
      ])
    );
  }

  function exportPdf() {
    if (!billing) return;
    printHtmlReport(
      "Rozliczenia zadań",
      `Wypracowano: ${formatMoneyLabel(billing.totals.totalAmount)} - Do zapłaty: ${formatMoneyLabel(billing.totals.unpaidAmount)}`,
      ["Zadanie", "Klient", "Projekt", "Kwota", "Status"],
      billing.items.map((item) => [
        item.taskTitle,
        item.clientName ?? "Bez klienta",
        item.projectName ?? "",
        item.amount,
        item.paidAt ? "Zapłacone" : "Do zapłaty"
      ])
    );
  }

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Finanse</p>
          <h3>Rozliczenia zadań</h3>
        </div>
        <div className="card-header-actions">
          <button className="secondary" disabled={!billing} onClick={exportPdf} type="button">
            Eksport PDF
          </button>
          <button className="secondary" disabled={!billing} onClick={exportCsv} type="button">
            Eksport CSV
          </button>
        </div>
      </div>
      {loading || !billing ? (
        <p className="placeholder-copy">Ładowanie danych...</p>
      ) : (
        <>
          <div className="metric-stack compact-stack report-totals">
            <div>
              <span>Wypracowano razem</span>
              <strong>{formatMoneyLabel(billing.totals.totalAmount)}</strong>
            </div>
            <div>
              <span>Zapłacone</span>
              <strong>{formatMoneyLabel(billing.totals.paidAmount)}</strong>
            </div>
            <div>
              <span>Do zapłaty</span>
              <strong>{formatMoneyLabel(billing.totals.unpaidAmount)}</strong>
            </div>
          </div>
          <div className="table report-invoice-table">
            <div className="table-head row row-invoice row-head">
              <span>Zadanie</span>
              <span>Klient</span>
              <span>Projekt</span>
              <span className="cell-numeric">Kwota</span>
              <span>Status</span>
            </div>
            {billing.items.slice(0, 20).map((item) => (
              <div className="row row-invoice" key={item.taskId}>
                {onOpenTask ? (
                  <button className="task-link-button" onClick={() => onOpenTask(item.taskId)} type="button">
                    {item.taskTitle}
                  </button>
                ) : (
                  <span>{item.taskTitle}</span>
                )}
                <span>{item.clientName}</span>
                <span>{item.projectName ?? "Brak"}</span>
                <span className="cell-numeric">{formatMoneyLabel(item.amount)}</span>
                <span className="billing-status-cell">
                  <span className={item.paidAt ? "invoice-status is-paid" : "invoice-status is-unpaid"}>
                    {item.paidAt ? "Zapłacone" : "Do zapłaty"}
                  </span>
                  {isAdmin ? (
                    <button
                      className="secondary compact-action"
                      disabled={updatingId === item.taskId}
                      onClick={() => void togglePaid(item.taskId, !item.paidAt)}
                      type="button"
                    >
                      {item.paidAt ? "Cofnij" : "Oznacz jako zapłacone"}
                    </button>
                  ) : null}
                </span>
              </div>
            ))}
            {billing.items.length === 0 ? <p className="placeholder-copy">Brak zakonczonych płatnych zadań.</p> : null}
          </div>
        </>
      )}
    </article>
  );
}

function TaskBillingPage({ onOpenTask }: { onOpenTask?: (taskId: string) => void }) {
  const [billing, setBilling] = useState<TaskBillingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [tab, setTab] = useState<"unpaid" | "paid">("unpaid");

  function reload() {
    void fetchTaskBilling().then((response) => {
      setBilling(response);
      setLoading(false);
    });
  }

  useEffect(() => {
    let cancelled = false;
    void fetchTaskBilling().then((response) => {
      if (!cancelled) {
        setBilling(response);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function togglePaid(taskId: string, paid: boolean) {
    setUpdatingId(taskId);
    try {
      await setTaskPaid(taskId, paid);
      reload();
    } finally {
      setUpdatingId(null);
    }
  }

  const filteredItems = billing ? billing.items.filter((item) => (tab === "paid" ? Boolean(item.paidAt) : !item.paidAt)) : [];

  const clientGroups = (() => {
    const groups = new Map<string, { clientId: string; clientName: string; items: typeof filteredItems; subtotal: number }>();
    for (const item of filteredItems) {
      const groupKey = item.clientId ?? "none";
      const existing = groups.get(groupKey);
      if (existing) {
        existing.items.push(item);
        existing.subtotal += item.amount;
      } else {
        groups.set(groupKey, {
          clientId: groupKey,
          clientName: item.clientName ?? "Bez klienta",
          items: [item],
          subtotal: item.amount
        });
      }
    }
    return Array.from(groups.values()).sort((a, b) => b.subtotal - a.subtotal);
  })();

  function exportCsv() {
    if (!billing) return;
    downloadCsv(
      `rozliczenia-zadań-${tab === "paid" ? "zapłacone" : "niezaplacone"}.csv`,
      ["Zadanie", "Klient", "Projekt", "Termin", "Czas", "Kwota", "Status"],
      filteredItems.map((item) => [
        item.taskTitle,
        item.clientName ?? "Bez klienta",
        item.projectName ?? "",
        item.dueDate ? formatDueDateAbsolute(item.dueDate) : "",
        formatDurationLabel(item.trackedMinutes),
        item.amount,
        item.paidAt ? "Zapłacone" : "Do zapłaty"
      ])
    );
  }

  function exportPdf() {
    if (!billing) return;
    printHtmlReport(
      "Rozliczenia zadań",
      `Wypracowano: ${formatMoneyLabel(billing.totals.totalAmount)} - Do zapłaty: ${formatMoneyLabel(billing.totals.unpaidAmount)}`,
      ["Zadanie", "Klient", "Projekt", "Termin", "Czas", "Kwota", "Status"],
      filteredItems.map((item) => [
        item.taskTitle,
        item.clientName ?? "Bez klienta",
        item.projectName ?? "",
        item.dueDate ? formatDueDateAbsolute(item.dueDate) : "",
        formatDurationLabel(item.trackedMinutes),
        item.amount,
        item.paidAt ? "Zapłacone" : "Do zapłaty"
      ])
    );
  }

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Finanse</p>
          <h3>Rozliczenia zadań</h3>
        </div>
        <div className="card-header-actions">
          <button className="secondary" disabled={!billing} onClick={exportPdf} type="button">
            Eksport PDF
          </button>
          <button className="secondary" disabled={!billing} onClick={exportCsv} type="button">
            Eksport CSV
          </button>
        </div>
      </div>
      {loading || !billing ? (
        <p className="placeholder-copy">Ładowanie danych...</p>
      ) : (
        <>
          <div className="metric-stack compact-stack report-totals">
            <div>
              <span>Wypracowano razem</span>
              <strong>{formatMoneyLabel(billing.totals.totalAmount)}</strong>
            </div>
            <div>
              <span>Zapłacone</span>
              <strong>{formatMoneyLabel(billing.totals.paidAmount)}</strong>
            </div>
            <div>
              <span>Do zapłaty</span>
              <strong>{formatMoneyLabel(billing.totals.unpaidAmount)}</strong>
            </div>
          </div>
          <div className="granularity-tabs" role="tablist">
            <button
              className={tab === "unpaid" ? "granularity-tab is-active" : "granularity-tab"}
              onClick={() => setTab("unpaid")}
              role="tab"
              type="button"
            >
              Niezaplacone ({billing.items.filter((item) => !item.paidAt).length})
            </button>
            <button
              className={tab === "paid" ? "granularity-tab is-active" : "granularity-tab"}
              onClick={() => setTab("paid")}
              role="tab"
              type="button"
            >
              Zapłacone ({billing.items.filter((item) => item.paidAt).length})
            </button>
          </div>
          {clientGroups.length === 0 ? (
            <p className="placeholder-copy">Brak zadań w tej kategorii.</p>
          ) : (
            clientGroups.map((group) => (
              <div className="report-breakdown" key={group.clientId}>
                <h4>
                  {group.clientName} • {group.items.length} {group.items.length === 1 ? "zadanie" : "zadań"} •{" "}
                  {formatMoneyLabel(group.subtotal)}
                </h4>
                <div className="table report-invoice-table">
                  <div className="table-head row row-invoice-detailed row-head">
                    <span>Zadanie</span>
                    <span>Projekt</span>
                    <span>Termin</span>
                    <span className="cell-numeric">Czas</span>
                    <span className="cell-numeric">Kwota</span>
                    <span>Status</span>
                  </div>
                  {group.items.map((item) => (
                    <div className="row row-invoice-detailed" key={item.taskId}>
                      {onOpenTask ? (
                        <button className="task-link-button" onClick={() => onOpenTask(item.taskId)} type="button">
                          {item.taskTitle}
                        </button>
                      ) : (
                        <span>{item.taskTitle}</span>
                      )}
                      <span>{item.projectName ?? "Brak"}</span>
                      <span>{item.dueDate ? formatDueDateAbsolute(item.dueDate) : "Brak"}</span>
                      <span className="cell-numeric">{formatDurationLabel(item.trackedMinutes)}</span>
                      <span className="cell-numeric">{formatMoneyLabel(item.amount)}</span>
                      <span className="billing-status-cell">
                        <span className={item.paidAt ? "invoice-status is-paid" : "invoice-status is-unpaid"}>
                          {item.paidAt ? "Zapłacone" : "Do zapłaty"}
                        </span>
                        <button
                          className="secondary compact-action"
                          disabled={updatingId === item.taskId}
                          onClick={() => void togglePaid(item.taskId, !item.paidAt)}
                          type="button"
                        >
                          {item.paidAt ? "Cofnij rozliczenie" : "Oznacz jako zapłacone"}
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </article>
  );
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ClientNotesRemindersPanel({ clientId }: { clientId: string }) {
  const [notes, setNotes] = useState<NoteSummary[]>([]);
  const [reminders, setReminders] = useState<ReminderRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchNotesForClient(clientId), fetchRemindersForClient(clientId)])
      .then(([notesResponse, remindersResponse]) => {
        if (cancelled) return;
        setNotes(notesResponse.items);
        setReminders(remindersResponse.items);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return (
    <article className="card compact-card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Notatki i przypomnienia</p>
          <h3>Powiązane z tym klientem</h3>
        </div>
      </div>
      {loading ? <p className="placeholder-copy">Wczytywanie...</p> : null}
      {!loading && notes.length === 0 && reminders.length === 0 ? (
        <EmptyState message="Brak notatek i przypomnień powiązanych z tym klientem." />
      ) : null}
      {notes.length > 0 ? (
        <>
          <p className="eyebrow">Notatki</p>
          <div className="note-list">
            {notes.map((note) => (
              <div className="note-list-item" key={note.id}>
                <strong>{note.title}</strong>
                <span>
                  {note.createdByName} • {formatDateTimeLabel(note.updatedAt)}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : null}
      {reminders.length > 0 ? (
        <>
          <p className="eyebrow">Przypomnienia</p>
          <div className="reminder-list">
            {reminders.map((reminder) => (
              <div className="reminder-item" key={reminder.id}>
                <span className={reminder.enabled ? "" : "is-muted"}>
                  <strong>{reminder.title}</strong> — {describeReminder(reminder)}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </article>
  );
}

function DocumentsPanel({ clientId, allowUpload = true }: { clientId: string; allowUpload?: boolean }) {
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  function reload() {
    void fetchDocuments(clientId).then((response) => {
      setDocuments(response.items);
      setLoading(false);
    });
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchDocuments(clientId).then((response) => {
      if (!cancelled) {
        setDocuments(response.items);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  async function handleFileSelected(file: File | undefined) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      window.alert("Plik jest za duży (limit 10 MB).");
      return;
    }
    setUploading(true);
    try {
      const base64Data = await readFileAsBase64(file);
      await uploadDocument({ clientId, fileName: file.name, mimeType: file.type || "application/octet-stream", base64Data });
      reload();
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(documentId: string) {
    if (!window.confirm("Usunąć ten dokument?")) return;
    await deleteDocument(documentId);
    reload();
  }

  return (
    <article className="card compact-card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Dokumenty</p>
          <h3>Pliki klienta</h3>
        </div>
        {allowUpload ? (
          <label className="secondary compact-action file-upload-button">
            {uploading ? "Wysyłanie..." : "+ Dodaj plik"}
            <input
              disabled={uploading}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                void handleFileSelected(file);
              }}
              style={{ display: "none" }}
              type="file"
            />
          </label>
        ) : null}
      </div>
      {loading ? (
        <p className="placeholder-copy">Ładowanie danych...</p>
      ) : documents.length === 0 ? (
        <p className="placeholder-copy">Brak przesłanych dokumentów.</p>
      ) : (
        <div className="document-list">
          {documents.map((doc) => (
            <div className="document-row" key={doc.id}>
              <div>
                <strong>{doc.fileName}</strong>
                <span>
                  {formatFileSize(doc.sizeBytes)} • {formatDateTimeLabel(doc.createdAt)}
                  {doc.uploadedByName ? ` • ${doc.uploadedByName}` : ""}
                </span>
              </div>
              <div className="document-row-actions">
                <button className="secondary compact-action" onClick={() => void downloadDocument(doc.id, doc.fileName)} type="button">
                  Pobierz
                </button>
                {allowUpload ? (
                  <button
                    aria-label="Usuń dokument"
                    className="secondary compact-action icon-action danger"
                    onClick={() => void handleDelete(doc.id)}
                    title="Usuń dokument"
                    type="button"
                  >
                    <TrashIcon />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function LeavePanel({ isAdmin = false }: { isAdmin?: boolean }) {
  const [items, setItems] = useState<LeaveRequestRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [type, setType] = useState<LeaveType>("VACATION");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function reload() {
    void fetchLeaveRequests().then((response) => {
      setItems(response.items);
      setLoading(false);
    });
  }

  useEffect(() => {
    let cancelled = false;
    void fetchLeaveRequests().then((response) => {
      if (!cancelled) {
        setItems(response.items);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submitRequest() {
    if (!startDate || !endDate) return;
    setSubmitting(true);
    try {
      await createLeaveRequest({ startDate, endDate, type, reason: reason || undefined });
      setStartDate("");
      setEndDate("");
      setType("VACATION");
      setReason("");
      reload();
    } finally {
      setSubmitting(false);
    }
  }

  async function decide(id: string, status: "APPROVED" | "REJECTED") {
    await decideLeaveRequest(id, status);
    reload();
  }

  const typeLabel: Record<string, string> = {
    VACATION: "Urlop wypoczynkowy",
    SICK: "Zwolnienie chorobowe",
    OTHER: "Inne"
  };
  const statusLabel: Record<string, string> = {
    PENDING: "Oczekuje",
    APPROVED: "Zaakceptowany",
    REJECTED: "Odrzucony"
  };

  return (
    <section className="section-stack">
      <article className="card compact-card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Urlopy</p>
            <h3>Złóż wniosek</h3>
          </div>
        </div>
        <form
          className="task-form stacked-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitRequest();
          }}
        >
          <label className="field">
            <span>Od</span>
            <input onChange={(event) => setStartDate(event.target.value)} type="date" value={startDate} />
          </label>
          <label className="field">
            <span>Do</span>
            <input onChange={(event) => setEndDate(event.target.value)} type="date" value={endDate} />
          </label>
          <label className="field">
            <span>Typ</span>
            <select onChange={(event) => setType(event.target.value as LeaveType)} value={type}>
              <option value="VACATION">Urlop wypoczynkowy</option>
              <option value="SICK">Zwolnienie chorobowe</option>
              <option value="OTHER">Inne</option>
            </select>
          </label>
          <label className="field">
            <span>Powód (opcjonalnie)</span>
            <input onChange={(event) => setReason(event.target.value)} value={reason} />
          </label>
          <button disabled={submitting} type="submit">
            Złóż wniosek
          </button>
        </form>
      </article>
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Urlopy</p>
            <h3>{isAdmin ? "Wszystkie wnioski" : "Moje wnioski"}</h3>
          </div>
        </div>
        {loading ? (
          <p className="placeholder-copy">Ładowanie danych...</p>
        ) : (
          <div className="table">
            <div className={isAdmin ? "table-head row row-leave-admin row-head" : "table-head row row-leave row-head"}>
              {isAdmin ? <span>Pracownik</span> : null}
              <span>Zakres</span>
              <span>Typ</span>
              <span>Status</span>
              {isAdmin ? <span>Akcja</span> : null}
            </div>
            {items.map((item) => (
              <div className={isAdmin ? "row row-leave-admin" : "row row-leave"} key={item.id}>
                {isAdmin ? <span>{item.employeeName}</span> : null}
                <span>
                  {formatDueDateAbsolute(item.startDate)} - {formatDueDateAbsolute(item.endDate)}
                </span>
                <span>{typeLabel[item.type]}</span>
                <span className={`leave-status is-${item.status.toLowerCase()}`}>{statusLabel[item.status]}</span>
                {isAdmin ? (
                  <span className="task-row-actions">
                    {item.status === "PENDING" ? (
                      <>
                        <button className="secondary compact-action" onClick={() => void decide(item.id, "APPROVED")} type="button">
                          Akceptuj
                        </button>
                        <button className="secondary compact-action" onClick={() => void decide(item.id, "REJECTED")} type="button">
                          Odrzuc
                        </button>
                      </>
                    ) : null}
                  </span>
                ) : null}
              </div>
            ))}
            {items.length === 0 ? <p className="placeholder-copy">Brak wniosków.</p> : null}
          </div>
        )}
      </article>
    </section>
  );
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function startOfWeek(date: Date) {
  const weekday = (date.getDay() + 6) % 7;
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate() - weekday);
  return start;
}

function CalendarViewTabs({
  value,
  onChange
}: {
  value: "month" | "week" | "day";
  onChange: (value: "month" | "week" | "day") => void;
}) {
  const options: Array<{ key: "month" | "week" | "day"; label: string }> = [
    { key: "day", label: "Dzienny" },
    { key: "week", label: "Tygodniowy" },
    { key: "month", label: "Miesięczny" }
  ];

  return (
    <div className="granularity-tabs" role="tablist">
      {options.map((option) => (
        <button
          className={value === option.key ? "granularity-tab is-active" : "granularity-tab"}
          key={option.key}
          onClick={() => onChange(option.key)}
          role="tab"
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function buildDueDateForDay(originalDueDate: string | undefined, day: Date) {
  const base = originalDueDate ? new Date(originalDueDate) : null;
  const hours = base && !Number.isNaN(base.getTime()) ? base.getHours() : 12;
  const minutes = base && !Number.isNaN(base.getTime()) ? base.getMinutes() : 0;
  const seconds = base && !Number.isNaN(base.getTime()) ? base.getSeconds() : 0;
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes, seconds).toISOString();
}

function CalendarDropZone({
  day,
  allowManage,
  onDropTask,
  baseClassName,
  children
}: {
  day: Date;
  allowManage: boolean;
  onDropTask: (taskId: string, day: Date) => void;
  baseClassName: string;
  children: ReactNode;
}) {
  const { isDragOver, dropTargetProps } = useTaskDropTarget((taskId) => onDropTask(taskId, day), allowManage);
  const className = [baseClassName, isDragOver ? "is-drop-hover" : ""].filter(Boolean).join(" ");
  return (
    <div className={className} {...dropTargetProps}>
      {children}
    </div>
  );
}

function CalendarTaskChip({
  task,
  allowManage,
  onOpenTask,
  onReorderTask,
  onStartSession
}: {
  task: TaskSummary;
  allowManage: boolean;
  onOpenTask: (taskId: string) => void;
  onReorderTask?: (targetTaskId: string, draggedTaskId: string, edge: "before" | "after") => void;
  onStartSession: (taskId: string) => void;
}) {
  const dragSource = useTaskDragSource(task.id, allowManage);
  const canReorder = allowManage && Boolean(onReorderTask);
  const { dragOverEdge, reorderTargetProps } = useTaskReorderTarget(
    (draggedTaskId, edge) => onReorderTask?.(task.id, draggedTaskId, edge),
    canReorder
  );
  const className = ["calendar-task-chip", dragOverEdge ? `is-drag-over-${dragOverEdge}` : ""].filter(Boolean).join(" ");
  return (
    <span className={className} title={task.title} {...dragSource} {...(canReorder ? reorderTargetProps : {})}>
      <button className="calendar-task-chip-title" onClick={() => onOpenTask(task.id)} type="button">
        {task.title}
      </button>
      {allowManage && !task.activeSessionId ? (
        <button
          aria-label="Uruchom tracker"
          className="secondary tracker-action play calendar-task-chip-action"
          onClick={(event) => {
            event.stopPropagation();
            onStartSession(task.id);
          }}
          type="button"
        >
          <span className="tracker-icon play" aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}

function ReminderChip({ reminder }: { reminder: ReminderRecord }) {
  return (
    <span className="calendar-task-chip is-reminder-chip" title={`Przypomnienie: ${reminder.title}`}>
      <span aria-hidden="true" className="calendar-task-chip-icon">
        <BellIcon />
      </span>
      <span className="calendar-task-chip-title">{reminder.title}</span>
    </span>
  );
}

function CalendarPanel({
  tasks,
  onOpenTask,
  onReorderTask,
  onStartSession,
  onUpdateTask,
  onRequestCreateTask,
  allowManage
}: {
  tasks: TaskSummary[];
  onOpenTask: (taskId: string) => void;
  onReorderTask?: (taskId: string, payload: ReorderTaskRequest) => Promise<void>;
  onStartSession: (taskId: string) => void;
  onUpdateTask: (taskId: string, payload: UpdateTaskRequest) => Promise<void>;
  onRequestCreateTask?: (day: Date) => void;
  allowManage: boolean;
}) {
  function handleReorder(targetTaskId: string, draggedTaskId: string, edge: "before" | "after") {
    if (!onReorderTask || draggedTaskId === targetTaskId) return;
    void onReorderTask(draggedTaskId, edge === "before" ? { beforeTaskId: targetTaskId } : { afterTaskId: targetTaskId });
  }
  const [viewMode, setViewMode] = useState<"month" | "week" | "day">("month");
  const [monthOffset, setMonthOffset] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);
  const [dayOffset, setDayOffset] = useState(0);
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [reminders, setReminders] = useState<ReminderRecord[]>([]);
  const today = new Date();

  useEffect(() => {
    void fetchReminders().then(({ items }) => setReminders(items));
  }, []);

  function remindersForDay(day: Date) {
    return reminders.filter((reminder) => reminder.kind === "ONE_TIME" && reminder.enabled && reminder.fireAt && sameDay(new Date(reminder.fireAt), day));
  }

  const statusFilterToggle = (
    <button
      aria-pressed={showAllStatuses}
      className={showAllStatuses ? "secondary compact-action is-active" : "secondary compact-action"}
      onClick={() => setShowAllStatuses((value) => !value)}
      title={showAllStatuses ? "Pokaz tylko aktywne zadania" : "Pokaz zadania we wszystkich statusach"}
      type="button"
    >
      {showAllStatuses ? "Wszystkie statusy" : "Tylko aktywne"}
    </button>
  );

  function goPrev() {
    if (viewMode === "month") setMonthOffset((value) => value - 1);
    else if (viewMode === "week") setWeekOffset((value) => value - 1);
    else setDayOffset((value) => value - 1);
  }

  function goNext() {
    if (viewMode === "month") setMonthOffset((value) => value + 1);
    else if (viewMode === "week") setWeekOffset((value) => value + 1);
    else setDayOffset((value) => value + 1);
  }

  function goToday() {
    setMonthOffset(0);
    setWeekOffset(0);
    setDayOffset(0);
  }

  const tasksWithDueDate = tasks.filter((task) => task.dueDate && (showAllStatuses || !isClosedTaskStatus(task.status)));

  if (viewMode === "week") {
    const weekStart = new Date(startOfWeek(today).getTime() + weekOffset * 7 * 86400000);
    const days = Array.from({ length: 7 }, (_, index) => new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + index));
    const weekEnd = days[6];
    const rangeLabel = `${weekStart.getDate().toString().padStart(2, "0")}.${(weekStart.getMonth() + 1).toString().padStart(2, "0")} - ${weekEnd
      .getDate()
      .toString()
      .padStart(2, "0")}.${(weekEnd.getMonth() + 1).toString().padStart(2, "0")}.${weekEnd.getFullYear()}`;

    return (
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Kalendarz</p>
            <h3 className="calendar-month-label">{rangeLabel}</h3>
          </div>
          <div className="card-header-actions">
            <CalendarViewTabs onChange={setViewMode} value={viewMode} />
            {statusFilterToggle}
            <button className="secondary compact-action" onClick={goPrev} type="button">
              ‹ Poprzedni
            </button>
            <button className="secondary compact-action" onClick={goToday} type="button">
              Dzisiaj
            </button>
            <button className="secondary compact-action" onClick={goNext} type="button">
              Następny ›
            </button>
          </div>
        </div>
        <div className="calendar-week-grid">
          {days.map((day) => {
            const dayTasks = tasksWithDueDate.filter((task) => sameDay(new Date(task.dueDate!), day));
            const isToday = sameDay(day, today);
            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
            const holidayName = getPolishHolidayName(day);
            const cellClassName = ["calendar-cell", isToday ? "is-today" : "", isWeekend ? "is-weekend" : "", holidayName ? "is-holiday" : ""]
              .filter(Boolean)
              .join(" ");
            return (
              <CalendarDropZone
                allowManage={allowManage}
                baseClassName={cellClassName}
                day={day}
                key={day.toISOString()}
                onDropTask={(taskId, targetDay) =>
                  void onUpdateTask(taskId, { dueDate: buildDueDateForDay(tasks.find((t) => t.id === taskId)?.dueDate, targetDay) })
                }
              >
                <span className="calendar-cell-day">
                  {["Pon", "Wt", "Sr", "Czw", "Pt", "Sob", "Nie"][(day.getDay() + 6) % 7]} {day.getDate()}
                </span>
                {holidayName ? <span className="calendar-holiday-name">{holidayName}</span> : null}
                <div className="calendar-cell-tasks calendar-cell-tasks-scroll">
                  {dayTasks.map((task) => (
                    <CalendarTaskChip
                      allowManage={allowManage}
                      key={task.id}
                      onOpenTask={onOpenTask}
                      onReorderTask={handleReorder}
                      onStartSession={onStartSession}
                      task={task}
                    />
                  ))}
                  {remindersForDay(day).map((reminder) => (
                    <ReminderChip key={reminder.id} reminder={reminder} />
                  ))}
                  {dayTasks.length === 0 && remindersForDay(day).length === 0 ? <span className="calendar-empty-hint">Brak zadań</span> : null}
                </div>
                {allowManage && onRequestCreateTask ? (
                  <button
                    aria-label="Dodaj zadanie w tym dniu"
                    className="calendar-cell-add"
                    onClick={() => onRequestCreateTask(day)}
                    title="Dodaj zadanie w tym dniu"
                    type="button"
                  >
                    <PlusIcon />
                  </button>
                ) : null}
              </CalendarDropZone>
            );
          })}
        </div>
      </article>
    );
  }

  if (viewMode === "day") {
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() + dayOffset);
    const priorityRank: Record<TaskPriority, number> = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
    const dayTasks = tasksWithDueDate
      .filter((task) => sameDay(new Date(task.dueDate!), day))
      .sort((a, b) => priorityRank[a.priority] - priorityRank[b.priority]);
    const dayLabel = day.toLocaleDateString("pl-PL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

    return (
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Kalendarz</p>
            <h3 className="calendar-month-label">{dayLabel}</h3>
          </div>
          <div className="card-header-actions">
            <CalendarViewTabs onChange={setViewMode} value={viewMode} />
            {statusFilterToggle}
            <button className="secondary compact-action" onClick={goPrev} type="button">
              ‹ Poprzedni
            </button>
            <button className="secondary compact-action" onClick={goToday} type="button">
              Dzisiaj
            </button>
            <button className="secondary compact-action" onClick={goNext} type="button">
              Następny ›
            </button>
          </div>
        </div>
        <div className="calendar-day-list">
          {dayTasks.map((task) => (
            <div className="calendar-day-row" key={task.id}>
              <button className="task-link-button" onClick={() => onOpenTask(task.id)} type="button">
                <strong>{task.title}</strong>
              </button>
              <span className="inline-badges">
                <StatusBadge tone="priority" value={task.priority} />
                <StatusBadge value={task.status} />
              </span>
              <span>{task.clientName}</span>
              {allowManage && !task.activeSessionId ? (
                <button
                  aria-label="Uruchom tracker"
                  className="secondary tracker-action play"
                  onClick={() => onStartSession(task.id)}
                  type="button"
                >
                  <span className="tracker-icon play" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ))}
          {remindersForDay(day).map((reminder) => (
            <div className="calendar-day-row is-reminder-row" key={reminder.id}>
              <span className="task-link-button">
                <span aria-hidden="true" className="calendar-task-chip-icon">
                  <BellIcon />
                </span>
                <strong>{reminder.title}</strong>
              </span>
              <span>{describeReminder(reminder)}</span>
            </div>
          ))}
          {dayTasks.length === 0 && remindersForDay(day).length === 0 ? (
            <p className="placeholder-copy">Brak zadań i przypomnień z terminem tego dnia.</p>
          ) : null}
        </div>
      </article>
    );
  }

  const viewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const tasksByDay = new Map<number, TaskSummary[]>();
  for (const task of tasksWithDueDate) {
    const date = new Date(task.dueDate!);
    if (date.getFullYear() === year && date.getMonth() === month) {
      const list = tasksByDay.get(date.getDate()) ?? [];
      list.push(task);
      tasksByDay.set(date.getDate(), list);
    }
  }

  const cells: Array<number | null> = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthLabel = viewDate.toLocaleDateString("pl-PL", { month: "long", year: "numeric" });

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Kalendarz</p>
          <h3 className="calendar-month-label">{monthLabel}</h3>
        </div>
        <div className="card-header-actions">
          <CalendarViewTabs onChange={setViewMode} value={viewMode} />
          {statusFilterToggle}
          <button className="secondary compact-action" onClick={goPrev} type="button">
            ‹ Poprzedni
          </button>
          <button className="secondary compact-action" onClick={goToday} type="button">
            Dzisiaj
          </button>
          <button className="secondary compact-action" onClick={goNext} type="button">
            Następny ›
          </button>
        </div>
      </div>
      <div className="calendar-grid calendar-grid-head">
        {["Pon", "Wt", "Sr", "Czw", "Pt", "Sob", "Nie"].map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="calendar-grid">
        {cells.map((day, index) => {
          const dayTasks = day ? tasksByDay.get(day) ?? [] : [];
          const isToday = Boolean(day && isCurrentMonth && day === today.getDate());
          if (!day) {
            return <div className="calendar-cell" key={index} />;
          }
          const cellDate = new Date(year, month, day);
          const isWeekend = index % 7 >= 5;
          const holidayName = getPolishHolidayName(cellDate);
          const cellClassName = ["calendar-cell", isToday ? "is-today" : "", isWeekend ? "is-weekend" : "", holidayName ? "is-holiday" : ""]
            .filter(Boolean)
            .join(" ");
          return (
            <CalendarDropZone
              allowManage={allowManage}
              baseClassName={cellClassName}
              day={cellDate}
              key={index}
              onDropTask={(taskId, targetDay) =>
                void onUpdateTask(taskId, { dueDate: buildDueDateForDay(tasks.find((t) => t.id === taskId)?.dueDate, targetDay) })
              }
            >
              <span className="calendar-cell-day">{day}</span>
              {holidayName ? (
                <span className="calendar-holiday-name" title={holidayName}>
                  {holidayName}
                </span>
              ) : null}
              <div className="calendar-cell-tasks">
                {dayTasks.slice(0, 3).map((task) => (
                  <CalendarTaskChip
                    allowManage={allowManage}
                    key={task.id}
                    onOpenTask={onOpenTask}
                    onReorderTask={handleReorder}
                    onStartSession={onStartSession}
                    task={task}
                  />
                ))}
                {dayTasks.length > 3 ? <span className="calendar-more">+{dayTasks.length - 3}</span> : null}
                {remindersForDay(cellDate)
                  .slice(0, 2)
                  .map((reminder) => (
                    <ReminderChip key={reminder.id} reminder={reminder} />
                  ))}
                {remindersForDay(cellDate).length > 2 ? (
                  <span className="calendar-more">+{remindersForDay(cellDate).length - 2}</span>
                ) : null}
              </div>
              {allowManage && onRequestCreateTask ? (
                <button
                  aria-label="Dodaj zadanie w tym dniu"
                  className="calendar-cell-add"
                  onClick={() => onRequestCreateTask(cellDate)}
                  title="Dodaj zadanie w tym dniu"
                  type="button"
                >
                  <PlusIcon />
                </button>
              ) : null}
            </CalendarDropZone>
          );
        })}
      </div>
    </article>
  );
}

const KANBAN_COLUMNS: TaskStatus[] = ["TODO", "IN_PROGRESS", "BLOCKED", "REVIEW", "DONE", "TO_BE_BILLED", "BILLED"];

function KanbanCard({
  task,
  allowManage,
  onOpenTask,
  onReorderTask,
  onStartSession
}: {
  task: TaskSummary;
  allowManage: boolean;
  onOpenTask: (taskId: string) => void;
  onReorderTask?: (targetTaskId: string, draggedTaskId: string, edge: "before" | "after") => void;
  onStartSession: (taskId: string) => void;
}) {
  const dragSource = useTaskDragSource(task.id, allowManage);
  const canReorder = allowManage && Boolean(onReorderTask);
  const { dragOverEdge, reorderTargetProps } = useTaskReorderTarget(
    (draggedTaskId, edge) => onReorderTask?.(task.id, draggedTaskId, edge),
    canReorder
  );
  const className = ["kanban-card", dragOverEdge ? `is-drag-over-${dragOverEdge}` : ""].filter(Boolean).join(" ");
  return (
    <div className={className} {...dragSource} {...(canReorder ? reorderTargetProps : {})}>
      <a
        className="task-link-button"
        href={`#${encodeURIComponent(buildTaskDetailKey(task.id))}`}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          onOpenTask(task.id);
        }}
      >
        <strong>{task.title}</strong>
      </a>
      <div className="inline-badges">
        <StatusBadge tone="priority" value={task.priority} />
      </div>
      <p className="kanban-card-meta">
        {task.clientName}
        {task.dueDate ? ` • ${formatDueDateLabel(task.dueDate)}` : ""}
      </p>
      {allowManage && !task.activeSessionId ? (
        <button
          aria-label="Uruchom tracker"
          className="secondary tracker-action play"
          onClick={() => onStartSession(task.id)}
          type="button"
        >
          <span className="tracker-icon play" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function KanbanColumn({
  status,
  tasksInColumn,
  allowManage,
  onOpenTask,
  onReorderTask,
  onStartSession,
  onDropTask
}: {
  status: TaskStatus;
  tasksInColumn: TaskSummary[];
  allowManage: boolean;
  onOpenTask: (taskId: string) => void;
  onReorderTask?: (targetTaskId: string, draggedTaskId: string, edge: "before" | "after") => void;
  onStartSession: (taskId: string) => void;
  onDropTask: (taskId: string, status: TaskStatus) => void;
}) {
  const { isDragOver, dropTargetProps } = useTaskDropTarget((taskId) => onDropTask(taskId, status), allowManage);
  const className = ["kanban-column", isDragOver ? "is-drop-hover" : ""].filter(Boolean).join(" ");
  return (
    <div className={className} {...dropTargetProps}>
      <div className="kanban-column-head">
        <StatusBadge value={status} />
        <span className="ghost-tag">{tasksInColumn.length}</span>
      </div>
      <div className="kanban-column-body">
        {tasksInColumn.map((task) => (
          <KanbanCard
            allowManage={allowManage}
            key={task.id}
            onOpenTask={onOpenTask}
            onReorderTask={onReorderTask}
            onStartSession={onStartSession}
            task={task}
          />
        ))}
        {tasksInColumn.length === 0 ? <p className="placeholder-copy">Brak zadań</p> : null}
      </div>
    </div>
  );
}

function KanbanBoard({
  tasks,
  onOpenTask,
  onReorderTask,
  onStartSession,
  onUpdateTask,
  allowManage
}: {
  tasks: TaskSummary[];
  onOpenTask: (taskId: string) => void;
  onReorderTask?: (taskId: string, payload: ReorderTaskRequest) => Promise<void>;
  onStartSession: (taskId: string) => void;
  onUpdateTask: (taskId: string, payload: UpdateTaskRequest) => Promise<void>;
  allowManage: boolean;
}) {
  function handleReorder(targetTaskId: string, draggedTaskId: string, edge: "before" | "after") {
    if (!onReorderTask || draggedTaskId === targetTaskId) return;
    void onReorderTask(draggedTaskId, edge === "before" ? { beforeTaskId: targetTaskId } : { afterTaskId: targetTaskId });
  }

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Kanban</p>
          <h3 className="calendar-month-label">Tablica zadań</h3>
        </div>
      </div>
      <div className="kanban-board">
        {KANBAN_COLUMNS.map((status) => (
          <KanbanColumn
            allowManage={allowManage}
            key={status}
            onDropTask={(taskId, newStatus) => void onUpdateTask(taskId, { status: newStatus })}
            onOpenTask={onOpenTask}
            onReorderTask={handleReorder}
            onStartSession={onStartSession}
            status={status}
            tasksInColumn={tasks.filter((task) => task.status === status)}
          />
        ))}
      </div>
    </article>
  );
}

function Modal({
  title,
  onClose,
  children,
  wide = false,
  className,
  closeOnBackdropClick = true
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  className?: string;
  closeOnBackdropClick?: boolean;
}) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const cardClassName = `${wide ? "card modal-card is-wide" : "card modal-card"}${className ? ` ${className}` : ""}`;

  return createPortal(
    <div className="modal-backdrop" onClick={closeOnBackdropClick ? onClose : undefined} role="presentation">
      <article className={cardClassName} onClick={(event) => event.stopPropagation()}>
        <div className="card-header modal-head">
          <h3>{title}</h3>
          <button aria-label="Zamknij" className="modal-close-x" onClick={onClose} type="button">
            <CloseIcon />
          </button>
        </div>
        {children}
      </article>
    </div>,
    document.body
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error("Błąd renderowania panelu:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="page-shell">
          <main className="content">
            <article className="card error-boundary-card">
              <p className="eyebrow">Coś poszło nie tak</p>
              <h3>Nie udało się wyświetlić tego widoku</h3>
              <p className="placeholder-copy">Spróbuj odświeżyć stronę. Jeśli problem się powtórzy, skontaktuj się z administratorem.</p>
              <button onClick={() => window.location.reload()} type="button">
                Odśwież stronę
              </button>
            </article>
          </main>
        </div>
      );
    }

    return this.props.children;
  }
}

function PlaceholderCard({
  eyebrow,
  title,
  description
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <article className="card">
      <p className="eyebrow">{eyebrow}</p>
      <h3>{title}</h3>
      <p className="placeholder-copy">{description}</p>
    </article>
  );
}

function ClientOrderForm({
  clientId,
  projects,
  onCreateTask
}: {
  clientId: string;
  projects: ProjectRecord[];
  onCreateTask: (payload: CreateTaskRequest) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const { pending: creating, run: runCreate } = useAsyncAction();

  useEffect(() => {
    setProjectId(projects[0]?.id ?? "");
  }, [projects]);

  return (
    <article className="card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Zlecenie</p>
          <h3>Dodaj nowe zlecenie</h3>
        </div>
      </div>
      <form
        className="task-form stacked-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!title.trim() || !projectId) return;
          void runCreate(async () => {
            await onCreateTask({ clientId, projectId, title, description });
            setTitle("");
            setDescription("");
          });
        }}
      >
        <select onChange={(event) => setProjectId(event.target.value)} value={projectId}>
          <option value="">Wybierz projekt</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
        <input onChange={(event) => setTitle(event.target.value)} placeholder="Tytuł zlecenia" value={title} />
        <input onChange={(event) => setDescription(event.target.value)} placeholder="Opis i zakres prac" value={description} />
        <button disabled={!projectId || creating} type="submit">
          {creating ? <Spinner label="Dodawanie..." /> : "Dodaj zlecenie"}
        </button>
      </form>
      {projects.length === 0 ? <p className="placeholder-copy">Najpierw trzeba utworzyć projekt, do którego zostanie przypisane zadanie.</p> : null}
    </article>
  );
}

function ClientDetailPage({
  activeSession,
  allowEdit,
  allowManage = false,
  allowManageTasks,
  client,
  onBack,
  onCreateProject,
  onCreateTask,
  onCreateTaskComment,
  onDelete,
  onLoadTaskDetail,
  onOpenProject,
  onOpenTask,
  onSaveClient,
  onStartSession,
  onStopSession,
  onUpdateTask,
  projects,
  tasks,
  tickets,
  teamMembers
}: {
  activeSession?: ActiveWorkSession | null;
  allowEdit: boolean;
  allowManage?: boolean;
  allowManageTasks: boolean;
  client: ClientRecord;
  onBack: () => void;
  onCreateProject?: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onCreateTask: (payload: CreateTaskRequest) => Promise<void>;
  onCreateTaskComment: (taskId: string, payload: CreateTaskCommentRequest) => Promise<TaskCommentRecord[]>;
  onDelete?: (clientId: string) => Promise<void>;
  onLoadTaskDetail: (taskId: string) => Promise<TaskDetailRecord>;
  onOpenProject: (projectId: string) => void;
  onOpenTask: (taskId: string) => void;
  onSaveClient?: (clientId: string, payload: UpdateClientRequest) => Promise<void>;
  onStartSession: (taskId: string) => void;
  onStopSession: (sessionId: string) => void;
  onUpdateTask: (taskId: string, payload: UpdateTaskRequest) => Promise<void>;
  projects: ProjectRecord[];
  tasks: TaskSummary[];
  tickets: TicketRecord[];
  teamMembers: EmployeeRecord[];
}) {
  const [companyName, setCompanyName] = useState(client.companyName);
  const [billingEmail, setBillingEmail] = useState(client.billingEmail);
  const [phone, setPhone] = useState(client.phone);
  const [vatNumber, setVatNumber] = useState(client.vatNumber ?? "");
  const [accountManagerId, setAccountManagerId] = useState(client.accountManagerId ?? "");
  const [emailsEnabled, setEmailsEnabled] = useState(client.emailsEnabled);
  const [projectName, setProjectName] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const { pending: savingProfile, run: runSaveProfile } = useAsyncAction();
  const { pending: creatingProject, run: runCreateProject } = useAsyncAction();

  useEffect(() => {
    setCompanyName(client.companyName);
    setBillingEmail(client.billingEmail);
    setPhone(client.phone);
    setVatNumber(client.vatNumber ?? "");
    setAccountManagerId(client.accountManagerId ?? "");
    setEmailsEnabled(client.emailsEnabled);
  }, [client]);

  return (
    <section className="section-stack">
      <div className="detail-toolbar">
        <button className="secondary" onClick={onBack} type="button">
          Wróć
        </button>
        {allowManage && onSaveClient && onDelete ? (
          <div className="inline-badges">
            <button
              className="secondary"
              onClick={() => {
                void onSaveClient(client.id, {
                  ...clientToUpdatePayload(client),
                  status: client.status === "BLOCKED" ? "ACTIVE" : "BLOCKED"
                });
              }}
              type="button"
            >
              {client.status === "BLOCKED" ? "Odblokuj konto" : "Zablokuj konto"}
            </button>
            <button
              className="secondary danger"
              onClick={() => {
                if (
                  !window.confirm(
                    `Usunąć klienta "${client.companyName}"? Konto zostanie zdezaktywowane - historia projektów, zadań i ticketów zostanie zachowana.`
                  )
                )
                  return;
                void onDelete(client.id).then(() => onBack());
              }}
              type="button"
            >
              Usuń klienta
            </button>
          </div>
        ) : null}
      </div>

      <section className="panel-grid detail-layout">
        <article className="card compact-card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Firma</p>
              <h3>{client.companyName}</h3>
            </div>
            <StatusBadge value={client.status} />
          </div>
          <div className="employee-summary-grid">
            <div>
              <span>Email rozliczeniowy</span>
              <strong>{client.billingEmail}</strong>
            </div>
            <div>
              <span>Telefon</span>
              <strong>{client.phone || "Brak"}</strong>
            </div>
            <div>
              <span>NIP</span>
              <strong>{client.vatNumber || "Brak"}</strong>
            </div>
            <div>
              <span>Opiekun</span>
              <strong>{client.accountManagerName || "Brak"}</strong>
            </div>
            <div>
              <span>Projekty</span>
              <strong>{projects.length}</strong>
            </div>
            <div>
              <span>Zadania</span>
              <strong>{tasks.length}</strong>
            </div>
            <div>
              <span>Otwarte tickety</span>
              <strong>{client.openTickets}</strong>
            </div>
            <div>
              <span>Saldo</span>
              <strong>{client.balance}</strong>
            </div>
          </div>
        </article>

        {allowEdit && onSaveClient ? (
          <article className="card compact-card">
            <div className="card-header">
              <div>
                <p className="eyebrow">Edycja</p>
                <h3>Dane firmy</h3>
              </div>
            </div>
            <form
              className="task-form stacked-form"
              onSubmit={(event) => {
                event.preventDefault();
                void runSaveProfile(() =>
                  onSaveClient(client.id, { companyName, billingEmail, phone, vatNumber, accountManagerId: accountManagerId || undefined, emailsEnabled })
                );
              }}
            >
              <label className="field">
                <span>Nazwa firmy</span>
                <input onChange={(event) => setCompanyName(event.target.value)} placeholder="Nazwa firmy" value={companyName} />
              </label>
              <label className="field">
                <span>Email rozliczeniowy (opcjonalnie)</span>
                <input onChange={(event) => setBillingEmail(event.target.value)} placeholder="Email rozliczeniowy" value={billingEmail} />
              </label>
              <label className="field-inline-group">
                <input checked={emailsEnabled} onChange={(event) => setEmailsEnabled(event.target.checked)} type="checkbox" />
                <span>Wysyłaj powiadomienia e-mail do tego klienta</span>
              </label>
              <label className="field">
                <span>Telefon (opcjonalnie)</span>
                <input onChange={(event) => setPhone(event.target.value)} placeholder="Telefon" value={phone} />
              </label>
              <label className="field">
                <span>NIP</span>
                <input onChange={(event) => setVatNumber(event.target.value)} placeholder="Np. 1234567890" value={vatNumber} />
              </label>
              {teamMembers.length > 0 ? (
                <label className="field">
                  <span>Opiekun (pracownik)</span>
                  <select onChange={(event) => setAccountManagerId(event.target.value)} value={accountManagerId}>
                    <option value="">Brak przypisania</option>
                    {teamMembers.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.fullName}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <button disabled={savingProfile} type="submit">
                {savingProfile ? <Spinner label="Zapisywanie..." /> : "Zapisz dane"}
              </button>
            </form>
          </article>
        ) : null}
      </section>

      <section className="panel-grid detail-layout">
        <article className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Projekty</p>
              <h3>Powiązane projekty</h3>
            </div>
          </div>
          {projects.length > 0 ? <ProjectsTable onOpenProject={onOpenProject} projects={projects} /> : <EmptyState message="Brak projektów dla tej firmy." />}
        </article>

        {allowEdit && onCreateProject ? (
          <article className="card compact-card">
            <div className="card-header">
              <div>
                <p className="eyebrow">Nowy projekt</p>
                <h3>Dodaj projekt do firmy</h3>
              </div>
            </div>
            <form
              className="task-form stacked-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!projectName.trim()) return;
                void runCreateProject(async () => {
                  await onCreateProject({
                    clientId: client.id,
                    name: projectName,
                    description: projectDescription
                  });
                  setProjectName("");
                  setProjectDescription("");
                });
              }}
            >
              <input onChange={(event) => setProjectName(event.target.value)} placeholder="Nazwa projektu" value={projectName} />
              <textarea onChange={(event) => setProjectDescription(event.target.value)} placeholder="Opis projektu" value={projectDescription} />
              <button disabled={creatingProject} type="submit">
                {creatingProject ? <Spinner label="Dodawanie..." /> : "Dodaj projekt"}
              </button>
            </form>
          </article>
        ) : null}
      </section>

      <DocumentsPanel allowUpload={allowEdit} clientId={client.id} />

      <ClientNotesRemindersPanel clientId={client.id} />

      {allowManage ? (
        <article className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Raport</p>
              <h3>Czas i koszty pracy - {client.companyName}</h3>
            </div>
          </div>
          <TimeReportPanel clientId={client.id} onOpenTask={onOpenTask} scope="admin" />
        </article>
      ) : null}

      <EmployeeTaskBoard
        activeSession={activeSession}
        allTasks={tasks}
        allowManage={allowManageTasks}
        boardTitle={`Zadania firmy ${client.companyName}`}
        columnsViewKey="tasks-client-detail"
        clientOptions={[client]}
        onChangeTask={onUpdateTask}
        onCreateComment={onCreateTaskComment}
        onCreateTask={onCreateTask}
        onLoadDetail={onLoadTaskDetail}
        onOpenProject={onOpenProject}
        onOpenTask={onOpenTask}
        onStart={onStartSession}
        onStop={onStopSession}
        projectOptions={projects}
        readOnly={!allowManageTasks}
        readOnlyDateMode="absolute"
        showSummary={false}
        teamMembers={teamMembers}
        tasks={tasks}
      />

      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Tickety</p>
            <h3>Historia kontaktu</h3>
          </div>
        </div>
        <TicketsTable tickets={tickets} />
      </article>
    </section>
  );
}

function ProjectDetailPage({
  activeSession,
  allowEdit,
  allowManage = false,
  allowManageTasks,
  client,
  onBack,
  onCreateTask,
  onCreateTaskComment,
  onDelete,
  onLoadTaskDetail,
  onOpenClient,
  onOpenTask,
  onSaveProject,
  onStartSession,
  onStopSession,
  onUpdateTask,
  project,
  tasks,
  teamMembers
}: {
  activeSession?: ActiveWorkSession | null;
  allowEdit: boolean;
  allowManage?: boolean;
  allowManageTasks: boolean;
  client: ClientRecord;
  onBack: () => void;
  onCreateTask: (payload: CreateTaskRequest) => Promise<void>;
  onCreateTaskComment: (taskId: string, payload: CreateTaskCommentRequest) => Promise<TaskCommentRecord[]>;
  onDelete?: (projectId: string) => Promise<void>;
  onLoadTaskDetail: (taskId: string) => Promise<TaskDetailRecord>;
  onOpenClient: (clientId: string) => void;
  onOpenTask: (taskId: string) => void;
  onSaveProject?: (projectId: string, payload: UpdateProjectRequest) => Promise<void>;
  onStartSession: (taskId: string) => void;
  onStopSession: (sessionId: string) => void;
  onUpdateTask: (taskId: string, payload: UpdateTaskRequest) => Promise<void>;
  project: ProjectRecord;
  tasks: TaskSummary[];
  teamMembers: EmployeeRecord[];
}) {
  const [clientId, setClientId] = useState(project.clientId);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [status, setStatus] = useState<ProjectRecord["status"]>(project.status);
  const [budget, setBudget] = useState<ProjectBudgetResponse | null>(null);
  const [budgetUnavailable, setBudgetUnavailable] = useState(false);
  const { pending: savingProject, run: runSaveProject } = useAsyncAction();

  useEffect(() => {
    setClientId(project.clientId);
    setName(project.name);
    setDescription(project.description ?? "");
    setStatus(project.status);
  }, [project]);

  useEffect(() => {
    let cancelled = false;
    setBudget(null);
    setBudgetUnavailable(false);
    void fetchProjectBudget(project.id)
      .then((response) => {
        if (!cancelled) setBudget(response);
      })
      .catch(() => {
        // Brak uprawnien (np. pracownik zdalny) - karta projektu dziala dalej bez sekcji budzetu.
        if (!cancelled) setBudgetUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [project.id]);

  return (
    <section className="section-stack">
      <div className="detail-toolbar">
        <button className="secondary" onClick={onBack} type="button">
          Wróć
        </button>
        {allowManage && onSaveProject && onDelete ? (
          <div className="inline-badges">
            <button
              className="secondary"
              onClick={() => {
                void onSaveProject(project.id, {
                  ...projectToUpdatePayload(project),
                  status: project.status === "ON_HOLD" ? "ACTIVE" : "ON_HOLD"
                });
              }}
              type="button"
            >
              {project.status === "ON_HOLD" ? "Wznów projekt" : "Wstrzymaj projekt"}
            </button>
            <button
              className="secondary danger"
              onClick={() => {
                if (
                  !window.confirm(`Usunąć projekt "${project.name}"? Projekt zostanie zdezaktywowany - zadania pozostaną zachowane jako "bez projektu".`)
                )
                  return;
                void onDelete(project.id).then(() => onBack());
              }}
              type="button"
            >
              Usuń projekt
            </button>
          </div>
        ) : null}
      </div>

      <section className="panel-grid detail-layout">
        <article className="card compact-card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Projekt</p>
              <h3>{project.name}</h3>
            </div>
          </div>
          <div className="employee-summary-grid">
            <div>
              <span>Klient</span>
              <strong>
                <EntityLink label={client.companyName} onClick={() => onOpenClient(client.id)} />
              </strong>
            </div>
            <div>
              <span>Status</span>
              <strong>{formatBadgeValue(project.status, "status")}</strong>
            </div>
            <div>
              <span>Zadania</span>
              <strong>{project.taskCount}</strong>
            </div>
            <div>
              <span>Postęp</span>
              <ProjectProgressBar done={project.doneTaskCount} total={project.taskCount} />
            </div>
            <div>
              <span>Opis</span>
              <strong>{project.description || "Brak opisu"}</strong>
            </div>
          </div>
        </article>

        {budgetUnavailable ? null : (
        <article className="card compact-card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Finanse</p>
              <h3>Budżet: plan vs rzeczywistość</h3>
            </div>
          </div>
          {!budget ? (
            <p className="placeholder-copy">Ładowanie danych...</p>
          ) : budget.plannedAmount === 0 ? (
            <p className="placeholder-copy">
              Brak zaplanowanego budzetu (zadania rozliczane godzinowo). Wypracowano dotychczas{" "}
              {formatMoneyLabel(budget.actualAmount)}.
            </p>
          ) : (
            <div className="budget-compare">
              <div className="budget-compare-row">
                <span>Planowany (ceny stale)</span>
                <strong>{formatMoneyLabel(budget.plannedAmount)}</strong>
              </div>
              <div className="budget-compare-row">
                <span>Rzeczywisty</span>
                <strong>{formatMoneyLabel(budget.actualAmount)}</strong>
              </div>
              <ProjectProgressBar
                done={budget.actualAmount}
                total={budget.plannedAmount}
                tone={budget.actualAmount > budget.plannedAmount ? "danger" : "default"}
              />
              {budget.actualAmount > budget.plannedAmount ? (
                <p className="placeholder-copy budget-over">Przekroczono planowany budżet.</p>
              ) : null}
            </div>
          )}
        </article>
        )}

        {allowEdit && onSaveProject ? (
          <article className="card compact-card">
            <div className="card-header">
              <div>
                <p className="eyebrow">Edycja</p>
                <h3>Ustawienia projektu</h3>
              </div>
            </div>
            <form
              className="task-form stacked-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!onSaveProject) return;
                void runSaveProject(() =>
                  onSaveProject(project.id, {
                    clientId,
                    name,
                    description,
                    status
                  })
                );
              }}
            >
              <ClientPickerField currentLabel={client.companyName} onChange={setClientId} value={clientId} />
              <input onChange={(event) => setName(event.target.value)} placeholder="Nazwa projektu" value={name} />
              <textarea onChange={(event) => setDescription(event.target.value)} placeholder="Opis projektu" value={description} />
              <select onChange={(event) => setStatus(event.target.value as ProjectRecord["status"])} value={status}>
                <option value="ACTIVE">Aktywny</option>
                <option value="ON_HOLD">Wstrzymany</option>
                <option value="COMPLETED">Zakończony</option>
                <option value="ARCHIVED">Archiwalny</option>
              </select>
              <button disabled={savingProject} type="submit">
                {savingProject ? <Spinner label="Zapisywanie..." /> : "Zapisz projekt"}
              </button>
            </form>
          </article>
        ) : null}
      </section>

      <EmployeeTaskBoard
        activeSession={activeSession}
        allTasks={tasks}
        allowManage={allowManageTasks}
        boardTitle={`Zadania projektu ${project.name}`}
        columnsViewKey="tasks-project-detail"
        clientOptions={[client]}
        onChangeTask={onUpdateTask}
        onCreateComment={onCreateTaskComment}
        onCreateTask={onCreateTask}
        onLoadDetail={onLoadTaskDetail}
        onOpenClient={onOpenClient}
        onOpenTask={onOpenTask}
        onStart={onStartSession}
        onStop={onStopSession}
        projectOptions={[project]}
        quickAddLockedProjectId={project.id}
        readOnly={!allowManageTasks}
        readOnlyDateMode="absolute"
        showSummary={false}
        teamMembers={teamMembers}
        tasks={tasks}
      />
    </section>
  );
}

function ClientPanel({
  activeItem,
  data,
  onNavigate,
  onOpenClient,
  onOpenProject,
  onOpenTask,
  onLoadTaskDetail,
  onCreateTicket,
  onCreateTaskComment,
  onCloseTicket,
  onReopenTicket,
  onDeleteTask,
  onLoadTaskComments,
  onReplyToTicket,
  onSelectTicket,
  onUpdateContacts,
  onUpdateProfile,
  onCreateTask,
  sessionUser
}: {
  activeItem: string;
  data: ClientDashboardData;
  onNavigate: (itemKey: string) => void;
  onOpenClient: (clientId: string) => void;
  onOpenProject: (projectId: string) => void;
  onOpenTask: (taskId: string) => void;
  onLoadTaskDetail: (taskId: string) => Promise<TaskDetailRecord>;
  onCreateTicket: (payload: CreateTicketRequest) => Promise<TicketDetailRecord>;
  onCreateTaskComment: (taskId: string, payload: CreateTaskCommentRequest) => Promise<TaskCommentRecord[]>;
  onCloseTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onReopenTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onLoadTaskComments: (taskId: string) => Promise<TaskCommentRecord[]>;
  onReplyToTicket: (ticketId: string, payload: AddTicketMessageRequest) => Promise<TicketDetailRecord>;
  onSelectTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onUpdateContacts: (payload: UpdateClientContactsRequest) => Promise<void>;
  onUpdateProfile: (payload: UpsertClientProfileRequest) => Promise<void>;
  onCreateTask: (payload: CreateTaskRequest) => Promise<void>;
  sessionUser: SessionUser;
}) {
  if (activeItem === "client-tasks") {
    return (
      <EmployeeTaskBoard
        allTasks={data.tasks.filter((task) => !isClosedTaskStatus(task.status))}
        allowManage={false}
        clientOptions={[data.profile]}
        currentUserId={sessionUser.id}
        hideExactTime
        onChangeTask={async () => undefined}
        onCreateTask={onCreateTask}
        onCreateComment={onCreateTaskComment}
        onDeleteTask={onDeleteTask}
        onLoadDetail={onLoadTaskDetail}
        onOpenClient={onOpenClient}
        onOpenProject={onOpenProject}
        onOpenTask={onOpenTask}
        projectOptions={data.projects}
        readOnly
        readOnlyDateMode="absolute"
        showSummary={false}
        teamMembers={[]}
        onStart={() => undefined}
        onStop={() => undefined}
        boardTitle="Aktualne zadania klienta"
        columnsViewKey="tasks-client"
        tasks={data.tasks.filter((task) => !isClosedTaskStatus(task.status))}
      />
    );
  }

  if (activeItem === "client-orders") {
    return (
      <section className="panel-grid two-columns">
        <ClientOrderForm clientId={data.profile.id} onCreateTask={onCreateTask} projects={data.projects} />
        <PlaceholderCard
          description="Po dodaniu zlecenia pojawi się ono od razu na liście zadań i w kolejce obsługi."
          eyebrow="Workflow"
          title="Proces zgłoszenia"
        />
      </section>
    );
  }

  if (activeItem === "client-tickets") {
    return (
      <TicketCenter
        allowCreate
        onCloseTicket={onCloseTicket}
        onReopenTicket={onReopenTicket}
        onCreateTicket={onCreateTicket}
        onReplyToTicket={onReplyToTicket}
        onSelectTicket={onSelectTicket}
        tickets={data.tickets}
        viewerRole="CLIENT"
      />
    );
  }

  if (activeItem === "client-comments") {
    return (
      <TaskCommentsCenter tasks={data.tasks} onCreateComment={onCreateTaskComment} onLoadComments={onLoadTaskComments} />
    );
  }

  if (activeItem === "client-finance") {
    return <ClientFinanceCard data={data} />;
  }

  if (activeItem === "client-reports") {
    return (
      <section className="section-stack">
        <TimeReportPanel onOpenTask={onOpenTask} scope="client" />
        <TaskBillingPanel onOpenTask={onOpenTask} />
      </section>
    );
  }

  if (activeItem === "client-documents") {
    return <DocumentsPanel clientId={data.profile.id} />;
  }

  if (activeItem === "client-company") {
    return <ClientProfileEditor onSave={onUpdateProfile} profile={data.profile} />;
  }

  if (activeItem === "client-contacts") {
    return <ClientContactsEditor contacts={data.profile.contacts} onSave={onUpdateContacts} />;
  }

  return (
    <>
      <StatsGrid items={data.stats} />
      <section className="panel-grid two-columns dashboard-main">
        <article className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Zlecenia</p>
              <h3>Aktualne zadania klienta</h3>
            </div>
            <button onClick={() => onNavigate("client-orders")} type="button">
              Dodaj zlecenie
            </button>
          </div>
          <TasksTable onOpenClient={onOpenClient} onOpenProject={onOpenProject} tasks={data.tasks} />
        </article>
        <div className="section-stack">
          <ClientFinanceCard data={data} />
          <article className="card compact-card">
            <p className="eyebrow">Klient</p>
            <h3>Stan obsługi</h3>
            <div className="metric-stack compact-stack">
              <div>
                <span>Aktywne zlecenia</span>
                <strong>{data.tasks.length}</strong>
              </div>
              <div>
                <span>Otwarte tickety</span>
                <strong>{data.tickets.filter((ticket) => ticket.status !== "CLOSED").length}</strong>
              </div>
            </div>
          </article>
        </div>
      </section>
      <section className="panel-grid two-columns">
        <article className="card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Tickety</p>
              <h3>Zgłoszenia i pytania</h3>
            </div>
            <button className="secondary" onClick={() => onNavigate("client-tickets")} type="button">
              Nowy ticket
            </button>
          </div>
          <TicketsTable onOpenClient={onOpenClient} tickets={data.tickets} />
        </article>
        <div className="section-stack">
          <ClientProfileCard data={data} onEdit={() => onNavigate("client-company")} />
          <ClientContactsCard data={data} />
        </div>
      </section>
    </>
  );
}

function EmployeeProjectsView({
  onCreateProject,
  onOpenClient,
  onOpenProject,
  projects
}: {
  onCreateProject: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onOpenClient?: (clientId: string) => void;
  onOpenProject?: (projectId: string) => void;
  projects: ProjectRecord[];
}) {
  const { pending: creatingProject, run: runCreateProject } = useAsyncAction();
  const [showAddProject, setShowAddProject] = useState(false);
  const [draft, setDraft] = useState<CreateProjectRequest>({
    clientId: "",
    name: "",
    description: ""
  });
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(projects.length / PROJECTS_PAGE_SIZE));
  const pagedProjects = projects.slice((page - 1) * PROJECTS_PAGE_SIZE, page * PROJECTS_PAGE_SIZE);

  return (
    <>
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Projekty</p>
            <h3>Wszystkie projekty klientów</h3>
          </div>
          <button onClick={() => setShowAddProject(true)} type="button">
            + Dodaj projekt
          </button>
        </div>
        <ProjectsTable onOpenClient={onOpenClient} onOpenProject={onOpenProject} projects={pagedProjects} />
        {totalPages > 1 ? (
          <div className="task-list-pagination">
            <button className="secondary compact-action" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">
              ‹ Poprzednia
            </button>
            <span>
              Strona {page} z {totalPages}
            </span>
            <button
              className="secondary compact-action"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              type="button"
            >
              Następna ›
            </button>
          </div>
        ) : null}
      </article>

      {showAddProject ? (
        <Modal onClose={() => setShowAddProject(false)} title="Nowy projekt">
          <form
            className="task-form stacked-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!draft.name.trim() || !draft.clientId) return;
              void runCreateProject(async () => {
                await onCreateProject(draft);
                setDraft({ clientId: "", name: "", description: "" });
                setShowAddProject(false);
              });
            }}
          >
            <label className="field">
              <span>Klient</span>
              <ClientPickerField
                emptyOptionLabel="Wybierz klienta"
                onChange={(nextClientId) => setDraft({ ...draft, clientId: nextClientId })}
                value={draft.clientId}
              />
            </label>
            <label className="field">
              <span>Nazwa projektu</span>
              <input onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder="Nazwa projektu" value={draft.name} />
            </label>
            <label className="field">
              <span>Opis</span>
              <input
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                placeholder="Opis projektu"
                value={draft.description ?? ""}
              />
            </label>
            <button disabled={creatingProject} type="submit">
              {creatingProject ? <Spinner label="Dodawanie..." /> : "Dodaj projekt"}
            </button>
          </form>
        </Modal>
      ) : null}
    </>
  );
}

const NOTIFICATIONS_PAGE_SIZE = 20;

function NotificationsPage({
  onNavigate,
  onOpenTask,
  ticketsTarget
}: {
  onNavigate: (itemKey: string) => void;
  onOpenTask: (taskId: string) => void;
  ticketsTarget: string;
}) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<NotificationListResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchNotifications(page, NOTIFICATIONS_PAGE_SIZE).then((response) => {
      if (!cancelled) {
        setData(response);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [page]);

  function applyRead(notificationId: string) {
    setData((current) =>
      current
        ? {
            ...current,
            unreadCount: Math.max(0, current.unreadCount - 1),
            items: current.items.map((entry) => (entry.id === notificationId ? { ...entry, isRead: true } : entry))
          }
        : current
    );
  }

  function goToNotification(item: NotificationRecord) {
    if (!item.isRead) {
      applyRead(item.id);
      void markNotificationRead(item.id);
    }
    if (item.linkType === "task" && item.linkId) {
      onOpenTask(item.linkId);
    } else if (item.linkType === "note" && item.linkId) {
      window.localStorage.setItem(PENDING_NOTE_KEY, item.linkId);
      onNavigate("notes");
    } else if (item.linkType === "ticket" && item.linkId) {
      window.localStorage.setItem(PENDING_TICKET_KEY, item.linkId);
      onNavigate(ticketsTarget);
    } else if (item.linkType === "employee") {
      onNavigate("employee-profile");
    } else if (item.linkType === "changelog") {
      onNavigate("employee-changelog");
    }
  }

  function dismiss(event: React.MouseEvent, item: NotificationRecord) {
    event.stopPropagation();
    if (item.isRead) return;
    applyRead(item.id);
    void markNotificationRead(item.id);
  }

  function markAllRead() {
    setData((current) =>
      current ? { ...current, unreadCount: 0, items: current.items.map((entry) => ({ ...entry, isRead: true })) } : current
    );
    void markAllNotificationsRead();
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <article className="card compact-card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Powiadomienia</p>
          <h3>Historia powiadomień{data && data.unreadCount > 0 ? ` (${data.unreadCount} nieprzeczytanych)` : ""}</h3>
        </div>
        {data && data.unreadCount > 0 ? (
          <button className="secondary compact-action" onClick={markAllRead} type="button">
            Oznacz wszystkie jako przeczytane
          </button>
        ) : null}
      </div>
      {loading ? (
        <p className="placeholder-copy">Ładowanie...</p>
      ) : !data || data.items.length === 0 ? (
        <p className="placeholder-copy">Brak powiadomień.</p>
      ) : (
        <>
          <div className="notification-feed">
            {data.items.map((item) => (
              <div className={item.isRead ? "notification-feed-item" : "notification-feed-item is-unread"} key={item.id}>
                {!item.isRead ? (
                  <button
                    aria-label="Oznacz jako przeczytane"
                    className="notification-feed-dismiss"
                    onClick={(event) => dismiss(event, item)}
                    title="Oznacz jako przeczytane"
                    type="button"
                  >
                    <CloseIcon />
                  </button>
                ) : null}
                <button className="notification-feed-body" onClick={() => goToNotification(item)} type="button">
                  <div className="notification-feed-head">
                    <strong>{item.title}</strong>
                    <span>{formatDateTimeLabel(item.createdAt)}</span>
                  </div>
                  <p>{item.body}</p>
                </button>
              </div>
            ))}
          </div>
          {totalPages > 1 ? (
            <div className="task-list-pagination">
              <button className="secondary compact-action" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))} type="button">
                ‹ Poprzednia
              </button>
              <span>
                Strona {page} z {totalPages}
              </span>
              <button
                className="secondary compact-action"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                type="button"
              >
                Następna ›
              </button>
            </div>
          ) : null}
        </>
      )}
    </article>
  );
}

function EmployeePanel({
  activeItem,
  onChangePassword,
  onCreateTaskComment,
  data,
  isAdmin = false,
  isRemote = false,
  onCreateClient,
  onNavigate,
  onOpenClient,
  onOpenProject,
  onOpenTask,
  onDeleteTask,
  onDeleteRecurringSeries,
  onLoadTaskDetail,
  onLoadTaskComments,
  onCreateProject,
  onCreateTask,
  onCreateTicket,
  onCloseTicket,
  onReopenTicket,
  onReorderTask,
  onUpdateRecurringSeries,
  onReplyToTicket,
  onSelectTicket,
  sessionUser,
  onStartSession,
  onStopSession,
  onUpdateProfile,
  onUpdateTask
}: {
  activeItem: string;
  onChangePassword: (payload: ChangePasswordRequest) => Promise<void>;
  onCreateTaskComment: (taskId: string, payload: CreateTaskCommentRequest) => Promise<TaskCommentRecord[]>;
  data: EmployeeDashboardData;
  isAdmin?: boolean;
  isRemote?: boolean;
  onCreateClient: (payload: CreateClientRequest) => Promise<ClientRecord>;
  onNavigate: (itemKey: string) => void;
  onOpenClient: (clientId: string) => void;
  onOpenProject: (projectId: string) => void;
  onOpenTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => Promise<void>;
  onDeleteRecurringSeries: (seriesId: string) => Promise<void>;
  onLoadTaskDetail: (taskId: string) => Promise<TaskDetailRecord>;
  onLoadTaskComments: (taskId: string) => Promise<TaskCommentRecord[]>;
  onCreateProject: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onCreateTask: (payload: CreateTaskRequest) => Promise<void>;
  onCreateTicket: (payload: CreateTicketRequest) => Promise<TicketDetailRecord>;
  onCloseTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onReopenTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onReorderTask: (taskId: string, payload: ReorderTaskRequest) => Promise<void>;
  onUpdateRecurringSeries: (seriesId: string, payload: UpdateRecurringSeriesRequest) => Promise<void>;
  onReplyToTicket: (ticketId: string, payload: AddTicketMessageRequest) => Promise<TicketDetailRecord>;
  onSelectTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  sessionUser: SessionUser;
  onStartSession: (taskId: string) => void;
  onStopSession: (sessionId: string) => void;
  onUpdateProfile: (payload: UpdateMyProfileRequest) => Promise<void>;
  onUpdateTask: (taskId: string, payload: UpdateTaskRequest) => Promise<void>;
}) {
  const [calendarQuickAddDate, setCalendarQuickAddDate] = useState<Date | null>(null);
  // Closed (DONE/BILLED) work older than 90 days is no longer part of `data.tasks` (see
  // fetchTasks in getDashboardFromDb) - the Archive view fetches its own, unbounded, complete
  // history on demand instead, only while this tab is actually open.
  const [archiveTasks, setArchiveTasks] = useState<TaskSummary[]>([]);
  useEffect(() => {
    if (activeItem !== "employee-archive") return;
    let cancelled = false;
    fetchTaskArchive()
      .then((items) => {
        if (!cancelled) setArchiveTasks(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeItem]);
  const myHourlyRate = parseFloat((data.teamMembers ?? []).find((member) => member.id === sessionUser.id)?.hourlyRate ?? "0") || 0;
  const myTasks = isRemote
    ? data.tasks
      : data.tasks.filter(
        (task) =>
          !isClosedTaskStatus(task.status) &&
          (task.assignees.some((assignee) => assignee.id === sessionUser.id) ||
            (data.activeSession != null && task.activeSessionId === data.activeSession.id))
      );
  const myAllTasks = isRemote
    ? data.tasks
    : data.tasks.filter(
        (task) =>
          task.assignees.some((assignee) => assignee.id === sessionUser.id) ||
          (data.activeSession != null && task.activeSessionId === data.activeSession.id)
      );
  const allTasks = data.tasks.filter((task) => !isClosedTaskStatus(task.status));
  const unassignedTasks = data.tasks.filter((task) => !isClosedTaskStatus(task.status) && task.assignees.length === 0);
  const myRecurringSeries = data.recurringSeries.filter((series) => series.assigneeIds.includes(sessionUser.id));

  const taskManager = (
    <EmployeeTaskBoard
      activeSession={data.activeSession}
      allTasks={allTasks}
      allowManage={!isRemote}
      clientOptions={data.clients}
      currentUserId={sessionUser.id}
      isAdmin={isAdmin}
      onChangeTask={onUpdateTask}
      onCreateTask={onCreateTask}
      onCreateComment={onCreateTaskComment}
      onCreateClient={onCreateClient}
      onCreateProject={onCreateProject}
      onDeleteTask={onDeleteTask}
      onLoadDetail={onLoadTaskDetail}
      onOpenClient={onOpenClient}
      onOpenProject={onOpenProject}
      onOpenTask={onOpenTask}
      onReorderTask={onReorderTask}
      projectOptions={data.projects}
      teamMembers={data.teamMembers}
      onStart={onStartSession}
      onStop={onStopSession}
      boardTitle="Lista pracy"
      columnsViewKey="tasks-mine"
      tasks={myTasks}
      summaryTasks={myAllTasks}
      recurringSeries={isRemote ? undefined : myRecurringSeries}
      onUpdateRecurringSeries={onUpdateRecurringSeries}
      onDeleteRecurringSeries={onDeleteRecurringSeries}
      canChangeStatus={isRemote ? true : undefined}
      statusOptions={isRemote ? REMOTE_EMPLOYEE_STATUS_OPTIONS : undefined}
      canTrackTime={isRemote ? true : undefined}
    />
  );

  if (activeItem === "employee-all-tasks" && !isRemote) {
    return (
      <EmployeeTaskBoard
        activeSession={data.activeSession}
        allTasks={allTasks}
        allowManage={!isRemote}
        clientOptions={data.clients}
        currentUserId={sessionUser.id}
        isAdmin={isAdmin}
        onChangeTask={onUpdateTask}
        onCreateTask={onCreateTask}
        onCreateComment={onCreateTaskComment}
        onCreateClient={onCreateClient}
        onCreateProject={onCreateProject}
        onDeleteTask={onDeleteTask}
        onLoadDetail={onLoadTaskDetail}
        onOpenClient={onOpenClient}
        onOpenProject={onOpenProject}
      onOpenTask={onOpenTask}
        onReorderTask={onReorderTask}
        projectOptions={data.projects}
        teamMembers={data.teamMembers}
        onStart={onStartSession}
        onStop={onStopSession}
        showSummary={false}
        boardTitle="Podgląd wszystkich otwartych zadań"
        columnsViewKey="tasks-all"
        showAssigneeFilter
        tasks={allTasks}
      />
    );
  }

  if (activeItem === "employee-unassigned" && !isRemote) {
    return (
      <EmployeeTaskBoard
        activeSession={data.activeSession}
        allTasks={unassignedTasks}
        allowManage
        clientOptions={data.clients}
        currentUserId={sessionUser.id}
        isAdmin={isAdmin}
        onChangeTask={onUpdateTask}
        onCreateTask={onCreateTask}
        onCreateComment={onCreateTaskComment}
        onDeleteTask={onDeleteTask}
        onLoadDetail={onLoadTaskDetail}
        onOpenClient={onOpenClient}
        onOpenProject={onOpenProject}
      onOpenTask={onOpenTask}
        onReorderTask={onReorderTask}
        projectOptions={data.projects}
        showCreate={false}
        teamMembers={data.teamMembers}
        onStart={onStartSession}
        onStop={onStopSession}
        showSummary={false}
        boardTitle="Zadania oczekujące na przypisanie"
        columnsViewKey="tasks-unassigned"
        searchPlaceholder="Szukaj w oczekujących zadaniach"
        tasks={unassignedTasks}
      />
    );
  }

  if (activeItem === "employee-archive") {
    return (
      <EmployeeTaskBoard
        activeSession={data.activeSession}
        allTasks={archiveTasks}
        allowManage={!isRemote}
        clientOptions={data.clients}
        currentUserId={sessionUser.id}
        isAdmin={isAdmin}
        onChangeTask={onUpdateTask}
        onCreateTask={onCreateTask}
        onCreateComment={onCreateTaskComment}
        onDeleteTask={onDeleteTask}
        onLoadDetail={onLoadTaskDetail}
        onOpenClient={onOpenClient}
        onOpenProject={onOpenProject}
      onOpenTask={onOpenTask}
        onReorderTask={onReorderTask}
        paginated
        projectOptions={data.projects}
        readOnly={isRemote}
        columnsViewKey="tasks-archive"
        searchPlaceholder="Szukaj w archiwum zadań"
        showCreate={false}
        showDoneTasks
        showSummary={false}
        teamMembers={data.teamMembers}
        onStart={onStartSession}
        onStop={onStopSession}
        boardTitle="Archiwum zadań zrealizowanych"
        tasks={archiveTasks}
      />
    );
  }

  if (activeItem === "employee-notifications") {
    return <NotificationsPage onNavigate={onNavigate} onOpenTask={onOpenTask} ticketsTarget="employee-tickets" />;
  }

  if (activeItem === "employee-comments") {
    return <TaskCommentsCenter tasks={data.tasks} onCreateComment={onCreateTaskComment} onLoadComments={onLoadTaskComments} />;
  }

  if (activeItem === "notes") {
    return (
      <NotesPanel
        clientOptions={data.clients}
        currentUserId={sessionUser.id}
        isAdmin={isAdmin}
        projectOptions={data.projects}
        teamMembers={data.teamMembers}
      />
    );
  }

  if (activeItem === "reminders") {
    return <RemindersPage clientOptions={data.clients} projectOptions={data.projects} sessionUser={sessionUser} teamMembers={data.teamMembers} />;
  }

  if (activeItem === "employee-clients") {
    if (isRemote) return taskManager;
    return (
      <ClientDirectory
        allowCreate
        employees={data.teamMembers}
        onCreateClient={onCreateClient}
        onCreateProject={onCreateProject}
        onOpenClient={onOpenClient}
        onOpenProject={onOpenProject}
        projects={data.projects}
        sessionUser={sessionUser}
      />
    );
  }

  if (activeItem === "employee-projects") {
    if (isRemote) return taskManager;
    return (
      <EmployeeProjectsView
        onCreateProject={onCreateProject}
        onOpenClient={onOpenClient}
        onOpenProject={onOpenProject}
        projects={data.projects}
      />
    );
  }

  if (activeItem === "employee-tickets") {
    if (isRemote) return taskManager;
    return (
      <TicketCenter
        onCloseTicket={onCloseTicket}
        onReopenTicket={onReopenTicket}
        onCreateTicket={onCreateTicket}
        onReplyToTicket={onReplyToTicket}
        onSelectTicket={onSelectTicket}
        tickets={data.tickets}
      />
    );
  }

  if (activeItem === "employee-summary" || activeItem === "employee-billing") {
    return (
      <section className="section-stack">
        <article className="card compact-card task-summary-strip">
          <div className="card-header">
            <div>
              <p className="eyebrow">Rozliczenia</p>
              <h3>Czas i zarobki</h3>
            </div>
          </div>
          <div className="metric-stack compact-stack task-summary-metrics earnings-metrics">
            <div>
              <span>Łączny czas</span>
              <strong>{formatDurationLabel(myAllTasks.reduce((sum, task) => sum + task.trackedMinutes, 0))}</strong>
            </div>
            <div>
              <span>Godziny dla klienta</span>
              <strong>{myAllTasks.reduce((sum, task) => sum + task.roundedHoursForClient, 0)} h</strong>
            </div>
            <div>
              <span>Szacowany przychód</span>
              <strong>{Math.round(myAllTasks.reduce((sum, task) => sum + task.roundedHoursForClient, 0) * myHourlyRate)} zl</strong>
            </div>
          </div>
        </article>
        <TimeReportPanel onOpenTask={onOpenTask} scope="employee" />
      </section>
    );
  }

  if (activeItem === "employee-calendar") {
    return (
      <>
        <CalendarPanel
          allowManage={!isRemote}
          onOpenTask={onOpenTask}
          onReorderTask={onReorderTask}
          onRequestCreateTask={(day) => setCalendarQuickAddDate(day)}
          onStartSession={onStartSession}
          onUpdateTask={onUpdateTask}
          tasks={myAllTasks}
        />
        {calendarQuickAddDate ? (
          <TaskQuickAddModal
            currentUserId={sessionUser.id}
            initialDueDate={`${calendarQuickAddDate.getFullYear()}-${String(calendarQuickAddDate.getMonth() + 1).padStart(2, "0")}-${String(
              calendarQuickAddDate.getDate()
            ).padStart(2, "0")}`}
            onClose={() => setCalendarQuickAddDate(null)}
            onCreateClient={onCreateClient}
            onCreateProject={onCreateProject}
            onCreateTask={onCreateTask}
            projectOptions={data.projects}
            teamMembers={data.teamMembers}
          />
        ) : null}
      </>
    );
  }

  if (activeItem === "employee-kanban") {
    return (
      <KanbanBoard
        allowManage={!isRemote}
        onOpenTask={onOpenTask}
        onReorderTask={onReorderTask}
        onStartSession={onStartSession}
        onUpdateTask={onUpdateTask}
        tasks={myAllTasks}
      />
    );
  }

  if (activeItem === "employee-leave") {
    return <LeavePanel isAdmin={isAdmin} />;
  }

  if (activeItem === "employee-profile") {
    return (
      <>
        <MyProfileCard onUpdateProfile={onUpdateProfile} sessionUser={sessionUser} />
        <PasswordChangeCard onChangePassword={onChangePassword} />
      </>
    );
  }

  if (activeItem === "employee-settings") {
    const employeeRecord = data.teamMembers.find((member) => member.id === sessionUser.id);
    if (!employeeRecord) return null;
    return <MySettingsCard employeeRecord={employeeRecord} onUpdateProfile={onUpdateProfile} sessionUser={sessionUser} />;
  }

  if (activeItem === "employee-changelog") {
    return <ChangelogView />;
  }

  return taskManager;
}

function AdminSettingsCard({ data, tasks }: { data: AdminDashboardData; tasks: TaskSummary[] }) {
  const [clientStats, setClientStats] = useState<ClientStats | null>(null);

  useEffect(() => {
    fetchClientStats()
      .then(setClientStats)
      .catch(() => {});
  }, []);

  const activeEmployees = data.employees.filter((employee) => employee.status === "ACTIVE").length;
  const blockedEmployees = data.employees.filter((employee) => employee.status === "BLOCKED").length;
  const invitedEmployees = data.employees.filter((employee) => employee.status === "INVITED").length;
  const clientsWithoutManager = clientStats ? clientStats.total - clientStats.withManager : 0;
  const freeTasks = tasks.filter((task) => task.budgetType === "FREE" && !isClosedTaskStatus(task.status)).length;
  const openTickets = data.tickets.filter((ticket) => ticket.status !== "CLOSED").length;

  return (
    <section className="card settings-card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Konfiguracja</p>
          <h3>Stan systemu i ustawienia</h3>
        </div>
      </div>
      <div className="settings-grid">
        <div>
          <span>Zespół</span>
          <strong>{activeEmployees} aktywnych</strong>
          <p>
            {blockedEmployees} zablokowanych, {invitedEmployees} zaproszonych. Zarzadzaj w sekcji „Pracownicy".
          </p>
        </div>
        <div>
          <span>Klienci</span>
          <strong>{clientStats ? `${clientStats.total} w systemie` : "..."}</strong>
          <p>
            {clientsWithoutManager > 0
              ? `${clientsWithoutManager} bez przypisanego opiekuna.`
              : "Wszyscy klienci maja przypisanego opiekuna."}
          </p>
        </div>
        <div>
          <span>Zadania i tickety</span>
          <strong>{openTickets} otwartych ticketow</strong>
          <p>{freeTasks} aktywnych zadań oznaczonych jako bezplatne.</p>
        </div>
        <div>
          <span>Bezpieczenstwo</span>
          <strong>Domyślne hasło startowe</strong>
          <p>Nowe konta klientów i pracowników otrzymują hasło `Start123!` — poproś o jego zmianę przy pierwszym logowaniu.</p>
        </div>
      </div>
    </section>
  );
}

function AdminQuickActions({ onNavigate }: { onNavigate: (itemKey: string) => void }) {
  return (
    <article className="card compact-card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Skróty</p>
          <h3>Szybkie akcje administratora</h3>
        </div>
      </div>
      <div className="admin-action-grid">
        <button onClick={() => onNavigate("admin-employees")} type="button">
          Zarzadzaj pracownikami
        </button>
        <button className="secondary" onClick={() => onNavigate("admin-departments")} type="button">
          Otwórz działy
        </button>
        <button className="secondary" onClick={() => onNavigate("admin-reports")} type="button">
          Otwórz raporty
        </button>
        <button className="secondary" onClick={() => onNavigate("admin-tickets")} type="button">
          Przejrzyj tickety
        </button>
        <button className="secondary" onClick={() => onNavigate("admin-settings")} type="button">
          Ustawienia systemu
        </button>
      </div>
    </article>
  );
}

function AdminOperationsCard({
  employees,
  tickets,
  tasks
}: {
  employees: AdminDashboardData["employees"];
  tickets: AdminDashboardData["tickets"];
  tasks: TaskSummary[];
}) {
  const activeEmployees = employees.filter((employee) => employee.status === "ACTIVE").length;
  const blockedEmployees = employees.filter((employee) => employee.status !== "ACTIVE").length;
  const openTickets = tickets.filter((ticket) => ticket.status !== "CLOSED").length;
  const urgentTickets = tickets.filter((ticket) => ticket.priority === "URGENT" || ticket.priority === "HIGH").length;
  const activeTasks = tasks.filter((task) => !isClosedTaskStatus(task.status)).length;
  const trackedMinutes = tasks.reduce((sum, task) => sum + task.trackedMinutes, 0);

  return (
    <article className="card compact-card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Nadzór</p>
          <h3>Stan operacyjny</h3>
        </div>
      </div>
      <div className="metric-stack compact-stack">
        <div>
          <span>Zespół aktywny</span>
          <strong>{activeEmployees}</strong>
          <p>{blockedEmployees} kont wymaga uwagi</p>
        </div>
        <div>
          <span>Otwarte tickety</span>
          <strong>{openTickets}</strong>
          <p>{urgentTickets} zgłoszen ma wysoki priorytet</p>
        </div>
        <div>
          <span>Aktywne zadania</span>
          <strong>{activeTasks}</strong>
          <p>{formatDurationLabel(trackedMinutes)} zaraportowanego czasu</p>
        </div>
      </div>
    </article>
  );
}

function AdminAttentionCard({
  employees,
  tickets,
  tasks,
  onNavigate
}: {
  employees: AdminDashboardData["employees"];
  tickets: AdminDashboardData["tickets"];
  tasks: TaskSummary[];
  onNavigate: (itemKey: string) => void;
}) {
  const flaggedEmployees = employees.filter((employee) => employee.status !== "ACTIVE").slice(0, 3);
  const urgentTickets = tickets.filter((ticket) => ticket.status !== "CLOSED").slice(0, 4);
  const overdueTasks = tasks.filter((task) => task.status === "BLOCKED" || task.status === "REVIEW").slice(0, 4);

  return (
    <article className="card compact-card">
      <div className="card-header">
        <div>
          <p className="eyebrow">Do sprawdzenia</p>
          <h3>Kolejka administracyjna</h3>
        </div>
      </div>
      <div className="admin-queue">
        <div className="admin-queue-group">
          <span className="admin-queue-label">Pracownicy</span>
          {flaggedEmployees.length > 0 ? (
            flaggedEmployees.map((employee) => (
              <button className="admin-queue-item" key={employee.id} onClick={() => onNavigate("admin-employees")} type="button">
                <strong>{employee.fullName}</strong>
                <span>
                  {employeeRoleLabel(employee.role)} • {formatBadgeValue(employee.status, "status")}
                </span>
              </button>
            ))
          ) : (
            <p className="admin-empty">Brak kont wymagających interwencji.</p>
          )}
        </div>
        <div className="admin-queue-group">
          <span className="admin-queue-label">Tickety</span>
          {urgentTickets.length > 0 ? (
            urgentTickets.map((ticket) => (
              <button className="admin-queue-item" key={ticket.id} onClick={() => onNavigate("admin-tickets")} type="button">
                <strong>{ticket.subject}</strong>
                <span>
                  {ticket.clientName} • {formatBadgeValue(ticket.status, "status")}
                </span>
              </button>
            ))
          ) : (
            <p className="admin-empty">Brak otwartych ticketow.</p>
          )}
        </div>
        <div className="admin-queue-group">
          <span className="admin-queue-label">Zadania</span>
          {overdueTasks.length > 0 ? (
            overdueTasks.map((task) => (
              <button className="admin-queue-item" key={task.id} onClick={() => onNavigate("admin-tasks")} type="button">
                <strong>{task.title}</strong>
                <span>
                  {task.clientName} • {formatBadgeValue(task.status, "status")}
                </span>
              </button>
            ))
          ) : (
            <p className="admin-empty">Brak zadań w statusie review lub blocked.</p>
          )}
        </div>
      </div>
    </article>
  );
}

function AdminReportsPanel({
  data,
  onOpenTask
}: {
  data: AdminDashboardData;
  onOpenTask?: (taskId: string) => void;
}) {
  const state = useTimeReport("admin");
  const [clientStats, setClientStats] = useState<ClientStats | null>(null);

  useEffect(() => {
    fetchClientStats()
      .then(setClientStats)
      .catch(() => {});
  }, []);

  return (
    <>
      <StatsGrid items={data.stats} />
      <section className="panel-grid two-columns">
        <article className="card compact-card">
          <p className="eyebrow">Raport</p>
          <h3>Stan operacyjny</h3>
          <div className="metric-stack">
            <div>
              <span>Pracownicy aktywni</span>
              <strong>{data.employees.filter((employee) => employee.status === "ACTIVE").length}</strong>
            </div>
            <div>
              <span>Klienci w systemie</span>
              <strong>{clientStats ? clientStats.total : "..."}</strong>
            </div>
            <div>
              <span>Otwarte tickety</span>
              <strong>{data.tickets.filter((ticket) => ticket.status !== "CLOSED").length}</strong>
            </div>
          </div>
        </article>
        <TimeReportChartCard showBreakdown={false} state={state} />
      </section>
      {state.report ? <TimeReportBreakdownCard onOpenTask={onOpenTask} report={state.report} /> : null}
    </>
  );
}

function AdminPanel({
  activeItem,
  data,
  sessionUser,
  onNavigate,
  onCreateClient,
  onCreateDepartment,
  onCreateEmployee,
  onCreateProject,
  onCreateTicket,
  onCloseTicket,
  onReopenTicket,
  onDeleteClient,
  onDeleteEmployee,
  onDeleteProject,
  onDeleteTask,
  onOpenClient,
  onOpenEmployee,
  onOpenProject,
  onOpenTask,
  onReplyToTicket,
  onSelectTicket,
  onSendEmployeePasswordResetLink,
  onSetEmployeePassword,
  onUpdateClient,
  onUpdateEmployee,
  onUpdateProject,
  tasks
}: {
  activeItem: string;
  data: AdminDashboardData;
  sessionUser: SessionUser;
  onNavigate: (itemKey: string) => void;
  onCreateClient: (payload: CreateClientRequest) => Promise<ClientRecord>;
  onCreateDepartment: (payload: CreateDepartmentRequest) => Promise<void>;
  onCreateEmployee: (payload: CreateEmployeeRequest) => Promise<void>;
  onCreateProject: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onCreateTicket: (payload: CreateTicketRequest) => Promise<TicketDetailRecord>;
  onCloseTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onReopenTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onDeleteClient: (clientId: string) => Promise<void>;
  onDeleteEmployee: (employeeId: string) => Promise<void>;
  onDeleteProject: (projectId: string) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onOpenClient: (clientId: string) => void;
  onOpenEmployee: (employeeId: string) => void;
  onOpenProject: (projectId: string) => void;
  onOpenTask: (taskId: string) => void;
  onReplyToTicket: (ticketId: string, payload: AddTicketMessageRequest) => Promise<TicketDetailRecord>;
  onSelectTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onSendEmployeePasswordResetLink: (employeeId: string) => Promise<void>;
  onSetEmployeePassword: (employeeId: string, newPassword: string) => Promise<void>;
  onUpdateClient: (clientId: string, payload: UpdateClientRequest) => Promise<void>;
  onUpdateEmployee: (employeeId: string, payload: UpdateEmployeeRequest) => Promise<void>;
  onUpdateProject: (projectId: string, payload: UpdateProjectRequest) => Promise<void>;
  tasks: TaskSummary[];
}) {
  if (activeItem === "admin-departments") {
    return <DepartmentManager departments={data.departments} onCreate={onCreateDepartment} />;
  }

  if (activeItem === "admin-employees") {
    return (
      <EmployeeManager
        departments={data.departments}
        employees={data.employees}
        onCreate={onCreateEmployee}
        onDelete={onDeleteEmployee}
        onOpenEmployee={onOpenEmployee}
        onSendPasswordResetLink={onSendEmployeePasswordResetLink}
        onSetPassword={onSetEmployeePassword}
        onUpdate={onUpdateEmployee}
      />
    );
  }

  if (activeItem === "admin-clients") {
    return (
      <ClientDirectory
        allowCreate
        allowManage
        employees={data.employees}
        onCreateClient={onCreateClient}
        onCreateProject={onCreateProject}
        onDeleteClient={onDeleteClient}
        onOpenClient={onOpenClient}
        onOpenProject={onOpenProject}
        onUpdateClient={onUpdateClient}
        projects={data.projects}
        sessionUser={sessionUser}
      />
    );
  }

  if (activeItem === "admin-projects") {
    return (
      <ProjectManager
        onCreateProject={onCreateProject}
        onDeleteProject={onDeleteProject}
        onOpenClient={onOpenClient}
        onOpenProject={onOpenProject}
        onUpdateProject={onUpdateProject}
        projects={data.projects}
      />
    );
  }

  if (activeItem === "admin-tasks") {
    return (
      <article className="card">
        <div className="card-header">
          <div>
            <p className="eyebrow">Zadania</p>
            <h3>Przegląd wszystkich zadań</h3>
          </div>
        </div>
        <TasksTable isAdmin onDelete={onDeleteTask} onOpenClient={onOpenClient} onOpenProject={onOpenProject} tasks={tasks} />
      </article>
    );
  }

  if (activeItem === "admin-tickets") {
    return (
      <TicketCenter
        allowCreate
        onCloseTicket={onCloseTicket}
        onReopenTicket={onReopenTicket}
        onCreateTicket={onCreateTicket}
        onReplyToTicket={onReplyToTicket}
        onSelectTicket={onSelectTicket}
        tickets={data.tickets}
      />
    );
  }

  if (activeItem === "admin-reports") {
    return <AdminReportsPanel data={data} onOpenTask={onOpenTask} />;
  }

  if (activeItem === "admin-billing") {
    return <TaskBillingPage onOpenTask={onOpenTask} />;
  }

  if (activeItem === "admin-settings") {
    return <AdminSettingsCard data={data} tasks={tasks} />;
  }

  if (activeItem === "admin-integrations") {
    return <IntegrationsPage />;
  }

  return (
    <>
      <StatsGrid items={data.stats} />
      <section className="panel-grid dashboard-main">
        <section className="section-stack">
          <AdminOperationsCard employees={data.employees} tasks={tasks} tickets={data.tickets} />
          <AdminQuickActions onNavigate={onNavigate} />
          <AdminAttentionCard employees={data.employees} onNavigate={onNavigate} tasks={tasks} tickets={data.tickets} />
        </section>
        <article className="card compact-card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Pracownicy</p>
              <h3>Zespół i uprawnienia</h3>
            </div>
            <button onClick={() => onNavigate("admin-employees")} type="button">
              Otwórz panel
            </button>
          </div>
          <EmployeesTable employees={data.employees.slice(0, 6)} />
        </article>
      </section>
      <section className="panel-grid dashboard-main">
        <article className="card compact-card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Klienci</p>
              <h3>Obsługiwane firmy</h3>
            </div>
            <button className="secondary" onClick={() => onNavigate("admin-clients")} type="button">
              Pelna lista
            </button>
          </div>
          <RecentClientsCard onOpenClient={onOpenClient} />
        </article>
        <article className="card compact-card">
          <div className="card-header">
            <div>
              <p className="eyebrow">Raporty</p>
              <h3>Tickety i operacje</h3>
            </div>
            <button className="secondary" onClick={() => onNavigate("admin-reports")} type="button">
              Zobacz raport
            </button>
          </div>
          <TicketsTable onOpenClient={onOpenClient} tickets={data.tickets.slice(0, 6)} />
        </article>
      </section>
      <AdminSettingsCard data={data} tasks={tasks} />
    </>
  );
}

function LoginScreen({
  onForgotPassword,
  pending,
  error,
  onLogin,
  onResetPassword
}: {
  onForgotPassword: (email: string) => Promise<void>;
  pending: boolean;
  error: string | null;
  onLogin: (payload: { email: string; password: string; rememberMe?: boolean }) => void;
  onResetPassword: (payload: ResetPasswordRequest) => Promise<void>;
}) {
  const resetToken = new URLSearchParams(window.location.search).get("reset_token");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mode, setMode] = useState<"login" | "forgot" | "reset">(resetToken ? "reset" : "login");
  const [localMessage, setLocalMessage] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  return (
    <div className="login-shell">
      <section className="login-card">
        <div className="login-brand">
          <span className="login-brand-mark">S</span>
          <span>Scharmach</span>
        </div>
        <div>
          <h1>{mode === "reset" ? "Ustaw nowe hasło" : "Logowanie do platformy"}</h1>
        </div>

        {mode === "login" ? (
          <>
            <form
              className="login-form stacked"
              onSubmit={(event) => {
                event.preventDefault();
                onLogin({ email, password, rememberMe });
              }}
            >
              <div>
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="twoj@email.pl"
                  type="email"
                  value={email}
                />
              </div>
              <div>
                <label htmlFor="password">Hasło</label>
                <input
                  id="password"
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Hasło"
                  type="password"
                  value={password}
                />
              </div>
              <label className="field-inline-group">
                <input checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} type="checkbox" />
                <span>Zapamiętaj mnie (30 dni)</span>
              </label>
              <button disabled={pending} type="submit">
                {pending ? "Logowanie..." : "Zaloguj"}
              </button>
            </form>

            <button
              className="text-link"
              onClick={() => {
                setLocalError(null);
                setLocalMessage(null);
                setMode("forgot");
              }}
              type="button"
            >
              Nie pamietasz hasła?
            </button>
          </>
        ) : null}

        {mode === "forgot" ? (
          <>
            <form
              className="login-form stacked"
              onSubmit={(event) => {
                event.preventDefault();
                setLocalError(null);
                setLocalMessage(null);
                void onForgotPassword(recoveryEmail).then(() => {
                  setLocalMessage("Jeśli konto istnieje, wysłaliśmy link do ustawienia nowego hasła.");
                });
              }}
            >
              <div>
                <label htmlFor="recoveryEmail">Email</label>
                <input
                  id="recoveryEmail"
                  onChange={(event) => setRecoveryEmail(event.target.value)}
                  placeholder="twoj@email.pl"
                  type="email"
                  value={recoveryEmail}
                />
              </div>
              <button disabled={pending} type="submit">
                {pending ? "Wysyłanie..." : "Wyślij link resetujący"}
              </button>
            </form>

            <button
              className="text-link"
              onClick={() => {
                setLocalError(null);
                setLocalMessage(null);
                setMode("login");
              }}
              type="button"
            >
              Wróć do logowania
            </button>
          </>
        ) : null}

        {mode === "reset" ? (
          <>
            <form
              className="login-form stacked"
              onSubmit={(event) => {
                event.preventDefault();
                setLocalError(null);
                setLocalMessage(null);

                if (!resetToken) {
                  setLocalError("Brak tokenu resetu hasła.");
                  return;
                }

                if (newPassword !== confirmPassword) {
                  setLocalError("Nowe hasła nie sa identyczne.");
                  return;
                }

                void onResetPassword({ token: resetToken, newPassword }).then(() => {
                  setLocalMessage("Hasło zostało ustawione. Możesz się teraz zalogować.");
                  setNewPassword("");
                  setConfirmPassword("");
                  window.history.replaceState({}, document.title, window.location.pathname);
                  setMode("login");
                });
              }}
            >
              <div>
                <label htmlFor="newPassword">Nowe hasło</label>
                <input
                  id="newPassword"
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="Nowe hasło"
                  type="password"
                  value={newPassword}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword">Powtórz nowe hasło</label>
                <input
                  id="confirmPassword"
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Powtórz nowe hasło"
                  type="password"
                  value={confirmPassword}
                />
              </div>
              <button disabled={pending} type="submit">
                {pending ? "Zapisywanie..." : "Ustaw nowe hasło"}
              </button>
            </form>
          </>
        ) : null}

        {localError ? <p className="error-banner">{localError}</p> : null}
        {localMessage ? <p className="success-banner">{localMessage}</p> : null}
        {error ? <p className="error-banner">{error}</p> : null}
      </section>
    </div>
  );
}

function DashboardView({
  activeItem,
  activeRole,
  dashboard,
  sessionUser,
  onChangePassword,
  onCreateClient,
  onCreateDepartment,
  onCreateEmployee,
  onCreateProject,
  onCreateTicket,
  onCreateTaskComment,
  onCloseTicket,
  onReopenTicket,
  onDeleteClient,
  onDeleteEmployee,
  onDeleteProject,
  onDeleteTask,
  onDeleteRecurringSeries,
  onMenuSelect,
  openTaskId,
  onOpenTaskPopup,
  onCloseTaskPopup,
  onLoadTaskDetail,
  onLogout,
  onLoadTaskComments,
  onReorderTask,
  onUpdateRecurringSeries,
  onReplyToTicket,
  onSelectTicket,
  onCreateTask,
  onAddWorkSession,
  onDeleteWorkSession,
  onUpdateWorkSession,
  onStartSession,
  onStopSession,
  onUpdateTask,
  onUpdateClientContacts,
  onUpdateClientProfile,
  onUpdateClient,
  onUpdateEmployee,
  onUpdateProfile,
  onUpdateProject,
  onUpdateSessionUser,
  onSendEmployeePasswordResetLink,
  onSetEmployeePassword,
  error,
  successMessage
}: {
  activeItem: string;
  activeRole: RoleView;
  dashboard: DashboardResponse;
  sessionUser: SessionUser;
  onChangePassword: (payload: ChangePasswordRequest) => Promise<void>;
  onCreateClient: (payload: CreateClientRequest) => Promise<ClientRecord>;
  onCreateDepartment: (payload: CreateDepartmentRequest) => Promise<void>;
  onCreateEmployee: (payload: CreateEmployeeRequest) => Promise<void>;
  onCreateProject: (payload: CreateProjectRequest) => Promise<ProjectRecord>;
  onCreateTicket: (payload: CreateTicketRequest) => Promise<TicketDetailRecord>;
  onCreateTaskComment: (taskId: string, payload: CreateTaskCommentRequest) => Promise<TaskCommentRecord[]>;
  onCloseTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onReopenTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onDeleteClient: (clientId: string) => Promise<void>;
  onDeleteEmployee: (employeeId: string) => Promise<void>;
  onDeleteProject: (projectId: string) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onDeleteRecurringSeries: (seriesId: string) => Promise<void>;
  onUpdateRecurringSeries: (seriesId: string, payload: UpdateRecurringSeriesRequest) => Promise<void>;
  onMenuSelect: (itemKey: string) => void;
  openTaskId?: string | null;
  onOpenTaskPopup: (taskId: string) => void;
  onCloseTaskPopup: () => void;
  onLoadTaskDetail: (taskId: string) => Promise<TaskDetailRecord>;
  onLogout: () => void;
  onLoadTaskComments: (taskId: string) => Promise<TaskCommentRecord[]>;
  onReplyToTicket: (ticketId: string, payload: AddTicketMessageRequest) => Promise<TicketDetailRecord>;
  onSelectTicket: (ticketId: string) => Promise<TicketDetailRecord>;
  onCreateTask: (payload: CreateTaskRequest) => Promise<void>;
  onAddWorkSession: (taskId: string, payload: AddManualWorkSessionRequest) => Promise<TaskDetailRecord>;
  onDeleteWorkSession: (taskId: string, sessionId: string) => Promise<TaskDetailRecord>;
  onUpdateWorkSession: (taskId: string, sessionId: string, payload: UpdateWorkSessionRequest) => Promise<TaskDetailRecord>;
  onStartSession: (taskId: string) => void;
  onStopSession: (sessionId: string) => void;
  onUpdateTask: (taskId: string, payload: UpdateTaskRequest) => Promise<void>;
  onReorderTask: (taskId: string, payload: ReorderTaskRequest) => Promise<void>;
  onUpdateClientContacts: (payload: UpdateClientContactsRequest) => Promise<void>;
  onUpdateClientProfile: (payload: UpsertClientProfileRequest) => Promise<void>;
  onUpdateClient: (clientId: string, payload: UpdateClientRequest) => Promise<void>;
  onUpdateEmployee: (employeeId: string, payload: UpdateEmployeeRequest) => Promise<void>;
  onUpdateProfile: (payload: UpdateMyProfileRequest) => Promise<void>;
  onUpdateProject: (projectId: string, payload: UpdateProjectRequest) => Promise<void>;
  onUpdateSessionUser: (user: SessionUser) => void;
  onSendEmployeePasswordResetLink: (employeeId: string) => Promise<void>;
  onSetEmployeePassword: (employeeId: string, newPassword: string) => Promise<void>;
  error: string | null;
  successMessage: { token: number; text: string } | null;
}) {
  const [toastItems, setToastItems] = useState<Array<{ id: string; title: string; description: string }>>([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const notifications = buildTopNotifications(activeRole, dashboard);
  const visibleClientSources = useVisibleClientSourcesProviderValue();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeItem]);

  function selectMenuItem(itemKey: string) {
    onMenuSelect(itemKey);
    setMobileNavOpen(false);
  }

  useEffect(() => {
    const currentTopId = notifications[0]?.id;
    const previousTopId = window.sessionStorage.getItem(`top_notification_${activeRole}`);
    let timeout: number | undefined;

    if (currentTopId && previousTopId && currentTopId !== previousTopId) {
      const newest = notifications[0];
      const toastId = `${activeRole}-${newest.id}-${Date.now()}`;
      setToastItems((current) => [...current.slice(-2), { id: toastId, title: newest.title, description: newest.description }]);

      timeout = window.setTimeout(() => {
        setToastItems((current) => current.filter((item) => item.id !== toastId));
      }, 5000);
    }

    if (currentTopId) {
      window.sessionStorage.setItem(`top_notification_${activeRole}`, currentTopId);
    }

    return () => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
    };
  }, [activeRole, notifications]);

  useEffect(() => {
    if (!successMessage) return;

    const toastId = `success-${successMessage.token}`;
    setToastItems((current) => [...current.slice(-2), { id: toastId, title: "Zapisano", description: successMessage.text }]);

    const timeout = window.setTimeout(() => {
      setToastItems((current) => current.filter((item) => item.id !== toastId));
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  const employeeData: EmployeeDashboardData = {
    ...dashboard.employee,
    stats: dashboard.employee.stats ?? [],
    tasks: dashboard.employee.tasks ?? [],
    clients: dashboard.employee.clients ?? [],
    projects: dashboard.employee.projects ?? [],
    tickets: dashboard.employee.tickets ?? [],
    teamMembers: dashboard.employee.teamMembers ?? [],
    departments: dashboard.employee.departments ?? []
  };
  const adminData: AdminDashboardData = {
    ...dashboard.admin,
    stats: dashboard.admin.stats ?? [],
    employees: dashboard.admin.employees ?? [],
    clients: dashboard.admin.clients ?? [],
    projects: dashboard.admin.projects ?? [],
    tickets: dashboard.admin.tickets ?? [],
    departments: dashboard.admin.departments ?? []
  };
  const clientData: ClientDashboardData = {
    ...dashboard.client,
    stats: dashboard.client.stats ?? [],
    tasks: dashboard.client.tasks ?? [],
    projects: dashboard.client.projects ?? [],
    tickets: dashboard.client.tickets ?? []
  };
  const clientDetailId = parseClientDetailId(activeItem);
  const projectDetailId = parseProjectDetailId(activeItem);
  const employeeDetailId = parseEmployeeDetailId(activeItem);
  const sharedClients = activeRole === "CLIENT" ? [clientData.profile] : activeRole === "ADMIN" ? adminData.clients : employeeData.clients;
  const sharedProjects = activeRole === "CLIENT" ? clientData.projects : activeRole === "ADMIN" ? adminData.projects : employeeData.projects;
  const sharedTasks = activeRole === "CLIENT" ? clientData.tasks : employeeData.tasks;
  const sharedTickets = activeRole === "CLIENT" ? clientData.tickets : activeRole === "ADMIN" ? adminData.tickets : employeeData.tickets;
  const sharedTeamMembers = activeRole === "CLIENT" ? [] : employeeData.teamMembers;

  // The full client roster is no longer preloaded into the dashboard (see getDashboardFromDb) -
  // opening a specific client's detail page, or resolving the owning client of a project's detail
  // page, now fetches that one record on demand instead of finding it in an array that's normally
  // empty. Each fetch result is tagged with the id it was fetched for, so a stale fetch in flight
  // for a previous id/project never gets rendered as if it were the current one.
  const [clientDetailFetch, setClientDetailFetch] = useState<{ id: string; record: ClientRecord | null } | null>(null);
  const [projectOwningClientFetch, setProjectOwningClientFetch] = useState<{ id: string; record: ClientRecord | null } | null>(null);

  // A remote employee's "Rozliczone" (billed) tab shows BILLED tasks, which - like the Archive
  // view - are no longer part of the live, recency-bounded task set (see fetchTasks in
  // getDashboardFromDb). Fetched on demand, scoped to that one employee, only while this exact
  // tab is open.
  const remoteEmployeeKeyForArchive = parseRemoteEmployeeKey(activeItem);
  const [remoteEmployeeBilledTasks, setRemoteEmployeeBilledTasks] = useState<{ employeeId: string; tasks: TaskSummary[] } | null>(null);

  useEffect(() => {
    if (!remoteEmployeeKeyForArchive || remoteEmployeeKeyForArchive.tab !== "billed") return;
    const employeeId = remoteEmployeeKeyForArchive.employeeId;
    let cancelled = false;
    fetchTaskArchive(employeeId)
      .then((items) => {
        if (!cancelled) setRemoteEmployeeBilledTasks({ employeeId, tasks: items.filter((task) => task.status === "BILLED") });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [remoteEmployeeKeyForArchive?.employeeId, remoteEmployeeKeyForArchive?.tab]);
  const projectForDetail = projectDetailId ? sharedProjects.find((item) => item.id === projectDetailId) : undefined;
  const projectOwnerId = projectForDetail?.clientId;

  useEffect(() => {
    if (!clientDetailId) return;
    let cancelled = false;
    fetchClientById(clientDetailId)
      .then((record) => {
        if (!cancelled) setClientDetailFetch({ id: clientDetailId, record });
      })
      .catch(() => {
        if (!cancelled) setClientDetailFetch({ id: clientDetailId, record: null });
      });
    return () => {
      cancelled = true;
    };
  }, [clientDetailId]);

  useEffect(() => {
    if (!projectOwnerId) return;
    let cancelled = false;
    fetchClientById(projectOwnerId)
      .then((record) => {
        if (!cancelled) setProjectOwningClientFetch({ id: projectOwnerId, record });
      })
      .catch(() => {
        if (!cancelled) setProjectOwningClientFetch({ id: projectOwnerId, record: null });
      });
    return () => {
      cancelled = true;
    };
  }, [projectOwnerId]);

  function openClient(clientId: string) {
    onMenuSelect(buildClientDetailKey(clientId));
  }

  function openProject(projectId: string) {
    onMenuSelect(buildProjectDetailKey(projectId));
  }

  function openTask(taskId: string) {
    onOpenTaskPopup(taskId);
  }

  function openEmployee(employeeId: string) {
    onMenuSelect(buildEmployeeDetailKey(employeeId));
  }

  const openTaskRecord = openTaskId ? sharedTasks.find((item) => item.id === openTaskId) : undefined;
  const taskPopup = openTaskRecord ? (
    <Modal className="task-detail-modal" onClose={onCloseTaskPopup} title="Karta zadania" wide>
      <TaskDetailPage
        activeSession={employeeData.activeSession}
        allowManage={activeRole === "EMPLOYEE" || activeRole === "ADMIN"}
        clientOptions={sharedClients}
        currentUserId={sessionUser.id}
        hideExactTime={activeRole === "CLIENT"}
        isAdmin={activeRole === "ADMIN"}
        isModal
        onAddWorkSession={onAddWorkSession}
        onDeleteWorkSession={onDeleteWorkSession}
        onUpdateWorkSession={onUpdateWorkSession}
        onBack={onCloseTaskPopup}
        onChangeTask={onUpdateTask}
        onCreateComment={onCreateTaskComment}
        onDeleteTask={onDeleteTask}
        onLoadDetail={onLoadTaskDetail}
        onOpenClient={openClient}
        onOpenProject={openProject}
        onOpenTask={openTask}
        onStart={onStartSession}
        onStop={onStopSession}
        projectOptions={sharedProjects}
        readOnly={activeRole === "CLIENT" || activeRole === "REMOTE_EMPLOYEE"}
        task={openTaskRecord}
        teamMembers={sharedTeamMembers}
        canTrackTime={activeRole === "REMOTE_EMPLOYEE" ? true : undefined}
        canComment={activeRole === "REMOTE_EMPLOYEE" ? true : undefined}
      />
    </Modal>
  ) : null;

  if (clientDetailId) {
    const clientDetailReady = clientDetailFetch?.id === clientDetailId;
    const client = clientDetailReady ? clientDetailFetch.record : undefined;

    if (!clientDetailReady || client) {
      const clientProjects = client ? sharedProjects.filter((project) => project.clientId === client.id) : [];
      const clientTasks = client ? sharedTasks.filter((task) => task.clientId === client.id) : [];
      const clientTickets = client ? sharedTickets.filter((ticket) => ticket.clientId === client.id) : [];
      const canEditClient = activeRole !== "REMOTE_EMPLOYEE";
      const canManageTasks = activeRole === "EMPLOYEE" || activeRole === "ADMIN";

      return (
        <>
        <div className={`page-shell ${roleTone(activeRole)}`}>
          <aside className={mobileNavOpen ? "sidebar is-open" : "sidebar"}>
            <div>
              <p className="eyebrow">Scharmach</p>
              <h1>Workspace</h1>
              <SidebarMenu activeItem={activeItem} onLogout={onLogout} onSelect={selectMenuItem} role={activeRole} remoteEmployees={activeRole === "ADMIN" ? adminData.employees.filter((employee) => employee.role === "REMOTE_EMPLOYEE") : undefined} />
            </div>
          </aside>
          {mobileNavOpen ? <div className="sidebar-backdrop" onClick={() => setMobileNavOpen(false)} /> : null}
          <main className="content">
            <ToastStack items={toastItems} onDismiss={(id) => setToastItems((current) => current.filter((item) => item.id !== id))} />
            <TopBar
              activeItem={activeItem}
              activeRole={activeRole}
              dashboard={dashboard}
              onLogout={onLogout}
              onNavigate={onMenuSelect}
              onOpenTask={openTask}
              onStopSession={onStopSession}
              onToggleMenu={() => setMobileNavOpen((current) => !current)}
              onUpdateSessionUser={onUpdateSessionUser}
              sessionUser={sessionUser}
            />
            <section className="hero">
              <div>
                <h2>Karta firmy</h2>
              </div>
            </section>
            {error ? <p className="error-banner">{error}</p> : null}
            {client ? (
              <ClientDetailPage
                activeSession={employeeData.activeSession}
                allowEdit={canEditClient}
                allowManage={activeRole === "ADMIN"}
                allowManageTasks={canManageTasks}
                client={client}
                onBack={() => onMenuSelect(activeRole === "ADMIN" ? "admin-clients" : activeRole === "CLIENT" ? "client-company" : "employee-clients")}
                onCreateProject={activeRole === "EMPLOYEE" || activeRole === "ADMIN" ? onCreateProject : undefined}
                onCreateTask={onCreateTask}
                onCreateTaskComment={onCreateTaskComment}
                onDelete={onDeleteClient}
                onLoadTaskDetail={onLoadTaskDetail}
                onOpenProject={openProject}
                onOpenTask={openTask}
                onSaveClient={onUpdateClient}
                onStartSession={onStartSession}
                onStopSession={onStopSession}
                onUpdateTask={onUpdateTask}
                projects={clientProjects}
                tasks={clientTasks}
                tickets={clientTickets}
                teamMembers={sharedTeamMembers}
              />
            ) : (
              <p className="placeholder-copy">Wczytywanie karty klienta...</p>
            )}
          </main>
        </div>
        {taskPopup}
        </>
      );
    }
  }

  if (employeeDetailId && activeRole === "ADMIN") {
    const employee = adminData.employees.find((item) => item.id === employeeDetailId);

    if (employee) {
      return (
        <>
        <div className={`page-shell ${roleTone(activeRole)}`}>
          <aside className={mobileNavOpen ? "sidebar is-open" : "sidebar"}>
            <div>
              <p className="eyebrow">Scharmach</p>
              <h1>Workspace</h1>
              <SidebarMenu activeItem={activeItem} onLogout={onLogout} onSelect={selectMenuItem} role={activeRole} remoteEmployees={activeRole === "ADMIN" ? adminData.employees.filter((employee) => employee.role === "REMOTE_EMPLOYEE") : undefined} />
            </div>
          </aside>
          {mobileNavOpen ? <div className="sidebar-backdrop" onClick={() => setMobileNavOpen(false)} /> : null}
          <main className="content">
            <ToastStack items={toastItems} onDismiss={(id) => setToastItems((current) => current.filter((item) => item.id !== id))} />
            <TopBar
              activeItem={activeItem}
              activeRole={activeRole}
              dashboard={dashboard}
              onLogout={onLogout}
              onNavigate={onMenuSelect}
              onOpenTask={openTask}
              onStopSession={onStopSession}
              onToggleMenu={() => setMobileNavOpen((current) => !current)}
              onUpdateSessionUser={onUpdateSessionUser}
              sessionUser={sessionUser}
            />
            <section className="hero">
              <div>
                <h2>Karta pracownika</h2>
              </div>
            </section>
            {error ? <p className="error-banner">{error}</p> : null}
            <EmployeeDetailPage
              departments={adminData.departments}
              employee={employee}
              projects={adminData.projects}
              onBack={() => onMenuSelect("admin-employees")}
              onDelete={onDeleteEmployee}
              onUpdate={onUpdateEmployee}
            />
          </main>
        </div>
        {taskPopup}
        </>
      );
    }
  }

  const remoteEmployeeKey = parseRemoteEmployeeKey(activeItem);
  if (remoteEmployeeKey && activeRole === "ADMIN") {
    const remoteEmployee = adminData.employees.find((item) => item.id === remoteEmployeeKey.employeeId);

    if (remoteEmployee) {
      const employeeTasks = sharedTasks.filter((task) => task.assignees.some((assignee) => assignee.id === remoteEmployee.id));
      const tabTasks =
        remoteEmployeeKey.tab === "tasks"
          ? employeeTasks.filter((task) => !isClosedTaskStatus(task.status) && task.status !== "TO_BE_BILLED")
          : remoteEmployeeKey.tab === "billing"
            ? employeeTasks.filter((task) => task.status === "TO_BE_BILLED")
            : remoteEmployeeBilledTasks?.employeeId === remoteEmployeeKey.employeeId
              ? remoteEmployeeBilledTasks.tasks
              : [];
      const tabLabel = remoteEmployeeKey.tab === "tasks" ? "Zadania" : remoteEmployeeKey.tab === "billing" ? "Do rozliczenia" : "Rozliczone";

      return (
        <>
        <div className={`page-shell ${roleTone(activeRole)}`}>
          <aside className={mobileNavOpen ? "sidebar is-open" : "sidebar"}>
            <div>
              <p className="eyebrow">Scharmach</p>
              <h1>Workspace</h1>
              <SidebarMenu activeItem={activeItem} onLogout={onLogout} onSelect={selectMenuItem} role={activeRole} remoteEmployees={adminData.employees.filter((employee) => employee.role === "REMOTE_EMPLOYEE")} />
            </div>
          </aside>
          {mobileNavOpen ? <div className="sidebar-backdrop" onClick={() => setMobileNavOpen(false)} /> : null}
          <main className="content">
            <ToastStack items={toastItems} onDismiss={(id) => setToastItems((current) => current.filter((item) => item.id !== id))} />
            <TopBar
              activeItem={activeItem}
              activeRole={activeRole}
              dashboard={dashboard}
              onLogout={onLogout}
              onNavigate={onMenuSelect}
              onOpenTask={openTask}
              onStopSession={onStopSession}
              onToggleMenu={() => setMobileNavOpen((current) => !current)}
              onUpdateSessionUser={onUpdateSessionUser}
              sessionUser={sessionUser}
            />
            <section className="hero">
              <div>
                <h2>{tabLabel} - {remoteEmployee.fullName}</h2>
              </div>
            </section>
            {error ? <p className="error-banner">{error}</p> : null}
            <EmployeeTaskBoard
              key={`${remoteEmployeeKey.employeeId}:${remoteEmployeeKey.tab}`}
              activeSession={employeeData.activeSession}
              allTasks={tabTasks}
              allowManage
              clientOptions={sharedClients}
              currentUserId={sessionUser.id}
              isAdmin
              onChangeTask={onUpdateTask}
              onCreateTask={onCreateTask}
              onCreateComment={onCreateTaskComment}
              onDeleteTask={onDeleteTask}
              onLoadDetail={onLoadTaskDetail}
              onOpenClient={openClient}
              onOpenProject={openProject}
              onOpenTask={openTask}
              onStart={onStartSession}
              onStop={onStopSession}
              projectOptions={sharedProjects}
              quickAddAssigneeIds={[remoteEmployee.id]}
              showCreate={remoteEmployeeKey.tab === "tasks"}
              showDoneTasks={remoteEmployeeKey.tab === "billed"}
              showSummary={false}
              boardTitle={`${tabLabel} - ${remoteEmployee.fullName}`}
              columnsViewKey="tasks-remote-employee"
              tasks={tabTasks}
              teamMembers={sharedTeamMembers}
            />
          </main>
        </div>
        {taskPopup}
        </>
      );
    }
  }

  if (projectDetailId) {
    const project = sharedProjects.find((item) => item.id === projectDetailId);

    if (project) {
      const ownerReady = projectOwningClientFetch?.id === project.clientId;
      const client = ownerReady ? projectOwningClientFetch.record : undefined;
      const projectTasks = sharedTasks.filter((task) => task.projectId === project.id);
      const canEditProject = activeRole === "EMPLOYEE" || activeRole === "ADMIN";
      const canManageTasks = activeRole === "EMPLOYEE" || activeRole === "ADMIN";

      if (!ownerReady || client) {
        return (
          <>
          <div className={`page-shell ${roleTone(activeRole)}`}>
            <aside className={mobileNavOpen ? "sidebar is-open" : "sidebar"}>
              <div>
                <p className="eyebrow">Scharmach</p>
                <h1>Workspace</h1>
                <SidebarMenu activeItem={activeItem} onLogout={onLogout} onSelect={selectMenuItem} role={activeRole} remoteEmployees={activeRole === "ADMIN" ? adminData.employees.filter((employee) => employee.role === "REMOTE_EMPLOYEE") : undefined} />
              </div>
            </aside>
            {mobileNavOpen ? <div className="sidebar-backdrop" onClick={() => setMobileNavOpen(false)} /> : null}
            <main className="content">
              <ToastStack items={toastItems} onDismiss={(id) => setToastItems((current) => current.filter((item) => item.id !== id))} />
              <TopBar
                activeItem={activeItem}
                activeRole={activeRole}
                dashboard={dashboard}
                onLogout={onLogout}
                onNavigate={onMenuSelect}
                onOpenTask={openTask}
                onStopSession={onStopSession}
                onToggleMenu={() => setMobileNavOpen((current) => !current)}
                onUpdateSessionUser={onUpdateSessionUser}
                sessionUser={sessionUser}
              />
              <section className="hero">
                <div>
                  <h2>Karta projektu</h2>
                </div>
              </section>
              {error ? <p className="error-banner">{error}</p> : null}
              {client ? (
                <ProjectDetailPage
                  activeSession={employeeData.activeSession}
                  allowEdit={canEditProject}
                  allowManage={activeRole === "ADMIN"}
                  allowManageTasks={canManageTasks}
                  client={client}
                  onBack={() => onMenuSelect(activeRole === "ADMIN" ? "admin-projects" : activeRole === "CLIENT" ? "client-tasks" : "employee-projects")}
                  onCreateTask={onCreateTask}
                  onCreateTaskComment={onCreateTaskComment}
                  onDelete={onDeleteProject}
                  onLoadTaskDetail={onLoadTaskDetail}
                  onOpenClient={openClient}
                  onOpenTask={openTask}
                  onSaveProject={onUpdateProject}
                  onStartSession={onStartSession}
                  onStopSession={onStopSession}
                  onUpdateTask={onUpdateTask}
                  project={project}
                  tasks={projectTasks}
                  teamMembers={sharedTeamMembers}
                />
              ) : (
                <p className="placeholder-copy">Wczytywanie karty projektu...</p>
              )}
            </main>
          </div>
          {taskPopup}
          </>
        );
      }
    }
  }

  return (
    <VisibleClientSourcesContext.Provider value={visibleClientSources}>
    <div className={`page-shell ${roleTone(activeRole)}`}>
      <aside className={mobileNavOpen ? "sidebar is-open" : "sidebar"}>
        <div>
          <p className="eyebrow">Scharmach</p>
          <h1>Workspace</h1>
          <SidebarMenu activeItem={activeItem} onLogout={onLogout} onSelect={selectMenuItem} role={activeRole} remoteEmployees={activeRole === "ADMIN" ? adminData.employees.filter((employee) => employee.role === "REMOTE_EMPLOYEE") : undefined} />
        </div>
      </aside>

      {mobileNavOpen ? <div className="sidebar-backdrop" onClick={() => setMobileNavOpen(false)} /> : null}

      <main className="content">
        <ToastStack items={toastItems} onDismiss={(id) => setToastItems((current) => current.filter((item) => item.id !== id))} />
        <TopBar
          activeItem={activeItem}
          activeRole={activeRole}
          dashboard={dashboard}
          onLogout={onLogout}
          onNavigate={onMenuSelect}
          onOpenTask={openTask}
          onStopSession={onStopSession}
          onToggleMenu={() => setMobileNavOpen((current) => !current)}
          onUpdateSessionUser={onUpdateSessionUser}
          sessionUser={sessionUser}
        />
        <section className="hero">
          <div>
            <h2>{getMenuLabel(activeRole, activeItem)}</h2>
          </div>
        </section>

        {error ? <p className="error-banner">{error}</p> : null}

        {activeRole === "CLIENT" ? (
          <ClientPanel
            activeItem={activeItem}
            data={clientData}
            onLoadTaskDetail={onLoadTaskDetail}
            onNavigate={onMenuSelect}
            onOpenClient={openClient}
            onOpenProject={openProject}
            onOpenTask={openTask}
            onCreateTaskComment={onCreateTaskComment}
            onCreateTask={onCreateTask}
            onCreateTicket={onCreateTicket}
            onLoadTaskComments={onLoadTaskComments}
            onCloseTicket={onCloseTicket}
            onReopenTicket={onReopenTicket}
            onDeleteTask={onDeleteTask}
            onReplyToTicket={onReplyToTicket}
            onSelectTicket={onSelectTicket}
            onUpdateContacts={onUpdateClientContacts}
            onUpdateProfile={onUpdateClientProfile}
            sessionUser={sessionUser}
          />
        ) : null}
        {activeRole === "EMPLOYEE" || activeRole === "REMOTE_EMPLOYEE" || (activeRole === "ADMIN" && !activeItem.startsWith("admin-")) ? (
          <EmployeePanel
            activeItem={activeItem}
            onChangePassword={onChangePassword}
            onCreateTaskComment={onCreateTaskComment}
            onCreateClient={onCreateClient}
            isAdmin={activeRole === "ADMIN"}
            onNavigate={onMenuSelect}
            onOpenClient={openClient}
            onOpenProject={openProject}
            onOpenTask={openTask}
            onCreateProject={onCreateProject}
            data={employeeData}
            isRemote={activeRole === "REMOTE_EMPLOYEE"}
            onDeleteTask={onDeleteTask}
            onDeleteRecurringSeries={onDeleteRecurringSeries}
            onLoadTaskDetail={onLoadTaskDetail}
            onLoadTaskComments={onLoadTaskComments}
            onCreateTicket={onCreateTicket}
            onCreateTask={onCreateTask}
            onCloseTicket={onCloseTicket}
            onReopenTicket={onReopenTicket}
            onReorderTask={onReorderTask}
            onUpdateRecurringSeries={onUpdateRecurringSeries}
            onReplyToTicket={onReplyToTicket}
            onSelectTicket={onSelectTicket}
            sessionUser={sessionUser}
            onStartSession={onStartSession}
            onStopSession={onStopSession}
            onUpdateProfile={onUpdateProfile}
            onUpdateTask={onUpdateTask}
          />
        ) : null}
        {activeRole === "ADMIN" && activeItem.startsWith("admin-") ? (
          <AdminPanel
            activeItem={activeItem}
            data={adminData}
            sessionUser={sessionUser}
            onCreateClient={onCreateClient}
            onCreateDepartment={onCreateDepartment}
            onCreateTicket={onCreateTicket}
            onDeleteClient={onDeleteClient}
            onDeleteEmployee={onDeleteEmployee}
            onDeleteProject={onDeleteProject}
            onDeleteTask={onDeleteTask}
            onNavigate={onMenuSelect}
            onCreateEmployee={onCreateEmployee}
            onCreateProject={onCreateProject}
            onOpenClient={openClient}
            onOpenEmployee={openEmployee}
            onOpenProject={openProject}
            onOpenTask={openTask}
            onCloseTicket={onCloseTicket}
            onReopenTicket={onReopenTicket}
            onReplyToTicket={onReplyToTicket}
            onSelectTicket={onSelectTicket}
            onSendEmployeePasswordResetLink={onSendEmployeePasswordResetLink}
            onSetEmployeePassword={onSetEmployeePassword}
            onUpdateClient={onUpdateClient}
            onUpdateEmployee={onUpdateEmployee}
            onUpdateProject={onUpdateProject}
            tasks={employeeData.tasks}
          />
        ) : null}
      </main>
    </div>
    {taskPopup}
    </VisibleClientSourcesContext.Provider>
  );
}

export function App() {
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [activeRole, setActiveRole] = useState<RoleView>("CLIENT");
  const [activeMenuItem, setActiveMenuItem] = useState(defaultMenuItemByRole.CLIENT);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<{ token: number; text: string } | null>(null);

  function announceSuccess(text: string) {
    setSuccessMessage({ token: Date.now() + Math.random(), text });
  }

  useEffect(() => {
    const themeKey = sessionUser?.themeKey;
    if (themeKey && themeKey !== "gold-lion") {
      document.documentElement.setAttribute("data-theme", themeKey);
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
  }, [sessionUser?.themeKey]);

  async function refreshDashboard() {
    const dashboardResponse = await fetchDashboard();
    setDashboard(dashboardResponse);
  }

  // Re-fetching and recomputing the whole dashboard (every task/ticket/employee org-wide, plus
  // billing) after every single task/timer action is what makes those actions feel slow - the
  // UI would sit frozen for that entire round-trip just to reflect one task. Instead, patch the
  // one task (and active-session pointer) the server just returned straight into local state so
  // the UI updates instantly, then let the full refresh happen in the background to reconcile
  // derived stats (counts, tracked hours, etc.) a moment later without blocking the user.
  function patchEmployeeTask(task: TaskSummary, activeSession?: ActiveWorkSession | null) {
    setDashboard((current) => {
      if (!current) return current;
      const existingIndex = current.employee.tasks.findIndex((item) => item.id === task.id);
      const tasks =
        existingIndex === -1
          ? [task, ...current.employee.tasks]
          : current.employee.tasks.map((item) => (item.id === task.id ? task : item));
      return {
        ...current,
        employee: {
          ...current.employee,
          tasks,
          activeSession: activeSession !== undefined ? activeSession : current.employee.activeSession
        }
      };
    });
  }

  function refreshDashboardInBackground() {
    void refreshDashboard().catch(() => {
      // Keep the optimistic UI state if the background reconciliation temporarily fails.
    });
  }

  function handleMenuSelect(itemKey: string) {
    setActiveMenuItem(itemKey);
    setOpenTaskId(null);
    window.localStorage.setItem(ACTIVE_MENU_KEY, JSON.stringify(buildMenuStateSnapshot(activeRole, itemKey)));
    window.history.pushState({ menuItem: itemKey }, "", `#${encodeURIComponent(itemKey)}`);
  }

  function handleOpenTaskPopup(taskId: string) {
    setOpenTaskId(taskId);
    window.history.pushState({ menuItem: activeMenuItem, taskPopupId: taskId }, "", `#${encodeURIComponent(buildTaskDetailKey(taskId))}`);
  }

  function handleCloseTaskPopup() {
    setOpenTaskId(null);
    window.history.back();
  }

  useEffect(() => {
    function handlePopState(event: PopStateEvent) {
      const state = event.state as { menuItem?: string; taskPopupId?: string } | null;
      const hash = window.location.hash;
      const hashItem = hash ? decodeURIComponent(hash.slice(1)) : undefined;
      const hashTaskId = hashItem ? parseTaskDetailId(hashItem) : undefined;
      const nextTaskId = state?.taskPopupId ?? hashTaskId;

      if (nextTaskId) {
        setOpenTaskId(nextTaskId);
        return;
      }

      setOpenTaskId(null);
      const nextItem = state?.menuItem ?? hashItem;
      if (!nextItem) return;

      setActiveMenuItem(nextItem);
      window.localStorage.setItem(ACTIVE_MENU_KEY, JSON.stringify(buildMenuStateSnapshot(activeRole, nextItem)));
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [activeRole]);

  useEffect(() => {
    async function restoreSession() {
      const token = window.localStorage.getItem(AUTH_TOKEN_KEY);
      const storedUser = window.localStorage.getItem(AUTH_USER_KEY);
      const storedMenus = window.localStorage.getItem(ACTIVE_MENU_KEY);

      if (!token || !storedUser) {
        setBootstrapping(false);
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser) as SessionUser;
        const parsedMenus = storedMenus ? (JSON.parse(storedMenus) as Partial<Record<RoleView, string>>) : {};
        const restoredRole = parsedUser.role;
        const hash = window.location.hash;
        const hashItem = hash ? decodeURIComponent(hash.slice(1)) : undefined;
        const hashTaskId = hashItem ? parseTaskDetailId(hashItem) : undefined;
        const initialItem = (!hashTaskId && hashItem) || parsedMenus[restoredRole] || defaultMenuItemByRole[restoredRole];
        setSessionUser(parsedUser);
        setActiveRole(restoredRole);
        setActiveMenuItem(initialItem);
        if (hashTaskId) {
          setOpenTaskId(hashTaskId);
          window.history.replaceState(
            { menuItem: initialItem, taskPopupId: hashTaskId },
            "",
            `#${encodeURIComponent(buildTaskDetailKey(hashTaskId))}`
          );
        } else {
          window.history.replaceState({ menuItem: initialItem }, "", `#${encodeURIComponent(initialItem)}`);
        }
        try {
          await refreshDashboard();
        } catch (firstAttemptError) {
          // A single failed request right after boot (slow/transient network or a busy
          // server, e.g. an hourly integration sync) must not immediately look like an
          // expired session - retry once before deciding anything is actually wrong.
          if (firstAttemptError instanceof HttpError && firstAttemptError.status === 401) {
            throw firstAttemptError;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 1500));
          await refreshDashboard();
        }
      } catch (restoreError) {
        const isAuthFailure = restoreError instanceof HttpError && restoreError.status === 401;

        if (isAuthFailure) {
          window.localStorage.removeItem(AUTH_TOKEN_KEY);
          window.localStorage.removeItem(AUTH_USER_KEY);
          window.localStorage.removeItem(ACTIVE_ROLE_KEY);
          window.localStorage.removeItem(ACTIVE_MENU_KEY);
          setSessionUser(null);
          setDashboard(null);
          setError(restoreError.message);
        } else {
          // Keep the stored session/token intact - this was a connectivity/server problem,
          // not an invalid session, so the next reload should retry rather than force a login.
          setError(
            restoreError instanceof Error
              ? `Nie udało się połączyć z serwerem: ${restoreError.message}`
              : "Nie udało się połączyć z serwerem. Odśwież stronę, aby spróbować ponownie."
          );
        }
      } finally {
        setBootstrapping(false);
      }
    }

    void restoreSession();
  }, []);

  useEffect(() => {
    if (!sessionUser) return;

    const interval = window.setInterval(() => {
      void refreshDashboard().catch(() => {
        // Keep the current UI state if a background refresh temporarily fails.
      });
    }, 30000);

    return () => window.clearInterval(interval);
  }, [sessionUser]);

  async function handleLogin(payload: { email: string; password: string; rememberMe?: boolean }) {
    setPending(true);
    setError(null);

    try {
      const auth = await login(payload);
      window.localStorage.setItem(AUTH_TOKEN_KEY, auth.token);
      window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(auth.user));
      window.localStorage.setItem(ACTIVE_ROLE_KEY, auth.user.role);
      window.localStorage.setItem(
        ACTIVE_MENU_KEY,
        JSON.stringify({
          [auth.user.role]: defaultMenuItemByRole[auth.user.role]
        })
      );
      setSessionUser(auth.user);
      await refreshDashboard();
      setActiveRole(auth.user.role);
      setActiveMenuItem(defaultMenuItemByRole[auth.user.role]);
      window.history.replaceState(
        { menuItem: defaultMenuItemByRole[auth.user.role] },
        "",
        `#${encodeURIComponent(defaultMenuItemByRole[auth.user.role])}`
      );
    } catch (loginError) {
      window.localStorage.removeItem("auth_token");
      window.localStorage.removeItem(AUTH_USER_KEY);
      window.localStorage.removeItem(ACTIVE_ROLE_KEY);
      window.localStorage.removeItem(ACTIVE_MENU_KEY);
      setSessionUser(null);
      setDashboard(null);
      setError(loginError instanceof Error ? loginError.message : "Logowanie nie powiodło się.");
    } finally {
      setPending(false);
    }
  }

  async function handleForgotPassword(email: string) {
    setPending(true);
    setError(null);
    try {
      await requestPasswordReset({ email });
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się wysłać linku resetującego.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleResetPassword(payload: ResetPasswordRequest) {
    setPending(true);
    setError(null);
    try {
      await resetPassword(payload);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się ustawić nowego hasła.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  function handleLogout() {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
    window.localStorage.removeItem(AUTH_USER_KEY);
    window.localStorage.removeItem(ACTIVE_ROLE_KEY);
    window.localStorage.removeItem(ACTIVE_MENU_KEY);
    setSessionUser(null);
    setDashboard(null);
    setActiveRole("CLIENT");
    setActiveMenuItem(defaultMenuItemByRole.CLIENT);
    setError(null);
    window.history.replaceState({}, "", window.location.pathname);
  }

  async function handleCreateTask(payload: CreateTaskRequest) {
    setPending(true);
    setError(null);
    try {
      if (payload.recurrence) {
        if (!payload.clientId || !payload.projectId) {
          throw new Error("Zadania cykliczne wymagają wybrania klienta i projektu.");
        }
        await createRecurringTaskSeries({
          clientId: payload.clientId,
          projectId: payload.projectId,
          title: payload.title,
          description: payload.description,
          priority: payload.priority,
          budgetType: payload.budgetType,
          budgetAmount: payload.budgetAmount,
          assigneeIds: payload.assigneeIds,
          frequency: payload.recurrence.frequency,
          intervalDays: payload.recurrence.intervalDays,
          daysOfWeek: payload.recurrence.daysOfWeek,
          startDate: payload.dueDate ?? todayInputValue(),
          endDate: payload.recurrence.endDate
        });
        await refreshDashboard();
      } else {
        const { task } = await createTask(payload);
        patchEmployeeTask(task);
        refreshDashboardInBackground();
      }
      announceSuccess("Zlecenie zostało dodane.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się dodać zadania.");
    } finally {
      setPending(false);
    }
  }

  async function handleCreateClient(payload: CreateClientRequest) {
    setPending(true);
    setError(null);
    try {
      const { client } = await createClient(payload);
      await refreshDashboard();
      announceSuccess("Klient został dodany.");
      return client;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się dodać klienta.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleCreateProject(payload: CreateProjectRequest) {
    setPending(true);
    setError(null);
    try {
      const { project } = await createProject(payload);
      await refreshDashboard();
      announceSuccess("Projekt został utworzony.");
      return project;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się dodać projektu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateClientProfile(payload: UpsertClientProfileRequest) {
    setPending(true);
    setError(null);
    try {
      await updateClientProfile(payload);
      await refreshDashboard();
      announceSuccess("Dane firmy zostały zapisane.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zapisać danych firmy.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateClient(clientId: string, payload: UpdateClientRequest) {
    setPending(true);
    setError(null);
    try {
      await updateClient(clientId, payload);
      await refreshDashboard();
      announceSuccess("Dane klienta zostały zaktualizowane.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zapisać danych klienta.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleDeleteClient(clientId: string) {
    setPending(true);
    setError(null);
    try {
      await deleteClient(clientId);
      await refreshDashboard();
      announceSuccess("Klient został usunięty.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się usunąć klienta.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateClientContacts(payload: UpdateClientContactsRequest) {
    setPending(true);
    setError(null);
    try {
      await updateClientContacts(payload);
      await refreshDashboard();
      announceSuccess("Kontakty zostały zaktualizowane.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zapisać kontaktów.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleSelectTicket(ticketId: string) {
    const response = await fetchTicketDetail(ticketId);
    return response.ticket;
  }

  async function handleCreateTicket(payload: CreateTicketRequest) {
    setPending(true);
    setError(null);
    try {
      const response = await createTicket(payload);
      await refreshDashboard();
      announceSuccess("Zgłoszenie zostało utworzone.");
      return response.ticket;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się dodać ticketu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleLoadTaskComments(taskId: string) {
    const response = await fetchTaskComments(taskId);
    return response.items;
  }

  async function handleLoadTaskDetail(taskId: string) {
    const response = await fetchTaskDetail(taskId);
    return response.task;
  }

  async function handleCreateTaskComment(taskId: string, payload: CreateTaskCommentRequest) {
    setPending(true);
    setError(null);
    try {
      const response = await createTaskComment(taskId, payload);
      await refreshDashboard();
      announceSuccess("Komentarz został dodany.");
      return response.items;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się dodać komentarza.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleCreateEmployee(payload: CreateEmployeeRequest) {
    setPending(true);
    setError(null);
    try {
      await createEmployee(payload);
      await refreshDashboard();
      announceSuccess("Pracownik został dodany.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się dodać pracownika.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleCreateDepartment(payload: CreateDepartmentRequest) {
    setPending(true);
    setError(null);
    try {
      await createDepartment(payload);
      await refreshDashboard();
      announceSuccess("Dział został utworzony.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się dodać działu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateEmployee(employeeId: string, payload: UpdateEmployeeRequest) {
    setPending(true);
    setError(null);
    try {
      await updateEmployee(employeeId, payload);
      await refreshDashboard();
      announceSuccess("Dane pracownika zostały zapisane.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zapisać pracownika.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleDeleteEmployee(employeeId: string) {
    setPending(true);
    setError(null);
    try {
      await deleteEmployee(employeeId);
      await refreshDashboard();
      announceSuccess("Pracownik został usunięty.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się usunąć pracownika.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleSendEmployeePasswordResetLink(employeeId: string) {
    setPending(true);
    setError(null);
    try {
      await sendEmployeePasswordResetLink(employeeId);
      await refreshDashboard();
      announceSuccess("Link do ustawienia hasła został wysłany.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się wysłać linku do resetu hasła.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleSetEmployeePassword(employeeId: string, newPassword: string) {
    setPending(true);
    setError(null);
    try {
      await setEmployeePassword(employeeId, newPassword);
      await refreshDashboard();
      announceSuccess("Hasło zostało ustawione.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się ustawić hasła.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateProject(projectId: string, payload: UpdateProjectRequest) {
    setPending(true);
    setError(null);
    try {
      await updateProject(projectId, payload);
      await refreshDashboard();
      announceSuccess("Projekt został zaktualizowany.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zapisać projektu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleDeleteProject(projectId: string) {
    setPending(true);
    setError(null);
    try {
      await deleteProject(projectId);
      await refreshDashboard();
      announceSuccess("Projekt został usunięty.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się usunąć projektu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleChangePassword(payload: ChangePasswordRequest) {
    setPending(true);
    setError(null);
    try {
      const { token } = await changePassword(payload);
      // Zmiana hasla uniewaznia stare sesje - zapisujemy nowy token, zeby nie wylogowac biezacego urzadzenia.
      if (token) {
        window.localStorage.setItem(AUTH_TOKEN_KEY, token);
      }
      announceSuccess("Hasło zostało zmienione.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zmienić hasła.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateMyProfile(payload: UpdateMyProfileRequest) {
    setPending(true);
    setError(null);
    try {
      const response = await updateMyProfile(payload);
      setSessionUser(response.user);
      window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(response.user));
      announceSuccess("Dane zostały zapisane.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zapisać danych.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  function handleUpdateSessionUser(user: SessionUser) {
    setSessionUser(user);
    window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  }

  async function handleReplyToTicket(ticketId: string, payload: AddTicketMessageRequest) {
    setPending(true);
    setError(null);
    try {
      const response = await addTicketMessage(ticketId, payload);
      await refreshDashboard();
      announceSuccess("Odpowiedź została wysłana.");
      return response.ticket;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się wysłać odpowiedzi.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleCloseTicket(ticketId: string) {
    setPending(true);
    setError(null);
    try {
      const response = await closeTicket(ticketId);
      await refreshDashboard();
      announceSuccess("Ticket został zamknięty.");
      return response.ticket;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zamknąć ticketu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleReopenTicket(ticketId: string) {
    setPending(true);
    setError(null);
    try {
      const response = await reopenTicket(ticketId);
      await refreshDashboard();
      announceSuccess("Ticket został otwarty ponownie.");
      return response.ticket;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się otworzyć ticketu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleDeleteTask(taskId: string) {
    setPending(true);
    setError(null);
    try {
      await deleteTask(taskId);
      await refreshDashboard();
      announceSuccess("Zadanie zostało usunięte.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się usunąć zadania.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateRecurringSeries(seriesId: string, payload: UpdateRecurringSeriesRequest) {
    setPending(true);
    setError(null);
    try {
      await updateRecurringSeries(seriesId, payload);
      await refreshDashboard();
      announceSuccess("Seria zadań cyklicznych została zaktualizowana.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zaktualizować serii zadań cyklicznych.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleDeleteRecurringSeries(seriesId: string) {
    setPending(true);
    setError(null);
    try {
      await deleteRecurringSeries(seriesId);
      await refreshDashboard();
      announceSuccess("Seria zadań cyklicznych została usunięta.");
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się usunąć serii zadań cyklicznych.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleStartSession(taskId: string) {
    setPending(true);
    setError(null);
    try {
      const { task, session } = await startTaskSession(taskId);
      patchEmployeeTask(task, session);
      refreshDashboardInBackground();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się uruchomić trackera.");
    } finally {
      setPending(false);
    }
  }

  async function handleStopSession(sessionId: string) {
    setPending(true);
    setError(null);
    try {
      const { task, session } = await stopTaskSession(sessionId);
      patchEmployeeTask(task, session);
      refreshDashboardInBackground();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zatrzymać trackera.");
    } finally {
      setPending(false);
    }
  }

  async function handleAddManualWorkSession(taskId: string, payload: AddManualWorkSessionRequest) {
    setPending(true);
    setError(null);
    try {
      const { task } = await addManualWorkSession(taskId, payload);
      patchEmployeeTask(task);
      refreshDashboardInBackground();
      announceSuccess("Czas został dodany.");
      return task;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się dodać czasu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleDeleteWorkSession(taskId: string, sessionId: string) {
    setPending(true);
    setError(null);
    try {
      const { task } = await deleteWorkSession(taskId, sessionId);
      await refreshDashboard();
      announceSuccess("Wpis czasu został usunięty.");
      return task;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się usunąć wpisu czasu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateWorkSession(taskId: string, sessionId: string, payload: UpdateWorkSessionRequest) {
    setPending(true);
    setError(null);
    try {
      const { task } = await updateWorkSession(taskId, sessionId, payload);
      await refreshDashboard();
      announceSuccess("Wpis czasu został zaktualizowany.");
      return task;
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zaktualizować wpisu czasu.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleUpdateTask(taskId: string, payload: UpdateTaskRequest) {
    setPending(true);
    setError(null);
    try {
      await updateTask(taskId, payload);
      await refreshDashboard();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zaktualizować zadania.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  async function handleReorderTask(taskId: string, payload: ReorderTaskRequest) {
    setPending(true);
    setError(null);
    try {
      await reorderTask(taskId, payload);
      await refreshDashboard();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "Nie udało się zmienić kolejności zadania.");
      throw actionError;
    } finally {
      setPending(false);
    }
  }

  if (bootstrapping) {
    return (
      <div className="login-shell">
        <p className="placeholder-copy">Wczytywanie panelu...</p>
      </div>
    );
  }

  if (!sessionUser || !dashboard) {
    return (
      <LoginScreen
        error={error}
        onForgotPassword={handleForgotPassword}
        onLogin={handleLogin}
        onResetPassword={handleResetPassword}
        pending={pending}
      />
    );
  }

  return (
    <ErrorBoundary>
    <DashboardView
      activeItem={activeMenuItem}
      activeRole={activeRole}
      onChangePassword={handleChangePassword}
      onCreateClient={handleCreateClient}
      onCreateDepartment={handleCreateDepartment}
      onCreateEmployee={handleCreateEmployee}
      onCreateProject={handleCreateProject}
      onCreateTicket={handleCreateTicket}
      onCreateTaskComment={handleCreateTaskComment}
      dashboard={dashboard}
      onLoadTaskDetail={handleLoadTaskDetail}
      onMenuSelect={handleMenuSelect}
      openTaskId={openTaskId}
      onOpenTaskPopup={handleOpenTaskPopup}
      onCloseTaskPopup={handleCloseTaskPopup}
      onCreateTask={handleCreateTask}
      onAddWorkSession={handleAddManualWorkSession}
      onDeleteWorkSession={handleDeleteWorkSession}
      onUpdateWorkSession={handleUpdateWorkSession}
      onLogout={handleLogout}
      onLoadTaskComments={handleLoadTaskComments}
      onCloseTicket={handleCloseTicket}
      onReopenTicket={handleReopenTicket}
      onDeleteClient={handleDeleteClient}
      onDeleteEmployee={handleDeleteEmployee}
      onDeleteProject={handleDeleteProject}
      onDeleteTask={handleDeleteTask}
      onDeleteRecurringSeries={handleDeleteRecurringSeries}
      onReorderTask={handleReorderTask}
      onUpdateRecurringSeries={handleUpdateRecurringSeries}
      onReplyToTicket={handleReplyToTicket}
      onSelectTicket={handleSelectTicket}
      onStartSession={handleStartSession}
      onStopSession={handleStopSession}
      onUpdateTask={handleUpdateTask}
      onUpdateClientContacts={handleUpdateClientContacts}
      onUpdateClientProfile={handleUpdateClientProfile}
      onUpdateClient={handleUpdateClient}
      onUpdateEmployee={handleUpdateEmployee}
      onUpdateProfile={handleUpdateMyProfile}
      onUpdateProject={handleUpdateProject}
      onUpdateSessionUser={handleUpdateSessionUser}
      onSendEmployeePasswordResetLink={handleSendEmployeePasswordResetLink}
      onSetEmployeePassword={handleSetEmployeePassword}
      error={error}
      successMessage={successMessage}
      sessionUser={sessionUser}
    />
    </ErrorBoundary>
  );
}
